import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  IconButton,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBack,
  Home,
  AccountTree,
  Assessment,
  CheckCircleOutline,
  Close,
  CloudUpload,
  Delete,
  DeleteOutline,
  Download,
  DriveFileRenameOutline,
  ErrorOutline,
  ExpandLess,
  ExpandMore,
  FolderOpen,
  Hub,
  Info,
  LinkOff,
  OpenInNew,
  UploadFile,
  Refresh,
  Search,
  Queue,
  Logout,
  Translate,
  VisibilityOutlined,
  Workspaces,
  ZoomIn,
  ZoomOut,
  CenterFocusStrong,
  DataObject,
} from '@mui/icons-material';
import { grey, blue, orange } from '@mui/material/colors';
import ForceGraph2D from 'react-force-graph-2d';
import {
  applyProjectOntologies,
  applyProjectVs1,
  createProject,
  deleteProject,
  deleteProjectDocument,
  loadProjectOntologies,
  loadProjectViews,
  loadProjects,
  loadProjectViewSections,
  updateProjectEntityApplicability,
  analyzeProjectObjectApplicability,
  analyzeProjectRequirementApplicability,
  loadProjectObjectCandidateLocations,
  loadProjectDocumentPageContent,
  renameProject,
  loadProjectDocumentsGraph,
  uploadProjectOntology,
  uploadProjectViewFile,
  getIngestionUploadProgress,
  cancelIngestionUpload,
  downloadProjectViewTTL,
  downloadProjectOntologyTTL,
  generateProjectDocumentsGraph,
} from './api/projectApi';
import { analysisApi } from './api/analysisApi';
import EntityGraphDialog from './components/dialogs/EntityGraphDialog';
import { getIngestionDocumentGraph, viewIngestionDocument } from './api/fileApi';

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 Bytes';
  const base = 1024;
  const units = ['Bytes', 'KB', 'MB', 'GB'];
  const unitIndex = Math.floor(Math.log(bytes) / Math.log(base));
  return `${Math.round((bytes / Math.pow(base, unitIndex)) * 100) / 100} ${units[unitIndex]}`;
};

const isLikelyUrl = (value) => /^https?:\/\//i.test(String(value || '').trim());

const escapeRegExp = (value) => String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const triggerBlobDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

const isSupportedDocumentUploadFileName = (name) => /\.(pdf|docx)$/i.test(String(name || '').trim());

const normalizeDocumentNameForSort = (name) => (
  String(name || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
);

const normalizeEntityNameForComparison = (name) => (
  String(name || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
);

const getComparisonTokenStem = (token) => {
  const normalizedToken = normalizeEntityNameForComparison(token);
  if (!normalizedToken) return '';

  const suffixes = ['ations', 'ation', 'ments', 'ment', 'ings', 'ing', 'ers', 'er', 'eds', 'ed', 'ions', 'ion', 'ies', 'es', 's'];
  for (const suffix of suffixes) {
    if (normalizedToken.length - suffix.length >= 4 && normalizedToken.endsWith(suffix)) {
      return normalizedToken.slice(0, -suffix.length);
    }
  }

  return normalizedToken;
};

const buildComparisonTokenSet = (value) => new Set(
  normalizeEntityNameForComparison(value)
    .split(' ')
    .map((token) => getComparisonTokenStem(token))
    .filter(Boolean)
);

const calculateLevenshteinDistance = (left, right) => {
  const source = String(left || '');
  const target = String(right || '');

  if (source === target) return 0;
  if (!source.length) return target.length;
  if (!target.length) return source.length;

  const previous = Array.from({ length: target.length + 1 }, (_, index) => index);
  const current = new Array(target.length + 1).fill(0);

  for (let sourceIndex = 0; sourceIndex < source.length; sourceIndex += 1) {
    current[0] = sourceIndex + 1;

    for (let targetIndex = 0; targetIndex < target.length; targetIndex += 1) {
      const substitutionCost = source[sourceIndex] === target[targetIndex] ? 0 : 1;
      current[targetIndex + 1] = Math.min(
        current[targetIndex] + 1,
        previous[targetIndex + 1] + 1,
        previous[targetIndex] + substitutionCost,
      );
    }

    for (let index = 0; index < previous.length; index += 1) {
      previous[index] = current[index];
    }
  }

  return previous[target.length];
};

const getEntityNameSimilarityMeta = (leftName, rightName) => {
  const left = normalizeEntityNameForComparison(leftName);
  const right = normalizeEntityNameForComparison(rightName);
  const leftTokenSet = buildComparisonTokenSet(leftName);
  const rightTokenSet = buildComparisonTokenSet(rightName);
  const leftStemmed = Array.from(leftTokenSet).join(' ');
  const rightStemmed = Array.from(rightTokenSet).join(' ');

  if (!left || !right) {
    return { isMatch: false, isExact: false, score: 0 };
  }

  if (left === right || (leftStemmed && rightStemmed && leftStemmed === rightStemmed)) {
    return { isMatch: true, isExact: true, score: 1 };
  }

  if (left.length < 4 || right.length < 4) {
    return { isMatch: false, isExact: false, score: 0 };
  }

  const distance = calculateLevenshteinDistance(left, right);
  const longestLength = Math.max(left.length, right.length);
  const sharedPrefixLength = (() => {
    const limit = Math.min(left.length, right.length);
    let prefixLength = 0;
    while (prefixLength < limit && left[prefixLength] === right[prefixLength]) {
      prefixLength += 1;
    }
    return prefixLength;
  })();

  const tokenOverlap = (() => {
    const union = new Set([...leftTokenSet, ...rightTokenSet]);
    if (union.size === 0) return 0;

    let intersectionCount = 0;
    leftTokenSet.forEach((token) => {
      if (rightTokenSet.has(token)) intersectionCount += 1;
    });

    return intersectionCount / union.size;
  })();

  const subsetTokenScore = (() => {
    if (leftTokenSet.size === 0 || rightTokenSet.size === 0) return 0;

    const smallerSet = leftTokenSet.size <= rightTokenSet.size ? leftTokenSet : rightTokenSet;
    const largerSet = smallerSet === leftTokenSet ? rightTokenSet : leftTokenSet;
    let matchedTokenCount = 0;

    smallerSet.forEach((token) => {
      if (largerSet.has(token)) matchedTokenCount += 1;
    });

    if (matchedTokenCount === 0) return 0;
    return matchedTokenCount / smallerSet.size;
  })();

  const stemDistanceScore = (() => {
    if (!leftStemmed || !rightStemmed) return 0;
    const stemDistance = calculateLevenshteinDistance(leftStemmed, rightStemmed);
    const longestStemLength = Math.max(leftStemmed.length, rightStemmed.length, 1);
    return 1 - (stemDistance / longestStemLength);
  })();

  const containmentScore = left.includes(right) || right.includes(left)
    ? Math.min(left.length, right.length) / longestLength
    : 0;
  const stemContainmentScore = leftStemmed.includes(rightStemmed) || rightStemmed.includes(leftStemmed)
    ? Math.min(leftStemmed.length, rightStemmed.length) / Math.max(leftStemmed.length, rightStemmed.length, 1)
    : 0;
  const prefixScore = sharedPrefixLength / longestLength;
  const distanceScore = 1 - (distance / longestLength);
  const score = Math.max(distanceScore, stemDistanceScore, tokenOverlap, subsetTokenScore, containmentScore, stemContainmentScore, prefixScore * 0.92);

  const isMatch = score >= 0.9
    || (longestLength <= 12 && distance <= 2 && score >= 0.72)
    || (longestLength <= 20 && distance <= 3 && score >= 0.78)
    || (tokenOverlap >= 0.75 && Math.max(distanceScore, stemDistanceScore) >= 0.74)
    || (tokenOverlap >= 0.5 && stemDistanceScore >= 0.82)
    || (subsetTokenScore === 1 && Math.max(stemDistanceScore, stemContainmentScore) >= 0.58);

  return { isMatch, isExact: false, score };
};

const getRowEntityComparisonName = (row) => {
  const payload = row?.data && typeof row.data === 'object' ? row.data : {};
  const candidates = [
    row?.name_en,
    row?.name_english,
    payload.name_en,
    payload.name_english,
    payload.label_en,
    payload.label_english,
    payload.title_en,
    payload.title_english,
    payload.fieldName_en,
    payload.fieldName_english,
    row?.name,
    payload.name,
    payload.label,
    payload.title,
    payload.fieldName,
    payload.notation,
  ];

  return String(candidates.find((value) => String(value || '').trim()) || '').trim();
};

const extractObjectNameFromDescrText = (value) => {
  const rawText = String(value || '').trim();
  if (!rawText) return '';

  const codePrefixMatch = rawText.match(/^[\s-]*[A-Za-z0-9]+(?:-[A-Za-z0-9]+)*(?:\s*-\s*|\s+)/);
  const withoutPrefix = codePrefixMatch
    ? rawText.slice(codePrefixMatch[0].length).trim()
    : rawText;

  return withoutPrefix
    .replace(/^[-\s]+/, '')
    .replace(/\s+/g, ' ')
    .trim();
};

const normalizeExcelHeaderKey = (value) => (
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '')
);

const resolveExpectedApplicabilityFromExcelValue = (value) => {
  const normalizedValue = normalizeMetricText(value)
    .replace(/[\s/_-]+/g, ' ')
    .trim();

  if (!normalizedValue) return null;
  if (/\bno\s*aplica\b/.test(normalizedValue) || /\bnot\s*applicable\b/.test(normalizedValue)) {
    return false;
  }
  if (/\baplica\b/.test(normalizedValue) || /\bapplicable\b/.test(normalizedValue)) {
    return true;
  }
  return null;
};

const getObjectOriginalNameForComparison = (row) => {
  const payload = row?.data && typeof row.data === 'object' ? row.data : {};
  const candidates = [
    payload.label,
    payload.name,
    payload.title,
    payload.fieldName,
    row?.name,
    payload.notation,
    row?.name_en,
    payload.name_en,
    payload.label_en,
  ];

  const firstAvailable = String(candidates.find((item) => String(item || '').trim()) || '').trim();
  return extractObjectNameFromDescrText(firstAvailable);
};

const isMainRequirementsDocumentName = (name) => {
  const normalizedName = normalizeDocumentNameForSort(name);
  return normalizedName.includes('vraagspecificatie')
    || /requirements?\s*specification/.test(normalizedName);
};

const extractDocumentPartOrder = (name) => {
  const normalizedName = normalizeDocumentNameForSort(name);
  const partMatch = normalizedName.match(/\b(?:deel|part)\s*[-_\s]*0*(\d+)\b/i);
  if (!partMatch) {
    return Number.POSITIVE_INFINITY;
  }

  const parsedPart = Number(partMatch[1]);
  return Number.isFinite(parsedPart) ? parsedPart : Number.POSITIVE_INFINITY;
};

const sortDocumentUploadFiles = (files) => (
  (Array.isArray(files) ? files : [])
    .map((file, index) => ({
      file,
      index,
      isMainRequirements: isMainRequirementsDocumentName(file?.name),
      partOrder: extractDocumentPartOrder(file?.name),
      normalizedName: normalizeDocumentNameForSort(file?.name),
    }))
    .sort((left, right) => {
      if (left.isMainRequirements !== right.isMainRequirements) {
        return left.isMainRequirements ? -1 : 1;
      }

      if (left.isMainRequirements && right.isMainRequirements && left.partOrder !== right.partOrder) {
        return left.partOrder - right.partOrder;
      }

      if (left.normalizedName !== right.normalizedName) {
        return left.normalizedName.localeCompare(right.normalizedName, undefined, { numeric: true });
      }

      return left.index - right.index;
    })
    .map((item) => item.file)
);

const DOCUMENT_UPLOAD_POLL_BASE_INTERVAL_MS = 2000;
const DOCUMENT_UPLOAD_POLL_MAX_INTERVAL_MS = 10000;
const DOCUMENT_UPLOAD_POLL_MAX_TRANSIENT_ERRORS = 5;
const DOCUMENT_UPLOAD_INTER_FILE_DELAY_MS = 500;
const PROJECT_GRAPH_POLL_INTERVAL_MS = 4000;

const DOCUMENT_UPLOAD_STORAGE_KEY_PREFIX = 'tennet.projects.documents.queue';

const buildDocumentQueueStorageKey = (projectId) => {
  const normalizedProjectId = Number(projectId || 0);
  if (!normalizedProjectId) return '';
  return `${DOCUMENT_UPLOAD_STORAGE_KEY_PREFIX}.${normalizedProjectId}`;
};

const toUploadItemsSnapshot = (items) => (
  Array.isArray(items)
    ? items.map((item) => ({
      id: String(item?.id || ''),
      fileIndex: Number(item?.fileIndex || 0),
      name: String(item?.name || ''),
      taskId: String(item?.taskId || ''),
      status: String(item?.status || 'pending'),
      progress: Number(item?.progress || 0),
      step: String(item?.step || ''),
      pagesProcessed: Number(item?.pagesProcessed || 0),
      pagesTotal: Number(item?.pagesTotal || 0),
      error: String(item?.error || ''),
      file: null,
    }))
    : []
);

const formatDocumentUploadError = (error, fileName = '') => {
  const responseError = String(error?.response?.data?.error || '').trim();
  const responseMessage = String(error?.response?.data?.message || '').trim();
  const rawMessage = responseError || responseMessage || String(error?.message || '').trim();
  const lowered = rawMessage.toLowerCase();
  const fileLabel = String(fileName || '').trim();
  const prefix = fileLabel ? `${fileLabel}: ` : '';

  if (!rawMessage) {
    return `${prefix}Upload failed. Please try again.`;
  }

  if (/already\s+uploaded/.test(lowered)) {
    return `${prefix}File already exists in the platform.`;
  }
  if (/timeout/.test(lowered)) {
    return `${prefix}Processing timed out on server. Try again in a moment.`;
  }
  if (/unknown document_uri/.test(lowered)) {
    return `${prefix}Document URI is not linked to this project. Apply VS1/ontologies first.`;
  }
  if (/in use|being used by another process|permission denied|resource busy|file is locked/.test(lowered)) {
    return `${prefix}The file appears to be open or locked. Close it and try uploading again.`;
  }
  if (/network|failed to fetch|network error/.test(lowered)) {
    return `${prefix}Network error while uploading. Check connection and retry.`;
  }

  return `${prefix}${rawMessage}`;
};

const delayDocumentUpload = (milliseconds) => new Promise((resolve) => {
  window.setTimeout(resolve, milliseconds);
});

const normalizeGraphPayloadForViewer = (graphPayload) => {
  const graph = graphPayload && typeof graphPayload === 'object' ? graphPayload : {};
  const nodes = Array.isArray(graph.nodes)
    ? graph.nodes.map((node, index) => ({
      id: String(node?.id ?? `node-${index}`),
      label: String(node?.label || node?.name || node?.canonical_name || node?.id || `Node ${index + 1}`),
      entity_type: String(node?.entity_type || node?.type || 'entity'),
      raw: node,
    }))
    : [];

  const nodeIds = new Set(nodes.map((node) => node.id));
  const links = Array.isArray(graph.edges)
    ? graph.edges
      .map((edge, index) => {
        const source = String(edge?.source ?? '');
        const target = String(edge?.target ?? '');
        if (!source || !target || !nodeIds.has(source) || !nodeIds.has(target)) {
          return null;
        }

        return {
          id: String(edge?.id ?? `edge-${index}`),
          source,
          target,
          label: String(edge?.label || edge?.relation_type || ''),
          raw: edge,
        };
      })
      .filter(Boolean)
    : [];

  return { nodes, links };
};

const pickStableColorFromText = (text, palette) => {
  const safeText = String(text || 'default').trim().toLowerCase();
  let hash = 0;
  for (let index = 0; index < safeText.length; index += 1) {
    hash = ((hash << 5) - hash) + safeText.charCodeAt(index);
    hash |= 0;
  }
  return palette[Math.abs(hash) % palette.length];
};

const GRAPH_NODE_TYPE_COLORS = {
  requirement: '#42a5f5',
  class: '#66bb6a',
  object: '#ffa726',
  verification: '#ab47bc',
  entity: '#78909c',
};

const GRAPH_NODE_FALLBACK_PALETTE = ['#26a69a', '#5c6bc0', '#29b6f6', '#8d6e63', '#ef5350', '#7cb342'];
const GRAPH_LINK_FALLBACK_PALETTE = ['#546e7a', '#fb8c00', '#8e24aa', '#1e88e5', '#43a047', '#6d4c41'];

const DOCUMENTS_GRAPH_RENDER_PRESETS = {
  compact: { maxNodes: 450, maxLinks: 900 },
  extended: { maxNodes: 900, maxLinks: 1800 },
  max: { maxNodes: 1600, maxLinks: 3200 },
  full: { maxNodes: Number.POSITIVE_INFINITY, maxLinks: Number.POSITIVE_INFINITY },
};

const DOCUMENTS_GRAPH_SEARCH_LIMITS = { maxNodes: 1200, maxLinks: 2400 };

const getDocumentsGraphRenderLimits = ({ preset = 'compact', hasSearch = false, focusMode = 'all' }) => {
  if (focusMode === 'neighbors') {
    return DOCUMENTS_GRAPH_RENDER_PRESETS.max;
  }

  if (hasSearch) {
    return DOCUMENTS_GRAPH_SEARCH_LIMITS;
  }

  return DOCUMENTS_GRAPH_RENDER_PRESETS[preset] || DOCUMENTS_GRAPH_RENDER_PRESETS.compact;
};

const prioritizeDocumentsGraphData = ({
  nodes,
  links,
  maxNodes,
  maxLinks,
  forcedNodeIds,
}) => {
  const safeNodes = Array.isArray(nodes) ? nodes : [];
  const safeLinks = Array.isArray(links) ? links : [];
  const safeMaxNodes = Math.max(1, Number(maxNodes) || 1);
  const safeMaxLinks = Math.max(0, Number(maxLinks) || 0);
  const forcedIds = forcedNodeIds instanceof Set ? forcedNodeIds : new Set();

  if (safeNodes.length <= safeMaxNodes && safeLinks.length <= safeMaxLinks) {
    return {
      nodes: safeNodes,
      links: safeLinks,
      isLimited: false,
      droppedNodes: 0,
      droppedLinks: 0,
    };
  }

  const degreeById = new Map();
  for (const link of safeLinks) {
    const sourceId = String(typeof link?.source === 'object' ? link?.source?.id : link?.source || '');
    const targetId = String(typeof link?.target === 'object' ? link?.target?.id : link?.target || '');
    if (!sourceId || !targetId) continue;
    degreeById.set(sourceId, (degreeById.get(sourceId) || 0) + 1);
    degreeById.set(targetId, (degreeById.get(targetId) || 0) + 1);
  }

  const orderedNodes = safeNodes
    .map((node, index) => {
      const nodeId = String(node?.id || '');
      const degreeScore = degreeById.get(nodeId) || 0;
      const priorityScore = forcedIds.has(nodeId) ? 1_000_000 : 0;
      return { node, index, nodeId, score: priorityScore + degreeScore };
    })
    .sort((left, right) => {
      if (right.score !== left.score) return right.score - left.score;
      return left.index - right.index;
    });

  const keptNodeIds = new Set();
  const keepUntil = Math.max(safeMaxNodes, forcedIds.size);

  for (const nodeId of forcedIds) {
    if (nodeId) keptNodeIds.add(String(nodeId));
  }

  for (const entry of orderedNodes) {
    if (keptNodeIds.size >= keepUntil) break;
    if (!entry.nodeId) continue;
    keptNodeIds.add(entry.nodeId);
  }

  const keptNodes = safeNodes.filter((node) => keptNodeIds.has(String(node?.id || '')));

  const candidateLinks = safeLinks.filter((link) => {
    const sourceId = String(typeof link?.source === 'object' ? link?.source?.id : link?.source || '');
    const targetId = String(typeof link?.target === 'object' ? link?.target?.id : link?.target || '');
    return keptNodeIds.has(sourceId) && keptNodeIds.has(targetId);
  });

  const trimmedLinks = candidateLinks.length <= safeMaxLinks
    ? candidateLinks
    : candidateLinks
      .map((link, index) => {
        const sourceId = String(typeof link?.source === 'object' ? link?.source?.id : link?.source || '');
        const targetId = String(typeof link?.target === 'object' ? link?.target?.id : link?.target || '');
        const confidence = Number(link?.confidence ?? 0.5);
        const topologyScore = (degreeById.get(sourceId) || 0) + (degreeById.get(targetId) || 0);
        return {
          link,
          index,
          score: confidence * 10 + topologyScore,
        };
      })
      .sort((left, right) => {
        if (right.score !== left.score) return right.score - left.score;
        return left.index - right.index;
      })
      .slice(0, safeMaxLinks)
      .map((entry) => entry.link);

  const linkedNodeIds = new Set();
  for (const link of trimmedLinks) {
    linkedNodeIds.add(String(typeof link?.source === 'object' ? link?.source?.id : link?.source || ''));
    linkedNodeIds.add(String(typeof link?.target === 'object' ? link?.target?.id : link?.target || ''));
  }
  for (const nodeId of forcedIds) {
    if (nodeId) linkedNodeIds.add(String(nodeId));
  }

  const trimmedNodes = keptNodes.filter((node) => {
    if (trimmedLinks.length === 0) return true;
    return linkedNodeIds.has(String(node?.id || ''));
  });

  return {
    nodes: trimmedNodes,
    links: trimmedLinks,
    isLimited: trimmedNodes.length < safeNodes.length || trimmedLinks.length < safeLinks.length,
    droppedNodes: Math.max(0, safeNodes.length - trimmedNodes.length),
    droppedLinks: Math.max(0, safeLinks.length - trimmedLinks.length),
  };
};

const buildStaticDocumentsGraphLayout = ({ nodes, links, width, height }) => {
  const safeNodes = Array.isArray(nodes) ? nodes : [];
  const safeLinks = Array.isArray(links) ? links : [];
  const layoutWidth = Math.max(320, Number(width) || 0);
  const layoutHeight = Math.max(320, Number(height) || 0);

  if (safeNodes.length === 0) {
    return { nodes: [], links: safeLinks };
  }

  const degreeById = new Map();
  for (const link of safeLinks) {
    const sourceId = String(typeof link?.source === 'object' ? link?.source?.id : link?.source || '');
    const targetId = String(typeof link?.target === 'object' ? link?.target?.id : link?.target || '');
    if (!sourceId || !targetId) continue;
    degreeById.set(sourceId, (degreeById.get(sourceId) || 0) + 1);
    degreeById.set(targetId, (degreeById.get(targetId) || 0) + 1);
  }

  const sortedNodes = [...safeNodes].sort((left, right) => {
    const leftDegree = degreeById.get(String(left?.id || '')) || 0;
    const rightDegree = degreeById.get(String(right?.id || '')) || 0;
    if (rightDegree !== leftDegree) return rightDegree - leftDegree;
    return String(left?.label || left?.id || '').localeCompare(String(right?.label || right?.id || ''));
  });

  const centerX = layoutWidth / 2;
  const centerY = layoutHeight / 2;
  const ringCount = Math.max(1, Math.ceil(Math.sqrt(sortedNodes.length) / 2));
  const ringSpacing = Math.max(38, Math.min(layoutWidth, layoutHeight) / (2 * ringCount + 2));

  const positionedNodes = sortedNodes.map((node, index) => {
    const normalizedIndex = index + 1;
    const ringIndex = normalizedIndex === 1 ? 0 : Math.floor(Math.sqrt(normalizedIndex - 1));
    const nodesInRing = ringIndex === 0 ? 1 : Math.max(6, ringIndex * 8);
    const positionInRing = ringIndex === 0 ? 0 : (normalizedIndex - 1) - (ringIndex * ringIndex);
    const angle = ringIndex === 0 ? 0 : ((positionInRing % nodesInRing) / nodesInRing) * Math.PI * 2;
    const radius = ringIndex * ringSpacing;
    const x = centerX + (Math.cos(angle) * radius);
    const y = centerY + (Math.sin(angle) * radius);

    return {
      ...node,
      x,
      y,
      fx: x,
      fy: y,
    };
  });

  return {
    nodes: positionedNodes,
    links: safeLinks,
  };
};

const resolveGraphNodeTypeLabel = (node) => {
  const rawEntityType = String(node?.entity_type || '').trim();
  if (!rawEntityType) {
    return 'Entity';
  }

  return humanizeEntityType(rawEntityType) || 'Entity';
};

const resolveGraphNodeColor = (nodeTypeLabel) => {
  const normalizedType = String(nodeTypeLabel || '').toLowerCase();
  if (normalizedType.includes('requirement')) return GRAPH_NODE_TYPE_COLORS.requirement;
  if (normalizedType.includes('class')) return GRAPH_NODE_TYPE_COLORS.class;
  if (normalizedType.includes('object')) return GRAPH_NODE_TYPE_COLORS.object;
  if (normalizedType.includes('verification')) return GRAPH_NODE_TYPE_COLORS.verification;
  if (normalizedType.includes('entity')) return GRAPH_NODE_TYPE_COLORS.entity;
  return pickStableColorFromText(nodeTypeLabel, GRAPH_NODE_FALLBACK_PALETTE);
};

const resolveGraphLinkColor = (relationLabel) => {
  const normalizedLabel = String(relationLabel || '').trim();
  const knownColor = GRAPH_EDGE_COLORS[normalizedLabel];
  if (knownColor) {
    return knownColor;
  }
  return pickStableColorFromText(normalizedLabel || 'related', GRAPH_LINK_FALLBACK_PALETTE);
};

const decorateAndFilterDocumentsGraph = (graphData, rawSearchTerm) => {
  const safeData = graphData && typeof graphData === 'object' ? graphData : { nodes: [], links: [] };
  const sourceNodes = Array.isArray(safeData.nodes) ? safeData.nodes : [];
  const sourceLinks = Array.isArray(safeData.links) ? safeData.links : [];
  const searchTerm = String(rawSearchTerm || '').trim().toLowerCase();

  const nodeMap = new Map();
  const decoratedNodes = sourceNodes.map((node, index) => {
    const nodeId = String(node?.id ?? `node-${index}`);
    const nodeType = resolveGraphNodeTypeLabel(node);
    const decoratedNode = {
      ...node,
      id: nodeId,
      nodeType,
      color: resolveGraphNodeColor(nodeType),
    };
    nodeMap.set(nodeId, decoratedNode);
    return decoratedNode;
  });

  const decoratedLinks = sourceLinks.map((link, index) => {
    const sourceId = String(link?.source ?? '');
    const targetId = String(link?.target ?? '');
    const relationLabel = String(link?.label || 'related').trim() || 'related';
    return {
      ...link,
      id: String(link?.id ?? `link-${index}`),
      source: sourceId,
      target: targetId,
      relationLabel,
      color: resolveGraphLinkColor(relationLabel),
      sourceLabel: String(nodeMap.get(sourceId)?.label || sourceId),
      targetLabel: String(nodeMap.get(targetId)?.label || targetId),
    };
  });

  if (!searchTerm) {
    return {
      filteredData: { nodes: decoratedNodes, links: decoratedLinks },
      nodeTypeLegend: Array.from(new Set(decoratedNodes.map((node) => node.nodeType))).sort((a, b) => a.localeCompare(b)),
      relationLegend: Array.from(new Set(decoratedLinks.map((link) => link.relationLabel))).sort((a, b) => a.localeCompare(b)),
      counts: { nodes: decoratedNodes.length, links: decoratedLinks.length, sourceNodes: decoratedNodes.length, sourceLinks: decoratedLinks.length },
    };
  }

  const matchedNodeIds = new Set(
    decoratedNodes
      .filter((node) => {
        const searchable = `${node?.label || ''} ${node?.id || ''} ${node?.nodeType || ''}`.toLowerCase();
        return searchable.includes(searchTerm);
      })
      .map((node) => node.id)
  );

  const matchedLinks = decoratedLinks.filter((link) => {
    const searchable = `${link?.relationLabel || ''} ${link?.sourceLabel || ''} ${link?.targetLabel || ''}`.toLowerCase();
    return searchable.includes(searchTerm);
  });

  const visibleNodeIds = new Set(matchedNodeIds);
  matchedLinks.forEach((link) => {
    visibleNodeIds.add(link.source);
    visibleNodeIds.add(link.target);
  });

  const filteredNodes = decoratedNodes.filter((node) => visibleNodeIds.has(node.id));
  const filteredLinks = decoratedLinks.filter((link) => visibleNodeIds.has(link.source) && visibleNodeIds.has(link.target));

  return {
    filteredData: { nodes: filteredNodes, links: filteredLinks },
    nodeTypeLegend: Array.from(new Set(filteredNodes.map((node) => node.nodeType))).sort((a, b) => a.localeCompare(b)),
    relationLegend: Array.from(new Set(filteredLinks.map((link) => link.relationLabel))).sort((a, b) => a.localeCompare(b)),
    counts: { nodes: filteredNodes.length, links: filteredLinks.length, sourceNodes: decoratedNodes.length, sourceLinks: decoratedLinks.length },
  };
};

const isTransientDocumentUploadError = (error) => {
  const statusCode = Number(error?.response?.status || 0);
  if ([404, 408, 425, 429, 500, 502, 503, 504].includes(statusCode)) {
    return true;
  }

  const errorCode = String(error?.code || '').trim().toUpperCase();
  if (errorCode === 'ECONNABORTED' || errorCode === 'ERR_NETWORK') {
    return true;
  }

  const rawMessage = String(error?.message || '').trim().toLowerCase();
  return /network|timeout|temporar|failed to fetch|load failed|socket hang up/.test(rawMessage);
};

const isDocumentProcessingTimeoutError = (error) => {
  const rawMessage = String(error?.message || '').trim().toLowerCase();
  return rawMessage.includes('processing timeout');
};

const isLongRunningAnalysisRequestError = (error) => {
  const statusCode = Number(error?.response?.status || 0);
  if ([408, 499, 500, 502, 503, 504].includes(statusCode)) {
    return true;
  }

  const errorCode = String(error?.code || '').trim().toUpperCase();
  if (errorCode === 'ECONNABORTED' || errorCode === 'ERR_NETWORK') {
    return true;
  }

  const rawMessage = String(error?.message || '').trim().toLowerCase();
  return /timeout|network|socket hang up|failed to fetch|load failed/.test(rawMessage);
};

const pollIngestionDocumentProgress = async (taskId, onProgress, cancelRef) => {
  let transientErrorCount = 0;
  let pollAttemptCount = 0;
  let lastKnownState = {
    status: 'processing',
    progress: 0,
    step: 'Processing on server...',
    processedPages: 0,
    totalPages: 0,
    errorMessage: '',
  };

  while (true) {
    if (cancelRef?.current) {
      throw new Error('UPLOAD_CANCELLED');
    }

    try {
      const progressResponse = await getIngestionUploadProgress(taskId);
      const statusValue = String(progressResponse?.status || '').toLowerCase();
      const nextProgress = Number(progressResponse?.progress_percentage || 0);
      const nextStep = String(progressResponse?.current_step || '').trim();
      const processedPages = Number(progressResponse?.processed_pages || 0);
      const totalPages = Number(progressResponse?.total_pages || 0);

      transientErrorCount = 0;
      pollAttemptCount += 1;
      lastKnownState = {
        status: statusValue,
        progress: Math.min(100, Math.max(0, Math.round(nextProgress))),
        step: nextStep || 'Processing on server...',
        processedPages: Math.max(0, processedPages),
        totalPages: Math.max(0, totalPages),
        errorMessage: String(progressResponse?.error_message || '').trim(),
      };

      onProgress(lastKnownState);

      if (statusValue === 'completed') {
        return;
      }

      if (statusValue === 'failed') {
        throw new Error(progressResponse?.error_message || 'Error processing uploaded document.');
      }

      const nextDelay = Math.min(
        DOCUMENT_UPLOAD_POLL_MAX_INTERVAL_MS,
        DOCUMENT_UPLOAD_POLL_BASE_INTERVAL_MS + Math.floor(pollAttemptCount / 3) * 500,
      );
      await delayDocumentUpload(nextDelay);
    } catch (error) {
      if (!isTransientDocumentUploadError(error)) {
        throw error;
      }

      transientErrorCount += 1;
      if (transientErrorCount > DOCUMENT_UPLOAD_POLL_MAX_TRANSIENT_ERRORS) {
        throw error;
      }

      const retryDelay = Math.min(
        DOCUMENT_UPLOAD_POLL_MAX_INTERVAL_MS,
        DOCUMENT_UPLOAD_POLL_BASE_INTERVAL_MS * (2 ** transientErrorCount),
      );

      onProgress({
        ...lastKnownState,
        step: transientErrorCount === 1
          ? 'Waiting for server status...'
          : `Retrying server status (${transientErrorCount}/${DOCUMENT_UPLOAD_POLL_MAX_TRANSIENT_ERRORS})...`,
        errorMessage: '',
      });

      await delayDocumentUpload(retryDelay);
    }
  }
};

const TRANSLATION_STATUS_CHIP = {
  pending:     { label: 'Translation pending',  color: 'default' },
  translating: { label: 'Translating…',         color: 'info' },
  completed:   { label: 'Translated ✓',          color: 'success' },
  failed:      { label: 'Translation failed',   color: 'error' },
};

const KNOWN_RELATIONSHIP_LABELS = {
  'tennet:hasAsSource': 'Has As Source',
  'tennet:isDerivedFrom': 'Is Derived From',
  'tennet:isDerivedIn': 'Is Derived In',
  'tennet:refersTo': 'Refers To',
  'tennet:hasAsSubject': 'Has As Subject',
  'tennet:isVerificationOf': 'Is Verification Of',
  'nen2660:hasRequirement': 'Has Requirement',
  'nen2660:isPartOf': 'Is Part Of',
  'nen2660:hasPart': 'Has Part',
  'rdfs:subClassOf': 'Subclass Of',
};

const KNOWN_PROPERTY_LABELS = {
  'tennet:hasPhase': 'Phase',
  'tennet:hasVerificationMethod': 'Verification Method',
  'http://data.tennet.eu/def/hasPhase': 'Phase',
  'http://data.tennet.eu/def/hasVerificationMethod': 'Verification Method',
};

const humanizeKey = (key) => {
  if (!key) return '';
  // For full URIs, extract the last path/fragment segment
  let local = key;
  if (key.includes('://')) {
    local = key.split('/').pop().split('#').pop();
  } else if (key.includes(':')) {
    local = key.split(':').pop();
  } else {
    local = key.split('/').pop().split('#').pop();
  }
  return local.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());
};

const toTrimmedText = (value) => String(value ?? '').trim();

const getPrimaryEnglishText = (englishValue, fallbackValue) => {
  const englishText = toTrimmedText(englishValue);
  if (englishText) return englishText;
  return toTrimmedText(fallbackValue);
};

const getSecondaryOriginalText = (englishValue, originalValue) => {
  const englishText = toTrimmedText(englishValue);
  const originalText = toTrimmedText(originalValue);
  if (!englishText || !originalText || englishText === originalText) {
    return '';
  }
  return originalText;
};

const toUniqueNonEmptyValues = (rawValue) => {
  const values = Array.isArray(rawValue) ? rawValue : [rawValue];
  return Array.from(
    new Set(
      values
        .map((value) => toTrimmedText(value))
        .filter(Boolean)
    )
  );
};

const ENTITY_TYPE_LABELS = {
  'Requirement_Project': 'Requirement',
  'Requirement_Ontology': 'Requirement',
  'Class_Project': 'Class',
  'Class_Ontology': 'Class',
  'Object_Project': 'Object',
  'Object': 'Object',
  'VerificationPlan_Project': 'Verification Plan',
  'VerificationPlan_Ontology': 'Verification Plan',
};

const humanizeEntityType = (raw) => ENTITY_TYPE_LABELS[raw] || humanizeKey(raw) || raw;

const GRAPH_NODE_COLORS = {
  self: '#1976d2',
  Requirement: '#42a5f5',
  Class: '#66bb6a',
  Object: '#ffa726',
  'Verification Plan': '#ab47bc',
  default: '#90a4ae',
};

const GRAPH_EDGE_COLORS = {
  'Has As Source': '#FF9800',
  'Is Derived From': '#2196F3',
  'Is Verification Of': '#4CAF50',
  'Is Derived In': '#7E57C2',
  'Has Requirement': '#EF5350',
  'Refers To': '#9E9E9E',
  default: '#78909C',
};

const getPreferredLocalizedValue = (rawValue, preferEnglish = true) => {
  const baseValue = Array.isArray(rawValue) ? rawValue[0] : rawValue;
  const text = String(baseValue || '').trim();
  if (!text) return '';

  const lines = text
    .split(/\r?\n/)
    .map((line) => String(line || '').trim())
    .filter(Boolean);

  if (lines.length <= 1) return text;

  const isMetaLine = (line) => /\(annotationproperty\)/i.test(line);
  const englishLines = lines.filter((line) => !/^nl\s*:/i.test(line) && !isMetaLine(line));
  const dutchLines = lines
    .filter((line) => /^nl\s*:/i.test(line))
    .map((line) => line.replace(/^nl\s*:\s*/i, '').trim())
    .filter(Boolean);

  if (preferEnglish) {
    if (englishLines.length > 0) return englishLines[englishLines.length - 1];
    if (dutchLines.length > 0) return dutchLines[dutchLines.length - 1];
  } else {
    if (dutchLines.length > 0) return dutchLines[dutchLines.length - 1];
    if (englishLines.length > 0) return englishLines[englishLines.length - 1];
  }

  return text;
};

const getRowVakgebied = (row) => {
  const payload = row?.data && typeof row.data === 'object' ? row.data : {};
  const fields = Array.isArray(payload.fields) ? payload.fields : [];
  const vakField = fields.find((f) => f.fieldName === 'Vakgebied');
  if (vakField) {
    const values = vakField.values_en || vakField.values_english || vakField.values;
    const resolved = getPreferredLocalizedValue(values, true);
    if (resolved) return resolved;
  }
  const extra = payload.extra_properties && typeof payload.extra_properties === 'object' ? payload.extra_properties : {};
  // Try _english variant first (human-readable keys)
  const vakKeyEnglish = Object.keys(extra).find((k) => k.toLowerCase().includes('vakgebied') && k.toLowerCase().includes('english'));
  if (vakKeyEnglish) {
    const v = extra[vakKeyEnglish];
    const result = getPreferredLocalizedValue(v, true);
    if (result) return result;
  }
  // Direct key match (human-readable keys)
  const vakKey = Object.keys(extra).find((k) => k.toLowerCase().includes('vakgebied'));
  if (vakKey) {
    const v = extra[vakKey];
    const result = getPreferredLocalizedValue(v, true);
    if (result) return result;
  }
  // URI keys: look up label in _extra_property_labels to find the Vakgebied URI
  const labels = payload._extra_property_labels && typeof payload._extra_property_labels === 'object' ? payload._extra_property_labels : {};
  const vakUri = Object.keys(labels).find((uri) => String(labels[uri]).toLowerCase().includes('vakgebied'));
  if (vakUri && extra[vakUri]) {
    const v = extra[vakUri];
    const result = getPreferredLocalizedValue(v, true);
    if (result) return result;
  }
  return 'Unclassified';
};

const VS_SECTION_DEFINITIONS = [
  { key: 'objects', label: 'Objects' },
  { key: 'classes', label: 'Classes' },
  { key: 'requirements', label: 'Requirements' },
  { key: 'verificationPlans', label: 'Verification Plans' },
];

const createEmptyVsSections = () => ({
  objects: [],
  classes: [],
  requirements: [],
  verificationPlans: [],
});

const createEmptyVs1SectionSelection = () => ({
  objects: null,
  classes: null,
  requirements: null,
  verificationPlans: null,
});

const normalizeMetricText = (value) => String(value ?? '').trim().toLowerCase();

const isTruthyMetricValue = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value > 0;
  if (typeof value === 'string') {
    const normalizedValue = normalizeMetricText(value);
    return ['true', '1', 'yes', 'y'].includes(normalizedValue);
  }
  return false;
};

const getSectionRowValue = (row, key) => {
  if (!row || typeof row !== 'object') return undefined;
  if (row[key] !== undefined && row[key] !== null) return row[key];

  const payload = row.data;
  if (payload && typeof payload === 'object') {
    if (payload[key] !== undefined && payload[key] !== null) {
      return payload[key];
    }
  }

  return undefined;
};

const isRowValidated = (row) => {
  const directFlags = ['is_validated', 'validated', 'validation_completed', 'is_approved'];
  if (directFlags.some((key) => isTruthyMetricValue(getSectionRowValue(row, key)))) {
    return true;
  }

  const statusValue = normalizeMetricText(getSectionRowValue(row, 'validation_status'));
  return ['validated', 'approved', 'completed', 'done', 'success'].includes(statusValue);
};

const isRowApplies = (row) => {
  const directFlags = ['is_applicable', 'applicable', 'applies', 'is_applying'];
  for (const key of directFlags) {
    const rawValue = getSectionRowValue(row, key);
    if (rawValue !== undefined && rawValue !== null && String(rawValue).trim() !== '') {
      return isTruthyMetricValue(rawValue);
    }
  }

  const statusValue = normalizeMetricText(
    getSectionRowValue(row, 'applicability_status')
    || getSectionRowValue(row, 'applicable_status')
    || getSectionRowValue(row, 'apply_status')
  );

  if (['aplica', 'applies', 'applicable', 'yes', 'y', 'true', '1'].includes(statusValue)) {
    return true;
  }
  if (['no_aplica', 'no-aplica', 'no aplica', 'not_applicable', 'not applicable', 'false', '0', 'no', 'n'].includes(statusValue)) {
    return false;
  }

  return null;
};

const isRowIndeterminate = (row) => {
  const statusValue = normalizeMetricText(
    getSectionRowValue(row, 'applicability_status')
    || getSectionRowValue(row, 'applicable_status')
    || getSectionRowValue(row, 'apply_status')
  );

  if (['indeterminate', 'undetermined', 'unknown', 'inconclusive'].includes(statusValue)) {
    return true;
  }

  const finalDecision = getSectionRowValue(row, 'applicability_second_pass_llm_final_decision');
  if (finalDecision && typeof finalDecision === 'object' && Object.prototype.hasOwnProperty.call(finalDecision, 'applies')) {
    return finalDecision.applies === null;
  }

  const llmOverallLabel = normalizeMetricText(getSectionRowValue(row, 'applicability_llm_overall_label'));
  if (llmOverallLabel === 'indeterminate') {
    return true;
  }

  return false;
};

const getRowApplicabilityLabel = (row) => {
  const applies = isRowApplies(row);
  if (applies === true) return 'Applies';
  if (applies === false) return 'Does not apply';
  if (isRowIndeterminate(row)) return 'Indeterminate';
  return 'Not analyzed';
};

const isRowSmartAnalyzed = (row) => {
  const directFlags = ['smart_analysis_completed', 'smart_analyzed', 'is_smart_analyzed'];
  if (directFlags.some((key) => isTruthyMetricValue(getSectionRowValue(row, key)))) {
    return true;
  }

  const statusValue = normalizeMetricText(getSectionRowValue(row, 'smart_status'));
  return ['analyzed', 'analysed', 'completed', 'done', 'success'].includes(statusValue);
};

const getMetricPercentage = (count, total) => {
  if (!total) return '0%';
  return `${Math.round((count / total) * 100)}%`;
};

const getSectionItemLabel = (sectionLabel) => {
  const normalizedLabel = normalizeMetricText(sectionLabel);
  if (!normalizedLabel) {
    return 'item';
  }

  if (normalizedLabel.endsWith('s')) {
    return normalizedLabel.slice(0, -1);
  }

  return normalizedLabel;
};

const getSectionRowPrimaryText = (row) => {
  const payload = row?.data && typeof row.data === 'object' ? row.data : {};

  const candidateValues = [
    payload.label_en,
    payload.name_en,
    payload.title_en,
    payload.fieldName_en,
    payload.label_english,
    payload.name_english,
    payload.title_english,
    payload.fieldName_english,
    payload.label,
    payload.name,
    payload.title,
    payload.fieldName,
    payload.notation,
    payload.requirement_id,
    payload.code,
    row?.name,
  ];

  const selectedValue = candidateValues.find((value) => String(value || '').trim());
  return String(selectedValue || '').trim();
};

const getSectionRowDetailText = (row) => {
  const payload = row?.data && typeof row.data === 'object' ? row.data : {};

  const candidateValues = [
    payload.requirement_text_en,
    payload.text_en,
    payload.description_en,
    payload.comment_en,
    payload.definition_en,
    payload.label_en,
    payload.requirement_text_english,
    payload.requirement_text,
    payload.text_english,
    payload.text,
    payload.description_english,
    payload.description,
    payload.comment_english,
    payload.comment,
    payload.definition_english,
    payload.definition,
    payload.label_english,
    payload.label,
  ];

  const selectedValue = candidateValues.find((value) => String(value || '').trim());
  return String(selectedValue || '').trim();
};

const sanitizeExcelSheetName = (name) => {
  const baseName = String(name || 'Sheet')
    .replace(/[[\\/?*\]:]/g, ' ')
    .trim();
  return (baseName || 'Sheet').slice(0, 31);
};

const getSectionRowOriginalAndTranslatedTerms = (row, sectionKey = '') => {
  const payload = row?.data && typeof row.data === 'object' ? row.data : {};
  const normalizedSectionKey = String(sectionKey || '').trim().toLowerCase();

  const notation = String(payload.notation || '').trim();
  const baseOriginalLabel = String(
    payload.label || payload.name || payload.title || payload.fieldName || row?.name || ''
  ).trim();
  const baseTranslatedLabel = String(
    payload.label_english
    || payload.label_en
    || payload.name_english
    || payload.name_en
    || payload.title_english
    || payload.title_en
    || payload.fieldName_english
    || payload.fieldName_en
    || ''
  ).trim();

  if (normalizedSectionKey === 'objects') {
    const originalObjectText = [notation, baseOriginalLabel].filter(Boolean).join(' ').trim();
    const translatedObjectText = [notation, baseTranslatedLabel].filter(Boolean).join(' ').trim();

    return {
      original: originalObjectText || translatedObjectText || '-',
      translated: translatedObjectText || baseTranslatedLabel || baseOriginalLabel || originalObjectText || '-',
    };
  }

  const translatedCandidates = [
    payload.label_english,
    payload.label_en,
    payload.name_english,
    payload.name_en,
    payload.title_english,
    payload.title_en,
    payload.fieldName_english,
    payload.fieldName_en,
  ];

  const originalCandidates = [
    payload.label,
    payload.name,
    payload.title,
    payload.fieldName,
    row?.name,
    payload.notation,
  ];

  const translated = String(
    translatedCandidates.find((value) => String(value || '').trim()) || ''
  ).trim();

  const original = String(
    originalCandidates.find((value) => String(value || '').trim()) || ''
  ).trim();

  return {
    original: original || translated || '-',
    translated: translated || original || '-',
  };
};

const formatDetailDate = (rawValue) => {
  if (!rawValue) return '-';

  const parsedDate = new Date(rawValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return String(rawValue);
  }

  return parsedDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatVs1ApplySummaryMessage = (applySummary, baseMessage) => {
  const fallbackMessage = baseMessage || 'VS1 applied successfully.';
  if (!applySummary || typeof applySummary !== 'object') {
    return fallbackMessage;
  }

  const createdEntities = Number(applySummary.created_entities || 0);
  const createdRelationships = Number(applySummary.created_entity_relationships || 0);
  const removedEntities = Number(applySummary.removed_entities || 0);
  const removedRelationships = Number(applySummary.removed_entity_relationships || 0);

  return `${fallbackMessage} Created ${createdEntities} entities and ${createdRelationships} relationships; removed ${removedEntities} entities and ${removedRelationships} relationships.`;
};

const formatOntologiesApplySummaryMessage = (viewKey, applySummary, baseMessage) => {
  const normalizedView = String(viewKey || '').trim().toUpperCase() || 'VS1';
  const fallbackMessage = baseMessage || `Ontologies applied successfully for ${normalizedView}.`;

  if (!applySummary || typeof applySummary !== 'object') {
    return fallbackMessage;
  }

  const created = Number(applySummary.created_entities || 0);
  const createdRels = Number(applySummary.created_entity_relationships || 0);
  const removed = Number(applySummary.removed_entities || 0);
  const processed = Number(applySummary.ontologies_processed || 0);
  const skipped = Number(applySummary.ontologies_skipped || 0);

  return `${fallbackMessage} Processed ${processed} ontologies (${skipped} skipped). Created ${created} entities and ${createdRels} relationships; removed ${removed} previous entities.`;
};

const DocumentsStyleGraphViewer = ({ graphData, emptyMessage }) => {
  const graphRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const [graphDimensions, setGraphDimensions] = useState({ w: 0, h: 0 });
  const [inspectionMode, setInspectionMode] = useState('graph');
  const [preferLowMemoryGraph, setPreferLowMemoryGraph] = useState(true);
  const [graphLayoutFrozen, setGraphLayoutFrozen] = useState(false);
  const [visibleNodeTypes, setVisibleNodeTypes] = useState({});
  const [visibleRelationTypes, setVisibleRelationTypes] = useState({});
  const [graphNodeSearchTerm, setGraphNodeSearchTerm] = useState('');
  const [graphMinConfidence, setGraphMinConfidence] = useState('0');
  const [graphLayoutMode, setGraphLayoutMode] = useState('balanced');
  const [graphFocusMode, setGraphFocusMode] = useState('all');
  const [graphRenderPreset, setGraphRenderPreset] = useState('compact');
  const [selectedGraphNode, setSelectedGraphNode] = useState(null);
  const [selectedGraphEdge, setSelectedGraphEdge] = useState(null);
  const [graphHoveredNode, setGraphHoveredNode] = useState(null);
  const [graphHoveredEdge, setGraphHoveredEdge] = useState(null);

  const decoratedGraph = useMemo(() => decorateAndFilterDocumentsGraph(graphData, ''), [graphData]);
  const baseNodes = decoratedGraph.filteredData.nodes || [];
  const baseLinks = decoratedGraph.filteredData.links || [];

  const getEndpointId = useCallback((endpoint) => (typeof endpoint === 'object' ? endpoint?.id : endpoint), []);

  useEffect(() => {
    const nextVisibleTypes = {};
    for (const node of baseNodes) {
      const nodeType = String(node?.nodeType || 'Entity').trim() || 'Entity';
      nextVisibleTypes[nodeType] = true;
    }
    setVisibleNodeTypes(nextVisibleTypes);
  }, [baseNodes]);

  useEffect(() => {
    const nextVisibleRelationTypes = {};
    for (const link of baseLinks) {
      const relationType = String(link?.relationLabel || 'related').trim() || 'related';
      nextVisibleRelationTypes[relationType] = true;
    }
    setVisibleRelationTypes(nextVisibleRelationTypes);
  }, [baseLinks]);

  const typeCounts = useMemo(() => {
    const counts = {};
    for (const node of baseNodes) {
      const typeKey = String(node?.nodeType || 'Entity').trim() || 'Entity';
      counts[typeKey] = (counts[typeKey] || 0) + 1;
    }
    return counts;
  }, [baseNodes]);

  const relationTypeCounts = useMemo(() => {
    const counts = {};
    for (const link of baseLinks) {
      const relationKey = String(link?.relationLabel || 'related').trim() || 'related';
      counts[relationKey] = (counts[relationKey] || 0) + 1;
    }
    return counts;
  }, [baseLinks]);

  const sortedEntityTypes = useMemo(() => Object.keys(typeCounts).sort((a, b) => a.localeCompare(b)), [typeCounts]);
  const sortedRelationTypes = useMemo(() => Object.keys(relationTypeCounts).sort((a, b) => a.localeCompare(b)), [relationTypeCounts]);

  const filteredGraphData = useMemo(() => {
    const minConfidence = Math.max(0, Math.min(1, Number(graphMinConfidence) || 0));

    const nodes = baseNodes.filter((node) => {
      const typeKey = String(node?.nodeType || 'Entity').trim() || 'Entity';
      return Boolean(visibleNodeTypes[typeKey]);
    });
    const allowedNodeIds = new Set(nodes.map((node) => node.id));

    let focusNodeIds = null;
    if (graphFocusMode === 'neighbors' && selectedGraphNode?.id) {
      focusNodeIds = new Set([selectedGraphNode.id]);
      for (const link of baseLinks) {
        const sourceId = getEndpointId(link.source);
        const targetId = getEndpointId(link.target);
        if (sourceId === selectedGraphNode.id) focusNodeIds.add(targetId);
        if (targetId === selectedGraphNode.id) focusNodeIds.add(sourceId);
      }
    }

    const links = baseLinks.filter((link) => {
      const sourceId = getEndpointId(link.source);
      const targetId = getEndpointId(link.target);
      const relationKey = String(link?.relationLabel || 'related').trim() || 'related';
      return (
        allowedNodeIds.has(sourceId)
        && allowedNodeIds.has(targetId)
        && (!focusNodeIds || (focusNodeIds.has(sourceId) && focusNodeIds.has(targetId)))
        && Boolean(visibleRelationTypes[relationKey])
        && Number(link?.confidence ?? 1) >= minConfidence
      );
    });

    const visibleIds = new Set();
    for (const link of links) {
      visibleIds.add(getEndpointId(link.source));
      visibleIds.add(getEndpointId(link.target));
    }

    const filteredNodes = nodes.filter((node) => {
      if (focusNodeIds) return focusNodeIds.has(node.id);
      if (links.length === 0) return true;
      return visibleIds.has(node.id);
    });

    return { nodes: filteredNodes, links };
  }, [baseLinks, baseNodes, getEndpointId, graphFocusMode, graphMinConfidence, selectedGraphNode, visibleNodeTypes, visibleRelationTypes]);

  const normalizedGraphSearchTerm = useMemo(
    () => String(graphNodeSearchTerm || '').trim().toLowerCase(),
    [graphNodeSearchTerm]
  );

  const searchMatchedNodeIds = useMemo(() => {
    if (!normalizedGraphSearchTerm) return new Set();

    return new Set(
      filteredGraphData.nodes
        .filter((node) => `${node?.label || ''} ${node?.id || ''} ${node?.nodeType || ''}`.toLowerCase().includes(normalizedGraphSearchTerm))
        .slice(0, 120)
        .map((node) => node.id)
    );
  }, [filteredGraphData.nodes, normalizedGraphSearchTerm]);

  const renderedGraphMeta = useMemo(() => {
    if (graphRenderPreset === 'full') {
      return {
        nodes: filteredGraphData.nodes,
        links: filteredGraphData.links,
        isLimited: false,
        droppedNodes: 0,
        droppedLinks: 0,
      };
    }

    const limits = getDocumentsGraphRenderLimits({
      preset: graphRenderPreset,
      hasSearch: Boolean(normalizedGraphSearchTerm),
      focusMode: graphFocusMode,
    });

    const forcedNodeIds = new Set(searchMatchedNodeIds);
    if (selectedGraphNode?.id) {
      forcedNodeIds.add(String(selectedGraphNode.id));
    }

    return prioritizeDocumentsGraphData({
      nodes: filteredGraphData.nodes,
      links: filteredGraphData.links,
      maxNodes: limits.maxNodes,
      maxLinks: limits.maxLinks,
      forcedNodeIds,
    });
  }, [filteredGraphData.links, filteredGraphData.nodes, graphFocusMode, graphRenderPreset, normalizedGraphSearchTerm, searchMatchedNodeIds, selectedGraphNode?.id]);

  const renderedGraphData = useMemo(() => ({
    nodes: renderedGraphMeta.nodes,
    links: renderedGraphMeta.links,
  }), [renderedGraphMeta.links, renderedGraphMeta.nodes]);

  const graphSearchMatches = useMemo(() => {
    if (!normalizedGraphSearchTerm) return [];

    return filteredGraphData.nodes
      .filter((node) => `${node?.label || ''} ${node?.id || ''} ${node?.nodeType || ''}`.toLowerCase().includes(normalizedGraphSearchTerm))
      .sort((left, right) => String(left?.label || left?.id || '').localeCompare(String(right?.label || right?.id || '')))
      .slice(0, 220);
  }, [filteredGraphData.nodes, normalizedGraphSearchTerm]);

  const graphNodeMap = useMemo(() => {
    const map = {};
    for (const node of filteredGraphData.nodes) {
      map[node.id] = node;
    }
    return map;
  }, [filteredGraphData.nodes]);

  useEffect(() => {
    if (!selectedGraphNode) return;
    if (!graphNodeMap[selectedGraphNode.id]) {
      setSelectedGraphNode(null);
    }
  }, [graphNodeMap, selectedGraphNode]);

  useEffect(() => {
    if (!selectedGraphEdge) return;
    const exists = filteredGraphData.links.some((link) => link.id === selectedGraphEdge.id);
    if (!exists) {
      setSelectedGraphEdge(null);
    }
  }, [filteredGraphData.links, selectedGraphEdge]);

  const selectedGraphEdgeEndpoints = useMemo(() => {
    if (!selectedGraphEdge) return { source: null, target: null };
    const sourceId = getEndpointId(selectedGraphEdge.source);
    const targetId = getEndpointId(selectedGraphEdge.target);
    return {
      source: graphNodeMap[sourceId] || null,
      target: graphNodeMap[targetId] || null,
    };
  }, [getEndpointId, graphNodeMap, selectedGraphEdge]);

  const selectedGraphNodeRelationships = useMemo(() => {
    if (!selectedGraphNode) return { incoming: [], outgoing: [] };

    const incoming = [];
    const outgoing = [];
    for (const link of filteredGraphData.links) {
      const sourceId = getEndpointId(link.source);
      const targetId = getEndpointId(link.target);

      if (sourceId === selectedGraphNode.id && graphNodeMap[targetId]) {
        outgoing.push({ node: graphNodeMap[targetId], link });
      }
      if (targetId === selectedGraphNode.id && graphNodeMap[sourceId]) {
        incoming.push({ node: graphNodeMap[sourceId], link });
      }
    }

    return { incoming, outgoing };
  }, [filteredGraphData.links, getEndpointId, graphNodeMap, selectedGraphNode]);

  const graphCoverageSummary = useMemo(() => {
    const nodeCount = baseNodes.length;
    const edgeCount = baseLinks.length;
    const isolatedCount = Math.max(0, nodeCount - new Set(
      baseLinks.flatMap((link) => [getEndpointId(link.source), getEndpointId(link.target)])
    ).size);

    return { nodeCount, edgeCount, isolatedCount };
  }, [baseLinks, baseNodes, getEndpointId]);

  const isUnsafeFullGraph = inspectionMode === 'graph' && graphRenderPreset === 'full';
  const isLowMemoryFullGraph = isUnsafeFullGraph && preferLowMemoryGraph;
  const isDenseRenderedGraph = renderedGraphData.nodes.length > 700 || renderedGraphData.links.length > 1400;

  const displayGraphData = useMemo(() => {
    if (!isLowMemoryFullGraph) {
      return renderedGraphData;
    }

    return buildStaticDocumentsGraphLayout({
      nodes: renderedGraphData.nodes,
      links: renderedGraphData.links,
      width: graphDimensions.w,
      height: graphDimensions.h,
    });
  }, [graphDimensions.h, graphDimensions.w, isLowMemoryFullGraph, renderedGraphData]);

  useEffect(() => {
    setGraphLayoutFrozen(false);
  }, [graphRenderPreset, inspectionMode, filteredGraphData.links.length, filteredGraphData.nodes.length, graphLayoutMode, preferLowMemoryGraph]);

  const fullNodesTableRows = useMemo(() => {
    return baseNodes
      .map((node) => ({
        id: String(node?.id || ''),
        label: String(node?.label || node?.id || ''),
        nodeType: String(node?.nodeType || 'Entity'),
      }))
      .sort((left, right) => left.label.localeCompare(right.label));
  }, [baseNodes]);

  const fullLinksTableRows = useMemo(() => {
    return baseLinks
      .map((link) => {
        const sourceId = getEndpointId(link.source);
        const targetId = getEndpointId(link.target);
        return {
          id: String(link?.id || `${sourceId}-${targetId}`),
          relationLabel: String(link?.relationLabel || link?.label || 'related'),
          sourceId,
          sourceLabel: String(graphNodeMap[sourceId]?.label || sourceId),
          targetId,
          targetLabel: String(graphNodeMap[targetId]?.label || targetId),
        };
      })
      .sort((left, right) => {
        const relationCompare = left.relationLabel.localeCompare(right.relationLabel);
        if (relationCompare !== 0) return relationCompare;
        const sourceCompare = left.sourceLabel.localeCompare(right.sourceLabel);
        if (sourceCompare !== 0) return sourceCompare;
        return left.targetLabel.localeCompare(right.targetLabel);
      });
  }, [baseLinks, getEndpointId, graphNodeMap]);

  const handleExportFullGraphJson = useCallback(() => {
    const payload = {
      exported_at: new Date().toISOString(),
      counts: {
        nodes: baseNodes.length,
        links: baseLinks.length,
      },
      nodes: fullNodesTableRows,
      links: fullLinksTableRows,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json;charset=utf-8' });
    triggerBlobDownload(blob, 'documents_graph_full_inspection.json');
  }, [baseLinks.length, baseNodes.length, fullLinksTableRows, fullNodesTableRows]);

  const handleShowAllEntityTypes = useCallback(() => {
    setVisibleNodeTypes((previous) => {
      const next = {};
      for (const key of Object.keys(previous)) next[key] = true;
      return next;
    });
  }, []);

  const handleHideAllEntityTypes = useCallback(() => {
    setVisibleNodeTypes((previous) => {
      const next = {};
      for (const key of Object.keys(previous)) next[key] = false;
      return next;
    });
  }, []);

  const handleShowAllRelationTypes = useCallback(() => {
    setVisibleRelationTypes((previous) => {
      const next = {};
      for (const key of Object.keys(previous)) next[key] = true;
      return next;
    });
  }, []);

  const handleHideAllRelationTypes = useCallback(() => {
    setVisibleRelationTypes((previous) => {
      const next = {};
      for (const key of Object.keys(previous)) next[key] = false;
      return next;
    });
  }, []);

  const handleGraphZoomIn = useCallback(() => {
    if (!graphRef.current) return;
    const currentZoom = Number(graphRef.current.zoom() || 1);
    graphRef.current.zoom(currentZoom * 1.4, 300);
  }, []);

  const handleGraphZoomOut = useCallback(() => {
    if (!graphRef.current) return;
    const currentZoom = Number(graphRef.current.zoom() || 1);
    graphRef.current.zoom(currentZoom / 1.4, 300);
  }, []);

  const handleGraphZoomFit = useCallback(() => {
    graphRef.current?.zoomToFit(400, 40);
  }, []);

  const navigateToGraphNode = useCallback((node) => {
    if (!node) return;
    setSelectedGraphEdge(null);
    setSelectedGraphNode(node);
    if (graphRef.current && node?.x != null && node?.y != null) {
      graphRef.current.centerAt(node.x, node.y, 450);
      graphRef.current.zoom(2.3, 450);
    }
  }, []);

  useEffect(() => {
    if (isLowMemoryFullGraph) return;
    if (!graphRef.current) return;
    const graphApi = graphRef.current;
    const chargeStrength = isLowMemoryFullGraph
      ? -120
      : isUnsafeFullGraph
        ? -180
        : graphLayoutMode === 'compact'
          ? (isDenseRenderedGraph ? -320 : -520)
          : (isDenseRenderedGraph ? -560 : -850);
    const linkDistance = isLowMemoryFullGraph
      ? 34
      : isUnsafeFullGraph
        ? 52
        : graphLayoutMode === 'compact'
          ? (isDenseRenderedGraph ? 92 : 150)
          : (isDenseRenderedGraph ? 160 : 260);
    graphApi.d3Force('charge')?.strength(chargeStrength).distanceMax(isLowMemoryFullGraph ? 120 : (isUnsafeFullGraph ? 220 : (graphLayoutMode === 'compact' ? 520 : 850)));
    graphApi.d3Force('link')?.distance(linkDistance).strength(isLowMemoryFullGraph ? 0.09 : (isUnsafeFullGraph ? 0.16 : (graphLayoutMode === 'compact' ? 0.42 : 0.28)));
    if (!graphLayoutFrozen) {
      graphApi.d3ReheatSimulation?.();
    }
  }, [graphLayoutFrozen, graphLayoutMode, isDenseRenderedGraph, isLowMemoryFullGraph, isUnsafeFullGraph, renderedGraphData.links.length, renderedGraphData.nodes.length]);

  useEffect(() => () => {
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }
  }, []);

  if (!graphData?.nodes?.length) {
    return <Alert severity="info">{emptyMessage}</Alert>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, height: 'min(76dvh, 680px)', minHeight: { xs: 420, lg: 360 }, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
      <Box sx={{ width: { xs: '100%', lg: 220 }, minWidth: 0, maxHeight: { xs: '28%', lg: 'none' }, flexShrink: 0, borderRight: { xs: 'none', lg: '1px solid' }, borderBottom: { xs: '1px solid', lg: 'none' }, borderColor: 'divider', overflow: 'auto', bgcolor: '#fafafa', p: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.5, display: 'block', color: 'text.secondary' }}>
          Inspection Mode
        </Typography>
        <Stack spacing={0.6} sx={{ mb: 1.2 }}>
          <Button size="small" variant={inspectionMode === 'graph' ? 'contained' : 'outlined'} onClick={() => setInspectionMode('graph')}>
            Graph
          </Button>
          <Button size="small" variant={inspectionMode === 'nodes' ? 'contained' : 'outlined'} onClick={() => setInspectionMode('nodes')}>
            All Nodes
          </Button>
          <Button size="small" variant={inspectionMode === 'links' ? 'contained' : 'outlined'} onClick={() => setInspectionMode('links')}>
            All Links
          </Button>
          <Button size="small" variant="outlined" onClick={handleExportFullGraphJson}>
            Export JSON
          </Button>
        </Stack>

        <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.5, display: 'block', color: 'text.secondary' }}>
          Entity Types
        </Typography>
        <Stack direction="row" spacing={0.5} sx={{ mb: 0.5 }}>
          <Button size="small" variant="text" onClick={handleShowAllEntityTypes} sx={{ fontSize: '0.65rem', minWidth: 0, px: 0.75 }}>All</Button>
          <Button size="small" variant="text" onClick={handleHideAllEntityTypes} sx={{ fontSize: '0.65rem', minWidth: 0, px: 0.75 }}>None</Button>
        </Stack>
        <FormGroup>
          {sortedEntityTypes.map((entityType) => (
            <FormControlLabel
              key={entityType}
              sx={{ mr: 0, mb: 0.2, '& .MuiFormControlLabel-label': { ml: 0.25 } }}
              control={(
                <Checkbox
                  size="small"
                  checked={Boolean(visibleNodeTypes[entityType])}
                  onChange={() => setVisibleNodeTypes((previous) => ({ ...previous, [entityType]: !previous[entityType] }))}
                  sx={{ py: 0.1 }}
                />
              )}
              label={(
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: resolveGraphNodeColor(entityType), flexShrink: 0 }} />
                  <Typography variant="caption" sx={{ fontSize: '0.66rem' }} noWrap>{entityType}</Typography>
                  <Chip label={typeCounts[entityType] || 0} size="small" sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700 }} />
                </Stack>
              )}
            />
          ))}
        </FormGroup>

        <Divider sx={{ my: 1 }} />
        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.6, color: 'text.secondary' }}>
          Node Search
        </Typography>
        <TextField
          size="small"
          fullWidth
          placeholder="Search by node name"
          value={graphNodeSearchTerm}
          onChange={(event) => setGraphNodeSearchTerm(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: '1rem', color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          fullWidth
          label="Layout"
          value={graphLayoutMode}
          onChange={(event) => setGraphLayoutMode(event.target.value)}
          sx={{ mt: 0.8 }}
        >
          <MenuItem value="balanced">Balanced</MenuItem>
          <MenuItem value="compact">Compact</MenuItem>
        </TextField>
        <TextField
          size="small"
          fullWidth
          label="Min confidence"
          value={graphMinConfidence}
          onChange={(event) => setGraphMinConfidence(event.target.value)}
          sx={{ mt: 0.8 }}
          helperText="0.0 to 1.0"
        />
        <Stack direction="row" spacing={0.5} sx={{ mt: 0.8 }}>
          <Button size="small" variant={graphFocusMode === 'all' ? 'contained' : 'outlined'} onClick={() => setGraphFocusMode('all')}>Full</Button>
          <Button size="small" variant={graphFocusMode === 'neighbors' ? 'contained' : 'outlined'} onClick={() => setGraphFocusMode('neighbors')} disabled={!selectedGraphNode}>Neighbors</Button>
        </Stack>
        <TextField
          select
          size="small"
          fullWidth
          label="Render density"
          value={graphRenderPreset}
          onChange={(event) => setGraphRenderPreset(event.target.value)}
          sx={{ mt: 0.8 }}
          helperText="Compact improves responsiveness"
        >
          <MenuItem value="compact">Compact</MenuItem>
          <MenuItem value="extended">Extended</MenuItem>
          <MenuItem value="max">Max</MenuItem>
          <MenuItem value="full">Full (unsafe)</MenuItem>
        </TextField>
        {isUnsafeFullGraph && (
          <Alert severity="warning" sx={{ mt: 0.8, py: 0.25 }}>
            Rendering the full graph can consume a lot of RAM and make the tab unstable.
          </Alert>
        )}
        {isUnsafeFullGraph && (
          <FormControlLabel
            sx={{ mt: 0.5, alignItems: 'flex-start' }}
            control={(
              <Checkbox
                size="small"
                checked={preferLowMemoryGraph}
                onChange={(event) => setPreferLowMemoryGraph(event.target.checked)}
              />
            )}
            label={(
              <Typography variant="caption" color="text.secondary">
                Low memory mode
              </Typography>
            )}
          />
        )}
        {isLowMemoryFullGraph && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.2 }}>
            Freezes layout after initial fit and disables node hover/click interactions.
          </Typography>
        )}
        {renderedGraphMeta.isLimited && (
          <Alert severity="info" sx={{ mt: 0.8, py: 0.25 }}>
            {`Optimized rendering active: hidden ${renderedGraphMeta.droppedNodes} nodes and ${renderedGraphMeta.droppedLinks} links.`}
          </Alert>
        )}
        {!normalizedGraphSearchTerm ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.6 }}>
            Type text to list matching nodes.
          </Typography>
        ) : graphSearchMatches.length === 0 ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.6 }}>
            No nodes found.
          </Typography>
        ) : (
          <List dense disablePadding sx={{ maxHeight: 200, overflow: 'auto', mt: 0.6 }}>
            {graphSearchMatches.map((node) => (
              <ListItemButton key={`search-${node.id}`} sx={{ py: 0.3, px: 0.5 }} onClick={() => navigateToGraphNode(node)}>
                <Chip size="small" label={node.nodeType || 'Entity'} sx={{ height: 16, fontSize: '0.58rem', mr: 0.5, bgcolor: node.color || '#607d8b', color: '#fff', fontWeight: 700 }} />
                <Typography variant="caption" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {node.label || node.id}
                </Typography>
              </ListItemButton>
            ))}
          </List>
        )}

        <Divider sx={{ my: 1 }} />
        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5, color: 'text.secondary' }}>
          Relation Types
        </Typography>
        <Stack direction="row" spacing={0.5} sx={{ mb: 0.5 }}>
          <Button size="small" variant="text" onClick={handleShowAllRelationTypes} sx={{ fontSize: '0.65rem', minWidth: 0, px: 0.75 }}>All</Button>
          <Button size="small" variant="text" onClick={handleHideAllRelationTypes} sx={{ fontSize: '0.65rem', minWidth: 0, px: 0.75 }}>None</Button>
        </Stack>
        <FormGroup>
          {sortedRelationTypes.map((relationType) => (
            <FormControlLabel
              key={relationType}
              sx={{ mr: 0, mb: 0.2, '& .MuiFormControlLabel-label': { ml: 0.25 } }}
              control={(
                <Checkbox
                  size="small"
                  checked={Boolean(visibleRelationTypes[relationType])}
                  onChange={() => setVisibleRelationTypes((previous) => ({ ...previous, [relationType]: !previous[relationType] }))}
                  sx={{ py: 0.1 }}
                />
              )}
              label={(
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: resolveGraphLinkColor(relationType), flexShrink: 0 }} />
                  <Typography variant="caption" sx={{ fontSize: '0.66rem' }} noWrap>{relationType}</Typography>
                  <Chip label={relationTypeCounts[relationType] || 0} size="small" sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700 }} />
                </Stack>
              )}
            />
          ))}
        </FormGroup>
      </Box>

      <Box
        ref={inspectionMode === 'graph' ? (element) => {
          if (resizeObserverRef.current) {
            resizeObserverRef.current.disconnect();
            resizeObserverRef.current = null;
          }

          if (!element) return;

          const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
              const { width, height } = entry.contentRect;
              if (width > 0 && height > 0) {
                setGraphDimensions({ w: Math.round(width), h: Math.round(height) });
              }
            }
          });

          resizeObserver.observe(element);
          resizeObserverRef.current = resizeObserver;
        } : undefined}
        sx={{ width: '100%', flex: 1, minWidth: 0, minHeight: 0, position: 'relative', bgcolor: inspectionMode === 'graph' ? '#f0f4f8' : '#fff' }}
      >
        {inspectionMode === 'graph' ? (
          <>
            <Paper elevation={2} sx={{ position: 'absolute', top: 12, right: 12, zIndex: 2, display: 'flex', flexDirection: 'column', borderRadius: 2, overflow: 'hidden', bgcolor: 'rgba(255,255,255,0.95)' }}>
              <Tooltip title="Zoom in" placement="left">
                <IconButton size="small" onClick={handleGraphZoomIn}>
                  <ZoomIn fontSize="small" />
                </IconButton>
              </Tooltip>
              <Divider />
              <Tooltip title="Zoom out" placement="left">
                <IconButton size="small" onClick={handleGraphZoomOut}>
                  <ZoomOut fontSize="small" />
                </IconButton>
              </Tooltip>
              <Divider />
              <Tooltip title="Fit to view" placement="left">
                <IconButton size="small" onClick={handleGraphZoomFit}>
                  <CenterFocusStrong fontSize="small" />
                </IconButton>
              </Tooltip>
            </Paper>

            {graphDimensions.h > 0 && (
              <ForceGraph2D
                ref={graphRef}
                graphData={displayGraphData}
                width={graphDimensions.w}
                height={graphDimensions.h}
                backgroundColor="#f0f4f8"
                nodeCanvasObject={(node, ctx, globalScale) => {
                  const isSelected = !isLowMemoryFullGraph && selectedGraphNode?.id === node.id;
                  const isHovered = !isLowMemoryFullGraph && Boolean(graphHoveredNode && graphHoveredNode.id === node.id);
                  const nodeSearchText = `${node?.label || ''} ${node?.id || ''}`.toLowerCase();
                  const isSearchMatch = Boolean(normalizedGraphSearchTerm) && nodeSearchText.includes(normalizedGraphSearchTerm);
                  const color = node.color || '#2196f3';
                  const size = isLowMemoryFullGraph ? 5.5 : (isSelected ? 14 : (isSearchMatch ? 10 : 8));

                  ctx.beginPath();
                  ctx.arc(node.x, node.y, size, 0, 2 * Math.PI);
                  ctx.fillStyle = color;
                  ctx.globalAlpha = isSelected ? 1 : 0.9;
                  ctx.fill();
                  ctx.globalAlpha = 1;
                  ctx.strokeStyle = isSelected ? '#fff' : 'rgba(255,255,255,0.5)';
                  ctx.lineWidth = isSelected ? 2.4 : 1.2;
                  ctx.stroke();

                  if (!isSelected && !isHovered) return;
                  const label = String(node?.label || node?.id || '');
                  const fontSize = Math.max(10 / globalScale, 4);
                  ctx.font = `600 ${fontSize}px sans-serif`;
                  ctx.textAlign = 'center';
                  ctx.textBaseline = 'top';
                  ctx.fillStyle = 'rgba(255,255,255,0.92)';
                  const width = ctx.measureText(label).width;
                  ctx.fillRect(node.x - width / 2 - 4, node.y + size + 2, width + 8, fontSize + 4);
                  ctx.fillStyle = '#263238';
                  ctx.fillText(label, node.x, node.y + size + 4);
                }}
                nodeCanvasObjectMode={() => 'replace'}
                linkCanvasObject={(link, ctx) => {
                  const source = link.source;
                  const target = link.target;
                  if (!source || !target || source.x == null || target.x == null) return;

                  const isSelected = selectedGraphEdge?.id === link.id;
                  const isHovered = graphHoveredEdge?.id === link.id;
                  const color = link.color || '#78909c';

                  ctx.beginPath();
                  ctx.moveTo(source.x, source.y);
                  ctx.lineTo(target.x, target.y);
                  ctx.strokeStyle = color;
                  ctx.globalAlpha = isSelected ? 0.95 : (isHovered ? 0.82 : 0.45);
                  ctx.lineWidth = isSelected ? 3 : (isHovered ? 2.4 : 1.6);
                  ctx.stroke();
                  ctx.globalAlpha = 1;
                }}
                linkCanvasObjectMode={() => 'replace'}
                linkDirectionalArrowLength={0}
                enableNodeDrag={!isLowMemoryFullGraph}
                enableZoomInteraction
                enablePanInteraction
                cooldownTicks={isLowMemoryFullGraph ? 0 : (isUnsafeFullGraph ? 28 : (isDenseRenderedGraph ? 95 : 200))}
                d3AlphaDecay={isLowMemoryFullGraph ? 1 : (isUnsafeFullGraph ? 0.11 : (isDenseRenderedGraph ? 0.055 : 0.02))}
                d3VelocityDecay={0.3}
                d3AlphaMin={0.001}
                nodeRelSize={7}
                warmupTicks={isLowMemoryFullGraph ? 0 : (isUnsafeFullGraph ? 8 : (isDenseRenderedGraph ? 22 : 50))}
                enablePointerInteraction={!isLowMemoryFullGraph}
                onNodeClick={isLowMemoryFullGraph ? undefined : ((node) => {
                  if (!node) return;
                  const resolvedNode = graphNodeMap[node.id] || node;
                  setSelectedGraphEdge(null);
                  setSelectedGraphNode((previous) => (previous?.id === resolvedNode.id ? null : resolvedNode));
                })}
                onNodeHover={isLowMemoryFullGraph ? undefined : ((node) => setGraphHoveredNode(node || null))}
                onLinkClick={isLowMemoryFullGraph ? undefined : ((link) => {
                  if (!link) return;
                  setSelectedGraphNode(null);
                  setSelectedGraphEdge((previous) => (previous?.id === link.id ? null : link));
                })}
                onLinkHover={isLowMemoryFullGraph ? undefined : ((link) => setGraphHoveredEdge(link || null))}
                onNodeDragEnd={(node) => {
                  node.fx = node.x;
                  node.fy = node.y;
                }}
                onBackgroundClick={isLowMemoryFullGraph ? undefined : (() => {
                  setSelectedGraphNode(null);
                  setSelectedGraphEdge(null);
                })}
                onEngineStop={() => {
                  if (isLowMemoryFullGraph && !graphLayoutFrozen) {
                    displayGraphData.nodes.forEach((node) => {
                      if (node?.x != null && node?.y != null) {
                        node.fx = node.x;
                        node.fy = node.y;
                      }
                    });
                    setGraphLayoutFrozen(true);
                  }
                  if (graphRef.current && displayGraphData.nodes.length > 0) {
                    graphRef.current.zoomToFit(400, 40);
                  }
                }}
              />
            )}
          </>
        ) : inspectionMode === 'nodes' ? (
          <Box sx={{ height: '100%', overflow: 'auto', p: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              All Nodes ({fullNodesTableRows.length})
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Label</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fullNodesTableRows.map((node) => (
                    <TableRow key={`full-node-${node.id}`} hover>
                      <TableCell>{node.label}</TableCell>
                      <TableCell>{node.nodeType}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', overflowWrap: 'anywhere' }}>{node.id}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : (
          <Box sx={{ height: '100%', overflow: 'auto', p: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              All Links ({fullLinksTableRows.length})
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Relation</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Source</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Target</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fullLinksTableRows.map((link) => (
                    <TableRow key={`full-link-${link.id}`} hover>
                      <TableCell>{link.relationLabel}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{link.sourceLabel}</Typography>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}>{link.sourceId}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{link.targetLabel}</Typography>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}>{link.targetId}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Box>

      <Box sx={{ width: { xs: '100%', lg: 300 }, minWidth: 0, maxHeight: { xs: '28%', lg: 'none' }, flexShrink: 0, borderLeft: { xs: 'none', lg: '1px solid' }, borderTop: { xs: '1px solid', lg: 'none' }, borderColor: 'divider', overflow: 'auto', p: 1.25, bgcolor: '#fff' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          Inspector
        </Typography>

        <Paper variant="outlined" sx={{ p: 1, mb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {`Rendered nodes: ${renderedGraphData.nodes.length}/${filteredGraphData.nodes.length} (filtered/source: ${filteredGraphData.nodes.length}/${graphCoverageSummary.nodeCount})`}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {`Rendered edges: ${renderedGraphData.links.length}/${filteredGraphData.links.length} (filtered/source: ${filteredGraphData.links.length}/${graphCoverageSummary.edgeCount})`}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {`Isolated nodes: ${graphCoverageSummary.isolatedCount}`}
          </Typography>
        </Paper>

        {selectedGraphEdge ? (
          <Paper variant="outlined" sx={{ p: 1, mb: 1 }}>
            <Chip
              size="small"
              label={selectedGraphEdge.relationLabel || selectedGraphEdge.label || 'related'}
              sx={{ mb: 0.75, fontWeight: 700, bgcolor: selectedGraphEdge.color || '#607d8b', color: '#fff' }}
            />
            <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
              {`${selectedGraphEdgeEndpoints.source?.label || getEndpointId(selectedGraphEdge.source)} -> ${selectedGraphEdgeEndpoints.target?.label || getEndpointId(selectedGraphEdge.target)}`}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Confidence: {selectedGraphEdge.confidence != null ? Number(selectedGraphEdge.confidence).toFixed(3) : '-'}
            </Typography>
            <Stack direction="row" spacing={0.75} sx={{ mt: 1 }}>
              {selectedGraphEdgeEndpoints.source && (
                <Button size="small" variant="outlined" onClick={() => navigateToGraphNode(selectedGraphEdgeEndpoints.source)}>
                  Source
                </Button>
              )}
              {selectedGraphEdgeEndpoints.target && (
                <Button size="small" variant="outlined" onClick={() => navigateToGraphNode(selectedGraphEdgeEndpoints.target)}>
                  Target
                </Button>
              )}
            </Stack>
          </Paper>
        ) : selectedGraphNode ? (
          <>
            <Paper variant="outlined" sx={{ p: 1, mb: 1 }}>
              <Chip
                size="small"
                label={selectedGraphNode.nodeType || 'Entity'}
                sx={{ mb: 0.75, fontWeight: 700, bgcolor: selectedGraphNode.color || '#607d8b', color: '#fff' }}
              />
              <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                {selectedGraphNode.label || selectedGraphNode.id}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                ID: {selectedGraphNode.id}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Type: {selectedGraphNode.nodeType || 'Entity'}
              </Typography>
              <Button size="small" variant="outlined" sx={{ mt: 1 }} onClick={() => setGraphFocusMode('neighbors')}>
                Focus Neighbors
              </Button>
            </Paper>

            {selectedGraphNodeRelationships.incoming.length > 0 && (
              <Paper variant="outlined" sx={{ p: 1, mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                  Incoming ({selectedGraphNodeRelationships.incoming.length})
                </Typography>
                <List dense disablePadding sx={{ maxHeight: 170, overflow: 'auto' }}>
                  {selectedGraphNodeRelationships.incoming.map(({ node, link }, index) => (
                    <ListItemButton key={`in-${index}`} sx={{ py: 0.2, px: 0.5 }} onClick={() => navigateToGraphNode(node)}>
                      <Typography variant="caption" noWrap>
                        {`${node.label || node.id} <- ${link.relationLabel || link.label || 'related'}`}
                      </Typography>
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            )}

            {selectedGraphNodeRelationships.outgoing.length > 0 && (
              <Paper variant="outlined" sx={{ p: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                  Outgoing ({selectedGraphNodeRelationships.outgoing.length})
                </Typography>
                <List dense disablePadding sx={{ maxHeight: 170, overflow: 'auto' }}>
                  {selectedGraphNodeRelationships.outgoing.map(({ node, link }, index) => (
                    <ListItemButton key={`out-${index}`} sx={{ py: 0.2, px: 0.5 }} onClick={() => navigateToGraphNode(node)}>
                      <Typography variant="caption" noWrap>
                        {`${link.relationLabel || link.label || 'related'} -> ${node.label || node.id}`}
                      </Typography>
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            )}
          </>
        ) : (
          <Alert severity="info">Select a node or edge to inspect details.</Alert>
        )}
      </Box>
    </Box>
  );
};

const ProjectsTab = ({
  showSuccess,
  showError,
  userPermissions,
  onOpenProject,
  initialProjectId,
  projectDialogFullScreen = false,
  onCloseProjectModal,
  onNavigateHome,
  filesCount = 0,
  processingQueueCount = 0,
  onToggleProcessingPanel,
  onLogout,
}) => {
  const [createProjectDialogOpen, setCreateProjectDialogOpen] = useState(false);
  const [creatingProject, setCreatingProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [renameProjectDialogOpen, setRenameProjectDialogOpen] = useState(false);
  const [renamingProject, setRenamingProject] = useState(false);
  const [projectToRename, setProjectToRename] = useState(null);
  const [renameProjectName, setRenameProjectName] = useState('');
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectOntologies, setProjectOntologies] = useState([]);
  const [ontologiesSummary, setOntologiesSummary] = useState(null);
  const [loadingOntologies, setLoadingOntologies] = useState(false);
  const [applyingOntologies, setApplyingOntologies] = useState(false);
  const [applyConfirmation, setApplyConfirmation] = useState({
    open: false,
    kind: '',
    viewKey: '',
  });
  const [pendingOntologyKey, setPendingOntologyKey] = useState('');
  const [uploadingOntologyKey, setUploadingOntologyKey] = useState('');
  const [projectViews, setProjectViews] = useState([]);
  const [pendingProjectViewKey, setPendingProjectViewKey] = useState('');
  const [uploadingProjectViewKey, setUploadingProjectViewKey] = useState('');
  const [projectViewUploadProgress, setProjectViewUploadProgress] = useState(null);
  const [projectViewUploadStep, setProjectViewUploadStep] = useState('');
  const [projectViewUploadPages, setProjectViewUploadPages] = useState({ processed: 0, total: 0 });
  const [pendingDocumentUri, setPendingDocumentUri] = useState('');
  const [pendingDocumentLabel, setPendingDocumentLabel] = useState('');
  const [pendingDocumentNotation, setPendingDocumentNotation] = useState('');
  const [pendingReplaceRepositoryDocumentEntityId, setPendingReplaceRepositoryDocumentEntityId] = useState('');
  const [documentUploadItems, setDocumentUploadItems] = useState([]);
  const [documentUploadSummary, setDocumentUploadSummary] = useState({ total: 0, success: 0, duplicate: 0, failed: 0, cancelled: 0, skipped: 0 });
  const [documentUploadStatusFilter, setDocumentUploadStatusFilter] = useState('all');
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [deletingProject, setDeletingProject] = useState(false);
  const [projectViewMode, setProjectViewMode] = useState('explore');
  const [deepDiveSectionKey, setDeepDiveSectionKey] = useState('objects');
  const [objectTreeMode, setObjectTreeMode] = useState('hasPart');
  const [projectDetailTab, setProjectDetailTab] = useState('vs1');
  const [vs1Source, setVs1Source] = useState('project');
  const [vs1Sections, setVs1Sections] = useState(createEmptyVsSections());
  const [vs1SectionSelection, setVs1SectionSelection] = useState(createEmptyVs1SectionSelection());
  const [vs1SectionModalOpen, setVs1SectionModalOpen] = useState(false);
  const [vs1SectionModalKey, setVs1SectionModalKey] = useState('');
  const [vs1EntityDirectory, setVs1EntityDirectory] = useState({});
  const [vs1DetailTab, setVs1DetailTab] = useState(0);
  const [vs1ExpandedGroups, setVs1ExpandedGroups] = useState({});
  const [loadingVs1Sections, setLoadingVs1Sections] = useState(false);
  const [bootstrappingProjectDetail, setBootstrappingProjectDetail] = useState(false);
  const [candidateLocationsByKey, setCandidateLocationsByKey] = useState({});
  const [expandedCandidateLocationsKey, setExpandedCandidateLocationsKey] = useState('');
  const [loadingCandidateLocationsKey, setLoadingCandidateLocationsKey] = useState('');
  const [pagePreviewState, setPagePreviewState] = useState({
    open: false,
    loading: false,
    projectId: null,
    documentId: null,
    documentTitle: '',
    pageNumber: null,
    availablePages: [],
    textOriginal: '',
    textTranslated: '',
    locationContext: null,
    textMode: 'translated',
  });
  const [updatingVs1ApplicabilityRowId, setUpdatingVs1ApplicabilityRowId] = useState(null);
  const [runningObjectsApplicabilityAnalysis, setRunningObjectsApplicabilityAnalysis] = useState(false);
  const [runningRequirementsApplicabilityAnalysis, setRunningRequirementsApplicabilityAnalysis] = useState(false);
  const [runningSingleApplicabilityAnalysisKey, setRunningSingleApplicabilityAnalysisKey] = useState('');
  const [pendingApplicabilityTasks, setPendingApplicabilityTasks] = useState([]);
  const [reviewingObjectsApplicability, setReviewingObjectsApplicability] = useState(false);
  const [objectsApplicabilityReview, setObjectsApplicabilityReview] = useState(null);
  const [candidateLlmFilter, setCandidateLlmFilter] = useState('all');
  const [entityGraphOpen, setEntityGraphOpen] = useState(false);
  const vs1GraphRef = useRef(null);
  const vs1GraphContainerRef = useRef(null);
  const vs1GraphRoRef = useRef(null);
  const [vs1GraphDims, setVs1GraphDims] = useState({ w: 0, h: 0 });
  const [vs1HoveredNode, setVs1HoveredNode] = useState(null);
  const ontologyInputRef = useRef(null);
  const projectViewInputRef = useRef(null);
  const projectViewFolderInputRef = useRef(null);
  const objectsApplicabilityReviewInputRef = useRef(null);
  const documentUploadCancelRef = useRef(false);
  const projectDialogContentRef = useRef(null);
  const showErrorRef = useRef(showError);
  const [confirmDeleteDocumentDialogOpen, setConfirmDeleteDocumentDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [deletingDocument, setDeletingDocument] = useState(false);
  const [viewingProjectDocumentId, setViewingProjectDocumentId] = useState(null);
  const [projectDocumentsGraphDialogOpen, setProjectDocumentsGraphDialogOpen] = useState(false);
  const [projectDocumentsGraphLoading, setProjectDocumentsGraphLoading] = useState(false);
  const [projectDocumentsGraphError, setProjectDocumentsGraphError] = useState('');
  const [projectDocumentsGraphData, setProjectDocumentsGraphData] = useState({ nodes: [], links: [] });
  const [projectDocumentsGraphSearchTerm, setProjectDocumentsGraphSearchTerm] = useState('');
  const [singleDocumentGraphDialogOpen, setSingleDocumentGraphDialogOpen] = useState(false);
  const [singleDocumentGraphLoading, setSingleDocumentGraphLoading] = useState(false);
  const [singleDocumentGraphError, setSingleDocumentGraphError] = useState('');
  const [singleDocumentGraphTitle, setSingleDocumentGraphTitle] = useState('Document Graph');
  const [singleDocumentGraphData, setSingleDocumentGraphData] = useState({ nodes: [], links: [] });
  const [singleDocumentGraphSearchTerm, setSingleDocumentGraphSearchTerm] = useState('');
  const projectDocumentsGraphRef = useRef(null);
  const singleDocumentGraphRef = useRef(null);
  const projectDocumentsGraphRoRef = useRef(null);
  const singleDocumentGraphRoRef = useRef(null);
  const [projectDocumentsGraphDims, setProjectDocumentsGraphDims] = useState({ w: 0, h: 0 });
  const [singleDocumentGraphDims, setSingleDocumentGraphDims] = useState({ w: 0, h: 0 });
  const autoOpenedProjectIdRef = useRef(null);
  const vs1SectionsCacheRef = useRef(new Map());

  const trackQueuedApplicabilityTask = useCallback((taskId, scopeLabel) => {
    const normalizedTaskId = String(taskId || '').trim();
    if (!normalizedTaskId) {
      return;
    }

    setPendingApplicabilityTasks((previousTasks) => {
      if (previousTasks.some((task) => task.taskId === normalizedTaskId)) {
        return previousTasks;
      }
      return [
        ...previousTasks,
        {
          taskId: normalizedTaskId,
          scopeLabel: String(scopeLabel || '').trim() || 'Applicability analysis',
          seenActive: false,
        },
      ];
    });
  }, []);

  const isProjectDocumentsGraphGenerationInProgress = useMemo(() => {
    if (projectDetailTab !== 'documents') {
      return false;
    }

    const documentsView = projectViews.find((item) => item.key === 'documents') || null;
    return Boolean(documentsView?.graph_generation_in_progress);
  }, [projectDetailTab, projectViews]);

  const handleProjectDocumentsGraphContainerRef = useCallback((element) => {
    if (projectDocumentsGraphRoRef.current) {
      projectDocumentsGraphRoRef.current.disconnect();
      projectDocumentsGraphRoRef.current = null;
    }

    if (!element) {
      return;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          setProjectDocumentsGraphDims({ w: Math.round(width), h: Math.round(height) });
        }
      }
    });

    resizeObserver.observe(element);
    projectDocumentsGraphRoRef.current = resizeObserver;
  }, []);

  const handleSingleDocumentGraphContainerRef = useCallback((element) => {
    if (singleDocumentGraphRoRef.current) {
      singleDocumentGraphRoRef.current.disconnect();
      singleDocumentGraphRoRef.current = null;
    }

    if (!element) {
      return;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          setSingleDocumentGraphDims({ w: Math.round(width), h: Math.round(height) });
        }
      }
    });

    resizeObserver.observe(element);
    singleDocumentGraphRoRef.current = resizeObserver;
  }, []);

  const handleDocumentsGraphZoomIn = useCallback((graphRef) => {
    if (!graphRef?.current) return;
    const currentZoom = Number(graphRef.current.zoom() || 1);
    graphRef.current.zoom(currentZoom * 1.35, 250);
  }, []);

  const handleDocumentsGraphZoomOut = useCallback((graphRef) => {
    if (!graphRef?.current) return;
    const currentZoom = Number(graphRef.current.zoom() || 1);
    graphRef.current.zoom(currentZoom / 1.35, 250);
  }, []);

  const handleDocumentsGraphZoomFit = useCallback((graphRef) => {
    graphRef?.current?.zoomToFit(350, 40);
  }, []);

  useEffect(() => () => {
    if (projectDocumentsGraphRoRef.current) {
      projectDocumentsGraphRoRef.current.disconnect();
      projectDocumentsGraphRoRef.current = null;
    }

    if (singleDocumentGraphRoRef.current) {
      singleDocumentGraphRoRef.current.disconnect();
      singleDocumentGraphRoRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!projectDocumentsGraphDialogOpen || projectDocumentsGraphLoading) {
      return;
    }

    if (!projectDocumentsGraphData?.nodes?.length || projectDocumentsGraphDims.w <= 0 || projectDocumentsGraphDims.h <= 0) {
      return;
    }

    requestAnimationFrame(() => {
      handleDocumentsGraphZoomFit(projectDocumentsGraphRef);
    });
  }, [
    handleDocumentsGraphZoomFit,
    projectDocumentsGraphData,
    projectDocumentsGraphDialogOpen,
    projectDocumentsGraphDims.h,
    projectDocumentsGraphDims.w,
    projectDocumentsGraphLoading,
  ]);

  useEffect(() => {
    if (!singleDocumentGraphDialogOpen || singleDocumentGraphLoading) {
      return;
    }

    if (!singleDocumentGraphData?.nodes?.length || singleDocumentGraphDims.w <= 0 || singleDocumentGraphDims.h <= 0) {
      return;
    }

    requestAnimationFrame(() => {
      handleDocumentsGraphZoomFit(singleDocumentGraphRef);
    });
  }, [
    handleDocumentsGraphZoomFit,
    singleDocumentGraphData,
    singleDocumentGraphDialogOpen,
    singleDocumentGraphDims.h,
    singleDocumentGraphDims.w,
    singleDocumentGraphLoading,
  ]);

  const renderDocumentsGraphViewer = useCallback(({
    graphData,
    emptyMessage,
    searchTerm,
    onSearchTermChange,
    graphRef,
    graphDims,
    onGraphContainerRef,
  }) => {
    const { filteredData, nodeTypeLegend, relationLegend, counts } = decorateAndFilterDocumentsGraph(graphData, searchTerm);

    if (!graphData?.nodes?.length) {
      return <Alert severity="info">{emptyMessage}</Alert>;
    }

    if (!filteredData.nodes.length) {
      return (
        <Stack spacing={1.25}>
          <TextField
            size="small"
            fullWidth
            label="Search nodes or relations"
            placeholder="Try: requirement, class, verifies, label..."
            value={searchTerm}
            onChange={(event) => onSearchTermChange(event.target.value)}
          />
          <Alert severity="info">No matches found for that search.</Alert>
        </Stack>
      );
    }

    return (
      <Stack spacing={1.25}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }}>
          <TextField
            size="small"
            fullWidth
            label="Search nodes or relations"
            placeholder="Try: requirement, class, verifies, label..."
            value={searchTerm}
            onChange={(event) => onSearchTermChange(event.target.value)}
          />
          <Stack direction="row" spacing={0.6} alignItems="center" sx={{ flexShrink: 0 }}>
            <Tooltip title="Zoom in">
              <IconButton size="small" onClick={() => handleDocumentsGraphZoomIn(graphRef)}>
                <ZoomIn fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Zoom out">
              <IconButton size="small" onClick={() => handleDocumentsGraphZoomOut(graphRef)}>
                <ZoomOut fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Fit to view">
              <IconButton size="small" onClick={() => handleDocumentsGraphZoomFit(graphRef)}>
                <CenterFocusStrong fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip label={`Nodes ${counts.nodes}/${counts.sourceNodes}`} size="small" variant="outlined" />
          <Chip label={`Links ${counts.links}/${counts.sourceLinks}`} size="small" variant="outlined" />
        </Stack>

        <Stack spacing={0.8}>
          <Typography variant="caption" color="text.secondary">Node types</Typography>
          <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap>
            {nodeTypeLegend.map((nodeType) => (
              <Chip
                key={`node-type-${nodeType}`}
                size="small"
                label={nodeType}
                sx={{
                  bgcolor: resolveGraphNodeColor(nodeType),
                  color: '#fff',
                  fontWeight: 600,
                }}
              />
            ))}
          </Stack>
        </Stack>

        <Stack spacing={0.8}>
          <Typography variant="caption" color="text.secondary">Relation types</Typography>
          <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap>
            {relationLegend.map((relationLabel) => (
              <Chip
                key={`relation-type-${relationLabel}`}
                size="small"
                label={relationLabel}
                variant="outlined"
                sx={{
                  borderColor: resolveGraphLinkColor(relationLabel),
                  color: resolveGraphLinkColor(relationLabel),
                  fontWeight: 600,
                }}
              />
            ))}
          </Stack>
        </Stack>

        <Box
          ref={onGraphContainerRef}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
            bgcolor: '#f7fafc',
            width: '100%',
            height: { xs: 420, md: 620 },
            minHeight: 420,
          }}
        >
          {graphDims.w > 0 && graphDims.h > 0 && (
            <ForceGraph2D
              ref={graphRef}
              graphData={filteredData}
              width={graphDims.w}
              height={graphDims.h}
              backgroundColor="#f7fafc"
              nodeRelSize={6}
              nodeColor={(node) => node?.color || '#78909c'}
              linkColor={(link) => link?.color || '#78909c'}
              linkWidth={1.9}
              linkDirectionalArrowLength={5}
              linkDirectionalArrowRelPos={1}
              nodeLabel={(node) => `${node.label}${node.nodeType ? ` (${node.nodeType})` : ''}`}
              linkLabel={(link) => link.relationLabel || link.label || ''}
            />
          )}
        </Box>
      </Stack>
    );
  }, [handleDocumentsGraphZoomFit, handleDocumentsGraphZoomIn, handleDocumentsGraphZoomOut]);

  const summarizeUploadItems = useCallback((items, skippedCount = 0) => ({
    total: items.length,
    success: items.filter((item) => item.status === 'completed').length,
    duplicate: items.filter((item) => item.status === 'duplicate').length,
    failed: items.filter((item) => item.status === 'failed').length,
    cancelled: items.filter((item) => item.status === 'cancelled').length,
    skipped: Math.max(0, Number(skippedCount || 0)),
  }), []);

  const updateUploadItem = useCallback((itemId, patch) => {
    setDocumentUploadItems((previousItems) => previousItems.map((item) => (
      item.id === itemId ? { ...item, ...patch } : item
    )));
  }, []);

  useEffect(() => {
    const storageKey = buildDocumentQueueStorageKey(selectedProject?.id);
    if (!storageKey) {
      return;
    }

    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) {
        return;
      }

      const parsed = JSON.parse(raw);
      const storedItems = Array.isArray(parsed?.items) ? parsed.items : [];
      const storedSummary = parsed?.summary && typeof parsed.summary === 'object'
        ? parsed.summary
        : { total: 0, success: 0, duplicate: 0, failed: 0, cancelled: 0, skipped: 0 };
      const storedFilter = String(parsed?.filter || 'all').trim() || 'all';

      setDocumentUploadItems(toUploadItemsSnapshot(storedItems));
      setDocumentUploadSummary({
        total: Number(storedSummary.total || 0),
        success: Number(storedSummary.success || 0),
        duplicate: Number(storedSummary.duplicate || 0),
        failed: Number(storedSummary.failed || 0),
        cancelled: Number(storedSummary.cancelled || 0),
        skipped: Number(storedSummary.skipped || 0),
      });
      setDocumentUploadStatusFilter(storedFilter);
    } catch {
      // Ignore malformed local snapshot
    }
  }, [selectedProject?.id]);

  useEffect(() => {
    const storageKey = buildDocumentQueueStorageKey(selectedProject?.id);
    if (!storageKey) {
      return;
    }

    const payload = {
      updatedAt: new Date().toISOString(),
      items: toUploadItemsSnapshot(documentUploadItems),
      summary: documentUploadSummary,
      filter: documentUploadStatusFilter,
    };

    try {
      localStorage.setItem(storageKey, JSON.stringify(payload));
    } catch {
      // Ignore storage errors (quota/privacy mode)
    }
  }, [selectedProject?.id, documentUploadItems, documentUploadSummary, documentUploadStatusFilter]);

  const canCreateProjects = userPermissions?.can_create !== false;
  const canDeleteProjects = userPermissions?.can_delete === true;
  const canOpenProjects = typeof onOpenProject === 'function';
  const normalizedInitialProjectId = Number(initialProjectId || 0);

  // Compute grouped requirements by Type -> Vakgebied for tree view
  const groupedRequirements = useMemo(() => {
    const reqs = vs1Sections?.requirements || [];
    if (reqs.length === 0) return [];

    const getTypeLabel = (row) => {
      const payload = row?.data && typeof row.data === 'object' ? row.data : {};
      const types = Array.isArray(payload.types) ? payload.types : [];
      for (const typeUri of types) {
        const entry = (vs1EntityDirectory || {})[typeUri];
        if (entry && (entry.name_en || entry.name_english || entry.name)) return entry.name_en || entry.name_english || entry.name;
        // Fallback: last segment of URI
        const seg = String(typeUri).split('/').pop().split('#').pop();
        if (seg && !/^[0-9a-f-]{20,}$/i.test(seg)) return seg;
      }
      return 'Unclassified';
    };

    // Type -> Vakgebied -> rows
    const typeMap = {};
    for (const row of reqs) {
      const typeName = getTypeLabel(row);
      const vak = getRowVakgebied(row);
      if (!typeMap[typeName]) typeMap[typeName] = {};
      if (!typeMap[typeName][vak]) typeMap[typeName][vak] = [];
      typeMap[typeName][vak].push(row);
    }

    return Object.entries(typeMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([typeName, vakMap]) => ({
        typeName,
        vakGroups: Object.entries(vakMap).sort(([a], [b]) => a.localeCompare(b)),
        totalCount: Object.values(vakMap).reduce((s, arr) => s + arr.length, 0),
      }));
  }, [vs1Sections?.requirements, vs1EntityDirectory]);

  // Compute grouped objects by Superclass (from types) -> Class Project (resolved id)
  const groupedObjects = useMemo(() => {
    const objects = vs1Sections?.objects || [];
    if (objects.length === 0) return [];

    const getLocalToken = (value) => {
      const text = String(value || '').trim();
      if (!text) return '';
      const clean = text.replace(/^<|>$/g, '');
      const noHash = clean.includes('#') ? clean.split('#').pop() : clean;
      return noHash.split('/').pop().split(':').pop();
    };

    const isUuidToken = (token) => (
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(token)
    );

    const superTypeTokens = ['realobject', 'physicalobject', 'virtualobject', 'object', 'spatialregion'];

    const typeRefIndex = {};
    Object.entries(vs1EntityDirectory || {}).forEach(([uri, entry]) => {
      const token = getLocalToken(uri).toLowerCase();
      if (!token) return;

      const current = typeRefIndex[token];
      const isClassEntry = entry?.entity_type === 'Class_Project';
      const isCurrentClassEntry = current?.entry?.entity_type === 'Class_Project';
      if (!current || (isClassEntry && !isCurrentClassEntry)) {
        typeRefIndex[token] = { uri, entry };
      }
    });

    const resolveTypeRef = (value) => {
      const ref = String(value || '').trim();
      if (!ref) return null;

      const direct = vs1EntityDirectory[ref];
      if (direct) return { uri: ref, entry: direct };

      const token = getLocalToken(ref).toLowerCase();
      if (!token) return null;

      return typeRefIndex[token] || null;
    };

    const classifyObjectTypes = (payload) => {
      const typeUris = Array.isArray(payload.types)
        ? payload.types
        : (Array.isArray(payload.rdf_types) ? payload.rdf_types : []);

      const typeInfos = typeUris
        .map((uri) => String(uri || '').trim())
        .filter(Boolean)
        .map((uri) => {
          const local = getLocalToken(uri);
          const resolvedRef = resolveTypeRef(uri);
          const directory = resolvedRef?.entry;
          return {
            uri: resolvedRef?.uri || uri,
            local,
            localLower: local.toLowerCase(),
            isUuid: isUuidToken(local),
            entityType: directory?.entity_type || '',
            label: directory?.name_en || directory?.name_english || directory?.name || directory?.notation || local || (resolvedRef?.uri || uri),
          };
        });

      const superType = typeInfos
        .find((info) => !info.isUuid && superTypeTokens.includes(info.localLower))
        || typeInfos.find((info) => !info.isUuid && info.localLower.endsWith('object'))
        || typeInfos.find((info) => !info.isUuid)
        || null;

      const classType = typeInfos
        .find((info) => info.entityType === 'Class_Project')
        || typeInfos.find((info) => info.isUuid && info.uri.toLowerCase().includes('/id/') && Boolean(vs1EntityDirectory[info.uri]))
        || typeInfos.find((info) => info.isUuid && info.uri.toLowerCase().includes('/id/'))
        || typeInfos.find((info) => info.isUuid && Boolean(vs1EntityDirectory[info.uri]))
        || typeInfos.find((info) => !info.isUuid && !superTypeTokens.includes(info.localLower) && !info.localLower.endsWith('object'))
        || typeInfos.find((info) => info.isUuid)
        || null;

      return {
        superClassName: superType ? (superType.local || superType.label || superType.uri) : 'Unclassified',
        className: classType ? (classType.label || classType.local || classType.uri) : 'Unclassified',
        classUri: classType?.uri || '',
      };
    };

    const superMap = {};
    for (const row of objects) {
      const payload = row?.data && typeof row.data === 'object' ? row.data : {};
      const { superClassName, className, classUri } = classifyObjectTypes(payload);

      if (!superMap[superClassName]) superMap[superClassName] = {};
      const classKey = classUri || className;
      if (!superMap[superClassName][classKey]) {
        superMap[superClassName][classKey] = { className, rows: [] };
      }
      superMap[superClassName][classKey].rows.push(row);
    }

    return Object.entries(superMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([superClass, classGroups]) => ({
        superClass,
        classes: Object.values(classGroups)
          .sort((a, b) => a.className.localeCompare(b.className))
          .map(({ className, rows }) => ({ className, rows })),
      }));
  }, [vs1Sections?.objects, vs1EntityDirectory]);

  // Compute object tree using hasPart / isPartOf relationships
  const groupedObjectsByHasPart = useMemo(() => {
    const objects = vs1Sections?.objects || [];
    if (objects.length === 0) return [];

    const uriToRow = {};

    const getObjectSortLabel = (row) => {
      const payload = row?.data && typeof row.data === 'object' ? row.data : {};
      const notation = String(payload.notation || '').trim();
      const label = String(payload.label_english || payload.label_en || payload.label || row?.name_en || row?.name || '').trim();
      const primary = String(row?.name_en || row?.name || '').trim();
      return primary || (notation && label ? `${notation} - ${label}` : notation || label || '-');
    };

    const normalizePredicate = (predicate) => String(predicate || '').toLowerCase().replace(/[^a-z]/g, '');
    const toArray = (value) => (Array.isArray(value) ? value : (value != null ? [value] : []));

    for (const row of objects) {
      const payload = row?.data && typeof row.data === 'object' ? row.data : {};
      const rowUri = String(payload.uri || '').trim();
      if (rowUri) {
        uriToRow[rowUri] = row;
      }
    }

    const childrenByParent = {};
    const registerEdge = (parentUri, childUri) => {
      const normalizedParent = String(parentUri || '').trim();
      const normalizedChild = String(childUri || '').trim();
      if (!normalizedParent || !normalizedChild || normalizedParent === normalizedChild) {
        return;
      }
      if (!uriToRow[normalizedParent] || !uriToRow[normalizedChild]) {
        return;
      }
      if (!childrenByParent[normalizedParent]) {
        childrenByParent[normalizedParent] = new Set();
      }
      childrenByParent[normalizedParent].add(normalizedChild);
    };

    for (const row of objects) {
      const payload = row?.data && typeof row.data === 'object' ? row.data : {};
      const rowUri = String(payload.uri || '').trim();
      if (!rowUri) continue;

      const relationships = payload.relationships && typeof payload.relationships === 'object'
        ? payload.relationships
        : {};

      for (const [predicate, rawTargets] of Object.entries(relationships)) {
        const predicateToken = normalizePredicate(predicate);
        const targets = toArray(rawTargets)
          .map((target) => String(target || '').trim())
          .filter(Boolean);

        if (targets.length === 0) continue;

        if (predicateToken.includes('haspart')) {
          targets.forEach((targetUri) => registerEdge(rowUri, targetUri));
        }

        if (predicateToken.includes('ispartof')) {
          targets.forEach((targetUri) => registerEdge(targetUri, rowUri));
        }
      }
    }

    const childUris = new Set();
    Object.values(childrenByParent).forEach((childrenSet) => {
      childrenSet.forEach((childUri) => {
        childUris.add(childUri);
      });
    });

    const visited = new Set();
    const buildNode = (uri, ancestors = new Set()) => {
      const nextAncestors = new Set(ancestors);
      nextAncestors.add(uri);
      visited.add(uri);

      const sortedChildUris = Array.from(childrenByParent[uri] || [])
        .filter((childUri) => !nextAncestors.has(childUri))
        .sort((leftUri, rightUri) => {
          const leftLabel = getObjectSortLabel(uriToRow[leftUri]);
          const rightLabel = getObjectSortLabel(uriToRow[rightUri]);
          return leftLabel.localeCompare(rightLabel);
        });

      return {
        key: uri,
        row: uriToRow[uri],
        children: sortedChildUris.map((childUri) => buildNode(childUri, nextAncestors)),
      };
    };

    const uriRoots = Object.keys(uriToRow)
      .filter((uri) => !childUris.has(uri))
      .sort((leftUri, rightUri) => {
        const leftLabel = getObjectSortLabel(uriToRow[leftUri]);
        const rightLabel = getObjectSortLabel(uriToRow[rightUri]);
        return leftLabel.localeCompare(rightLabel);
      });

    const topLevelNodes = uriRoots.map((uri) => buildNode(uri));

    Object.keys(uriToRow)
      .filter((uri) => !visited.has(uri))
      .sort((leftUri, rightUri) => {
        const leftLabel = getObjectSortLabel(uriToRow[leftUri]);
        const rightLabel = getObjectSortLabel(uriToRow[rightUri]);
        return leftLabel.localeCompare(rightLabel);
      })
      .forEach((uri) => {
        topLevelNodes.push(buildNode(uri));
      });

    objects
      .filter((row) => {
        const payload = row?.data && typeof row.data === 'object' ? row.data : {};
        return !String(payload.uri || '').trim();
      })
      .sort((leftRow, rightRow) => getObjectSortLabel(leftRow).localeCompare(getObjectSortLabel(rightRow)))
      .forEach((row, index) => {
        topLevelNodes.push({
          key: `row-${row?.id || index}`,
          row,
          children: [],
        });
      });

    return topLevelNodes;
  }, [vs1Sections?.objects]);

  const objectHasPartConflicts = useMemo(() => {
    const parentKeys = new Set();

    const visitNode = (node) => {
      const row = node?.row;
      const status = normalizeMetricText(getSectionRowValue(row, 'applicability_status'));
      const isNodeApplies = status === 'aplica';
      const isNodeNoApplies = status === 'no_aplica';

      let hasApplyingDescendant = false;
      const children = Array.isArray(node?.children) ? node.children : [];

      children.forEach((childNode) => {
        if (visitNode(childNode)) {
          hasApplyingDescendant = true;
        }
      });

      if (isNodeNoApplies && hasApplyingDescendant) {
        parentKeys.add(String(node?.key || row?.id || ''));
      }

      return isNodeApplies || hasApplyingDescendant;
    };

    groupedObjectsByHasPart.forEach((node) => {
      visitNode(node);
    });

    return {
      parentKeys,
      count: parentKeys.size,
    };
  }, [groupedObjectsByHasPart]);

  // Keep only non-enumeration classes in VS1 Classes tree
  const visibleClassRows = useMemo(() => {
    const classes = vs1Sections?.classes || [];
    if (classes.length === 0) return [];

    const getLocalToken = (value) => {
      const text = String(value || '').trim();
      if (!text) return '';
      const clean = text.replace(/^<|>$/g, '');
      const noHash = clean.includes('#') ? clean.split('#').pop() : clean;
      return noHash.split('/').pop().split(':').pop();
    };

    const isEnumerationSubclass = (payload) => {
      const rels = payload?.relationships && typeof payload.relationships === 'object' ? payload.relationships : {};
      const subclassEntry = Object.entries(rels).find(([predicate]) =>
        String(predicate || '').toLowerCase().includes('subclassof')
      );

      if (!subclassEntry) return false;
      const value = subclassEntry[1];
      const subclassValues = Array.isArray(value) ? value : (value ? [value] : []);

      return subclassValues
        .map((item) => getLocalToken(item).toLowerCase())
        .some((token) => token === 'enumerationtype');
    };

    return classes.filter((row) => {
      const payload = row?.data && typeof row.data === 'object' ? row.data : {};
      return !isEnumerationSubclass(payload);
    });
  }, [vs1Sections?.classes]);

  // Compute hierarchical class tree using rdfs:subClassOf relationships
  const groupedClasses = useMemo(() => {
    const classes = visibleClassRows;
    if (classes.length === 0) return [];

    // Build URI → row map
    const uriToRow = {};
    for (const row of classes) {
      const payload = row?.data && typeof row.data === 'object' ? row.data : {};
      const uri = String(payload.uri || '').trim();
      if (uri) uriToRow[uri] = row;
    }

    // Build parent → children map from rdfs:subClassOf
    const childrenOfUri = {};       // parentUri -> [childRow]
    const hasParentInSet = new Set(); // URIs with an internal parent
    const externalParentChildren = {}; // externalUri -> [childRow]

    for (const row of classes) {
      const payload = row?.data && typeof row.data === 'object' ? row.data : {};
      const rels = payload.relationships && typeof payload.relationships === 'object' ? payload.relationships : {};
      const subClassEntry = Object.entries(rels).find(([p]) => String(p || '').toLowerCase().includes('subclassof'));
      if (!subClassEntry) continue;
      const allValues = Array.isArray(subClassEntry[1]) ? subClassEntry[1] : [subClassEntry[1]];
      const uri = String(payload.uri || '').trim();

      // Only the FIRST rdfs:subClassOf value is the real superclass;
      // the rest are restrictions / attribute constraints.
      const firstParent = String(allValues[0] || '').trim();
      if (!firstParent) continue;

      if (uriToRow[firstParent]) {
        // Internal parent
        if (!childrenOfUri[firstParent]) childrenOfUri[firstParent] = [];
        childrenOfUri[firstParent].push(row);
        if (uri) hasParentInSet.add(uri);
      } else {
        // External parent (e.g. nen2660 Activity, RealObject, etc.)
        if (!externalParentChildren[firstParent]) externalParentChildren[firstParent] = [];
        externalParentChildren[firstParent].push(row);
        if (uri) hasParentInSet.add(uri);
      }
    }

    // Resolve external URI to a readable label
    const resolveExternalLabel = (uri) => {
      const entry = (vs1EntityDirectory || {})[uri];
      if (entry && (entry.name_en || entry.name_english || entry.name)) return entry.name_en || entry.name_english || entry.name;
      const s = String(uri);
      const frag = s.includes('#') ? s.split('#').pop() : s.split('/').pop();
      // Skip pure UUIDs
      if (/^[0-9a-f]{8}-[0-9a-f]{4}/i.test(frag)) return uri;
      return frag || uri;
    };

    const resolveLabel = (row) => {
      const p = row?.data && typeof row.data === 'object' ? row.data : {};
      const n = String(p.notation || '').trim();
      const l = String(p.label_en || p.label_english || p.label || row?.name_en || row?.name || '').trim();
      return String(p.name_en || p.name_english || row?.name_en || row?.name || p.name || '').trim() || (n && l ? `${n} - ${l}` : n || l || '-');
    };

    // Build tree nodes recursively
    const buildNode = (row) => {
      const payload = row?.data && typeof row.data === 'object' ? row.data : {};
      const uri = String(payload.uri || '').trim();
      const children = (childrenOfUri[uri] || [])
        .sort((a, b) => resolveLabel(a).localeCompare(resolveLabel(b)));
      return {
        row,
        label: resolveLabel(row),
        hasChildren: children.length > 0,
        children: children.map(buildNode),
        isVirtual: false,
      };
    };

    // True orphans: no internal AND no external parent
    const trueOrphans = classes.filter((row) => {
      const payload = row?.data && typeof row.data === 'object' ? row.data : {};
      const uri = String(payload.uri || '').trim();
      return !hasParentInSet.has(uri);
    });

    // Build top-level: external parent virtual folders + true orphans
    const topLevel = [];

    // External parent folders
    for (const [extUri, extChildren] of Object.entries(externalParentChildren)) {
      const label = resolveExternalLabel(extUri);
      const sortedChildren = extChildren
        .sort((a, b) => resolveLabel(a).localeCompare(resolveLabel(b)))
        .map(buildNode);
      topLevel.push({
        row: { id: `ext-${extUri}`, name: label },
        label,
        hasChildren: true,
        children: sortedChildren,
        isVirtual: true,
      });
    }

    // True orphans as direct nodes
    for (const row of trueOrphans) {
      topLevel.push(buildNode(row));
    }

    topLevel.sort((a, b) => a.label.localeCompare(b.label));
    return topLevel;
  }, [visibleClassRows, vs1EntityDirectory]);

  // Compute grouped verification plans by Phase → Verification Method for tree view
  const groupedVerificationPlans = useMemo(() => {
    const vps = vs1Sections?.verificationPlans || [];
    if (vps.length === 0) return [];

    const resolveValue = (val, payload = {}) => {
      if (!val) return '';
      const s = String(val).trim();
      if (!s) return '';

      const targetLabels = payload && typeof payload._target_labels === 'object' ? payload._target_labels : {};
      const targetInfo = targetLabels[s];
      if (targetInfo && typeof targetInfo === 'object') {
        const targetName = String(targetInfo.name_english || targetInfo.name || targetInfo.notation || '').trim();
        if (targetName) return targetName;
      }

      const directoryEntry = vs1EntityDirectory[s];
      if (directoryEntry) {
        return directoryEntry.name_english || directoryEntry.name || directoryEntry.notation || s.split('/').pop().split('#').pop();
      }

      if (s.startsWith('http://') || s.startsWith('https://')) {
        return s.split('/').pop().split('#').pop();
      }
      return s;
    };

    // Get attribute value from relationships OR extra_properties
    const getAttrValues = (payload, key) => {
      // Also try the full URI variant when key is prefixed (tennet:xxx → http://data.tennet.eu/def/xxx)
      const keys = [key];
      if (key.startsWith('tennet:')) keys.push('http://data.tennet.eu/def/' + key.slice('tennet:'.length));
      const rels = payload.relationships && typeof payload.relationships === 'object' ? payload.relationships : {};
      const extra = payload.extra_properties && typeof payload.extra_properties === 'object' ? payload.extra_properties : {};
      for (const k of keys) {
        const relVals = Array.isArray(rels[k]) ? rels[k] : (rels[k] ? [rels[k]] : []);
        if (relVals.length > 0) return relVals;
        const attrVal = extra[k];
        if (attrVal != null) return Array.isArray(attrVal) ? attrVal : [attrVal];
      }
      return [];
    };

    const phaseMap = {};
    for (const row of vps) {
      const payload = row?.data && typeof row.data === 'object' ? row.data : {};

      const phaseVals = getAttrValues(payload, 'tennet:hasPhase');
      const phaseName = phaseVals.length > 0 ? resolveValue(phaseVals[0], payload) : 'Unclassified';

      const methodVals = getAttrValues(payload, 'tennet:hasVerificationMethod');
      const methodName = methodVals.length > 0 ? resolveValue(methodVals[0], payload) : 'Unclassified';

      if (!phaseMap[phaseName]) phaseMap[phaseName] = {};
      if (!phaseMap[phaseName][methodName]) phaseMap[phaseName][methodName] = [];
      phaseMap[phaseName][methodName].push(row);
    }

    return Object.entries(phaseMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([phase, methods]) => ({
        phase,
        methods: Object.entries(methods)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([method, rows]) => ({ method, rows })),
      }));
  }, [vs1Sections?.verificationPlans, vs1EntityDirectory]);

  // Build graph data for the selected entity, memoized to prevent flicker
  const vs1GraphDataCache = useRef({ uri: null, data: null });
  const buildVs1GraphData = useCallback((dp) => {
    const uri = dp?.uri || null;
    if (uri && vs1GraphDataCache.current.uri === uri) return vs1GraphDataCache.current.data;

    const nodes = [];
    const links = [];
    const nodeSet = new Set();

    const selfLabel = dp.notation
      ? (dp.label ? `${dp.notation} — ${dp.label}` : dp.notation)
      : (dp.label || dp.name || 'Selected');
    const selfId = dp.uri || 'self';
    nodes.push({ id: selfId, label: selfLabel, type: 'self', color: GRAPH_NODE_COLORS.self });
    nodeSet.add(selfId);

    const resolveUriForGraph = (targetUri) => {
      const entry = vs1EntityDirectory[targetUri];
      if (entry) return { notation: entry.notation, name: entry.name, type: humanizeEntityType(entry.entity_type) };
      return null;
    };

    if (dp.relationships && typeof dp.relationships === 'object') {
      Object.entries(dp.relationships).forEach(([predicate, targets]) => {
        const predLabel = KNOWN_RELATIONSHIP_LABELS[predicate] || humanizeKey(predicate.split('/').pop().split('#').pop());
        const edgeColor = GRAPH_EDGE_COLORS[predLabel] || GRAPH_EDGE_COLORS.default;
        const targetList = Array.isArray(targets) ? targets : [targets];
        targetList.forEach((targetUri) => {
          if (!targetUri) return;
          const resolved = resolveUriForGraph(targetUri);
          if (!nodeSet.has(targetUri)) {
            const nodeLabel = resolved
              ? (resolved.notation ? (resolved.name && resolved.name !== resolved.notation ? `${resolved.notation} — ${resolved.name}` : resolved.notation) : resolved.name)
              : targetUri.split('/').pop().split('#').pop();
            const entityType = resolved ? humanizeEntityType(resolved.type) : '';
            nodes.push({ id: targetUri, label: nodeLabel, type: entityType, color: GRAPH_NODE_COLORS[entityType] || GRAPH_NODE_COLORS.default });
            nodeSet.add(targetUri);
          }
          links.push({ source: selfId, target: targetUri, label: predLabel, color: edgeColor });
        });
      });
    }

    if (Array.isArray(dp.incoming_relationships)) {
      dp.incoming_relationships.forEach((rel) => {
        const fromUri = rel.from;
        if (!fromUri) return;
        const predLabel = KNOWN_RELATIONSHIP_LABELS[rel.predicate] || humanizeKey((rel.predicate || '').split('/').pop().split('#').pop());
        const edgeColor = GRAPH_EDGE_COLORS[predLabel] || GRAPH_EDGE_COLORS.default;
        const resolved = resolveUriForGraph(fromUri);
        if (!nodeSet.has(fromUri)) {
          const nodeLabel = resolved
            ? (resolved.notation ? (resolved.name && resolved.name !== resolved.notation ? `${resolved.notation} — ${resolved.name}` : resolved.notation) : resolved.name)
            : fromUri.split('/').pop().split('#').pop();
          const entityType = resolved ? humanizeEntityType(resolved.type) : '';
          nodes.push({ id: fromUri, label: nodeLabel, type: entityType, color: GRAPH_NODE_COLORS[entityType] || GRAPH_NODE_COLORS.default });
          nodeSet.add(fromUri);
        }
        links.push({ source: fromUri, target: selfId, label: predLabel, color: edgeColor });
      });
    }

    const result = { nodes, links };
    vs1GraphDataCache.current = { uri, data: result };
    return result;
  }, [vs1EntityDirectory]);

  useEffect(() => {
    showErrorRef.current = showError;
  }, [showError]);

  const scrollProjectDialogToBottom = useCallback(() => {
    const contentElement = projectDialogContentRef.current;
    if (!contentElement) {
      return;
    }

    contentElement.scrollTop = contentElement.scrollHeight;
  }, []);

  const queueScrollProjectDialogToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollProjectDialogToBottom();
      });
    });
  }, [scrollProjectDialogToBottom]);

  const fetchProjects = useCallback(async () => {
    try {
      setLoadingProjects(true);
      const response = await loadProjects();
      setProjects(response.projects || []);
    } catch (error) {
      if (showErrorRef.current) {
        showErrorRef.current(error.response?.data?.error || 'Error loading projects.');
      }
    } finally {
      setLoadingProjects(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    setVs1SectionSelection((previousSelection) => {
      const nextSelection = { ...createEmptyVs1SectionSelection() };
      let hasChanges = false;

      Object.keys(nextSelection).forEach((sectionKey) => {
        const sourceRows = Array.isArray(vs1Sections?.[sectionKey]) ? vs1Sections[sectionKey] : [];
        const rows = sectionKey === 'classes' ? visibleClassRows : sourceRows;
        const previousId = previousSelection?.[sectionKey];
        const existsInCurrentRows = rows.some(
          (row) => String(row?.id ?? '') === String(previousId ?? '')
        );
        const firstRowId = rows.length > 0 ? rows[0]?.id ?? null : null;

        nextSelection[sectionKey] = existsInCurrentRows ? previousId : firstRowId;

        if (nextSelection[sectionKey] !== previousId) {
          hasChanges = true;
        }
      });

      return hasChanges ? nextSelection : previousSelection;
    });
  }, [vs1Sections, visibleClassRows]);

  const fetchProjectOntologies = useCallback(async (projectId, viewKey = 'vs1') => {
    try {
      setLoadingOntologies(true);
      const response = await loadProjectOntologies(projectId, viewKey);
      setProjectOntologies(response.ontologies || []);
      setOntologiesSummary(response.summary || null);
      setProjectViews(response.project_views?.tabs || []);
    } catch (error) {
      setProjectOntologies([]);
      setOntologiesSummary(null);
      setProjectViews([]);
      if (showErrorRef.current) {
        showErrorRef.current(error.response?.data?.error || 'Error loading project ontologies.');
      }
    } finally {
      setLoadingOntologies(false);
    }
  }, []);

  const refreshProjectViews = useCallback(async (projectId, options = {}) => {
    const { silent = false } = options;
    if (!projectId) {
      return [];
    }

    try {
      const response = await loadProjectViews(projectId);
      const nextViews = response?.views?.tabs || [];
      setProjectViews(nextViews);
      return nextViews;
    } catch (error) {
      if (!silent && showErrorRef.current) {
        showErrorRef.current(error.response?.data?.error || 'Error loading project views.');
      }
      return [];
    }
  }, []);

  useEffect(() => {
    if (!selectedProject?.id || !isProjectDocumentsGraphGenerationInProgress) {
      return;
    }

    let disposed = false;

    const refreshProgress = async () => {
      if (disposed) {
        return;
      }
      await refreshProjectViews(selectedProject.id, { silent: true });
    };

    // Refresh immediately to reduce stale UI right after queueing.
    refreshProgress();

    const timer = window.setInterval(() => {
      refreshProgress();
    }, PROJECT_GRAPH_POLL_INTERVAL_MS);

    return () => {
      disposed = true;
      window.clearInterval(timer);
    };
  }, [isProjectDocumentsGraphGenerationInProgress, refreshProjectViews, selectedProject?.id]);

  const fetchVs1Sections = useCallback(async (projectId, source = 'project', options = {}) => {
    const normalizedProjectId = Number(projectId || 0);
    const normalizedSource = String(source || 'project').trim().toLowerCase() || 'project';
    const { force = false } = options || {};

    if (!normalizedProjectId) {
      setVs1Sections(createEmptyVsSections());
      setVs1EntityDirectory({});
      return;
    }

    const cacheKey = `${normalizedProjectId}:${normalizedSource}`;
    if (!force) {
      const cached = vs1SectionsCacheRef.current.get(cacheKey);
      if (cached) {
        setVs1Sections(cached.sections || createEmptyVsSections());
        setVs1EntityDirectory(cached.entityDirectory || {});
        return;
      }
    }

    try {
      setLoadingVs1Sections(true);
      const response = await loadProjectViewSections(normalizedProjectId, 'vs1', normalizedSource);
      const nextSections = response.sections || createEmptyVsSections();
      const nextEntityDirectory = response.entity_directory || {};
      setVs1Sections(nextSections);
      setVs1EntityDirectory(nextEntityDirectory);
      vs1SectionsCacheRef.current.set(cacheKey, {
        sections: nextSections,
        entityDirectory: nextEntityDirectory,
      });
    } catch (error) {
      setVs1Sections(createEmptyVsSections());
      setVs1EntityDirectory({});
      vs1SectionsCacheRef.current.delete(cacheKey);
      if (showErrorRef.current) {
        showErrorRef.current(error.response?.data?.error || 'Error loading VS1 sections.');
      }
    } finally {
      setLoadingVs1Sections(false);
    }
  }, []);

  useEffect(() => {
    const projectId = Number(selectedProject?.id || 0);
    if (!projectId || pendingApplicabilityTasks.length === 0) {
      return undefined;
    }

    let disposed = false;

    const pollApplicabilityTasks = async () => {
      try {
        const queueState = await analysisApi.getProcessingQueue();
        if (disposed) {
          return;
        }

        const activeTaskIds = new Set();
        const currentTaskId = String(queueState?.current_task?.id || '').trim();
        if (currentTaskId) {
          activeTaskIds.add(currentTaskId);
        }

        const queuedTasks = Array.isArray(queueState?.queue) ? queueState.queue : [];
        queuedTasks.forEach((queueItem) => {
          const queuedTaskId = String(queueItem?.id || '').trim();
          if (queuedTaskId) {
            activeTaskIds.add(queuedTaskId);
          }
        });

        const nextPendingTasks = pendingApplicabilityTasks.map((task) => (
          activeTaskIds.has(task.taskId)
            ? { ...task, seenActive: true }
            : task
        ));

        const finishedTasks = nextPendingTasks.filter((task) => task.seenActive && !activeTaskIds.has(task.taskId));
        if (finishedTasks.length === 0) {
          setPendingApplicabilityTasks(nextPendingTasks);
          return;
        }

        setPendingApplicabilityTasks(
          nextPendingTasks.filter((task) => activeTaskIds.has(task.taskId))
        );

        await fetchVs1Sections(projectId, vs1Source, { force: true });

        if (showSuccess) {
          if (finishedTasks.length === 1) {
            showSuccess(`${finishedTasks[0].scopeLabel} finished. Data refreshed automatically.`);
          } else {
            showSuccess('Queued applicability analyses finished. Data refreshed automatically.');
          }
        }
      } catch (_queueError) {
        // Keep polling; transient queue failures should not break auto-refresh behavior.
      }
    };

    pollApplicabilityTasks();
    const intervalId = setInterval(pollApplicabilityTasks, 3000);

    return () => {
      disposed = true;
      clearInterval(intervalId);
    };
  }, [fetchVs1Sections, pendingApplicabilityTasks, selectedProject?.id, showSuccess, vs1Source]);

  const handleAddProjectClick = () => {
    if (!canCreateProjects || creatingProject) return;
    setNewProjectName('');
    setCreateProjectDialogOpen(true);
  };

  const handleCloseCreateProjectDialog = () => {
    if (creatingProject) return;
    setCreateProjectDialogOpen(false);
    setNewProjectName('');
  };

  const handleCreateProject = async () => {
    if (!canCreateProjects) return;

    const normalizedName = newProjectName.trim();
    if (!normalizedName) {
      if (showError) {
        showError('Project name is required.');
      }
      return;
    }

    try {
      setCreatingProject(true);
      const response = await createProject(normalizedName);
      const createdProject = response?.project;

      if (!createdProject) {
        throw new Error('Project was not created.');
      }

      setProjects((prevProjects) => [
        createdProject,
        ...prevProjects.filter((item) => item.id !== createdProject.id),
      ]);

      setCreateProjectDialogOpen(false);
      setNewProjectName('');

      await handleProjectRowClick(createdProject);

      if (showSuccess) {
        showSuccess('Project created successfully.');
      }
    } catch (error) {
      if (showError) {
        showError(error.response?.data?.error || error.message || 'Error creating project.');
      }
    } finally {
      setCreatingProject(false);
    }
  };

  const handleRenameProjectClick = (event, project) => {
    event?.stopPropagation();
    if (!canCreateProjects || !project) return;

    setProjectToRename(project);
    setRenameProjectName(project.name || '');
    setRenameProjectDialogOpen(true);
  };

  const handleCloseRenameProjectDialog = () => {
    if (renamingProject) return;

    setRenameProjectDialogOpen(false);
    setProjectToRename(null);
    setRenameProjectName('');
  };

  const handleConfirmRenameProject = async () => {
    if (!canCreateProjects || !projectToRename) return;

    const normalizedName = renameProjectName.trim();
    if (!normalizedName) {
      if (showError) {
        showError('Project name is required.');
      }
      return;
    }

    try {
      setRenamingProject(true);
      const response = await renameProject(projectToRename.id, normalizedName);
      const updatedProject = response?.project;

      if (!updatedProject) {
        throw new Error('Project could not be renamed.');
      }

      setProjects((prevProjects) =>
        prevProjects.map((item) =>
          item.id === updatedProject.id ? updatedProject : item
        )
      );

      if (selectedProject?.id === updatedProject.id) {
        setSelectedProject(updatedProject);
      }

      if (showSuccess) {
        showSuccess(`Project renamed to "${updatedProject.name}".`);
      }

      setRenameProjectDialogOpen(false);
      setProjectToRename(null);
      setRenameProjectName('');
    } catch (error) {
      if (showError) {
        showError(error.response?.data?.error || error.message || 'Error renaming project.');
      }
    } finally {
      setRenamingProject(false);
    }
  };

  const handleDeleteProjectClick = (event, project) => {
    event?.stopPropagation();
    if (!canDeleteProjects || !project) return;

    setProjectToDelete(project);
    setConfirmDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    if (deletingProject) return;
    setConfirmDeleteDialogOpen(false);
    setProjectToDelete(null);
  };

  const handleConfirmDeleteProject = async () => {
    if (!canDeleteProjects || !projectToDelete) return;

    try {
      setDeletingProject(true);
      await deleteProject(projectToDelete.id);

      setProjects((prevProjects) =>
        prevProjects.filter((item) => item.id !== projectToDelete.id)
      );

      if (selectedProject?.id === projectToDelete.id) {
        handleCloseProjectDialog();
      }

      if (showSuccess) {
        showSuccess(`Project "${projectToDelete.name}" deleted successfully.`);
      }
    } catch (error) {
      if (showError) {
        showError(error.response?.data?.error || error.message || 'Error deleting project.');
      }
    } finally {
      setDeletingProject(false);
      setConfirmDeleteDialogOpen(false);
      setProjectToDelete(null);
    }
  };

  const handleDeleteDocument = (documentItem) => {
    setDocumentToDelete(documentItem);
    setConfirmDeleteDocumentDialogOpen(true);
  };

  const handleViewProjectDocument = async (documentItem) => {
    const documentModelId = Number(documentItem?.document_model_id || 0);
    const repositoryDocumentId = Number(documentItem?.repository_document_entity_id || 0);

    if (!documentModelId) {
      const fileUrl = String(documentItem?.file_url || '').trim();
      if (fileUrl) {
        window.open(fileUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      if (showError) {
        showError('Document preview is not available for this file yet.');
      }
      return;
    }

    try {
      setViewingProjectDocumentId(repositoryDocumentId || documentModelId);
      const fileBlob = await viewIngestionDocument(documentModelId);
      const objectUrl = window.URL.createObjectURL(fileBlob);
      const popup = window.open(objectUrl, '_blank', 'noopener,noreferrer');

      if (!popup) {
        window.URL.revokeObjectURL(objectUrl);
        throw new Error('Popup blocked by browser. Please allow popups for this site.');
      }

      window.setTimeout(() => {
        window.URL.revokeObjectURL(objectUrl);
      }, 60000);
    } catch (error) {
      if (showError) {
        showError(error?.response?.data?.error || error?.message || 'Error opening document preview.');
      }
    } finally {
      setViewingProjectDocumentId(null);
    }
  };

  const handleOpenOriginalDocumentById = useCallback(async (documentModelId) => {
    const resolvedDocumentId = Number(documentModelId || 0);
    if (!resolvedDocumentId) {
      if (showError) {
        showError('Document ID is not available for this location.');
      }
      return;
    }

    try {
      const fileBlob = await viewIngestionDocument(resolvedDocumentId);
      const objectUrl = window.URL.createObjectURL(fileBlob);
      const popup = window.open(objectUrl, '_blank', 'noopener,noreferrer');

      if (!popup) {
        window.URL.revokeObjectURL(objectUrl);
        throw new Error('Popup blocked by browser. Please allow popups for this site.');
      }

      window.setTimeout(() => {
        window.URL.revokeObjectURL(objectUrl);
      }, 60000);
    } catch (error) {
      if (showError) {
        showError(error?.response?.data?.error || error?.message || 'Error opening original document.');
      }
    }
  }, [showError]);

  const handleLoadCandidateLocations = useCallback(async (row, candidate, options = {}) => {
    const projectId = Number(selectedProject?.id || 0);
    const entityId = Number(row?.id || 0);
    const nodeId = Number(candidate?.nodeId || candidate?.node_id || 0);
    if (!projectId || !entityId || !nodeId) {
      if (showError) {
        showError('Candidate node is missing an identifier.');
      }
      return;
    }

    const key = `${entityId}:${nodeId}`;
    const shouldToggleOnly = options.toggleOnly === true;

    if (shouldToggleOnly && candidateLocationsByKey[key]) {
      setExpandedCandidateLocationsKey((prev) => (prev === key ? '' : key));
      return;
    }

    try {
      setLoadingCandidateLocationsKey(key);
      const response = await loadProjectObjectCandidateLocations(projectId, entityId, nodeId, 'vs1');
      setCandidateLocationsByKey((previous) => ({
        ...previous,
        [key]: {
          locations: Array.isArray(response?.locations) ? response.locations : [],
          documents: Array.isArray(response?.documents) ? response.documents : [],
          summary: response?.summary || {},
          loadedAt: Date.now(),
        },
      }));
      setExpandedCandidateLocationsKey(key);
    } catch (error) {
      if (showError) {
        showError(error?.response?.data?.error || error?.message || 'Could not load candidate locations.');
      }
    } finally {
      setLoadingCandidateLocationsKey('');
    }
  }, [candidateLocationsByKey, selectedProject?.id, showError]);

  const handleLoadDocumentPagePreview = useCallback(async (documentId, pageNumber, locationContext = null) => {
    const projectId = Number(selectedProject?.id || 0);
    const resolvedDocumentId = Number(documentId || 0);
    if (!projectId || !resolvedDocumentId) {
      return;
    }

    setPagePreviewState((previous) => ({
      ...previous,
      open: true,
      loading: true,
      projectId,
      documentId: resolvedDocumentId,
      pageNumber: pageNumber || previous.pageNumber,
      locationContext: locationContext || previous.locationContext || null,
    }));

    try {
      const response = await loadProjectDocumentPageContent(projectId, resolvedDocumentId, pageNumber || null);
      const page = response?.page || null;
      const availablePages = Array.isArray(response?.available_pages) ? response.available_pages : [];
      setPagePreviewState((previous) => ({
        ...previous,
        open: true,
        loading: false,
        projectId,
        documentId: resolvedDocumentId,
        documentTitle: String(response?.document_title || previous.documentTitle || ''),
        pageNumber: page?.page_number ?? null,
        availablePages,
        textOriginal: String(page?.text_original || ''),
        textTranslated: String(page?.text_translated || ''),
      }));
    } catch (error) {
      setPagePreviewState((previous) => ({
        ...previous,
        loading: false,
      }));
      if (showError) {
        showError(error?.response?.data?.error || error?.message || 'Could not load page content.');
      }
    }
  }, [selectedProject?.id, showError]);

  const handlePagePreviewNavigate = useCallback(async (direction) => {
    const availablePages = Array.isArray(pagePreviewState.availablePages) ? pagePreviewState.availablePages : [];
    const currentPage = Number(pagePreviewState.pageNumber || 0);
    const currentIndex = availablePages.findIndex((page) => Number(page) === currentPage);
    if (currentIndex < 0) {
      return;
    }

    const nextIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
    if (nextIndex < 0 || nextIndex >= availablePages.length) {
      return;
    }

    const nextPageNumber = availablePages[nextIndex];
    await handleLoadDocumentPagePreview(
      pagePreviewState.documentId,
      nextPageNumber,
      pagePreviewState.locationContext || null,
    );
  }, [handleLoadDocumentPagePreview, pagePreviewState.availablePages, pagePreviewState.documentId, pagePreviewState.locationContext, pagePreviewState.pageNumber]);

  const handleClosePagePreview = useCallback(() => {
    setPagePreviewState((previous) => ({
      ...previous,
      open: false,
      loading: false,
      locationContext: null,
    }));
  }, []);

  useEffect(() => {
    if (!pagePreviewState.open || pagePreviewState.loading) {
      return undefined;
    }

    const candidateTexts = [];
    if (Array.isArray(pagePreviewState.locationContext?.mentionTexts)) {
      candidateTexts.push(...pagePreviewState.locationContext.mentionTexts);
    }
    if (pagePreviewState.locationContext?.extractedText) {
      candidateTexts.push(pagePreviewState.locationContext.extractedText);
    }

    const hasMention = candidateTexts.some((value) => String(value || '').trim().length >= 4);
    if (!hasMention) {
      return undefined;
    }

    const rafId = window.requestAnimationFrame(() => {
      const dialogPaper = document.querySelector('[data-page-preview-dialog="true"]');
      const firstHighlight = dialogPaper?.querySelector('[data-mention-highlight="primary"]');
      if (firstHighlight && typeof firstHighlight.scrollIntoView === 'function') {
        firstHighlight.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });

    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [
    pagePreviewState.loading,
    pagePreviewState.locationContext,
    pagePreviewState.open,
    pagePreviewState.pageNumber,
    pagePreviewState.textOriginal,
    pagePreviewState.textTranslated,
  ]);

  const renderPagePreviewDialog = useCallback(() => {
    if (!pagePreviewState.open) {
      return null;
    }

    const mentionCandidates = (() => {
      const sourceValues = [];
      if (Array.isArray(pagePreviewState.locationContext?.mentionTexts)) {
        sourceValues.push(...pagePreviewState.locationContext.mentionTexts);
      }
      if (pagePreviewState.locationContext?.extractedText) {
        sourceValues.push(pagePreviewState.locationContext.extractedText);
      }

      const dedup = Array.from(new Set(
        sourceValues
          .map((value) => String(value || '').trim())
          .filter((value) => value.length >= 4)
      ));

      return dedup.sort((left, right) => right.length - left.length);
    })();

    const renderTextWithHighlights = (textValue, emptyFallback) => {
      const displayText = String(textValue || '').trim() || emptyFallback;
      if (!displayText || mentionCandidates.length === 0) {
        return (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: grey[900] }}>
            {displayText}
          </Typography>
        );
      }

      const lowerText = displayText.toLowerCase();
      const matchingMention = mentionCandidates.find((mention) => lowerText.includes(mention.toLowerCase()));
      if (!matchingMention) {
        return (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: grey[900] }}>
            {displayText}
          </Typography>
        );
      }

      const regex = new RegExp(`(${escapeRegExp(matchingMention)})`, 'ig');
      const parts = displayText.split(regex);
      let markedPrimary = false;

      return (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: grey[900] }}>
          {parts.map((part, index) => {
            if (part.toLowerCase() === matchingMention.toLowerCase()) {
              const isPrimary = !markedPrimary;
              if (isPrimary) {
                markedPrimary = true;
              }
              return (
                <span
                  key={`highlight-${index}`}
                  data-mention-highlight={isPrimary ? 'primary' : 'secondary'}
                  style={{ textDecoration: 'underline', fontWeight: 700, backgroundColor: '#fff59d', padding: '0 1px' }}
                >
                  {part}
                </span>
              );
            }
            return <span key={`plain-${index}`}>{part}</span>;
          })}
        </Typography>
      );
    };

    return (
      <Dialog
        open={pagePreviewState.open}
        onClose={handleClosePagePreview}
        maxWidth="xl"
        fullWidth
        fullHeight
        PaperProps={{
          'data-page-preview-dialog': 'true',
        }}
        sx={{
          '& .MuiDialog-paper': {
            height: '95vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {pagePreviewState.documentTitle || `Document ${pagePreviewState.documentId}`} · Page {pagePreviewState.pageNumber || '-'}
            </Typography>
            {pagePreviewState.locationContext?.candidateName && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
                Candidate: {pagePreviewState.locationContext.candidateName}
              </Typography>
            )}
            {pagePreviewState.locationContext?.extractedText && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: grey[700], whiteSpace: 'pre-wrap' }}>
                Mention: "{pagePreviewState.locationContext.extractedText}"
              </Typography>
            )}
            {mentionCandidates.length > 0 && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: grey[600] }}>
                Highlighted mentions: {mentionCandidates.slice(0, 3).join(' | ')}
              </Typography>
            )}
          </Box>
          <Button onClick={handleClosePagePreview} sx={{ ml: 2 }}>Close</Button>
        </DialogTitle>

        <DialogContent sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', pb: 1 }}>
          <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => handlePagePreviewNavigate('prev')}
              disabled={pagePreviewState.loading || !pagePreviewState.availablePages.includes(Number(pagePreviewState.pageNumber || 0)) || Number(pagePreviewState.pageNumber || 0) === Number(pagePreviewState.availablePages[0] || 0)}
            >
              Previous Page
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => handlePagePreviewNavigate('next')}
              disabled={pagePreviewState.loading || !pagePreviewState.availablePages.includes(Number(pagePreviewState.pageNumber || 0)) || Number(pagePreviewState.pageNumber || 0) === Number(pagePreviewState.availablePages[pagePreviewState.availablePages.length - 1] || 0)}
            >
              Next Page
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={() => handleOpenOriginalDocumentById(pagePreviewState.documentId)}
              disabled={!pagePreviewState.documentId}
            >
              Open Original Document
            </Button>
          </Box>

          {pagePreviewState.loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
              <LinearProgress sx={{ width: '100%', mb: 0.6 }} />
              <Typography variant="caption" color="text.secondary">Loading page content...</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 1.5, flex: 1, minHeight: 0, overflow: 'auto' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75, color: grey[700] }}>
                  Translated Text
                </Typography>
                <Box sx={{ flex: 1, overflowY: 'auto', p: 1.25, borderRadius: 1, bgcolor: grey[50], border: '1px solid', borderColor: grey[200] }}>
                  {renderTextWithHighlights(pagePreviewState.textTranslated, 'No translated text available for this page.')}
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75, color: grey[700] }}>
                  Original Text
                </Typography>
                <Box sx={{ flex: 1, overflowY: 'auto', p: 1.25, borderRadius: 1, bgcolor: grey[50], border: '1px solid', borderColor: grey[200] }}>
                  {renderTextWithHighlights(pagePreviewState.textOriginal, 'No original text available for this page.')}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    );
  }, [
    handleClosePagePreview,
    handleOpenOriginalDocumentById,
    handlePagePreviewNavigate,
    pagePreviewState.availablePages,
    pagePreviewState.documentId,
    pagePreviewState.documentTitle,
    pagePreviewState.loading,
    pagePreviewState.locationContext,
    pagePreviewState.open,
    pagePreviewState.pageNumber,
    pagePreviewState.textOriginal,
    pagePreviewState.textTranslated,
  ]);

  const handleOpenProjectDocumentsGraph = async () => {
    if (!selectedProject?.id) {
      return;
    }

    try {
      setProjectDocumentsGraphDialogOpen(true);
      setProjectDocumentsGraphLoading(true);
      setProjectDocumentsGraphError('');
      setProjectDocumentsGraphSearchTerm('');

      const response = await loadProjectDocumentsGraph(selectedProject.id);
      setProjectDocumentsGraphData(normalizeGraphPayloadForViewer(response?.graph));
    } catch (error) {
      setProjectDocumentsGraphData({ nodes: [], links: [] });
      setProjectDocumentsGraphError(error?.response?.data?.error || error?.message || 'Error loading project graph.');
    } finally {
      setProjectDocumentsGraphLoading(false);
    }
  };

  const handleCloseProjectDocumentsGraphDialog = useCallback(() => {
    try {
      projectDocumentsGraphRef.current?.graphData?.({ nodes: [], links: [] });
    } catch {
      // Ignore cleanup errors from graph instance lifecycle.
    }

    setProjectDocumentsGraphDialogOpen(false);
    setProjectDocumentsGraphLoading(false);
    setProjectDocumentsGraphError('');
    setProjectDocumentsGraphSearchTerm('');
    setProjectDocumentsGraphDims({ w: 0, h: 0 });
    setProjectDocumentsGraphData({ nodes: [], links: [] });
  }, []);

  const handleCloseSingleDocumentGraphDialog = useCallback(() => {
    try {
      singleDocumentGraphRef.current?.graphData?.({ nodes: [], links: [] });
    } catch {
      // Ignore cleanup errors from graph instance lifecycle.
    }

    setSingleDocumentGraphDialogOpen(false);
    setSingleDocumentGraphLoading(false);
    setSingleDocumentGraphError('');
    setSingleDocumentGraphSearchTerm('');
    setSingleDocumentGraphTitle('Document Graph');
    setSingleDocumentGraphDims({ w: 0, h: 0 });
    setSingleDocumentGraphData({ nodes: [], links: [] });
  }, []);

  const handleOpenSingleDocumentGraph = async (documentItem) => {
    const documentModelId = Number(documentItem?.document_model_id || 0);
    if (!documentModelId) {
      if (showError) {
        showError('Document graph is not available yet for this file.');
      }
      return;
    }

    try {
      setSingleDocumentGraphDialogOpen(true);
      setSingleDocumentGraphLoading(true);
      setSingleDocumentGraphError('');
      setSingleDocumentGraphSearchTerm('');
      setSingleDocumentGraphTitle(documentItem?.original_filename || documentItem?.label || 'Document Graph');

      const response = await getIngestionDocumentGraph(documentModelId);
      setSingleDocumentGraphData(normalizeGraphPayloadForViewer(response?.graph));
    } catch (error) {
      setSingleDocumentGraphData({ nodes: [], links: [] });
      setSingleDocumentGraphError(error?.response?.data?.error || error?.message || 'Error loading document graph.');
    } finally {
      setSingleDocumentGraphLoading(false);
    }
  };

  const handleGenerateProjectDocumentsGraph = async (options = {}) => {
    if (!selectedProject?.id) {
      return;
    }

    const hasPendingDocumentUploads = documentUploadItems.some((item) => (
      item.status === 'pending' || item.status === 'uploading' || item.status === 'processing'
    ));

    if (hasPendingDocumentUploads || uploadingProjectViewKey === 'documents') {
      if (showError) {
        showError('Wait until all document uploads and processing are completed before generating the project graph.');
      }
      return;
    }

    try {
      const response = await generateProjectDocumentsGraph(selectedProject.id, options);
      await refreshProjectViews(selectedProject.id, { silent: true });

      if (showSuccess) {
        const queuedMessage = response?.message || 'Project graph generation queued.';
        showSuccess(queuedMessage);
      }
    } catch (error) {
      if (showError) {
        showError(error?.response?.data?.error || error?.message || 'Error generating project graph.');
      }
    }
  };

  const handleConfirmDeleteDocument = async () => {
    if (!documentToDelete) return;

    const projectId = Number(selectedProject?.id || 0);
    const repositoryDocumentEntityId = Number(documentToDelete.repository_document_entity_id || 0);

    if (!projectId) {
      if (showError) {
        showError('Project ID not found. Cannot unlink document.');
      }
      return;
    }

    if (!repositoryDocumentEntityId) {
      if (showError) {
        showError('Project document ID not found. Cannot unlink.');
      }
      return;
    }

    try {
      setDeletingDocument(true);
      await deleteProjectDocument(projectId, repositoryDocumentEntityId);

      // Refresh project views to update the documents list
      await refreshProjectViews(projectId, { silent: true });

      if (showSuccess) {
        showSuccess(`Document "${documentToDelete.original_filename || documentToDelete.label}" removed from this project. The file and processed data were kept.`);
      }
    } catch (error) {
      if (showError) {
        showError(error.response?.data?.error || error.message || 'Error unlinking document from project.');
      }
    } finally {
      setDeletingDocument(false);
      setConfirmDeleteDocumentDialogOpen(false);
      setDocumentToDelete(null);
    }
  };

  const handleProjectRowClick = async (project, options = {}) => {
    const { forceModal = false } = options;

    if (project && canOpenProjects && !forceModal) {
      onOpenProject(project);
      return;
    }

    setSelectedProject(project);
    setProjectDialogOpen(true);
    setBootstrappingProjectDetail(true);
    setObjectsApplicabilityReview(null);
    setProjectDetailTab('vs1');
    setVs1Source('project');
    setVs1SectionModalOpen(false);
    setVs1SectionModalKey('');
    setVs1SectionSelection(createEmptyVs1SectionSelection());
    setProjectDocumentsGraphData({ nodes: [], links: [] });

    try {
      // Run initial requests in parallel so VS1 sections can render as soon as possible.
      await Promise.allSettled([
        fetchVs1Sections(project.id, 'project'),
        fetchProjectOntologies(project.id, 'vs1'),
        refreshProjectViews(project.id, { silent: true }),
      ]);
    } finally {
      setBootstrappingProjectDetail(false);
    }
  };

  useEffect(() => {
    autoOpenedProjectIdRef.current = null;
  }, [normalizedInitialProjectId]);

  useEffect(() => {
    if (!normalizedInitialProjectId || loadingProjects) {
      return;
    }

    if (projectDialogOpen && Number(selectedProject?.id || 0) === normalizedInitialProjectId) {
      return;
    }

    if (autoOpenedProjectIdRef.current === normalizedInitialProjectId) {
      return;
    }

    const matchingProject = projects.find(
      (projectItem) => Number(projectItem?.id || 0) === normalizedInitialProjectId,
    );

    if (!matchingProject) {
      return;
    }

    autoOpenedProjectIdRef.current = normalizedInitialProjectId;
    handleProjectRowClick(matchingProject, { forceModal: true });
  }, [normalizedInitialProjectId, loadingProjects, projectDialogOpen, selectedProject?.id, projects]);

  const handleProjectDetailTabChange = (event, value) => {
    setProjectDetailTab(value);
    if (value !== 'vs1') {
      setVs1SectionModalOpen(false);
      setVs1SectionModalKey('');
    }

    if (selectedProject) {
      fetchProjectOntologies(selectedProject.id, value);
      if (value === 'documents') {
        refreshProjectViews(selectedProject.id, { silent: true });
      }
      if (value === 'vs1') {
        fetchVs1Sections(selectedProject.id, vs1Source);
      }
    }
  };

  const handleVs1SourceChange = async (event, value) => {
    if (!value || value === vs1Source) {
      return;
    }

    setVs1Source(value);
    setVs1SectionModalOpen(false);
    setVs1SectionModalKey('');
    setVs1SectionSelection(createEmptyVs1SectionSelection());
    setObjectsApplicabilityReview(null);
    queueScrollProjectDialogToBottom();

    if (selectedProject && projectDetailTab === 'vs1') {
      await fetchVs1Sections(selectedProject.id, value);
      queueScrollProjectDialogToBottom();
    }
  };

  const handleApplyVs1 = async () => {
    if (!selectedProject || projectDetailTab !== 'vs1') {
      return;
    }

    setApplyConfirmation({ open: true, kind: 'vs1', viewKey: 'vs1' });
  };

  const handleConfirmApply = async () => {
    const { kind } = applyConfirmation;
    setApplyConfirmation({ open: false, kind: '', viewKey: '' });

    if (kind === 'vs1') {
      try {
        setLoadingVs1Sections(true);
        const response = await applyProjectVs1(selectedProject.id);
        setVs1Source('project');
        setVs1SectionModalOpen(false);
        setVs1SectionModalKey('');
        setVs1SectionSelection(createEmptyVs1SectionSelection());
        await fetchProjectOntologies(selectedProject.id, 'vs1');
        await fetchVs1Sections(selectedProject.id, 'project', { force: true });

        if (showSuccess) {
          showSuccess(
            formatVs1ApplySummaryMessage(
              response?.apply_summary,
              'VS1 applied successfully.'
            )
          );
        }
      } catch (error) {
        if (showErrorRef.current) {
          showErrorRef.current(error.response?.data?.error || 'Error applying VS1.');
        }
      } finally {
        setLoadingVs1Sections(false);
      }
    }
  };

  const handleApplyOntologies = async () => {
    if (!selectedProject || projectDetailTab === 'documents') {
      return;
    }

    setApplyConfirmation({ open: true, kind: 'ontologies', viewKey: projectDetailTab });
  };

  const handleConfirmApplyOntologies = async () => {
    const { viewKey } = applyConfirmation;
    setApplyConfirmation({ open: false, kind: '', viewKey: '' });

    try {
      setApplyingOntologies(true);
      const response = await applyProjectOntologies(selectedProject.id, viewKey);
      await fetchProjectOntologies(selectedProject.id, viewKey);

      if (viewKey === 'vs1') {
        setVs1Source('ontology');
        setVs1SectionModalOpen(false);
        setVs1SectionModalKey('');
        setVs1SectionSelection(createEmptyVs1SectionSelection());

        // Use sections returned by the apply response if available, otherwise refetch
        if (response?.sections) {
          setVs1Sections(response.sections);
          setVs1EntityDirectory(response.entity_directory || {});
          vs1SectionsCacheRef.current.set(`${Number(selectedProject.id)}:ontology`, {
            sections: response.sections,
            entityDirectory: response.entity_directory || {},
          });
        } else {
          await fetchVs1Sections(selectedProject.id, 'ontology', { force: true });
        }
      }

      if (showSuccess) {
        showSuccess(
          formatOntologiesApplySummaryMessage(
            viewKey,
            response?.apply_summary,
            response?.message || `Ontologies applied successfully for ${String(viewKey || '').toUpperCase() || 'VS1'}.`
          )
        );
      }
    } catch (error) {
      if (showErrorRef.current) {
        showErrorRef.current(error.response?.data?.error || 'Error applying ontologies.');
      }
    } finally {
      setApplyingOntologies(false);
    }
  };

  const handleOpenVs1SectionModal = (sectionKey, rowId = null) => {
    const normalizedSectionKey = String(sectionKey || '').trim();
    if (!normalizedSectionKey) {
      return;
    }

    if (rowId !== null && rowId !== undefined) {
      setVs1SectionSelection((previousSelection) => ({
        ...previousSelection,
        [normalizedSectionKey]: rowId,
      }));
    }

    setVs1SectionModalKey(normalizedSectionKey);
    setVs1SectionModalOpen(true);
  };

  const handleCloseVs1SectionModal = () => {
    setVs1SectionModalOpen(false);
    setVs1SectionModalKey('');
  };

  const handleSelectVs1SectionRow = (sectionKey, rowId) => {
    setVs1DetailTab(0);
    setVs1SectionSelection((previousSelection) => ({
      ...previousSelection,
      [sectionKey]: rowId,
    }));
  };

  const handleNavigateToVs1LinkedRow = useCallback((sectionKey, rowId, options = {}) => {
    const normalizedSectionKey = String(sectionKey || '').trim();
    const numericRowId = Number(rowId || 0);
    const preferModal = Boolean(options?.preferModal);
    if (!normalizedSectionKey || !numericRowId) {
      return;
    }

    const expansionUpdates = {};

    if (normalizedSectionKey === 'requirements') {
      groupedRequirements.forEach(({ typeName, vakGroups }) => {
        const matchingVakGroup = (vakGroups || []).find(([, vakRows]) =>
          (vakRows || []).some((row) => Number(row?.id || 0) === numericRowId)
        );
        if (!matchingVakGroup) {
          return;
        }
        const vakName = String(matchingVakGroup[0] || 'Unclassified');
        expansionUpdates[`req-type-${typeName}`] = true;
        expansionUpdates[`req-vak-${typeName}-${vakName}`] = true;
      });
    }

    if (normalizedSectionKey === 'objects') {
      groupedObjects.forEach(({ superClass, classes }) => {
        (classes || []).forEach(({ className, rows }) => {
          const hasTarget = (rows || []).some((row) => Number(row?.id || 0) === numericRowId);
          if (!hasTarget) {
            return;
          }
          expansionUpdates[`obj-super-${superClass}`] = true;
          expansionUpdates[`obj-class-${superClass}-${className}`] = true;
        });
      });

      const markHasPartPath = (nodes, ancestorKeys = []) => {
        (nodes || []).forEach((node) => {
          const nodeKey = String(node?.key || '');
          const rowIdValue = Number(node?.row?.id || 0);
          const nextAncestors = nodeKey ? [...ancestorKeys, nodeKey] : ancestorKeys;

          if (rowIdValue === numericRowId) {
            nextAncestors.forEach((ancestorKey) => {
              expansionUpdates[`obj-haspart-${ancestorKey}`] = true;
            });
          }

          if (Array.isArray(node?.children) && node.children.length > 0) {
            markHasPartPath(node.children, nextAncestors);
          }
        });
      };

      markHasPartPath(groupedObjectsByHasPart, []);
    }

    if (Object.keys(expansionUpdates).length > 0) {
      setVs1ExpandedGroups((previousGroups) => ({
        ...previousGroups,
        ...expansionUpdates,
      }));
    }

    setVs1DetailTab(0);
    setVs1SectionSelection((previousSelection) => ({
      ...previousSelection,
      [normalizedSectionKey]: numericRowId,
    }));
    setDeepDiveSectionKey(normalizedSectionKey);

    if (preferModal) {
      setVs1SectionModalKey(normalizedSectionKey);
      setVs1SectionModalOpen(true);
    }
  }, [groupedObjects, groupedObjectsByHasPart, groupedRequirements]);

  const handleUpdateVs1RowApplicability = useCallback(async (row, statusValue) => {
    const projectId = Number(selectedProject?.id || 0);
    const rowId = Number(row?.id || 0);
    const normalizedStatus = String(statusValue || '').trim().toLowerCase();
    if (!projectId || !rowId || !normalizedStatus) {
      return;
    }

    try {
      setUpdatingVs1ApplicabilityRowId(rowId);
      await updateProjectEntityApplicability(projectId, rowId, normalizedStatus);
      await fetchVs1Sections(projectId, vs1Source, { force: true });
      if (showSuccess) {
        showSuccess(normalizedStatus === 'aplica' ? 'Marked as Applies.' : 'Marked as Does not apply.');
      }
    } catch (error) {
      if (showError) {
        showError(error?.response?.data?.error || 'Could not update applicability status.');
      }
    } finally {
      setUpdatingVs1ApplicabilityRowId(null);
    }
  }, [fetchVs1Sections, selectedProject?.id, showError, showSuccess, vs1Source]);

  const handleRunObjectsApplicabilityAnalysis = useCallback(async () => {
    const projectId = Number(selectedProject?.id || 0);
    if (!projectId) {
      return;
    }

    try {
      setRunningObjectsApplicabilityAnalysis(true);
      const response = await analyzeProjectObjectApplicability(projectId, 'vs1');

      if (response?.queued) {
        trackQueuedApplicabilityTask(response?.task_id, 'Object applicability analysis');
        if (showSuccess) {
          showSuccess(
            response?.already_running
              ? 'Object applicability analysis was already queued or processing. Monitoring the existing task.'
              : 'Object applicability analysis queued in Celery. You can monitor it from the processing queue.'
          );
        }
        if (typeof onToggleProcessingPanel === 'function') {
          onToggleProcessingPanel(true);
        }
        return;
      }

      await fetchVs1Sections(projectId, vs1Source, { force: true });
      if (showSuccess) {
        const aplicaCount = Number(response?.aplica_count || 0);
        const noAplicaCount = Number(response?.no_aplica_count || 0);
        const indeterminateCount = Number(response?.indeterminate_count || 0);
        showSuccess(`Analysis completed. Applies: ${aplicaCount}, Does not apply: ${noAplicaCount}, Indeterminate: ${indeterminateCount}.`);
      }
    } catch (error) {
      if (showError) {
        if (isLongRunningAnalysisRequestError(error)) {
          showError(
            'The analysis request was closed due to timeout/network, but the backend may still be processing. Wait a moment and reopen/refresh the objects section.'
          );
        } else {
          showError(error?.response?.data?.error || 'Could not run objects applicability analysis.');
        }
      }

      // Try a best-effort refresh in case backend finished after request interruption.
      try {
        await fetchVs1Sections(projectId, vs1Source, { force: true });
      } catch (refreshError) {
        // Ignore refresh errors here; the main error has already been surfaced.
      }
    } finally {
      setRunningObjectsApplicabilityAnalysis(false);
    }
  }, [fetchVs1Sections, onToggleProcessingPanel, selectedProject?.id, showError, showSuccess, trackQueuedApplicabilityTask, vs1Source]);

  const handleRunRequirementsApplicabilityAnalysis = useCallback(async () => {
    const projectId = Number(selectedProject?.id || 0);
    if (!projectId) {
      return;
    }

    try {
      setRunningRequirementsApplicabilityAnalysis(true);
      const response = await analyzeProjectRequirementApplicability(projectId, 'vs1', vs1Source);

      if (response?.queued) {
        trackQueuedApplicabilityTask(response?.task_id, 'Requirement applicability analysis');
        if (showSuccess) {
          showSuccess(
            response?.already_running
              ? 'Requirement applicability analysis was already queued or processing. Monitoring the existing task.'
              : 'Requirement applicability analysis queued in Celery. You can monitor it from the processing queue.'
          );
        }
        if (typeof onToggleProcessingPanel === 'function') {
          onToggleProcessingPanel(true);
        }
        return;
      }

      await fetchVs1Sections(projectId, vs1Source, { force: true });
      if (showSuccess) {
        const directCount = Number(response?.direct_apply_count || 0);
        const documentsCount = Number(response?.needs_project_documents_count || 0);
        const dependsCount = Number(response?.depends_on_other_object_count || 0);
        showSuccess(
          `First pass completed. Direct: ${directCount}, Needs documents: ${documentsCount}, Depends on other object: ${dependsCount}.`
        );
      }
    } catch (error) {
      if (showError) {
        if (isLongRunningAnalysisRequestError(error)) {
          showError(
            'The analysis request was closed due to timeout/network, but the backend may still be processing. Wait a moment and reopen/refresh the requirements section.'
          );
        } else {
          showError(error?.response?.data?.error || 'Could not run requirements applicability first pass.');
        }
      }

      try {
        await fetchVs1Sections(projectId, vs1Source, { force: true });
      } catch (refreshError) {
        // Ignore refresh errors here; the main error has already been surfaced.
      }
    } finally {
      setRunningRequirementsApplicabilityAnalysis(false);
    }
  }, [fetchVs1Sections, onToggleProcessingPanel, selectedProject?.id, showError, showSuccess, trackQueuedApplicabilityTask, vs1Source]);

  const handleRunSingleApplicabilityAnalysis = useCallback(async (sectionKey, row) => {
    const projectId = Number(selectedProject?.id || 0);
    const rowId = Number(row?.id || 0);
    const normalizedSectionKey = String(sectionKey || '').trim().toLowerCase();
    if (!projectId || !rowId || !['objects', 'requirements'].includes(normalizedSectionKey)) {
      return;
    }

    const requestKey = `${normalizedSectionKey}:${rowId}`;

    try {
      setRunningSingleApplicabilityAnalysisKey(requestKey);
      let response;

      if (normalizedSectionKey === 'objects') {
        response = await analyzeProjectObjectApplicability(projectId, 'vs1', rowId);
      } else {
        response = await analyzeProjectRequirementApplicability(projectId, 'vs1', vs1Source, rowId);
      }

      if (response?.queued) {
        trackQueuedApplicabilityTask(
          response?.task_id,
          normalizedSectionKey === 'objects' ? 'Object analysis' : 'Requirement analysis'
        );
        if (showSuccess) {
          const rowLabel = normalizedSectionKey === 'objects' ? 'Object' : 'Requirement';
          showSuccess(
            response?.already_running
              ? `${rowLabel} analysis was already queued or processing. Monitoring the existing task.`
              : `${rowLabel} analysis queued in Celery. It will update once processing finishes.`
          );
        }
        if (typeof onToggleProcessingPanel === 'function') {
          onToggleProcessingPanel(true);
        }
        return;
      }

      await fetchVs1Sections(projectId, vs1Source, { force: true });
      if (showSuccess) {
        const appliesCount = Number(response?.aplica_count ?? response?.direct_apply_count ?? 0);
        const notAppliesCount = Number(response?.no_aplica_count ?? 0);
        const indeterminateCount = Number(response?.indeterminate_count ?? 0);
        const rowLabel = normalizedSectionKey === 'objects' ? 'Object' : 'Requirement';
        showSuccess(`${rowLabel} analysis completed. Applies: ${appliesCount}, Does not apply: ${notAppliesCount}, Indeterminate: ${indeterminateCount}.`);
      }
    } catch (error) {
      if (showError) {
        if (isLongRunningAnalysisRequestError(error)) {
          showError(
            normalizedSectionKey === 'objects'
              ? 'The object analysis request was interrupted, but the backend may still be processing. Refresh the objects section in a moment.'
              : 'The requirement analysis request was interrupted, but the backend may still be processing. Refresh the requirements section in a moment.'
          );
        } else {
          showError(
            error?.response?.data?.error
            || (normalizedSectionKey === 'objects'
              ? 'Could not run object applicability analysis.'
              : 'Could not run requirement applicability analysis.')
          );
        }
      }

      try {
        await fetchVs1Sections(projectId, vs1Source, { force: true });
      } catch (refreshError) {
        // Ignore refresh errors here; the main error has already been surfaced.
      }
    } finally {
      setRunningSingleApplicabilityAnalysisKey('');
    }
  }, [fetchVs1Sections, onToggleProcessingPanel, selectedProject?.id, showError, showSuccess, trackQueuedApplicabilityTask, vs1Source]);

  const handleOpenObjectsApplicabilityReviewPicker = useCallback(() => {
    if (reviewingObjectsApplicability || loadingVs1Sections) {
      return;
    }

    objectsApplicabilityReviewInputRef.current?.click();
  }, [loadingVs1Sections, reviewingObjectsApplicability]);

  const handleDownloadObjectsApplicabilityDifferences = useCallback(async () => {
    const review = objectsApplicabilityReview;
    if (!review) {
      if (showError) {
        showError('No review available for export.');
      }
      return;
    }

    const incorrectItems = Array.isArray(review.incorrectMatches) ? review.incorrectMatches : [];
    const unmatchedItems = Array.isArray(review.unmatchedEntries) ? review.unmatchedEntries : [];

    if (incorrectItems.length === 0 && unmatchedItems.length === 0) {
      if (showError) {
        showError('No differences to download.');
      }
      return;
    }

    const XLSX = await import('xlsx');

    const timestamp = new Date().toISOString().replace(/[:]/g, '-');
    const projectSlug = String(selectedProject?.name || selectedProject?.id || 'project')
      .trim()
      .replace(/\s+/g, '_')
      .replace(/[^a-zA-Z0-9_-]/g, '');

    const differenceRows = [
      ...incorrectItems.map((item) => ({
        Difference_Type: 'Incorrect',
        Object_Name: String(item?.objectName || ''),
        Expected_From_Excel: String(item?.expectedLabel || ''),
        Actual_In_System: String(item?.actualLabel || ''),
        Actual_Status_Raw: String(item?.actualStatusRaw || ''),
        Actual_Applicability_Reason: String(item?.actualReason || ''),
        Actual_Decision_Source: String(item?.actualDecisionSource || ''),
        System_Search_Targets: String(item?.actualSearchTargets || ''),
        System_Search_Targets_Count: Number(item?.actualSearchTargetsCount || 0),
        System_Search_Criteria: String(item?.actualSearchTargetsReason || ''),
        System_Top_Candidate: String(item?.actualTopCandidate || ''),
        System_Top_Candidate_Score: item?.actualTopCandidateScore !== '' ? Number(item?.actualTopCandidateScore || 0) : '',
        System_Top_20_Candidates: String(item?.actualTopCandidates || ''),
        Source_File_Sheet_Row: String(item?.source || ''),
        Similarity_Score: Number(item?.similarityScore || 0),
      })),
      ...unmatchedItems.map((item) => ({
        Difference_Type: 'Not Found',
        Object_Name: String(item?.objectName || ''),
        Expected_From_Excel: String(item?.expectedLabel || ''),
        Actual_In_System: '',
        Actual_Status_Raw: '',
        Actual_Applicability_Reason: '',
        Actual_Decision_Source: '',
        System_Search_Targets: '',
        System_Search_Targets_Count: '',
        System_Search_Criteria: '',
        System_Top_Candidate: '',
        System_Top_Candidate_Score: '',
        System_Top_20_Candidates: '',
        Source_File_Sheet_Row: String(item?.source || ''),
        Similarity_Score: '',
      })),
    ];

    const reasonsRows = Array.isArray(review.allApplicabilityReasons)
      ? review.allApplicabilityReasons.map((item) => ({
        Object_Name: String(item?.objectName || ''),
        Applies: String(item?.appliesLabel || ''),
        Applicability_Status_Raw: String(item?.statusRaw || ''),
        Applicability_Reason: String(item?.reason || ''),
        Decision_Source: String(item?.decisionSource || ''),
        System_Search_Targets: String(item?.searchTargets || ''),
        System_Search_Targets_Count: Number(item?.searchTargetsCount || 0),
        System_Search_Criteria: String(item?.searchTargetsReason || ''),
        System_Top_Candidate: String(item?.topCandidate || ''),
        System_Top_Candidate_Score: item?.topCandidateScore !== '' ? Number(item?.topCandidateScore || 0) : '',
        System_Top_20_Candidates: String(item?.topCandidates || ''),
      }))
      : [];

    const summaryRows = [{
      Generated_At: String(review.generatedAt || ''),
      Project: String(selectedProject?.name || selectedProject?.id || ''),
      Files: Number(review.filesCount || 0),
      Compared_Rows: Number(review.comparedRows || 0),
      Matched_Rows: Number(review.matchedCount || 0),
      Reviewed_Matched_Rows: Number(review.reviewedMatchedCount || 0),
      Unmatched_Rows: Number(review.unmatchedCount || 0),
      Correct_Rows: Number(review.correctCount || 0),
      Incorrect_Rows: Number(review.incorrectCount || 0),
      Actual_Applies_Rows: Number(review.actualAppliesCount || 0),
      Actual_No_Applies_Rows: Number(review.actualNoAppliesCount || 0),
      Actual_Unreviewed_Rows: Number(review.actualUnreviewedCount || 0),
      Accuracy_Percent: Number(review.accuracy || 0),
    }];

    const workbook = XLSX.utils.book_new();

    const summaryWs = XLSX.utils.json_to_sheet(summaryRows);
    summaryWs['!cols'] = [
      { wch: 24 }, { wch: 24 }, { wch: 10 }, { wch: 14 }, { wch: 16 },
      { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 16 }, { wch: 19 },
      { wch: 20 }, { wch: 16 },
    ];
    XLSX.utils.book_append_sheet(workbook, summaryWs, 'Summary');

    const diffsWs = XLSX.utils.json_to_sheet(differenceRows);
    diffsWs['!cols'] = [
      { wch: 14 }, { wch: 34 }, { wch: 18 }, { wch: 18 }, { wch: 18 },
      { wch: 44 }, { wch: 24 }, { wch: 40 }, { wch: 12 }, { wch: 34 },
      { wch: 34 }, { wch: 14 }, { wch: 52 }, { wch: 44 }, { wch: 14 },
    ];
    diffsWs['!autofilter'] = { ref: 'A1:O1' };
    XLSX.utils.book_append_sheet(workbook, diffsWs, 'Differences');

    const reasonsWs = XLSX.utils.json_to_sheet(reasonsRows);
    reasonsWs['!cols'] = [
      { wch: 34 }, { wch: 12 }, { wch: 18 }, { wch: 56 }, { wch: 24 },
      { wch: 40 }, { wch: 12 }, { wch: 34 }, { wch: 34 }, { wch: 14 }, { wch: 52 },
    ];
    reasonsWs['!autofilter'] = { ref: 'A1:K1' };
    XLSX.utils.book_append_sheet(workbook, reasonsWs, 'Applicability Reasons');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const excelBlob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    triggerBlobDownload(excelBlob, `objects_applicability_differences_${projectSlug}_${timestamp}.xlsx`);
  }, [objectsApplicabilityReview, selectedProject?.id, selectedProject?.name, showError]);

  const handleObjectsApplicabilityReviewFileSelect = useCallback(async (event) => {
    const selectedFiles = Array.from(event?.target?.files || []);
    event.target.value = '';

    if (selectedFiles.length === 0) {
      return;
    }

    const objectsRows = Array.isArray(vs1Sections?.objects) ? vs1Sections.objects : [];
    if (objectsRows.length === 0) {
      if (showError) {
        showError('No objects available to compare.');
      }
      return;
    }

    try {
      setReviewingObjectsApplicability(true);

      const XLSX = await import('xlsx');
      const excelEntriesRaw = [];
      const ignoredRowsByMissingColumns = [];

      const resolveColumnKey = (rowKeys, aliases) => {
        const normalizedEntries = rowKeys.map((key) => ({
          key,
          normalized: normalizeExcelHeaderKey(key),
        }));

        for (const alias of aliases) {
          const normalizedAlias = normalizeExcelHeaderKey(alias);
          const exact = normalizedEntries.find((entry) => entry.normalized === normalizedAlias);
          if (exact) return exact.key;
        }

        for (const alias of aliases) {
          const normalizedAlias = normalizeExcelHeaderKey(alias);
          const partial = normalizedEntries.find((entry) => entry.normalized.includes(normalizedAlias));
          if (partial) return partial.key;
        }

        return null;
      };

      for (const file of selectedFiles) {
        const fileBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(fileBuffer, { type: 'array' });

        const firstSheetName = Array.isArray(workbook.SheetNames) ? workbook.SheetNames[0] : null;
        if (!firstSheetName) {
          continue;
        }

        const sheet = workbook.Sheets[firstSheetName];
        if (!sheet) continue;

        const sheetMatrix = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          defval: '',
          raw: false,
          blankrows: true,
        });

        if (!Array.isArray(sheetMatrix) || sheetMatrix.length === 0) continue;

        let headerRowIndex = -1;
        let headerLabels = [];
        let descrColumn = null;
        let applicabilityColumn = null;
        let fallbackApplicabilityHeader = null;

        const headerScanLimit = Math.min(sheetMatrix.length, 30);
        for (let candidateIndex = 0; candidateIndex < headerScanLimit; candidateIndex += 1) {
          const candidateRow = Array.isArray(sheetMatrix[candidateIndex]) ? sheetMatrix[candidateIndex] : [];
          const candidateHeaders = candidateRow.map((cellValue, cellIndex) => {
            const label = String(cellValue || '').trim();
            return label || `__col_${cellIndex}__`;
          });

          if (candidateHeaders.length === 0) continue;

          const candidateDescrColumn = resolveColumnKey(candidateHeaders, ['DESCR', 'Description', 'Omschrijving']);
          const candidateApplicabilityColumn = resolveColumnKey(candidateHeaders, ['Aplica/No Aplica', 'Aplica No Aplica', 'Aplica', 'Applicable']);

          if (candidateDescrColumn && candidateApplicabilityColumn) {
            headerRowIndex = candidateIndex;
            headerLabels = candidateHeaders;
            descrColumn = candidateDescrColumn;
            applicabilityColumn = candidateApplicabilityColumn;
            break;
          }

          if (!candidateDescrColumn && candidateApplicabilityColumn && !fallbackApplicabilityHeader) {
            fallbackApplicabilityHeader = {
              rowIndex: candidateIndex,
              labels: candidateHeaders,
              applicability: candidateApplicabilityColumn,
            };
          }
        }

        if (headerRowIndex < 0 && fallbackApplicabilityHeader) {
          headerRowIndex = fallbackApplicabilityHeader.rowIndex;
          headerLabels = fallbackApplicabilityHeader.labels;
          applicabilityColumn = fallbackApplicabilityHeader.applicability;
          descrColumn = headerLabels[0] || '__col_0__';
        }

        if (headerRowIndex < 0 || !descrColumn || !applicabilityColumn) {
          ignoredRowsByMissingColumns.push(`${file.name} / ${firstSheetName}`);
          continue;
        }

        const dataStartRowIndex = descrColumn === (headerLabels[0] || '__col_0__')
          ? Math.max(headerRowIndex + 1, 2)
          : headerRowIndex + 1;
        const dataRowsWithIndex = sheetMatrix
          .map((rowValues, worksheetRowIndex) => ({ rowValues, worksheetRowIndex }))
          .filter(({ worksheetRowIndex }) => worksheetRowIndex >= dataStartRowIndex);

        dataRowsWithIndex.forEach(({ rowValues, worksheetRowIndex }) => {
          const safeValues = Array.isArray(rowValues) ? rowValues : [];
          const sheetRow = {};
          headerLabels.forEach((headerLabel, headerIndex) => {
            sheetRow[headerLabel] = safeValues[headerIndex] ?? '';
          });

          const descrRaw = sheetRow?.[descrColumn];
          const directApplicabilityRaw = sheetRow?.[applicabilityColumn];
          const rowCellValues = Object.values(sheetRow || {});
          const inferredApplicabilityRaw = rowCellValues.find((cellValue) => (
            resolveExpectedApplicabilityFromExcelValue(cellValue) !== null
          ));
          const applicabilityRaw = (String(directApplicabilityRaw || '').trim()
            ? directApplicabilityRaw
            : inferredApplicabilityRaw);
          const hasApplicabilitySignal = String(applicabilityRaw || '').trim() !== '';
          const normalizedObjectName = normalizeEntityNameForComparison(extractObjectNameFromDescrText(descrRaw));
          let expectedApplies = resolveExpectedApplicabilityFromExcelValue(applicabilityRaw);

          // Business rule: an empty applicability cell means "Aplica".
          if (expectedApplies === null && normalizedObjectName && !hasApplicabilitySignal) {
            expectedApplies = true;
          }

          if (!normalizedObjectName || expectedApplies === null) {
            return;
          }

          excelEntriesRaw.push({
            objectName: extractObjectNameFromDescrText(descrRaw),
            normalizedObjectName,
            expectedApplies,
            source: `${file.name} / ${firstSheetName} / row ${worksheetRowIndex + 1}`,
          });
        });
      }

      if (excelEntriesRaw.length === 0) {
        setObjectsApplicabilityReview(null);
        if (showError) {
          showError('No valid rows found with DESCR and Applies/Does not apply columns.');
        }
        return;
      }

      const excelEntries = excelEntriesRaw;

      const objectCandidates = objectsRows
        .map((row) => {
          const objectName = getObjectOriginalNameForComparison(row);
          const normalizedObjectName = normalizeEntityNameForComparison(objectName);
          if (!normalizedObjectName) return null;
          return {
            row,
            rowId: Number(row?.id || 0),
            objectName,
            normalizedObjectName,
          };
        })
        .filter(Boolean);

      const exactMatchesByName = new Map();
      objectCandidates.forEach((candidate) => {
        const current = exactMatchesByName.get(candidate.normalizedObjectName) || [];
        current.push(candidate);
        exactMatchesByName.set(candidate.normalizedObjectName, current);
      });

      const incorrectMatches = [];
      const unmatchedEntries = [];
      const matchedRowIds = new Set();
      const matchedNormalizedExcelNames = new Set();
      const unmatchedNormalizedExcelNames = new Set();
      const uniqueComparedNormalizedNames = new Set(
        excelEntries
          .map((entry) => String(entry?.normalizedObjectName || '').trim())
          .filter(Boolean)
      );

      let matchedCount = 0;
      let correctCount = 0;
      let expectedAppliesCount = 0;
      let expectedNoAppliesCount = 0;
      let actualAppliesCount = 0;
      let actualNoAppliesCount = 0;
      let actualUnreviewedCount = 0;
      let reviewedMatchedCount = 0;

      for (const entry of excelEntries) {
        if (entry.expectedApplies) {
          expectedAppliesCount += 1;
        } else {
          expectedNoAppliesCount += 1;
        }

        const exactCandidates = exactMatchesByName.get(entry.normalizedObjectName) || [];
        let matchedCandidate = exactCandidates[0] || null;
        let similarityScore = matchedCandidate ? 1 : 0;

        if (!matchedCandidate) {
          let bestCandidate = null;
          let bestScore = 0;

          objectCandidates.forEach((candidate) => {
            const similarity = getEntityNameSimilarityMeta(entry.objectName, candidate.objectName);
            if (similarity.isMatch && similarity.score > bestScore) {
              bestCandidate = candidate;
              bestScore = similarity.score;
            }
          });

          matchedCandidate = bestCandidate;
          similarityScore = bestScore;
        }

        if (!matchedCandidate) {
          unmatchedNormalizedExcelNames.add(entry.normalizedObjectName);
          unmatchedEntries.push({
            objectName: entry.objectName,
            expectedLabel: entry.expectedApplies ? 'Applies' : 'Does not apply',
            source: entry.source,
          });
          continue;
        }

        matchedCount += 1;
        matchedRowIds.add(matchedCandidate.rowId);
        matchedNormalizedExcelNames.add(entry.normalizedObjectName);
        const actualApplies = isRowApplies(matchedCandidate.row);
        if (actualApplies === true) {
          actualAppliesCount += 1;
          reviewedMatchedCount += 1;
        } else if (actualApplies === false) {
          actualNoAppliesCount += 1;
          reviewedMatchedCount += 1;
        } else {
          actualUnreviewedCount += 1;
        }

        const isCorrect = actualApplies !== null && actualApplies === entry.expectedApplies;
        if (isCorrect) {
          correctCount += 1;
        } else {
          const actualStatusRaw = String(
            getSectionRowValue(matchedCandidate.row, 'applicability_status')
            || getSectionRowValue(matchedCandidate.row, 'applicable_status')
            || getSectionRowValue(matchedCandidate.row, 'apply_status')
            || ''
          ).trim();
          const actualReason = String(getSectionRowValue(matchedCandidate.row, 'applicability_reason') || '').trim();
          const actualDecisionSource = String(getSectionRowValue(matchedCandidate.row, 'applicability_decision_source_label') || '').trim();
          const searchTargetsArray = Array.isArray(getSectionRowValue(matchedCandidate.row, 'applicability_search_targets'))
            ? getSectionRowValue(matchedCandidate.row, 'applicability_search_targets')
              .map((target) => String(target || '').trim())
              .filter(Boolean)
            : [];
          const searchTargetsReason = String(getSectionRowValue(matchedCandidate.row, 'applicability_search_targets_reason') || '').trim();
          const topCandidatesArray = Array.isArray(getSectionRowValue(matchedCandidate.row, 'applicability_top_candidates'))
            ? getSectionRowValue(matchedCandidate.row, 'applicability_top_candidates')
              .map((candidate) => ({
                name: String(candidate?.name || '').trim(),
                score: Number(candidate?.score || 0),
              }))
              .filter((candidate) => candidate.name)
              .slice(0, 20)
            : [];
          const topCandidate = topCandidatesArray[0] || null;

          incorrectMatches.push({
            objectName: matchedCandidate.objectName,
            expectedLabel: entry.expectedApplies ? 'Applies' : 'Does not apply',
            actualLabel: actualApplies === true ? 'Applies' : (actualApplies === false ? 'Does not apply' : 'Unreviewed'),
            actualStatusRaw,
            actualReason,
            actualDecisionSource,
            actualSearchTargets: searchTargetsArray.join(' | '),
            actualSearchTargetsCount: searchTargetsArray.length,
            actualSearchTargetsReason: searchTargetsReason,
            actualTopCandidate: topCandidate?.name || '',
            actualTopCandidateScore: topCandidate ? topCandidate.score : '',
            actualTopCandidates: topCandidatesArray
              .map((candidate, idx) => `${idx + 1}. ${candidate.name} (${candidate.score.toFixed(3)})`)
              .join(' | '),
            source: entry.source,
            similarityScore,
          });
        }
      }

      const incorrectCount = Math.max(0, matchedCount - correctCount);
      const accuracy = reviewedMatchedCount > 0
        ? Math.round((correctCount * 10000) / reviewedMatchedCount) / 100
        : 0;

      const reviewSummary = {
        generatedAt: new Date().toISOString(),
        filesCount: selectedFiles.length,
        totalObjectsInSystem: objectCandidates.length,
        comparedRows: excelEntries.length,
        comparedUniqueObjects: uniqueComparedNormalizedNames.size,
        matchedCount,
        unmatchedCount: unmatchedEntries.length,
        matchedUniqueObjects: matchedNormalizedExcelNames.size,
        unmatchedUniqueObjects: unmatchedNormalizedExcelNames.size,
        matchedSystemObjects: matchedRowIds.size,
        correctCount,
        incorrectCount,
        expectedAppliesCount,
        expectedNoAppliesCount,
        actualAppliesCount,
        actualNoAppliesCount,
        actualUnreviewedCount,
        reviewedMatchedCount,
        accuracy,
        allApplicabilityReasons: objectCandidates
          .map((candidate) => {
            const statusRaw = String(
              getSectionRowValue(candidate.row, 'applicability_status')
              || getSectionRowValue(candidate.row, 'applicable_status')
              || getSectionRowValue(candidate.row, 'apply_status')
              || ''
            ).trim();
            const reason = String(getSectionRowValue(candidate.row, 'applicability_reason') || '').trim();
            const decisionSource = String(getSectionRowValue(candidate.row, 'applicability_decision_source_label') || '').trim();
            const searchTargets = Array.isArray(getSectionRowValue(candidate.row, 'applicability_search_targets'))
              ? getSectionRowValue(candidate.row, 'applicability_search_targets')
                .map((target) => String(target || '').trim())
                .filter(Boolean)
              : [];
            const searchTargetsReason = String(getSectionRowValue(candidate.row, 'applicability_search_targets_reason') || '').trim();
            const topCandidates = Array.isArray(getSectionRowValue(candidate.row, 'applicability_top_candidates'))
              ? getSectionRowValue(candidate.row, 'applicability_top_candidates')
                .map((candidateTop) => ({
                  name: String(candidateTop?.name || '').trim(),
                  score: Number(candidateTop?.score || 0),
                }))
                .filter((candidateTop) => candidateTop.name)
                .slice(0, 20)
              : [];
            const topCandidate = topCandidates[0] || null;
            const applies = isRowApplies(candidate.row);
            return {
              objectName: candidate.objectName,
              appliesLabel: applies === true ? 'Applies' : (applies === false ? 'Does not apply' : 'Unreviewed'),
              statusRaw,
              reason,
              decisionSource,
              searchTargets: searchTargets.join(' | '),
              searchTargetsCount: searchTargets.length,
              searchTargetsReason,
              topCandidate: topCandidate?.name || '',
              topCandidateScore: topCandidate ? topCandidate.score : '',
              topCandidates: topCandidates
                .map((candidateTop, idx) => `${idx + 1}. ${candidateTop.name} (${candidateTop.score.toFixed(3)})`)
                .join(' | '),
            };
          })
          .filter((entry) => (
            entry.reason
            || entry.decisionSource
            || entry.statusRaw
            || entry.searchTargets
            || entry.searchTargetsReason
            || entry.topCandidate
          )),
        ignoredSheetsByMissingColumns: ignoredRowsByMissingColumns,
        incorrectMatches,
        unmatchedEntries,
      };

      setObjectsApplicabilityReview(reviewSummary);

      if (showSuccess) {
        showSuccess(
          `Review completed. Matches: ${matchedCount}/${excelEntries.length} rows. Correct: ${correctCount}. Incorrect: ${incorrectCount}.`
        );
      }
    } catch (error) {
      setObjectsApplicabilityReview(null);
      if (showError) {
        showError(error?.message || 'Could not review applicability with Excel files.');
      }
    } finally {
      setReviewingObjectsApplicability(false);
    }
  }, [showError, showSuccess, vs1Sections?.objects]);

  const handleCloseProjectDialog = () => {
    setProjectDialogOpen(false);
    setSelectedProject(null);
    setRenameProjectDialogOpen(false);
    setRenamingProject(false);
    setProjectToRename(null);
    setRenameProjectName('');
    setProjectOntologies([]);
    setOntologiesSummary(null);
    setProjectViews([]);
    setProjectDocumentsGraphData({ nodes: [], links: [] });
    setApplyingOntologies(false);
    setPendingOntologyKey('');
    setUploadingOntologyKey('');
    setPendingProjectViewKey('');
    setUploadingProjectViewKey('');
    setPendingDocumentUri('');
    setPendingDocumentLabel('');
    setPendingDocumentNotation('');
    setPendingReplaceRepositoryDocumentEntityId('');
    setDocumentUploadItems([]);
    setDocumentUploadSummary({ total: 0, success: 0, duplicate: 0, failed: 0, cancelled: 0, skipped: 0 });
    setDocumentUploadStatusFilter('all');
    setProjectViewMode('explore');
    setDeepDiveSectionKey('objects');
    setProjectDetailTab('vs1');
    setVs1Source('project');
    setVs1Sections(createEmptyVsSections());
    setVs1SectionSelection(createEmptyVs1SectionSelection());
    setVs1SectionModalOpen(false);
    setVs1SectionModalKey('');
    setVs1EntityDirectory({});
    setVs1DetailTab(0);
    setVs1ExpandedGroups({});
    setLoadingVs1Sections(false);
    setBootstrappingProjectDetail(false);
    setCandidateLocationsByKey({});
    setExpandedCandidateLocationsKey('');
    setLoadingCandidateLocationsKey('');
    setPagePreviewState({
      open: false,
      loading: false,
      projectId: null,
      documentId: null,
      documentTitle: '',
      pageNumber: null,
      availablePages: [],
      textOriginal: '',
      textTranslated: '',
      locationContext: null,
      textMode: 'translated',
    });
    setReviewingObjectsApplicability(false);
    setObjectsApplicabilityReview(null);

    if (typeof onCloseProjectModal === 'function') {
      onCloseProjectModal();
    }
  };

  const handleUploadOntologyClick = (ontologyKey) => {
    if (!canCreateProjects || !selectedProject) return;
    setPendingOntologyKey(ontologyKey);
    ontologyInputRef.current?.click();
  };

  const handleUploadProjectViewClick = (viewKey, options = {}) => {
    if (!canCreateProjects || !selectedProject) return;
    setPendingProjectViewKey(viewKey);

    if (viewKey === 'documents') {
      const documentUri = String(options.documentUri || '').trim();
      setPendingDocumentUri(documentUri);
      setPendingDocumentLabel(String(options.documentLabel || '').trim());
      setPendingDocumentNotation(String(options.documentNotation || '').trim());
      setPendingReplaceRepositoryDocumentEntityId(String(options.replaceRepositoryDocumentEntityId || '').trim());
    } else {
      setPendingDocumentUri('');
      setPendingDocumentLabel('');
      setPendingDocumentNotation('');
      setPendingReplaceRepositoryDocumentEntityId('');
    }

    const triggerInputClick = () => {
      const targetInputRef = (viewKey === 'documents' && options.uploadMode === 'folder')
        ? projectViewFolderInputRef
        : projectViewInputRef;
      const inputElement = targetInputRef.current;
      if (!inputElement) return;
      inputElement.value = '';
      inputElement.click();
    };

    requestAnimationFrame(triggerInputClick);
  };

  const handleProjectViewFileSelect = async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = '';

    if (!selectedProject || !pendingProjectViewKey || selectedFiles.length === 0 || !canCreateProjects) {
      return;
    }

    try {
      setUploadingProjectViewKey(pendingProjectViewKey);
      setProjectViewUploadPages({ processed: 0, total: 0 });

      if (pendingProjectViewKey === 'documents') {
        documentUploadCancelRef.current = false;
        const isReplaceMode = Boolean(String(pendingReplaceRepositoryDocumentEntityId || '').trim());
        const documentFiles = sortDocumentUploadFiles(selectedFiles.filter((file) => isSupportedDocumentUploadFileName(file?.name)));
        const skippedFilesCount = Math.max(0, selectedFiles.length - documentFiles.length);

        if (documentFiles.length === 0) {
          if (showError) {
            showError('Only PDF or DOCX files are allowed for document uploads.');
          }
          return;
        }

        const initialItems = documentFiles.map((file, index) => ({
          id: `${Date.now()}-${index}-${file.name}`,
          fileIndex: index,
          file,
          name: file.name,
          status: 'pending',
          progress: 0,
          step: 'Pending',
          pagesProcessed: 0,
          pagesTotal: 0,
          error: '',
          taskId: null,
        }));

        setDocumentUploadItems(initialItems);
        setDocumentUploadSummary({ total: documentFiles.length, success: 0, duplicate: 0, failed: 0, cancelled: 0, skipped: skippedFilesCount });

        const extraFields = (pendingDocumentUri)
          ? {
              document_uri: pendingDocumentUri,
              document_label: pendingDocumentLabel,
              document_notation: pendingDocumentNotation,
            }
          : {};

        if (pendingReplaceRepositoryDocumentEntityId) {
          extraFields.replace_repository_document_entity_id = pendingReplaceRepositoryDocumentEntityId;
        }

        for (const uploadItem of initialItems) {
          let shouldRefreshViews = false;

          if (documentUploadCancelRef.current) {
            updateUploadItem(uploadItem.id, {
              status: 'cancelled',
              step: 'Cancelled by user',
            });
            continue;
          }

          const fileToUpload = documentFiles[uploadItem.fileIndex];

          updateUploadItem(uploadItem.id, {
            status: 'uploading',
            progress: 0,
            step: isReplaceMode ? 'Uploading replacement file to server...' : 'Uploading file to server...',
            error: '',
          });
          setProjectViewUploadStep(isReplaceMode ? `Uploading replacement ${uploadItem.name}` : `Uploading ${uploadItem.name}`);
          setProjectViewUploadProgress(0);

          try {
            const uploadResponse = await uploadProjectViewFile(
              selectedProject.id,
              pendingProjectViewKey,
              fileToUpload,
              extraFields,
              (progressEvent) => {
                const totalBytes = Number(progressEvent?.total || 0);
                const loadedBytes = Number(progressEvent?.loaded || 0);
                if (totalBytes <= 0) {
                  return;
                }

                const percentage = Math.min(100, Math.max(0, Math.round((loadedBytes * 100) / totalBytes)));
                setProjectViewUploadProgress(percentage);
                updateUploadItem(uploadItem.id, {
                  status: 'uploading',
                  progress: percentage,
                  step: `Uploading (${percentage}%)`,
                });
              },
            );

            if (!uploadResponse?.task_id) {
              const message = String(uploadResponse?.message || '').trim();
              const isDuplicate = /already\s+uploaded/i.test(message);
              const shouldMarkDuplicate = isDuplicate && !isReplaceMode;

              updateUploadItem(uploadItem.id, {
                status: shouldMarkDuplicate ? 'duplicate' : 'completed',
                progress: 100,
                step: isReplaceMode
                  ? (message || 'Replacement uploaded and queued for reprocessing')
                  : (message || (isDuplicate ? 'Already uploaded' : 'Uploaded')),
                taskId: null,
              });
              shouldRefreshViews = true;
            } else {
              updateUploadItem(uploadItem.id, {
                status: 'processing',
                step: isReplaceMode ? 'Reprocessing replacement document on server...' : 'Processing on server...',
                progress: 100,
                taskId: uploadResponse.task_id,
              });

              await pollIngestionDocumentProgress(uploadResponse.task_id, (progressState) => {
                setProjectViewUploadProgress(progressState.progress);
                setProjectViewUploadStep(progressState.step);
                setProjectViewUploadPages({
                  processed: progressState.processedPages,
                  total: progressState.totalPages,
                });

                updateUploadItem(uploadItem.id, {
                  status: progressState.status === 'completed' ? 'completed' : 'processing',
                  progress: progressState.progress,
                  step: progressState.step,
                  pagesProcessed: progressState.processedPages,
                  pagesTotal: progressState.totalPages,
                  error: progressState.errorMessage,
                });
              }, documentUploadCancelRef);

              updateUploadItem(uploadItem.id, {
                status: 'completed',
                progress: 100,
                step: isReplaceMode
                  ? 'Replacement reprocessed: uploaded, extracted, summarized and translated'
                  : 'Uploaded, extracted, summarized and translated',
                taskId: null,
              });
              shouldRefreshViews = true;
            }
          } catch (itemError) {
            const errorMessage = String(itemError?.message || '').toLowerCase();
            const isCancelledError = errorMessage === 'upload_cancelled'
              || errorMessage.includes('cancelled')
              || errorMessage.includes('canceled')
              || errorMessage.includes('revoked');

            if (isCancelledError) {
              updateUploadItem(uploadItem.id, {
                status: 'cancelled',
                progress: 0,
                step: 'Cancelled by user',
                error: '',
                taskId: null,
              });
            } else if (isDocumentProcessingTimeoutError(itemError)) {
              updateUploadItem(uploadItem.id, {
                status: 'processing',
                progress: 99,
                step: 'Still processing on server. Refresh in a moment to update status.',
                error: '',
              });
              shouldRefreshViews = true;
            } else {
            const actionableError = formatDocumentUploadError(itemError, uploadItem.name);
            updateUploadItem(uploadItem.id, {
              status: 'failed',
              progress: 100,
              step: 'Failed',
              error: actionableError,
              taskId: null,
            });
            }
          }

          setDocumentUploadItems((currentItems) => {
            const nextSummary = summarizeUploadItems(currentItems, skippedFilesCount);
            setDocumentUploadSummary(nextSummary);
            return currentItems;
          });

          if (shouldRefreshViews && selectedProject?.id) {
            await refreshProjectViews(selectedProject.id, { silent: true });
          }

          if (!documentUploadCancelRef.current && uploadItem.fileIndex < initialItems.length - 1) {
            await delayDocumentUpload(DOCUMENT_UPLOAD_INTER_FILE_DELAY_MS);
          }
        }

        await refreshProjectViews(selectedProject.id, { silent: true });

        if (showSuccess) {
          setDocumentUploadItems((currentItems) => {
            const nextSummary = summarizeUploadItems(currentItems, skippedFilesCount);
            setDocumentUploadSummary(nextSummary);
            const stillProcessingCount = currentItems.filter((item) => (
              item.status === 'processing' || item.status === 'uploading' || item.status === 'pending'
            )).length;

            showSuccess(
              isReplaceMode
                ? `${documentUploadCancelRef.current ? 'Replacement cancelled.' : 'Replacement upload finished:'} ${nextSummary.success} reprocessed, ${nextSummary.failed} failed${stillProcessingCount ? `, ${stillProcessingCount} still processing on server` : ''}${nextSummary.skipped ? `, ${nextSummary.skipped} skipped (unsupported format)` : ''}.`
                : `${documentUploadCancelRef.current ? 'Bulk upload cancelled.' : 'Bulk upload finished:'} ${nextSummary.success} uploaded, ${nextSummary.duplicate} duplicates, ${nextSummary.failed} failed${stillProcessingCount ? `, ${stillProcessingCount} still processing on server` : ''}${nextSummary.skipped ? `, ${nextSummary.skipped} skipped (unsupported format)` : ''}.`
            );
            return currentItems;
          });
        }
      } else {
        const selectedFile = selectedFiles[0];
        setProjectViewUploadProgress(null);
        setProjectViewUploadStep('Uploading file to server...');

        const lastResponse = await uploadProjectViewFile(
          selectedProject.id,
          pendingProjectViewKey,
          selectedFile,
          {},
        );

        const updatedViews = lastResponse?.views?.tabs || [];
        if (updatedViews.length > 0) {
          setProjectViews(updatedViews);
        }

        if (lastResponse?.project_summary) {
          setProjects((prevProjects) =>
            prevProjects.map((projectItem) =>
              projectItem.id === selectedProject.id
                ? { ...projectItem, ontologies_summary: lastResponse.project_summary }
                : projectItem
            )
          );
        }

        await fetchProjectOntologies(selectedProject.id, pendingProjectViewKey);

        if (projectDetailTab === 'vs1') {
          await fetchVs1Sections(selectedProject.id, vs1Source, { force: true });
        }

        if (showSuccess) {
          if (pendingProjectViewKey === 'vs1') {
            showSuccess(
              formatVs1ApplySummaryMessage(
                lastResponse?.apply_summary,
                'VS1 TTL uploaded and applied successfully.'
              )
            );
          } else {
            showSuccess(`${pendingProjectViewKey.toUpperCase()} TTL uploaded successfully.`);
          }
        }
      }
    } catch (error) {
      if (showError) {
        showError(formatDocumentUploadError(error) || 'Error uploading project view file.');
      }
    } finally {
      setUploadingProjectViewKey('');
      setProjectViewUploadProgress(null);
      setProjectViewUploadStep('');
      setProjectViewUploadPages({ processed: 0, total: 0 });
      setPendingProjectViewKey('');
      setPendingDocumentUri('');
      setPendingDocumentLabel('');
      setPendingDocumentNotation('');
      setPendingReplaceRepositoryDocumentEntityId('');
    }
  };

  const handleReplaceDocument = (documentItem) => {
    if (!canCreateProjects || !documentItem) {
      return;
    }

    const repositoryDocumentEntityId = Number(documentItem?.repository_document_entity_id || 0);
    if (!repositoryDocumentEntityId) {
      if (showError) {
        showError('Project document ID not found. Cannot replace this document.');
      }
      return;
    }

    handleUploadProjectViewClick('documents', {
      uploadMode: 'single',
      replaceRepositoryDocumentEntityId: repositoryDocumentEntityId,
      documentUri: String(documentItem?.document_uri || '').trim(),
      documentLabel: String(documentItem?.label || documentItem?.original_filename || '').trim(),
      documentNotation: String(documentItem?.notation || '').trim(),
    });
  };

  const handleCancelDocumentUploadQueue = async () => {
    if (uploadingProjectViewKey !== 'documents') {
      return;
    }

    documentUploadCancelRef.current = true;
    setProjectViewUploadStep('Cancelling after current document...');

    // Revoke all processing tasks in Celery
    const processingItems = documentUploadItems.filter(
      (item) => item.taskId && (item.status === 'processing' || item.status === 'uploading')
    );

    for (const item of processingItems) {
      try {
        await cancelIngestionUpload(item.taskId);
        updateUploadItem(item.id, {
          status: 'cancelled',
          step: 'Cancelled by user',
          error: '',
        });
      } catch (error) {
        if (showError) {
          showError(`Failed to cancel ${item.name}: ${error?.response?.data?.error || error.message}`);
        }
      }
    }
  };

  const handleClearDocumentUploadQueue = () => {
    documentUploadCancelRef.current = false;
    setDocumentUploadItems([]);
    setDocumentUploadSummary({ total: 0, success: 0, duplicate: 0, failed: 0, cancelled: 0, skipped: 0 });
    setDocumentUploadStatusFilter('all');
    setProjectViewUploadProgress(null);
    setProjectViewUploadStep('');
    setProjectViewUploadPages({ processed: 0, total: 0 });

    const storageKey = buildDocumentQueueStorageKey(selectedProject?.id);
    if (storageKey) {
      try {
        localStorage.removeItem(storageKey);
      } catch {
        // ignore
      }
    }
  };

  const handleExportDocumentUploadReport = (format = 'csv') => {
    const items = Array.isArray(documentUploadItems) ? documentUploadItems : [];
    if (items.length === 0) {
      if (showError) {
        showError('No upload data available to export.');
      }
      return;
    }

    const timestamp = new Date().toISOString().replace(/[:]/g, '-');
    const projectSlug = String(selectedProject?.name || selectedProject?.id || 'project')
      .trim()
      .replace(/\s+/g, '_')
      .replace(/[^a-zA-Z0-9_-]/g, '');

    const rows = items.map((item) => ({
      file: String(item?.name || ''),
      status: String(item?.status || ''),
      progress: Number(item?.progress || 0),
      detail: String(item?.step || ''),
      error: String(item?.error || ''),
      pages_processed: Number(item?.pagesProcessed || 0),
      pages_total: Number(item?.pagesTotal || 0),
    }));

    if (String(format).toLowerCase() === 'json') {
      const payload = {
        project_id: Number(selectedProject?.id || 0),
        project_name: String(selectedProject?.name || ''),
        generated_at: new Date().toISOString(),
        summary: documentUploadSummary,
        items: rows,
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json;charset=utf-8' });
      triggerBlobDownload(blob, `document_upload_report_${projectSlug}_${timestamp}.json`);
      return;
    }

    const headers = ['file', 'status', 'progress', 'detail', 'error', 'pages_processed', 'pages_total'];
    const csvLines = [headers.join(',')]
      .concat(rows.map((row) => headers.map((key) => {
        const value = String(row[key] ?? '');
        return `"${value.replace(/"/g, '""')}"`;
      }).join(',')));

    const csvBlob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8' });
    triggerBlobDownload(csvBlob, `document_upload_report_${projectSlug}_${timestamp}.csv`);
  };

  const handleRetryFailedDocumentUploads = async () => {
    if (!selectedProject || !canCreateProjects || uploadingProjectViewKey === 'documents') {
      return;
    }

    const failedItems = documentUploadItems.filter((item) => item.status === 'failed' && item.file instanceof File);
    if (failedItems.length === 0) {
      if (showError) {
        showError('There are no failed documents with retry data.');
      }
      return;
    }

    const updateUploadItem = (itemId, patch) => {
      setDocumentUploadItems((previousItems) => previousItems.map((item) => (
        item.id === itemId ? { ...item, ...patch } : item
      )));
    };

    try {
      documentUploadCancelRef.current = false;
      setUploadingProjectViewKey('documents');
      setProjectViewUploadPages({ processed: 0, total: 0 });

      const extraFields = (pendingDocumentUri)
        ? {
            document_uri: pendingDocumentUri,
            document_label: pendingDocumentLabel,
            document_notation: pendingDocumentNotation,
          }
        : {};

      for (const uploadItem of failedItems) {
        let shouldRefreshViews = false;

        if (documentUploadCancelRef.current) {
          updateUploadItem(uploadItem.id, {
            status: 'cancelled',
            step: 'Cancelled by user',
            error: '',
          });
          continue;
        }

        updateUploadItem(uploadItem.id, {
          status: 'uploading',
          progress: 0,
          step: 'Retrying upload...',
          error: '',
        });

        try {
          const uploadResponse = await uploadProjectViewFile(
            selectedProject.id,
            'documents',
            uploadItem.file,
            extraFields,
            (progressEvent) => {
              const totalBytes = Number(progressEvent?.total || 0);
              const loadedBytes = Number(progressEvent?.loaded || 0);
              if (totalBytes <= 0) {
                return;
              }

              const percentage = Math.min(100, Math.max(0, Math.round((loadedBytes * 100) / totalBytes)));
              setProjectViewUploadProgress(percentage);
              setProjectViewUploadStep(`Retrying ${uploadItem.name}`);
              updateUploadItem(uploadItem.id, {
                status: 'uploading',
                progress: percentage,
                step: `Uploading (${percentage}%)`,
              });
            },
          );

          if (!uploadResponse?.task_id) {
            const message = String(uploadResponse?.message || '').trim();
            const isDuplicate = /already\s+uploaded/i.test(message);
            updateUploadItem(uploadItem.id, {
              status: isDuplicate ? 'duplicate' : 'completed',
              progress: 100,
              step: message || (isDuplicate ? 'Already uploaded' : 'Uploaded'),
              error: '',
              taskId: null,
            });
            shouldRefreshViews = true;
          } else {
            updateUploadItem(uploadItem.id, {
              status: 'processing',
              step: 'Processing on server...',
              progress: 100,
              taskId: uploadResponse.task_id,
              error: '',
            });

            await pollIngestionDocumentProgress(uploadResponse.task_id, (progressState) => {
              setProjectViewUploadProgress(progressState.progress);
              setProjectViewUploadStep(progressState.step);
              setProjectViewUploadPages({
                processed: progressState.processedPages,
                total: progressState.totalPages,
              });

              updateUploadItem(uploadItem.id, {
                status: progressState.status === 'completed' ? 'completed' : 'processing',
                progress: progressState.progress,
                step: progressState.step,
                pagesProcessed: progressState.processedPages,
                pagesTotal: progressState.totalPages,
                error: progressState.errorMessage,
              });
            }, documentUploadCancelRef);

            updateUploadItem(uploadItem.id, {
              status: 'completed',
              progress: 100,
              step: 'Uploaded, extracted, summarized and translated',
              error: '',
              taskId: null,
            });
            shouldRefreshViews = true;
          }
        } catch (itemError) {
          const errorMessage = String(itemError?.message || '').toLowerCase();
          const isCancelledError = errorMessage === 'upload_cancelled'
            || errorMessage.includes('cancelled')
            || errorMessage.includes('canceled')
            || errorMessage.includes('revoked');

          if (isCancelledError) {
            updateUploadItem(uploadItem.id, {
              status: 'cancelled',
              progress: 0,
              step: 'Cancelled by user',
              error: '',
              taskId: null,
            });
          } else if (isDocumentProcessingTimeoutError(itemError)) {
            updateUploadItem(uploadItem.id, {
              status: 'processing',
              progress: 99,
              step: 'Still processing on server. Refresh in a moment to update status.',
              error: '',
            });
            shouldRefreshViews = true;
          } else {
            const actionableError = formatDocumentUploadError(itemError, uploadItem.name);
            updateUploadItem(uploadItem.id, {
              status: 'failed',
              progress: 100,
              step: 'Failed',
              error: actionableError,
              taskId: null,
            });
          }
        }

        setDocumentUploadItems((currentItems) => {
          setDocumentUploadSummary(summarizeUploadItems(currentItems, Number(documentUploadSummary.skipped || 0)));
          return currentItems;
        });

        if (shouldRefreshViews && selectedProject?.id) {
          await refreshProjectViews(selectedProject.id, { silent: true });
        }

        if (!documentUploadCancelRef.current && uploadItem !== failedItems[failedItems.length - 1]) {
          await delayDocumentUpload(DOCUMENT_UPLOAD_INTER_FILE_DELAY_MS);
        }
      }

      await refreshProjectViews(selectedProject.id, { silent: true });

      setDocumentUploadItems((currentItems) => {
        const nextSummary = summarizeUploadItems(currentItems, Number(documentUploadSummary.skipped || 0));
        setDocumentUploadSummary(nextSummary);
        const stillProcessingCount = currentItems.filter((item) => (
          item.status === 'processing' || item.status === 'uploading' || item.status === 'pending'
        )).length;
        if (showSuccess) {
          showSuccess(
            `${documentUploadCancelRef.current ? 'Retry cancelled.' : 'Retry finished:'} ${nextSummary.success} uploaded, ${nextSummary.duplicate} duplicates, ${nextSummary.failed} failed${stillProcessingCount ? `, ${stillProcessingCount} still processing on server` : ''}.`
          );
        }
        return currentItems;
      });
    } catch (error) {
      if (showError) {
        showError(formatDocumentUploadError(error) || 'Error retrying failed document uploads.');
      }
    } finally {
      setUploadingProjectViewKey('');
      setProjectViewUploadProgress(null);
      setProjectViewUploadStep('');
      setProjectViewUploadPages({ processed: 0, total: 0 });
    }
  };

  const handleOntologyFileSelect = async (event) => {
    const selectedFile = event.target.files?.[0] || null;
    event.target.value = '';

    if (!selectedFile || !selectedProject || !pendingOntologyKey || !canCreateProjects) {
      return;
    }

    try {
      setUploadingOntologyKey(pendingOntologyKey);
      const response = await uploadProjectOntology(
        selectedProject.id,
        pendingOntologyKey,
        selectedFile,
        projectDetailTab
      );
      const uploadedOntology = response?.ontology;

      if (uploadedOntology) {
        setProjectOntologies((prevOntologies) =>
          prevOntologies.map((item) =>
            item.key === uploadedOntology.key ? uploadedOntology : item
          )
        );
      }

      if (response?.summary) {
        setOntologiesSummary(response.summary);
      }

      if (response?.project_summary) {
        setProjects((prevProjects) =>
          prevProjects.map((projectItem) =>
            projectItem.id === selectedProject.id
              ? { ...projectItem, ontologies_summary: response.project_summary }
              : projectItem
          )
        );
      }

      await fetchProjectOntologies(selectedProject.id, projectDetailTab);

      if (projectDetailTab === 'vs1') {
        await fetchVs1Sections(selectedProject.id, vs1Source, { force: true });
      }

      if (showSuccess) {
        showSuccess('Project ontology uploaded successfully.');
      }
    } catch (error) {
      if (showError) {
        showError(error.response?.data?.error || error.message || 'Error uploading ontology file.');
      }
    } finally {
      setUploadingOntologyKey('');
      setPendingOntologyKey('');
    }
  };

  const handleOpenProjectWorkspace = () => {
    if (!selectedProject || !canOpenProjects) return;
    onOpenProject(selectedProject);
    setProjectDialogOpen(false);
  };

  const handleProjectViewModeChange = (event, newMode) => {
    if (!newMode) {
      return;
    }

    setProjectViewMode(newMode);

    if (newMode === 'explore') {
      setProjectDetailTab('vs1');
      if (selectedProject?.id) {
        fetchProjectOntologies(selectedProject.id, 'vs1');
        fetchVs1Sections(selectedProject.id, vs1Source);
      }
    }
  };

  const getLocalizedDirectoryName = useCallback((entry, preferEnglish = true) => {
    if (!entry || typeof entry !== 'object') return '';
    return String(
      preferEnglish
        ? (entry.name_en || entry.name_english || entry.name || entry.notation || '')
        : (entry.name || entry.name_en || entry.name_english || entry.notation || '')
    ).trim();
  }, []);

  const getLocalizedRowLabel = useCallback((row, sectionKey = '', preferEnglish = true) => {
    const payload = row?.data && typeof row.data === 'object' ? row.data : {};
    const notation = String(payload.notation || '').trim();
    const directName = String(
      preferEnglish
        ? (row?.name_en || row?.name_english || '')
        : (row?.name || '')
    ).trim();
    const payloadName = String(
      preferEnglish
        ? (payload.name_en || payload.name_english || payload.label_en || payload.label_english || payload.name || payload.label || '')
        : (payload.name || payload.label || payload.name_en || payload.name_english || payload.label_en || payload.label_english || '')
    ).trim();

    if (directName) return directName;

    if (sectionKey === 'objects' || sectionKey === 'requirements') {
      const baseLabel = payloadName;
      if (notation && baseLabel) return `${notation} - ${baseLabel}`;
    }

    return payloadName || notation || '-';
  }, []);

  const getLocalizedTargetValue = useCallback((rawValue, payload = {}, preferEnglish = true) => {
    const text = String(rawValue || '').trim();
    if (!text) return '';

    const targetLabels = payload && typeof payload._target_labels === 'object' ? payload._target_labels : {};
    const targetInfo = targetLabels[text];
    if (targetInfo && typeof targetInfo === 'object') {
      const targetName = String(
        preferEnglish
          ? (targetInfo.name_en || targetInfo.name_english || targetInfo.name || targetInfo.notation || '')
          : (targetInfo.name || targetInfo.name_en || targetInfo.name_english || targetInfo.notation || '')
      ).trim();
      if (targetName) return targetName;
    }

    const directoryEntry = vs1EntityDirectory[text];
    if (directoryEntry) {
      const directoryName = getLocalizedDirectoryName(directoryEntry, preferEnglish);
      if (directoryName) return directoryName;
    }

    if (text.startsWith('http://') || text.startsWith('https://')) {
      return text.split('/').pop().split('#').pop();
    }

    return text;
  }, [getLocalizedDirectoryName, vs1EntityDirectory]);

  const getLocalizedVakgebied = useCallback((row, preferEnglish = true) => {
    const payload = row?.data && typeof row.data === 'object' ? row.data : {};
    const fields = Array.isArray(payload.fields) ? payload.fields : [];

    const vakField = fields.find((field) => {
      const originalName = String(field?.fieldName || '').trim().toLowerCase();
      const englishName = String(field?.fieldName_en || field?.fieldName_english || '').trim().toLowerCase();
      return originalName === 'vakgebied' || englishName === 'vakgebied';
    });

    if (vakField) {
      const values = preferEnglish
        ? (vakField.values_en || vakField.values_english || vakField.values)
        : (vakField.values || vakField.values_en || vakField.values_english);
      const resolved = getPreferredLocalizedValue(values, preferEnglish);
      if (resolved) return resolved;
    }

    const extra = payload.extra_properties && typeof payload.extra_properties === 'object' ? payload.extra_properties : {};
    const labels = payload._extra_property_labels && typeof payload._extra_property_labels === 'object' ? payload._extra_property_labels : {};

    const vakUri = Object.keys(labels).find((uri) => {
      const baseLabel = String(labels[uri] || '').trim().toLowerCase();
      const englishLabel = String(labels[`${uri}_en`] || labels[`${uri}_english`] || '').trim().toLowerCase();
      return preferEnglish
        ? englishLabel.includes('vakgebied') || baseLabel.includes('vakgebied') || englishLabel.includes('discipline') || baseLabel.includes('discipline')
        : baseLabel.includes('vakgebied');
    });

    if (vakUri) {
      const rawValue = preferEnglish
        ? (extra[`${vakUri}_en`] ?? extra[`${vakUri}_english`] ?? extra[vakUri])
        : (extra[vakUri] ?? extra[`${vakUri}_en`] ?? extra[`${vakUri}_english`]);
      const resolved = getPreferredLocalizedValue(rawValue, preferEnglish);
      if (resolved) return resolved;
    }

    return 'Unclassified';
  }, []);

  const buildRequirementTreeRowsForExport = useCallback((preferEnglish = true) => {
    const requirements = vs1Sections?.requirements || [];
    if (requirements.length === 0) return [];

    const getTypeLabel = (row) => {
      const payload = row?.data && typeof row.data === 'object' ? row.data : {};
      const types = Array.isArray(payload.types) ? payload.types : [];
      for (const typeUri of types) {
        const entry = vs1EntityDirectory[typeUri];
        if (entry) {
          const name = getLocalizedDirectoryName(entry, preferEnglish);
          if (name) return name;
        }
        const seg = String(typeUri).split('/').pop().split('#').pop();
        if (seg && !/^[0-9a-f-]{20,}$/i.test(seg)) return seg;
      }
      return 'Unclassified';
    };

    const typeMap = {};
    requirements.forEach((row) => {
      const typeName = getTypeLabel(row);
      const vakName = getLocalizedVakgebied(row, preferEnglish);
      if (!typeMap[typeName]) typeMap[typeName] = {};
      if (!typeMap[typeName][vakName]) typeMap[typeName][vakName] = [];
      typeMap[typeName][vakName].push(row);
    });

    const sheetRows = [];
    Object.entries(typeMap)
      .sort(([left], [right]) => left.localeCompare(right))
      .forEach(([typeName, vakMap]) => {
        sheetRows.push([typeName, '', '']);
        Object.entries(vakMap)
          .sort(([left], [right]) => left.localeCompare(right))
          .forEach(([vakName, vakRows]) => {
            sheetRows.push(['', vakName, '']);
            vakRows
              .slice()
              .sort((left, right) => getLocalizedRowLabel(left, 'requirements', preferEnglish).localeCompare(getLocalizedRowLabel(right, 'requirements', preferEnglish)))
              .forEach((row) => {
                sheetRows.push(['', '', getLocalizedRowLabel(row, 'requirements', preferEnglish)]);
              });
          });
      });

    return sheetRows;
  }, [getLocalizedDirectoryName, getLocalizedRowLabel, getLocalizedVakgebied, vs1EntityDirectory, vs1Sections?.requirements]);

  const buildVerificationPlanTreeRowsForExport = useCallback((preferEnglish = true) => {
    const verificationPlans = vs1Sections?.verificationPlans || [];
    if (verificationPlans.length === 0) return [];

    const getAttrValues = (payload, key) => {
      const keys = [key];
      if (key.startsWith('tennet:')) keys.push(`http://data.tennet.eu/def/${key.slice('tennet:'.length)}`);
      const rels = payload.relationships && typeof payload.relationships === 'object' ? payload.relationships : {};
      const extra = payload.extra_properties && typeof payload.extra_properties === 'object' ? payload.extra_properties : {};
      for (const currentKey of keys) {
        const relVals = Array.isArray(rels[currentKey]) ? rels[currentKey] : (rels[currentKey] ? [rels[currentKey]] : []);
        if (relVals.length > 0) return relVals;
        const attrVal = extra[currentKey];
        if (attrVal != null) return Array.isArray(attrVal) ? attrVal : [attrVal];
      }
      return [];
    };

    const phaseMap = {};
    verificationPlans.forEach((row) => {
      const payload = row?.data && typeof row.data === 'object' ? row.data : {};
      const phaseVals = getAttrValues(payload, 'tennet:hasPhase');
      const methodVals = getAttrValues(payload, 'tennet:hasVerificationMethod');
      const phaseName = phaseVals.length > 0 ? getLocalizedTargetValue(phaseVals[0], payload, preferEnglish) : 'Unclassified';
      const methodName = methodVals.length > 0 ? getLocalizedTargetValue(methodVals[0], payload, preferEnglish) : 'Unclassified';

      if (!phaseMap[phaseName]) phaseMap[phaseName] = {};
      if (!phaseMap[phaseName][methodName]) phaseMap[phaseName][methodName] = [];
      phaseMap[phaseName][methodName].push(row);
    });

    const sheetRows = [];
    Object.entries(phaseMap)
      .sort(([left], [right]) => left.localeCompare(right))
      .forEach(([phaseName, methods]) => {
        sheetRows.push([phaseName, '', '']);
        Object.entries(methods)
          .sort(([left], [right]) => left.localeCompare(right))
          .forEach(([methodName, methodRows]) => {
            sheetRows.push(['', methodName, '']);
            methodRows
              .slice()
              .sort((left, right) => getLocalizedRowLabel(left, 'verificationPlans', preferEnglish).localeCompare(getLocalizedRowLabel(right, 'verificationPlans', preferEnglish)))
              .forEach((row) => {
                sheetRows.push(['', '', getLocalizedRowLabel(row, 'verificationPlans', preferEnglish)]);
              });
          });
      });

    return sheetRows;
  }, [getLocalizedRowLabel, getLocalizedTargetValue, vs1Sections?.verificationPlans]);

  const buildClassTreeRowsForExport = useCallback((preferEnglish = true) => {
    const classes = visibleClassRows || [];
    if (classes.length === 0) return [];

    const uriToRow = {};
    classes.forEach((row) => {
      const payload = row?.data && typeof row.data === 'object' ? row.data : {};
      const uri = String(payload.uri || '').trim();
      if (uri) uriToRow[uri] = row;
    });

    const childrenOfUri = {};
    const hasParentInSet = new Set();
    const externalParentChildren = {};

    classes.forEach((row) => {
      const payload = row?.data && typeof row.data === 'object' ? row.data : {};
      const rels = payload.relationships && typeof payload.relationships === 'object' ? payload.relationships : {};
      const subClassEntry = Object.entries(rels).find(([predicate]) => String(predicate || '').toLowerCase().includes('subclassof'));
      if (!subClassEntry) return;
      const allValues = Array.isArray(subClassEntry[1]) ? subClassEntry[1] : [subClassEntry[1]];
      const uri = String(payload.uri || '').trim();
      const firstParent = String(allValues[0] || '').trim();
      if (!firstParent) return;

      if (uriToRow[firstParent]) {
        if (!childrenOfUri[firstParent]) childrenOfUri[firstParent] = [];
        childrenOfUri[firstParent].push(row);
        if (uri) hasParentInSet.add(uri);
      } else {
        if (!externalParentChildren[firstParent]) externalParentChildren[firstParent] = [];
        externalParentChildren[firstParent].push(row);
        if (uri) hasParentInSet.add(uri);
      }
    });

    const resolveExternalLabel = (uri) => {
      const entry = vs1EntityDirectory[uri];
      if (entry) {
        const name = getLocalizedDirectoryName(entry, preferEnglish);
        if (name) return name;
      }
      const text = String(uri || '');
      const fragment = text.includes('#') ? text.split('#').pop() : text.split('/').pop();
      if (/^[0-9a-f]{8}-[0-9a-f]{4}/i.test(fragment)) return uri;
      return fragment || uri;
    };

    const buildNode = (row) => {
      const payload = row?.data && typeof row.data === 'object' ? row.data : {};
      const uri = String(payload.uri || '').trim();
      const children = (childrenOfUri[uri] || [])
        .slice()
        .sort((left, right) => getLocalizedRowLabel(left, 'classes', preferEnglish).localeCompare(getLocalizedRowLabel(right, 'classes', preferEnglish)));
      return {
        row,
        label: getLocalizedRowLabel(row, 'classes', preferEnglish),
        children: children.map(buildNode),
        isVirtual: false,
      };
    };

    const topLevel = [];
    Object.entries(externalParentChildren).forEach(([externalUri, externalChildren]) => {
      topLevel.push({
        row: { id: `ext-${externalUri}` },
        label: resolveExternalLabel(externalUri),
        children: externalChildren
          .slice()
          .sort((left, right) => getLocalizedRowLabel(left, 'classes', preferEnglish).localeCompare(getLocalizedRowLabel(right, 'classes', preferEnglish)))
          .map(buildNode),
        isVirtual: true,
      });
    });

    classes.forEach((row) => {
      const payload = row?.data && typeof row.data === 'object' ? row.data : {};
      const uri = String(payload.uri || '').trim();
      if (!hasParentInSet.has(uri)) {
        topLevel.push(buildNode(row));
      }
    });

    topLevel.sort((left, right) => left.label.localeCompare(right.label));

    const sheetRows = [];
    const appendNode = (node, depth = 0) => {
      const currentRow = [];
      currentRow[depth] = node.label;
      sheetRows.push(currentRow);
      node.children.forEach((child) => appendNode(child, depth + 1));
    };

    topLevel.forEach((node) => appendNode(node, 0));
    return sheetRows;
  }, [getLocalizedDirectoryName, getLocalizedRowLabel, visibleClassRows, vs1EntityDirectory]);

  const buildObjectHasPartTreeRowsForExport = useCallback((preferEnglish = true) => {
    const objects = vs1Sections?.objects || [];
    if (objects.length === 0) return [];

    const uriToRow = {};
    objects.forEach((row) => {
      const payload = row?.data && typeof row.data === 'object' ? row.data : {};
      const uri = String(payload.uri || '').trim();
      if (uri) uriToRow[uri] = row;
    });

    const childrenByUri = {};
    const hasInternalParent = new Set();

    objects.forEach((row) => {
      const payload = row?.data && typeof row.data === 'object' ? row.data : {};
      const uri = String(payload.uri || '').trim();
      const rels = payload.relationships && typeof payload.relationships === 'object' ? payload.relationships : {};
      const outgoingHasPart = Array.isArray(rels['nen2660:hasPart']) ? rels['nen2660:hasPart'] : (rels['nen2660:hasPart'] ? [rels['nen2660:hasPart']] : []);
      outgoingHasPart.forEach((childUri) => {
        const normalizedChildUri = String(childUri || '').trim();
        if (!normalizedChildUri || !uriToRow[normalizedChildUri]) return;
        if (!childrenByUri[uri]) childrenByUri[uri] = [];
        childrenByUri[uri].push(uriToRow[normalizedChildUri]);
        hasInternalParent.add(normalizedChildUri);
      });
    });

    if (Object.keys(childrenByUri).length === 0) {
      return objects
        .slice()
        .sort((left, right) => getLocalizedRowLabel(left, 'objects', preferEnglish).localeCompare(getLocalizedRowLabel(right, 'objects', preferEnglish)))
        .map((row) => [getLocalizedRowLabel(row, 'objects', preferEnglish)]);
    }

    const roots = objects.filter((row) => {
      const payload = row?.data && typeof row.data === 'object' ? row.data : {};
      const uri = String(payload.uri || '').trim();
      return uri && !hasInternalParent.has(uri);
    });

    const visited = new Set();
    const sheetRows = [];
    const appendNode = (row, depth = 0) => {
      const payload = row?.data && typeof row.data === 'object' ? row.data : {};
      const uri = String(payload.uri || '').trim();
      if (uri && visited.has(uri)) return;
      if (uri) visited.add(uri);

      const currentRow = [];
      currentRow[depth] = getLocalizedRowLabel(row, 'objects', preferEnglish);
      sheetRows.push(currentRow);

      const children = (childrenByUri[uri] || [])
        .slice()
        .sort((left, right) => getLocalizedRowLabel(left, 'objects', preferEnglish).localeCompare(getLocalizedRowLabel(right, 'objects', preferEnglish)));
      children.forEach((child) => appendNode(child, depth + 1));
    };

    roots
      .slice()
      .sort((left, right) => getLocalizedRowLabel(left, 'objects', preferEnglish).localeCompare(getLocalizedRowLabel(right, 'objects', preferEnglish)))
      .forEach((row) => appendNode(row, 0));

    objects.forEach((row) => appendNode(row, 0));
    return sheetRows;
  }, [getLocalizedRowLabel, vs1Sections?.objects]);

  const buildTreeRowsForExport = useCallback((sectionKey, preferEnglish = true) => {
    if (sectionKey === 'objects') return buildObjectHasPartTreeRowsForExport(preferEnglish);
    if (sectionKey === 'classes') return buildClassTreeRowsForExport(preferEnglish);
    if (sectionKey === 'requirements') return buildRequirementTreeRowsForExport(preferEnglish);
    if (sectionKey === 'verificationPlans') return buildVerificationPlanTreeRowsForExport(preferEnglish);
    return [];
  }, [buildClassTreeRowsForExport, buildObjectHasPartTreeRowsForExport, buildRequirementTreeRowsForExport, buildVerificationPlanTreeRowsForExport]);

  const handleDownloadVs1SectionExcel = useCallback(async (section, rows, sectionItemLabel) => {
    try {
      if (!Array.isArray(rows) || rows.length === 0) {
        if (showError) {
          showError(`No ${section.label.toLowerCase()} found to export.`);
        }
        return;
      }

      const XLSX = await import('xlsx');
      const sectionTitle = String(sectionItemLabel || section.label || 'ITEM')
        .replace(/_/g, ' ')
        .trim()
        .toUpperCase();

      const resolveApplicabilityLabel = (row) => {
        return getRowApplicabilityLabel(row);
      };

      const isRequirementsSection = String(section?.key || '').trim().toLowerCase() === 'requirements';
      const isObjectsSection = String(section?.key || '').trim().toLowerCase() === 'objects';

      const uniqueRows = [];
      const seenPairs = new Set();
      rows.forEach((row) => {
        const payload = row?.data && typeof row.data === 'object' ? row.data : {};
        const terms = getSectionRowOriginalAndTranslatedTerms(row, section.key);
        const applicabilityLabel = resolveApplicabilityLabel(row);

        if (isRequirementsSection) {
          const notation = String(payload.notation || '').trim() || '-';
          const requirementTextOriginal = String(payload.requirement_text || '').trim() || '-';
          const requirementTextTranslated = String(
            payload.requirement_text_english || payload.requirement_text_en || ''
          ).trim() || '-';
          const pairKey = `${notation}|||${terms.original}|||${terms.translated}|||${requirementTextOriginal}|||${requirementTextTranslated}|||${applicabilityLabel}`;
          if (seenPairs.has(pairKey)) return;
          seenPairs.add(pairKey);
          uniqueRows.push([
            notation,
            terms.original,
            terms.translated,
            requirementTextOriginal,
            requirementTextTranslated,
            applicabilityLabel,
          ]);
          return;
        }

        if (isObjectsSection) {
          const notation = String(payload.notation || '').trim() || '-';
          const originalObjectTerm = String(
            payload.label || payload.name || payload.title || payload.fieldName || row?.name || ''
          ).trim() || '-';
          const translatedObjectTerm = String(
            payload.label_english
            || payload.label_en
            || payload.name_english
            || payload.name_en
            || payload.title_english
            || payload.title_en
            || payload.fieldName_english
            || payload.fieldName_en
            || ''
          ).trim() || originalObjectTerm;
          const applicabilityReason = String(getSectionRowValue(row, 'applicability_reason') || '').trim() || '-';
          const pairKey = `${notation}|||${originalObjectTerm}|||${translatedObjectTerm}|||${applicabilityLabel}|||${applicabilityReason}`;
          if (seenPairs.has(pairKey)) return;
          seenPairs.add(pairKey);
          uniqueRows.push([
            notation,
            originalObjectTerm,
            translatedObjectTerm,
            applicabilityLabel,
            applicabilityReason,
          ]);
          return;
        }

        const pairKey = `${terms.original}|||${terms.translated}|||${applicabilityLabel}`;
        if (seenPairs.has(pairKey)) return;
        seenPairs.add(pairKey);
        uniqueRows.push([terms.original, terms.translated, applicabilityLabel]);
      });

      // Sort alphabetically by original term (label).
      const sortColumnIndex = isObjectsSection ? 1 : 0;
      uniqueRows.sort((a, b) => String(a[sortColumnIndex] || '').localeCompare(String(b[sortColumnIndex] || ''), 'en', { sensitivity: 'base' }));

      const worksheetHeader = isRequirementsSection
        ? ['Notation', 'Original term', 'Translated term', 'Requirement text (original)', 'Requirement text (translated)', 'Applicability']
        : isObjectsSection
          ? ['Notation', 'Original term', 'Translated term', 'Applicability', 'Applicability reason']
          : ['Original term', 'Translated term', 'Applicability'];

      const worksheetData = [
        [sectionTitle],
        [],
        worksheetHeader,
        ...uniqueRows,
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      worksheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: worksheetHeader.length - 1 } }];
      worksheet['!cols'] = isRequirementsSection
        ? [{ wch: 20 }, { wch: 40 }, { wch: 40 }, { wch: 60 }, { wch: 60 }, { wch: 20 }]
        : isObjectsSection
          ? [{ wch: 20 }, { wch: 40 }, { wch: 40 }, { wch: 20 }, { wch: 90 }]
          : [{ wch: 48 }, { wch: 48 }, { wch: 20 }];

      const originalTreeRows = buildTreeRowsForExport(section.key, false);
      const translatedTreeRows = buildTreeRowsForExport(section.key, true);

      const buildTreeWorksheet = (sheetTitle, treeRows) => {
        const maxDepth = Math.max(
          1,
          ...treeRows.map((row) => (Array.isArray(row) ? row.length : 0))
        );
        const headerRow = Array.from({ length: maxDepth }, (_, index) => `Level ${index + 1}`);
        const treeWorksheetData = [
          [sheetTitle],
          [],
          headerRow,
          ...treeRows,
        ];
        const treeWorksheet = XLSX.utils.aoa_to_sheet(treeWorksheetData);
        treeWorksheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: maxDepth - 1 } }];
        treeWorksheet['!cols'] = Array.from({ length: maxDepth }, () => ({ wch: 36 }));
        return treeWorksheet;
      };

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, sanitizeExcelSheetName(sectionTitle));
      XLSX.utils.book_append_sheet(
        workbook,
        buildTreeWorksheet(`${sectionTitle} ORIGINAL TREE`, originalTreeRows),
        sanitizeExcelSheetName(`${sectionTitle} Original`)
      );
      XLSX.utils.book_append_sheet(
        workbook,
        buildTreeWorksheet(`${sectionTitle} TRANSLATED TREE`, translatedTreeRows),
        sanitizeExcelSheetName(`${sectionTitle} Translated`)
      );

      const workbookBytes = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob(
        [workbookBytes],
        { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
      );

      const filenameBase = String(section.key || sectionTitle)
        .trim()
        .replace(/[^a-zA-Z0-9-_]+/g, '_')
        .replace(/^_+|_+$/g, '') || 'section';

      triggerBlobDownload(blob, `${filenameBase}_terms.xlsx`);
      if (showSuccess) {
        showSuccess(`${section.label} Excel downloaded.`);
      }
    } catch (error) {
      if (showError) {
        showError(error?.message || `Failed to export ${section.label} Excel.`);
      }
    }
  }, [buildTreeRowsForExport, showError, showSuccess]);

  const renderVs1SectionCard = (section, options = {}) => {
    const { isModalView = false } = options;
    const sourceRows = vs1Sections?.[section.key] || [];
    const rows = section.key === 'classes' ? visibleClassRows : sourceRows;
    const totalCount = rows.length;
    const appliesCount = rows.filter((row) => isRowApplies(row) === true).length;
    const notAppliesCount = rows.filter((row) => isRowApplies(row) === false).length;
    const indeterminateCount = rows.filter((row) => isRowIndeterminate(row)).length;
    const smartAnalyzedCount = rows.filter(isRowSmartAnalyzed).length;

    const summaryMetrics = [
      {
        key: 'total',
        label: section.label,
        value: totalCount,
        percentage: null,
      },
      {
        key: 'applies',
        label: 'Applies',
        value: appliesCount,
        percentage: getMetricPercentage(appliesCount, totalCount),
      },
      {
        key: 'notApplies',
        label: 'Does not apply',
        value: notAppliesCount,
        percentage: getMetricPercentage(notAppliesCount, totalCount),
      },
      {
        key: 'indeterminate',
        label: 'Indeterminate',
        value: indeterminateCount,
        percentage: getMetricPercentage(indeterminateCount, totalCount),
      },
    ];

    if (section.key === 'requirements') {
      summaryMetrics.push({
        key: 'smartAnalyzed',
        label: 'SMART Analyzed',
        value: smartAnalyzedCount,
        percentage: getMetricPercentage(smartAnalyzedCount, totalCount),
      });
    }

    if (section.key === 'objects') {
      summaryMetrics.push({
        key: 'conflicts',
        label: 'Conflicts',
        value: objectHasPartConflicts.count,
        percentage: getMetricPercentage(objectHasPartConflicts.count, totalCount),
      });
    }

    const sectionItemLabel = getSectionItemLabel(section.label);
    const selectedRowId = vs1SectionSelection?.[section.key];
    const selectedRow = rows.find(
      (row) => String(row?.id ?? '') === String(selectedRowId ?? '')
    ) || null;

    return (
      <Paper
        key={section.key}
        variant="outlined"
        sx={{
          p: 1.5,
          borderColor: 'divider',
          minHeight: isModalView ? 0 : 240,
          ...(isModalView
            ? {
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              width: '100%',
            }
            : {}),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
            cursor: isModalView ? 'default' : 'pointer',
          }}
          onClick={() => {
            if (!isModalView) {
              handleOpenVs1SectionModal(section.key);
            }
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {section.label}
          </Typography>
          {(section.key === 'objects' || section.key === 'requirements') && (
            <Button
              size="small"
              variant="outlined"
              onClick={(event) => {
                event.stopPropagation();
                if (section.key === 'objects') {
                  handleRunObjectsApplicabilityAnalysis();
                  return;
                }
                handleRunRequirementsApplicabilityAnalysis();
              }}
              disabled={(
                (section.key === 'objects' && runningObjectsApplicabilityAnalysis)
                || (section.key === 'requirements' && runningRequirementsApplicabilityAnalysis)
                || loadingVs1Sections
              )}
              sx={{ ml: 1 }}
            >
              {(section.key === 'objects' && runningObjectsApplicabilityAnalysis)
                ? 'Analyzing...'
                : (section.key === 'requirements' && runningRequirementsApplicabilityAnalysis)
                  ? 'Analyzing...'
                  : 'Analyze applies/does not apply'}
            </Button>
          )}
          {!isModalView && (
            <Typography variant="caption" color="primary">
              Click to open
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              md: (section.key === 'requirements' || section.key === 'objects')
                ? 'repeat(5, minmax(0, 1fr))'
                : (section.key === 'classes' || section.key === 'verificationPlans')
                  ? 'repeat(4, minmax(0, 1fr))'
                  : 'repeat(3, minmax(0, 1fr))',
            },
            gap: 1,
            mb: 1.5,
          }}
        >
          {summaryMetrics.map((metric) => (
            <Paper
              key={`${section.key}-${metric.key}`}
              variant="outlined"
              sx={{
                p: 1,
                borderColor: metric.key === 'conflicts' && metric.value > 0 ? 'error.main' : 'divider',
                bgcolor: 'background.default',
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {metric.label}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  lineHeight: 1.2,
                  color: metric.key === 'conflicts' && metric.value > 0 ? 'error.main' : 'text.primary',
                }}
              >
                {metric.value}
              </Typography>
              {metric.percentage !== null && (
                <Typography
                  variant="caption"
                  color={metric.key === 'conflicts' && metric.value > 0 ? 'error.main' : 'text.secondary'}
                >
                  {metric.percentage}
                </Typography>
              )}
            </Paper>
          ))}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: isModalView
              ? { xs: '1fr', lg: 'minmax(220px, 0.7fr) minmax(0, 1.3fr)' }
              : '1fr',
            ...(isModalView
              ? {
                flex: 1,
                height: 0,
                maxHeight: '100%',
                minHeight: 0,
                overflow: 'hidden',
              }
              : {}),
            gap: 1.5,
          }}
        >
          <Paper
            variant="outlined"
            sx={{
              borderColor: 'divider',
              overflow: 'hidden',
              ...(isModalView
                ? {
                  height: '100%',
                  maxHeight: '100%',
                  minHeight: 0,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }
                : {}),
            }}
          >
            <Box
              sx={{
                px: 1.25,
                py: 1,
                flexShrink: 0,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.default',
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {section.label}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {isModalView
                    ? `Select a ${sectionItemLabel} to view details`
                    : `Click a ${sectionItemLabel} to open details`}
                </Typography>
                <Tooltip title={`Download ${section.label} as Excel`}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => handleDownloadVs1SectionExcel(section, rows, sectionItemLabel)}
                      disabled={rows.length === 0}
                      sx={{ p: 0.25 }}
                      aria-label={`Download ${section.label} Excel`}
                    >
                      <Download fontSize="inherit" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
              {section.key === 'objects' && (
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={objectTreeMode}
                  onChange={(_, newValue) => {
                    if (newValue) {
                      setObjectTreeMode(newValue);
                    }
                  }}
                  sx={{ mt: 0.75 }}
                >
                  <ToggleButton value="hasPart">Has Part tree</ToggleButton>
                  <ToggleButton value="classification">List</ToggleButton>
                </ToggleButtonGroup>
              )}
            </Box>

            <List
              dense
              disablePadding
              sx={{
                ...(isModalView
                  ? {
                    flex: 1,
                    height: '100%',
                    maxHeight: '100%',
                    minHeight: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    overscrollBehavior: 'contain',
                  }
                  : {
                    maxHeight: 240,
                    overflowY: 'auto',
                  }),
              }}
            >
              {rows.length === 0 ? (
                <Box sx={{ px: 1.25, py: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    No {section.label.toLowerCase()} found for this source.
                  </Typography>
                </Box>
              ) : section.key === 'objects' ? (
                (() => {
                  const getObjectRowPrimary = (row) => {
                    const rp = row?.data && typeof row.data === 'object' ? row.data : {};
                    const rowNotation = String(rp.notation || '').trim();
                    const rowLabel = String(rp.label_english || rp.label_en || rp.label || row?.name_en || row?.name || '').trim();
                    return String(row?.name_en || row?.name || '').trim()
                      || (rowNotation && rowLabel ? `${rowNotation} - ${rowLabel}` : rowNotation || rowLabel || '-');
                  };

                  const openObjectRow = (row) => {
                    if (isModalView) {
                      handleSelectVs1SectionRow(section.key, row.id);
                      return;
                    }
                    handleOpenVs1SectionModal(section.key, row.id);
                  };

                  const renderObjectRowItem = (row, paddingLeft = 6) => {
                    const applicabilityStatus = normalizeMetricText(getSectionRowValue(row, 'applicability_status'));
                    const hasApplies = applicabilityStatus === 'aplica';
                    const hasNoApplies = applicabilityStatus === 'no_aplica';
                    const decisionSourceLabel = String(getSectionRowValue(row, 'applicability_decision_source_label') || '').trim();
                    const rawSearchTargets = getSectionRowValue(row, 'applicability_search_targets');
                    const searchTargetsReason = String(getSectionRowValue(row, 'applicability_search_targets_reason') || '').trim();
                    const rawTopCandidates = getSectionRowValue(row, 'applicability_top_candidates');
                    const rawComponentCandidates = getSectionRowValue(row, 'applicability_component_candidates');
                    const searchTargets = Array.isArray(rawSearchTargets)
                      ? rawSearchTargets
                        .map((target) => String(target || '').trim())
                        .filter(Boolean)
                      : [];
                    const closestNodes = hasNoApplies && Array.isArray(rawTopCandidates)
                      ? rawTopCandidates
                        .map((candidate) => ({
                          nodeId: String(candidate?.node_id || candidate?.nodeId || '').trim(),
                          name: String(candidate?.name || '').trim(),
                          score: Number(candidate?.score || 0),
                        }))
                        .filter((candidate) => candidate.name)
                        .slice(0, 20)
                      : [];
                    const componentNodes = hasNoApplies && Array.isArray(rawComponentCandidates)
                      ? rawComponentCandidates
                        .map((candidate) => ({
                          nodeId: String(candidate?.node_id || candidate?.nodeId || '').trim(),
                          name: String(candidate?.name || '').trim(),
                          score: Number(candidate?.score || 0),
                          matchedToken: String(candidate?.matched_token || candidate?.matchedToken || '').trim(),
                        }))
                        .filter((candidate) => candidate.name)
                      : [];
                    const indicatorTooltip = (
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                          {getRowApplicabilityLabel(row)}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          {String(getSectionRowValue(row, 'applicability_reason') || 'Run the analysis to calculate and save the reason.').trim()}
                        </Typography>
                        {decisionSourceLabel && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.35, color: 'text.secondary' }}>
                            Decided by: {decisionSourceLabel}
                          </Typography>
                        )}
                        {searchTargets.length > 0 && (
                          <Box sx={{ mt: 0.6 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                              Nodes searched by the system
                            </Typography>
                            <Box sx={{ maxHeight: 120, overflowY: 'auto', pr: 0.3 }}>
                              {searchTargets.map((target, index) => (
                                <Typography
                                  key={`${row.id}-target-${target}-${index}`}
                                  variant="caption"
                                  sx={{ display: 'block', lineHeight: 1.25 }}
                                >
                                  {index + 1}. {target}
                                </Typography>
                              ))}
                            </Box>
                            {searchTargetsReason && (
                              <Typography variant="caption" sx={{ display: 'block', mt: 0.35, color: 'text.secondary' }}>
                                Criteria: {searchTargetsReason}
                              </Typography>
                            )}
                          </Box>
                        )}
                        {hasNoApplies && closestNodes.length > 0 && (
                          <Box sx={{ mt: 0.6 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                              Top 20 closest nodes (score)
                            </Typography>
                            <Box sx={{ maxHeight: 140, overflowY: 'auto', pr: 0.3 }}>
                              {closestNodes.map((candidate, index) => (
                                <Typography
                                  key={`${row.id}-near-${candidate.nodeId || candidate.name}-${index}`}
                                  variant="caption"
                                  sx={{ display: 'block', lineHeight: 1.25 }}
                                >
                                  {index + 1}. {candidate.name} ({candidate.score.toFixed(3)})
                                </Typography>
                              ))}
                            </Box>
                          </Box>
                        )}
                        {hasNoApplies && componentNodes.length > 0 && (
                          <Box sx={{ mt: 0.6 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                              Nodes by object term
                            </Typography>
                            <Box sx={{ maxHeight: 120, overflowY: 'auto', pr: 0.3 }}>
                              {componentNodes.map((candidate, index) => (
                                <Typography
                                  key={`${row.id}-component-${candidate.nodeId || candidate.name}-${index}`}
                                  variant="caption"
                                  sx={{ display: 'block', lineHeight: 1.25 }}
                                >
                                  {index + 1}. {candidate.name}
                                  {candidate.matchedToken ? ` [${candidate.matchedToken}]` : ''}
                                  {` (${candidate.score.toFixed(3)})`}
                                </Typography>
                              ))}
                            </Box>
                          </Box>
                        )}
                        {hasNoApplies && closestNodes.length === 0 && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.35 }}>
                            Run "Analyze applies/does not apply" to generate nearby nodes.
                          </Typography>
                        )}
                      </Box>
                    );

                    return (
                      <ListItemButton
                        key={`${section.key}-${row.id}`}
                        alignItems="flex-start"
                        selected={isModalView && Boolean(selectedRow) && String(selectedRow.id) === String(row.id)}
                        onClick={() => openObjectRow(row)}
                        sx={{ pl: paddingLeft }}
                      >
                        <Tooltip title={indicatorTooltip} placement="top" arrow>
                          <Box
                            sx={{
                              width: 18,
                              height: 18,
                              minWidth: 18,
                              borderRadius: '50%',
                              bgcolor: hasApplies ? 'success.main' : (hasNoApplies ? 'error.main' : 'grey.500'),
                              color: '#fff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 1,
                              mt: 0.15,
                              flexShrink: 0,
                            }}
                          >
                            {hasApplies ? (
                              <CheckCircleOutline sx={{ fontSize: 13 }} />
                            ) : hasNoApplies ? (
                              <Close sx={{ fontSize: 13 }} />
                            ) : (
                              <Info sx={{ fontSize: 13 }} />
                            )}
                          </Box>
                        </Tooltip>
                        <ListItemText
                          primary={getObjectRowPrimary(row)}
                          primaryTypographyProps={{
                            variant: 'body2',
                            sx: { fontWeight: 600, fontSize: '0.78rem' },
                          }}
                        />
                      </ListItemButton>
                    );
                  };

                  const renderClassificationTree = () => (
                    groupedObjects.map(({ superClass, classes }) => {
                      const superKey = `obj-super-${superClass}`;
                      const isSuperExpanded = vs1ExpandedGroups[superKey] === true;
                      const superCount = classes.reduce((sum, item) => sum + item.rows.length, 0);
                      return (
                        <React.Fragment key={superKey}>
                          <ListItemButton
                            onClick={() => setVs1ExpandedGroups((prev) => ({ ...prev, [superKey]: !isSuperExpanded }))}
                            sx={{ py: 0.25, bgcolor: grey[50], borderBottom: '1px solid', borderColor: 'divider' }}
                          >
                            <FolderOpen sx={{ fontSize: 16, color: grey[500], mr: 1 }} />
                            <ListItemText
                              primary={`${superClass} (${superCount})`}
                              primaryTypographyProps={{ variant: 'caption', sx: { fontWeight: 700, color: grey[700] } }}
                            />
                            {isSuperExpanded ? <ExpandLess sx={{ fontSize: 16, color: grey[500] }} /> : <ExpandMore sx={{ fontSize: 16, color: grey[500] }} />}
                          </ListItemButton>
                          <Collapse in={isSuperExpanded} timeout="auto" unmountOnExit>
                            <List dense disablePadding>
                              {classes.map(({ className, rows: classRows }) => {
                                const classKey = `obj-class-${superClass}-${className}`;
                                const isClassExpanded = vs1ExpandedGroups[classKey] === true;
                                return (
                                  <React.Fragment key={classKey}>
                                    <ListItemButton
                                      onClick={() => setVs1ExpandedGroups((prev) => ({ ...prev, [classKey]: !isClassExpanded }))}
                                      sx={{ py: 0.25, pl: 3, bgcolor: 'background.default', borderBottom: '1px solid', borderColor: 'divider' }}
                                    >
                                      <Workspaces sx={{ fontSize: 14, color: grey[400], mr: 1 }} />
                                      <ListItemText
                                        primary={`${className} (${classRows.length})`}
                                        primaryTypographyProps={{ variant: 'caption', sx: { fontWeight: 600, color: grey[600] } }}
                                      />
                                      {isClassExpanded ? <ExpandLess sx={{ fontSize: 14, color: grey[400] }} /> : <ExpandMore sx={{ fontSize: 14, color: grey[400] }} />}
                                    </ListItemButton>
                                    <Collapse in={isClassExpanded} timeout="auto" unmountOnExit>
                                      <List dense disablePadding>
                                        {classRows.map((row) => renderObjectRowItem(row, 6))}
                                      </List>
                                    </Collapse>
                                  </React.Fragment>
                                );
                              })}
                            </List>
                          </Collapse>
                        </React.Fragment>
                      );
                    })
                  );

                  const renderHasPartNode = (node, depth = 0) => {
                    const nodeKey = `obj-haspart-${node.key}`;
                    const isExpanded = vs1ExpandedGroups[nodeKey] === true;
                    const hasChildren = Array.isArray(node.children) && node.children.length > 0;
                    const basePadding = 1.5 + depth * 2;
                    const isConflictParent = objectHasPartConflicts.parentKeys.has(String(node?.key || node?.row?.id || ''));
                    const applicabilityStatus = normalizeMetricText(getSectionRowValue(node.row, 'applicability_status'));
                    const hasApplies = applicabilityStatus === 'aplica';
                    const hasNoApplies = applicabilityStatus === 'no_aplica';
                    const parentIndicatorTooltip = getRowApplicabilityLabel(node.row);

                    if (!hasChildren) {
                      return renderObjectRowItem(node.row, 2 + depth * 2);
                    }

                    return (
                      <React.Fragment key={nodeKey}>
                        <ListItemButton
                          alignItems="flex-start"
                          selected={isModalView && Boolean(selectedRow) && String(selectedRow.id) === String(node.row.id)}
                          onClick={() => openObjectRow(node.row)}
                          sx={{
                            py: 0.25,
                            pl: basePadding,
                            bgcolor: isConflictParent
                              ? 'rgba(211, 47, 47, 0.10)'
                              : (depth === 0 ? grey[50] : 'background.default'),
                            borderBottom: '1px solid',
                            borderColor: isConflictParent ? 'error.main' : 'divider',
                          }}
                        >
                          <Tooltip title={parentIndicatorTooltip} placement="top" arrow>
                            <Box
                              sx={{
                                width: 18,
                                height: 18,
                                minWidth: 18,
                                borderRadius: '50%',
                                bgcolor: hasApplies ? 'success.main' : (hasNoApplies ? 'error.main' : 'grey.500'),
                                color: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mr: 1,
                                mt: 0.15,
                                flexShrink: 0,
                              }}
                            >
                              {hasApplies ? (
                                <CheckCircleOutline sx={{ fontSize: 13 }} />
                              ) : hasNoApplies ? (
                                <Close sx={{ fontSize: 13 }} />
                              ) : (
                                <Info sx={{ fontSize: 13 }} />
                              )}
                            </Box>
                          </Tooltip>
                          <FolderOpen sx={{ fontSize: 16, color: isConflictParent ? 'error.main' : grey[500], mr: 1, mt: 0.1 }} />
                          <ListItemText
                            primary={`${getObjectRowPrimary(node.row)} (${node.children.length})`}
                            primaryTypographyProps={{
                              variant: 'caption',
                              sx: {
                                fontWeight: 700,
                                color: isConflictParent ? 'error.main' : grey[700],
                              },
                            }}
                          />
                          <IconButton
                            size="small"
                            sx={{ p: 0.25, ml: 0.5 }}
                            onClick={(event) => {
                              event.stopPropagation();
                              setVs1ExpandedGroups((prev) => ({ ...prev, [nodeKey]: !isExpanded }));
                            }}
                          >
                            {isExpanded ? <ExpandLess sx={{ fontSize: 16, color: grey[500] }} /> : <ExpandMore sx={{ fontSize: 16, color: grey[500] }} />}
                          </IconButton>
                        </ListItemButton>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <List dense disablePadding>
                            {node.children.map((childNode) => renderHasPartNode(childNode, depth + 1))}
                          </List>
                        </Collapse>
                      </React.Fragment>
                    );
                  };

                  if (objectTreeMode === 'classification') {
                    return renderClassificationTree();
                  }

                  return groupedObjectsByHasPart.map((node) => renderHasPartNode(node, 0));
                })()
              ) : section.key === 'classes' ? (
                // Recursive class tree based on rdfs:subClassOf hierarchy
                (() => {
                  const renderClassNode = (node, depth = 0) => {
                    const { row, label, hasChildren, children, isVirtual } = node;
                    const nodeKey = `cls-node-${row.id}`;
                    const isExpanded = vs1ExpandedGroups[nodeKey] === true;

                    if (!hasChildren && !isVirtual) {
                      // Leaf node — just a clickable item
                      return (
                        <ListItemButton
                          key={`${section.key}-${row.id}`}
                          alignItems="flex-start"
                          selected={isModalView && Boolean(selectedRow) && String(selectedRow.id) === String(row.id)}
                          onClick={() => {
                            if (isModalView) { handleSelectVs1SectionRow(section.key, row.id); return; }
                            handleOpenVs1SectionModal(section.key, row.id);
                          }}
                          sx={{ pl: 2 + depth * 2 }}
                        >
                          <ListItemText
                            primary={label}
                            primaryTypographyProps={{ variant: 'body2', sx: { fontWeight: 600, fontSize: '0.78rem' } }}
                          />
                        </ListItemButton>
                      );
                    }

                    // Folder node — collapsible
                    const descendantCount = (() => {
                      let count = 0;
                      const walk = (n) => { if (!n.isVirtual) count++; n.children.forEach(walk); };
                      walk(node);
                      return count;
                    })();

                    return (
                      <React.Fragment key={nodeKey}>
                        <ListItemButton
                          onClick={() => setVs1ExpandedGroups((prev) => ({ ...prev, [nodeKey]: !isExpanded }))}
                          sx={{ py: 0.25, pl: 1.5 + depth * 2, bgcolor: depth === 0 ? grey[100] : grey[50], borderBottom: '1px solid', borderColor: 'divider' }}
                        >
                          <FolderOpen sx={{ fontSize: 16, color: depth === 0 ? 'primary.main' : grey[500], mr: 1 }} />
                          <ListItemText
                            primary={`${label} (${descendantCount})`}
                            primaryTypographyProps={{ variant: 'caption', sx: { fontWeight: 700, color: depth === 0 ? grey[800] : grey[700] } }}
                          />
                          {isExpanded ? <ExpandLess sx={{ fontSize: 16, color: grey[500] }} /> : <ExpandMore sx={{ fontSize: 16, color: grey[500] }} />}
                        </ListItemButton>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <List dense disablePadding>
                            {!isVirtual && (
                              <ListItemButton
                                key={`${section.key}-self-${row.id}`}
                                selected={isModalView && Boolean(selectedRow) && String(selectedRow.id) === String(row.id)}
                                onClick={() => {
                                  if (isModalView) { handleSelectVs1SectionRow(section.key, row.id); return; }
                                  handleOpenVs1SectionModal(section.key, row.id);
                                }}
                                sx={{ pl: 3 + depth * 2, bgcolor: 'rgba(25, 118, 210, 0.04)' }}
                              >
                                <Info sx={{ fontSize: 14, color: 'primary.main', mr: 0.5 }} />
                                <ListItemText
                                  primary={label}
                                  primaryTypographyProps={{ variant: 'body2', sx: { fontWeight: 600, fontSize: '0.78rem', fontStyle: 'italic', color: 'primary.main' } }}
                                />
                              </ListItemButton>
                            )}
                            {children.map((child) => renderClassNode(child, depth + 1))}
                          </List>
                        </Collapse>
                      </React.Fragment>
                    );
                  };

                  return groupedClasses.map((node) => renderClassNode(node, 0));
                })()
              ) : section.key === 'requirements' ? (
                groupedRequirements.map(({ typeName, vakGroups, totalCount }) => {
                  const typeKey = `req-type-${typeName}`;
                  const isTypeExpanded = vs1ExpandedGroups[typeKey] === true;
                  return (
                    <React.Fragment key={typeKey}>
                      <ListItemButton
                        onClick={() => setVs1ExpandedGroups((prev) => ({ ...prev, [typeKey]: !isTypeExpanded }))}
                        sx={{ py: 0.25, bgcolor: grey[100], borderBottom: '1px solid', borderColor: 'divider' }}
                      >
                        <FolderOpen sx={{ fontSize: 16, color: 'primary.main', mr: 1 }} />
                        <ListItemText
                          primary={`${typeName} (${totalCount})`}
                          primaryTypographyProps={{ variant: 'caption', sx: { fontWeight: 700, color: grey[800] } }}
                        />
                        {isTypeExpanded ? <ExpandLess sx={{ fontSize: 16, color: grey[500] }} /> : <ExpandMore sx={{ fontSize: 16, color: grey[500] }} />}
                      </ListItemButton>
                      <Collapse in={isTypeExpanded} timeout="auto" unmountOnExit>
                        <List dense disablePadding>
                          {vakGroups.map(([vakName, vakRows]) => {
                            const vakKey = `req-vak-${typeName}-${vakName}`;
                            const isVakExpanded = vs1ExpandedGroups[vakKey] === true;
                            return (
                              <React.Fragment key={vakKey}>
                                <ListItemButton
                                  onClick={() => setVs1ExpandedGroups((prev) => ({ ...prev, [vakKey]: !isVakExpanded }))}
                                  sx={{ py: 0.25, pl: 3, bgcolor: grey[50], borderBottom: '1px solid', borderColor: 'divider' }}
                                >
                                  <FolderOpen sx={{ fontSize: 14, color: grey[500], mr: 1 }} />
                                  <ListItemText
                                    primary={`${vakName} (${vakRows.length})`}
                                    primaryTypographyProps={{ variant: 'caption', sx: { fontWeight: 600, color: grey[700] } }}
                                  />
                                  {isVakExpanded ? <ExpandLess sx={{ fontSize: 14, color: grey[500] }} /> : <ExpandMore sx={{ fontSize: 14, color: grey[500] }} />}
                                </ListItemButton>
                                <Collapse in={isVakExpanded} timeout="auto" unmountOnExit>
                                  <List dense disablePadding>
                                    {vakRows.map((row) => {
                                      const rp = row?.data && typeof row.data === 'object' ? row.data : {};
                                      const rowNotation = String(rp.notation || '').trim();
                                      const rowLabel = String(rp.label_english || rp.label_en || rp.label || row?.name_en || row?.name || '').trim();
                                      const rowPrimary = rowNotation && rowLabel ? `${rowNotation} — ${rowLabel}` : rowNotation || rowLabel || '-';
                                      const applicabilityStatus = normalizeMetricText(getSectionRowValue(row, 'applicability_status'));
                                      const hasApplies = applicabilityStatus === 'aplica';
                                      const hasNoApplies = applicabilityStatus === 'no_aplica';
                                      const indicatorTooltip = (
                                        <Box>
                                          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                                            {getRowApplicabilityLabel(row)}
                                          </Typography>
                                          <Typography variant="caption" sx={{ display: 'block' }}>
                                            {String(getSectionRowValue(row, 'applicability_reason') || 'Run the analysis to calculate and save the reason.').trim()}
                                          </Typography>
                                        </Box>
                                      );

                                      return (
                                        <ListItemButton
                                          key={`${section.key}-${row.id}`}
                                          alignItems="flex-start"
                                          selected={isModalView && Boolean(selectedRow) && String(selectedRow.id) === String(row.id)}
                                          onClick={() => {
                                            if (isModalView) {
                                              handleSelectVs1SectionRow(section.key, row.id);
                                              return;
                                            }
                                            handleOpenVs1SectionModal(section.key, row.id);
                                          }}
                                          sx={{ pl: 6 }}
                                        >
                                          <Tooltip title={indicatorTooltip} placement="top" arrow>
                                            <Box
                                              sx={{
                                                width: 18,
                                                height: 18,
                                                minWidth: 18,
                                                borderRadius: '50%',
                                                bgcolor: hasApplies ? 'success.main' : (hasNoApplies ? 'error.main' : 'grey.500'),
                                                color: '#fff',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                mr: 1,
                                                mt: 0.15,
                                                flexShrink: 0,
                                              }}
                                            >
                                              {hasApplies ? (
                                                <CheckCircleOutline sx={{ fontSize: 13 }} />
                                              ) : hasNoApplies ? (
                                                <Close sx={{ fontSize: 13 }} />
                                              ) : (
                                                <Info sx={{ fontSize: 13 }} />
                                              )}
                                            </Box>
                                          </Tooltip>
                                          <ListItemText
                                            primary={rowPrimary}
                                            primaryTypographyProps={{
                                              variant: 'body2',
                                              sx: { fontWeight: 600, fontSize: '0.78rem' },
                                            }}
                                          />
                                        </ListItemButton>
                                      );
                                    })}
                                  </List>
                                </Collapse>
                              </React.Fragment>
                            );
                          })}
                        </List>
                      </Collapse>
                    </React.Fragment>
                  );
                })
              ) : section.key === 'verificationPlans' ? (
                // Grouped tree by Phase → Verification Method
                groupedVerificationPlans.map(({ phase, methods }) => {
                  const phaseKey = `vp-phase-${phase}`;
                  const isPhaseExpanded = vs1ExpandedGroups[phaseKey] === true;
                  const phaseCount = methods.reduce((sum, m) => sum + m.rows.length, 0);
                  return (
                    <React.Fragment key={phaseKey}>
                      <ListItemButton
                        onClick={() => setVs1ExpandedGroups((prev) => ({ ...prev, [phaseKey]: !isPhaseExpanded }))}
                        sx={{ py: 0.25, bgcolor: grey[50], borderBottom: '1px solid', borderColor: 'divider' }}
                      >
                        <FolderOpen sx={{ fontSize: 16, color: grey[500], mr: 1 }} />
                        <ListItemText
                          primary={`${phase} (${phaseCount})`}
                          primaryTypographyProps={{ variant: 'caption', sx: { fontWeight: 700, color: grey[700] } }}
                        />
                        {isPhaseExpanded ? <ExpandLess sx={{ fontSize: 16, color: grey[500] }} /> : <ExpandMore sx={{ fontSize: 16, color: grey[500] }} />}
                      </ListItemButton>
                      <Collapse in={isPhaseExpanded} timeout="auto" unmountOnExit>
                        <List dense disablePadding>
                          {methods.map(({ method, rows: methodRows }) => {
                            const methodKey = `vp-method-${phase}-${method}`;
                            const isMethodExpanded = vs1ExpandedGroups[methodKey] === true;
                            return (
                              <React.Fragment key={methodKey}>
                                <ListItemButton
                                  onClick={() => setVs1ExpandedGroups((prev) => ({ ...prev, [methodKey]: !isMethodExpanded }))}
                                  sx={{ py: 0.25, pl: 3, bgcolor: 'background.default', borderBottom: '1px solid', borderColor: 'divider' }}
                                >
                                  <Workspaces sx={{ fontSize: 14, color: grey[400], mr: 1 }} />
                                  <ListItemText
                                    primary={`${method} (${methodRows.length})`}
                                    primaryTypographyProps={{ variant: 'caption', sx: { fontWeight: 600, color: grey[600] } }}
                                  />
                                  {isMethodExpanded ? <ExpandLess sx={{ fontSize: 14, color: grey[400] }} /> : <ExpandMore sx={{ fontSize: 14, color: grey[400] }} />}
                                </ListItemButton>
                                <Collapse in={isMethodExpanded} timeout="auto" unmountOnExit>
                                  <List dense disablePadding>
                                    {methodRows.map((row) => {
                                      const rp = row?.data && typeof row.data === 'object' ? row.data : {};
                                      const rpRels = rp.relationships && typeof rp.relationships === 'object' ? rp.relationships : {};
                                      const rpTargetLabels = rp._target_labels && typeof rp._target_labels === 'object' ? rp._target_labels : {};

                                      // Build label as "Requirement name - Class name"
                                      const verifOfUris = Array.isArray(rpRels['tennet:isVerificationOf']) ? rpRels['tennet:isVerificationOf'] : (rpRels['tennet:isVerificationOf'] ? [rpRels['tennet:isVerificationOf']] : []);
                                      const subjectUris = Array.isArray(rpRels['tennet:hasAsSubject']) ? rpRels['tennet:hasAsSubject'] : (rpRels['tennet:hasAsSubject'] ? [rpRels['tennet:hasAsSubject']] : []);

                                      const resolveTargetName = (uri) => {
                                        const targetInfo = rpTargetLabels[uri];
                                        if (targetInfo && typeof targetInfo === 'object') {
                                          const targetName = String(
                                            targetInfo.name_english
                                            || targetInfo.name_en
                                            || targetInfo.name
                                            || targetInfo.notation
                                            || ''
                                          ).trim();
                                          if (targetName) return targetName;
                                        }

                                        const entry = vs1EntityDirectory[uri];
                                        if (entry) return entry.notation || entry.name_en || entry.name_english || entry.name || uri.split('/').pop();

                                        const fallback = String(uri || '').split('/').pop().split('#').pop();
                                        if (/^[0-9a-f]{8}-[0-9a-f]{4}/i.test(fallback)) {
                                          return humanizeKey(uri);
                                        }
                                        return fallback;
                                      };

                                      const reqName = verifOfUris.length > 0 ? resolveTargetName(verifOfUris[0]) : '';
                                      const className = subjectUris.length > 0 ? resolveTargetName(subjectUris[0]) : '';
                                      const rowPrimary = reqName && className
                                        ? `${reqName} — ${className}`
                                        : reqName || className || String(rp.notation || rp.label_english || rp.label_en || rp.label || row?.name_en || row?.name || '-').trim();

                                      return (
                                        <ListItemButton
                                          key={`${section.key}-${row.id}`}
                                          alignItems="flex-start"
                                          selected={isModalView && Boolean(selectedRow) && String(selectedRow.id) === String(row.id)}
                                          onClick={() => {
                                            if (isModalView) {
                                              handleSelectVs1SectionRow(section.key, row.id);
                                              return;
                                            }
                                            handleOpenVs1SectionModal(section.key, row.id);
                                          }}
                                          sx={{ pl: 6 }}
                                        >
                                          <ListItemText
                                            primary={rowPrimary}
                                            primaryTypographyProps={{
                                              variant: 'body2',
                                              sx: { fontWeight: 600, fontSize: '0.78rem' },
                                            }}
                                          />
                                        </ListItemButton>
                                      );
                                    })}
                                  </List>
                                </Collapse>
                              </React.Fragment>
                            );
                          })}
                        </List>
                      </Collapse>
                    </React.Fragment>
                  );
                })
              ) : (
                // Flat list for non-requirement sections
                rows.map((row) => {
                  const rowPrimary = getSectionRowPrimaryText(row) || '-';
                  const rowDetail = getSectionRowDetailText(row);

                  return (
                    <ListItemButton
                      key={`${section.key}-${row.id}`}
                      alignItems="flex-start"
                      selected={isModalView && Boolean(selectedRow) && String(selectedRow.id) === String(row.id)}
                      onClick={() => {
                        if (isModalView) {
                          handleSelectVs1SectionRow(section.key, row.id);
                          return;
                        }

                        handleOpenVs1SectionModal(section.key, row.id);
                      }}
                    >
                      <ListItemText
                        primary={rowPrimary}
                        secondary={rowDetail || row.name || 'No additional detail'}
                        primaryTypographyProps={{
                          variant: 'body2',
                          sx: { fontWeight: 600 },
                        }}
                        secondaryTypographyProps={{
                          variant: 'caption',
                          color: 'text.secondary',
                          sx: {
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          },
                        }}
                      />
                    </ListItemButton>
                  );
                })
              )}
            </List>

            {section.key === 'objects' && (
              <Box
                sx={{
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  px: 1.25,
                  py: 1,
                  bgcolor: 'background.paper',
                }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  justifyContent="space-between"
                >
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleOpenObjectsApplicabilityReviewPicker}
                    disabled={reviewingObjectsApplicability || rows.length === 0 || loadingVs1Sections}
                  >
                    {reviewingObjectsApplicability ? 'Reviewing...' : 'Review applies/does not apply with Excel'}
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    You can upload one or multiple Excel files. DESCR and Applies/Does not apply columns are used.
                  </Typography>
                </Stack>

                {objectsApplicabilityReview && (
                  <Stack spacing={0.75} sx={{ mt: 1 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      <Chip size="small" variant="outlined" label={`Files: ${objectsApplicabilityReview.filesCount}`} />
                      <Chip size="small" variant="outlined" label={`Compared rows: ${objectsApplicabilityReview.comparedRows}`} />
                      <Chip size="small" variant="outlined" label={`Unique objects in Excel: ${objectsApplicabilityReview.comparedUniqueObjects}`} />
                      <Chip size="small" color="info" variant="outlined" label={`Matched rows: ${objectsApplicabilityReview.matchedCount}`} />
                      <Chip size="small" color="warning" variant="outlined" label={`Unmatched rows: ${objectsApplicabilityReview.unmatchedCount}`} />
                      <Chip size="small" color="info" variant="outlined" label={`Matched system objects: ${objectsApplicabilityReview.matchedSystemObjects}/${objectsApplicabilityReview.totalObjectsInSystem}`} />
                      <Chip size="small" color="success" variant="outlined" label={`Correct: ${objectsApplicabilityReview.correctCount}`} />
                      <Chip size="small" color="error" variant="outlined" label={`Incorrect: ${objectsApplicabilityReview.incorrectCount}`} />
                    </Box>

                    <Typography variant="caption" color="text.secondary">
                      Accuracy on reviewed matched rows: {objectsApplicabilityReview.accuracy}% ·
                      Unique objects matched/unmatched (Excel): {objectsApplicabilityReview.matchedUniqueObjects}/{objectsApplicabilityReview.unmatchedUniqueObjects} ·
                      Expected Applies/Does not apply: {objectsApplicabilityReview.expectedAppliesCount}/{objectsApplicabilityReview.expectedNoAppliesCount} ·
                      Current Applies/Does not apply/Unreviewed: {objectsApplicabilityReview.actualAppliesCount}/{objectsApplicabilityReview.actualNoAppliesCount}/{objectsApplicabilityReview.actualUnreviewedCount}
                    </Typography>

                    {objectsApplicabilityReview && (
                      <Box>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Download fontSize="small" />}
                          onClick={handleDownloadObjectsApplicabilityDifferences}
                        >
                          Download differences (Excel)
                        </Button>
                      </Box>
                    )}

                    {objectsApplicabilityReview.ignoredSheetsByMissingColumns.length > 0 && (
                      <Typography variant="caption" color="warning.main">
                        Sheets skipped because DESCR and Applies/Does not apply columns were missing: {objectsApplicabilityReview.ignoredSheetsByMissingColumns.join(', ')}
                      </Typography>
                    )}

                    {objectsApplicabilityReview.incorrectMatches.length > 0 && (
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                          Difference examples
                        </Typography>
                        <List dense disablePadding>
                          {objectsApplicabilityReview.incorrectMatches.slice(0, 8).map((item, index) => (
                            <ListItemText
                              key={`incorrect-review-${item.objectName}-${index}`}
                              primary={`${item.objectName}: expected ${item.expectedLabel}, actual ${item.actualLabel}`}
                              secondary={`${item.source}${item.similarityScore < 1 ? ` · similarity ${item.similarityScore.toFixed(2)}` : ''}`}
                              primaryTypographyProps={{ variant: 'caption', sx: { color: 'error.main' } }}
                              secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                            />
                          ))}
                        </List>
                      </Box>
                    )}

                    {objectsApplicabilityReview.unmatchedEntries.length > 0 && (
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                          Excel objects not found
                        </Typography>
                        <List dense disablePadding>
                          {objectsApplicabilityReview.unmatchedEntries.slice(0, 8).map((item, index) => (
                            <ListItemText
                              key={`unmatched-review-${item.objectName}-${index}`}
                              primary={`${item.objectName} (${item.expectedLabel})`}
                              secondary={item.source}
                              primaryTypographyProps={{ variant: 'caption', sx: { color: 'warning.dark' } }}
                              secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                            />
                          ))}
                        </List>
                      </Box>
                    )}
                  </Stack>
                )}
              </Box>
            )}
          </Paper>

          {isModalView && (
            <Paper
              variant="outlined"
              sx={{
                borderColor: 'divider',
                minHeight: 0,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              {selectedRow ? (() => {
                const dp = selectedRow.data && typeof selectedRow.data === 'object' ? selectedRow.data : {};
                const selectedDecisionSourceLabel = String(getSectionRowValue(selectedRow, 'applicability_decision_source_label') || '').trim();
                const selectedSearchTargets = Array.isArray(getSectionRowValue(selectedRow, 'applicability_search_targets'))
                  ? getSectionRowValue(selectedRow, 'applicability_search_targets')
                    .map((target) => String(target || '').trim())
                    .filter(Boolean)
                  : [];
                const selectedSearchTargetsReason = String(getSectionRowValue(selectedRow, 'applicability_search_targets_reason') || '').trim();
                const selectedApplicabilityStatusRaw = String(
                  getSectionRowValue(selectedRow, 'applicability_status')
                  || getSectionRowValue(selectedRow, 'applicable_status')
                  || getSectionRowValue(selectedRow, 'apply_status')
                  || ''
                ).trim();
                const selectedApplicabilityReason = String(getSectionRowValue(selectedRow, 'applicability_reason') || '').trim();
                const selectedTopCandidates = Array.isArray(getSectionRowValue(selectedRow, 'applicability_top_candidates'))
                  ? getSectionRowValue(selectedRow, 'applicability_top_candidates')
                    .map((candidate) => ({
                      nodeId: Number(candidate?.node_id || 0),
                      name: String(candidate?.name || '').trim(),
                      score: Number(candidate?.score || 0),
                      targetScore: Number(candidate?.target_score || 0),
                      llmLabel: String(candidate?.llm_label || '').trim(),
                      matchedSearchTargets: Array.isArray(candidate?.matched_search_targets) ? candidate.matched_search_targets : [],
                      matchedCompositeSearchTargets: Array.isArray(candidate?.matched_composite_search_targets) ? candidate.matched_composite_search_targets : [],
                    }))
                    .filter((candidate) => candidate.name)
                  : [];
                const selectedTopCandidate = selectedTopCandidates[0] || null;
                const selectedSecondPassDocuments = Array.isArray(getSectionRowValue(selectedRow, 'applicability_second_pass_documents'))
                  ? getSectionRowValue(selectedRow, 'applicability_second_pass_documents')
                  : [];
                const selectedSecondPassSnippets = Array.isArray(getSectionRowValue(selectedRow, 'applicability_second_pass_snippets'))
                  ? getSectionRowValue(selectedRow, 'applicability_second_pass_snippets')
                  : [];
                const selectedSecondPassMentionsCount = Number(getSectionRowValue(selectedRow, 'applicability_second_pass_mentions_count') || 0);
                const selectedSecondPassPrimaryObject = String(
                  getSectionRowValue(selectedRow, 'applicability_second_pass_primary_object')
                  || getSectionRowValue(selectedRow, 'applicability_first_pass_primary_object')
                  || ''
                ).trim();
                const selectedSecondPassRequiredProperties = Array.isArray(getSectionRowValue(selectedRow, 'applicability_second_pass_required_properties'))
                  ? getSectionRowValue(selectedRow, 'applicability_second_pass_required_properties')
                    .map((item) => String(item || '').trim())
                    .filter(Boolean)
                  : [];
                const selectedPropertySearchTargets = Array.isArray(getSectionRowValue(selectedRow, 'applicability_second_pass_property_search_targets'))
                  ? getSectionRowValue(selectedRow, 'applicability_second_pass_property_search_targets')
                    .map((target) => String(target || '').trim())
                    .filter(Boolean)
                  : [];
                const selectedPropertyDocuments = Array.isArray(getSectionRowValue(selectedRow, 'applicability_second_pass_property_documents'))
                  ? getSectionRowValue(selectedRow, 'applicability_second_pass_property_documents')
                  : [];
                const selectedPropertySnippets = Array.isArray(getSectionRowValue(selectedRow, 'applicability_second_pass_property_snippets'))
                  ? getSectionRowValue(selectedRow, 'applicability_second_pass_property_snippets')
                  : [];
                const selectedPropertyMentionsCount = Number(getSectionRowValue(selectedRow, 'applicability_second_pass_property_mentions_count') || 0);
                const selectedSecondPassLlmDecision = (() => {
                  const value = getSectionRowValue(selectedRow, 'applicability_second_pass_llm_final_decision');
                  return value && typeof value === 'object' ? value : null;
                })();
                const selectedSecondPassLlmContextStats = selectedSecondPassLlmDecision && typeof selectedSecondPassLlmDecision.context_stats === 'object'
                  ? selectedSecondPassLlmDecision.context_stats
                  : null;
                const selectedEvidenceDocuments = (() => {
                  const docsByKey = new Map();

                  const ensureDoc = (documentIdRaw, titleRaw = '') => {
                    const numericId = Number(documentIdRaw || 0);
                    const fallbackKey = titleRaw ? `title:${String(titleRaw).trim().toLowerCase()}` : '';
                    const key = numericId > 0 ? `id:${numericId}` : fallbackKey;
                    if (!key) {
                      return null;
                    }

                    if (!docsByKey.has(key)) {
                      docsByKey.set(key, {
                        key,
                        documentId: numericId > 0 ? numericId : null,
                        title: String(titleRaw || '').trim(),
                        pages: new Set(),
                        pageMentions: new Map(),
                        mentionsCount: 0,
                      });
                    }

                    const entry = docsByKey.get(key);
                    if (!entry.title && titleRaw) {
                      entry.title = String(titleRaw || '').trim();
                    }
                    return entry;
                  };

                  selectedSecondPassDocuments.forEach((doc) => {
                    if (!doc || typeof doc !== 'object') {
                      return;
                    }
                    const entry = ensureDoc(doc.document_id, doc.document_title);
                    if (!entry) {
                      return;
                    }
                    const pages = Array.isArray(doc.pages) ? doc.pages : [];
                    pages.forEach((page) => {
                      const parsedPage = Number(page || 0);
                      if (parsedPage > 0) {
                        entry.pages.add(parsedPage);
                      }
                    });
                    entry.mentionsCount += Number(doc.mentions_count || 0);
                  });

                  selectedSecondPassSnippets.forEach((snippet) => {
                    if (!snippet || typeof snippet !== 'object') {
                      return;
                    }
                    const entry = ensureDoc(snippet.document_id, snippet.document_title);
                    if (!entry) {
                      return;
                    }
                    const parsedPage = Number(snippet.page_number || 0);
                    if (parsedPage > 0) {
                      entry.pages.add(parsedPage);
                    }
                    const excerpt = String(snippet.excerpt || '').trim();
                    if (parsedPage > 0 && excerpt) {
                      const mentionSet = entry.pageMentions.get(parsedPage) || new Set();
                      mentionSet.add(excerpt);
                      entry.pageMentions.set(parsedPage, mentionSet);
                    }
                    entry.mentionsCount += 1;
                  });

                  const llmDocuments = selectedSecondPassLlmDecision && Array.isArray(selectedSecondPassLlmDecision.documents_analyzed)
                    ? selectedSecondPassLlmDecision.documents_analyzed
                    : [];

                  llmDocuments.forEach((doc) => {
                    if (!doc || typeof doc !== 'object') {
                      return;
                    }
                    const entry = ensureDoc(doc.id, doc.title);
                    if (!entry) {
                      return;
                    }
                    const pages = Array.isArray(doc.pages) ? doc.pages : [];
                    pages.forEach((page) => {
                      const parsedPage = Number(page || 0);
                      if (parsedPage > 0) {
                        entry.pages.add(parsedPage);
                      }
                    });
                  });

                  return Array.from(docsByKey.values())
                    .map((entry) => ({
                      ...entry,
                      pages: Array.from(entry.pages).sort((a, b) => a - b),
                      pageMentionsByPage: Array.from(entry.pageMentions.entries()).reduce((acc, [pageNumber, mentionSet]) => {
                        acc[String(pageNumber)] = Array.from(mentionSet);
                        return acc;
                      }, {}),
                    }))
                    .sort((left, right) => {
                      const leftTitle = String(left.title || '').toLowerCase();
                      const rightTitle = String(right.title || '').toLowerCase();
                      if (leftTitle && rightTitle && leftTitle !== rightTitle) {
                        return leftTitle.localeCompare(rightTitle);
                      }
                      return Number(left.documentId || 0) - Number(right.documentId || 0);
                    });
                })();
                const selectedPropertyEvidenceDocuments = (() => {
                  const docsByKey = new Map();

                  const ensureDoc = (documentIdRaw, titleRaw = '') => {
                    const numericId = Number(documentIdRaw || 0);
                    const fallbackKey = titleRaw ? `title:${String(titleRaw).trim().toLowerCase()}` : '';
                    const key = numericId > 0 ? `id:${numericId}` : fallbackKey;
                    if (!key) {
                      return null;
                    }

                    if (!docsByKey.has(key)) {
                      docsByKey.set(key, {
                        key,
                        documentId: numericId > 0 ? numericId : null,
                        title: String(titleRaw || '').trim(),
                        pages: new Set(),
                        pageMentions: new Map(),
                        mentionsCount: 0,
                      });
                    }

                    const entry = docsByKey.get(key);
                    if (!entry.title && titleRaw) {
                      entry.title = String(titleRaw || '').trim();
                    }
                    return entry;
                  };

                  selectedPropertyDocuments.forEach((doc) => {
                    if (!doc || typeof doc !== 'object') {
                      return;
                    }
                    const entry = ensureDoc(doc.document_id, doc.document_title);
                    if (!entry) {
                      return;
                    }
                    const pages = Array.isArray(doc.pages) ? doc.pages : [];
                    pages.forEach((page) => {
                      const parsedPage = Number(page || 0);
                      if (parsedPage > 0) {
                        entry.pages.add(parsedPage);
                      }
                    });
                    entry.mentionsCount += Number(doc.mentions_count || 0);
                  });

                  selectedPropertySnippets.forEach((snippet) => {
                    if (!snippet || typeof snippet !== 'object') {
                      return;
                    }
                    const entry = ensureDoc(snippet.document_id, snippet.document_title);
                    if (!entry) {
                      return;
                    }
                    const parsedPage = Number(snippet.page_number || 0);
                    if (parsedPage > 0) {
                      entry.pages.add(parsedPage);
                    }
                    const excerpt = String(snippet.excerpt || '').trim();
                    if (parsedPage > 0 && excerpt) {
                      const mentionSet = entry.pageMentions.get(parsedPage) || new Set();
                      mentionSet.add(excerpt);
                      entry.pageMentions.set(parsedPage, mentionSet);
                    }
                    entry.mentionsCount += 1;
                  });

                  return Array.from(docsByKey.values())
                    .map((entry) => ({
                      ...entry,
                      pages: Array.from(entry.pages).sort((a, b) => a - b),
                      pageMentionsByPage: Array.from(entry.pageMentions.entries()).reduce((acc, [pageNumber, mentionSet]) => {
                        acc[String(pageNumber)] = Array.from(mentionSet);
                        return acc;
                      }, {}),
                    }))
                    .sort((left, right) => {
                      const leftTitle = String(left.title || '').toLowerCase();
                      const rightTitle = String(right.title || '').toLowerCase();
                      if (leftTitle && rightTitle && leftTitle !== rightTitle) {
                        return leftTitle.localeCompare(rightTitle);
                      }
                      return Number(left.documentId || 0) - Number(right.documentId || 0);
                    });
                })();
                const relevanceSeedTerms = (() => {
                  const genericNoise = new Set([
                    'tennet',
                    'project',
                    'document',
                    'requirements',
                    'requirement',
                    'substation',
                    'station',
                    'drawing',
                    'page',
                  ]);

                  const rawTerms = [
                    selectedSecondPassPrimaryObject,
                    ...selectedSecondPassRequiredProperties,
                    ...selectedPropertySearchTargets,
                    ...selectedSearchTargets,
                  ];

                  const normalizedTerms = [];
                  const seen = new Set();
                  rawTerms.forEach((term) => {
                    const parts = normalizeEntityNameForComparison(term).split(' ').filter(Boolean);
                    parts.forEach((part) => {
                      if (part.length < 3 || genericNoise.has(part) || seen.has(part)) {
                        return;
                      }
                      seen.add(part);
                      normalizedTerms.push(part);
                    });
                  });

                  return normalizedTerms;
                })();

                const getRankedMentionTextsForPage = (doc, pageNumber) => {
                  const rawMentions = Array.isArray(doc?.pageMentionsByPage?.[String(pageNumber)])
                    ? doc.pageMentionsByPage[String(pageNumber)]
                    : [];

                  const scoredMentions = rawMentions
                    .map((mention) => {
                      const text = String(mention || '').trim();
                      const normalizedMention = normalizeEntityNameForComparison(text);
                      if (!text || !normalizedMention) {
                        return null;
                      }

                      let score = 0;
                      relevanceSeedTerms.forEach((term) => {
                        if (normalizedMention.includes(term)) {
                          score += Math.min(6, term.length);
                        }
                      });

                      // Penalize very generic organization markers that often create noisy highlight.
                      if (normalizedMention === 'tennet' || normalizedMention.startsWith('tennet ')) {
                        score -= 4;
                      }

                      score += Math.min(3, Math.floor(text.length / 40));

                      return {
                        text,
                        score,
                      };
                    })
                    .filter(Boolean)
                    .sort((left, right) => {
                      if (right.score !== left.score) {
                        return right.score - left.score;
                      }
                      return right.text.length - left.text.length;
                    });

                  if (scoredMentions.length === 0) {
                    return [];
                  }

                  const relevant = scoredMentions.filter((item) => item.score > 0).map((item) => item.text);
                  if (relevant.length > 0) {
                    return relevant.slice(0, 8);
                  }

                  return scoredMentions.map((item) => item.text).slice(0, 8);
                };
                const selectedApplicabilityLabel = (() => {
                  const applicabilityLabel = getRowApplicabilityLabel(selectedRow);
                  return applicabilityLabel === 'Not analyzed' ? 'Unreviewed' : applicabilityLabel;
                })();
                const dpFields = Array.isArray(dp.fields) ? dp.fields : [];
                const dpRefersTo = Array.isArray(dp.refersToDocuments) ? dp.refersToDocuments : [];
                const dpHasSource = Array.isArray(dp.hasAsSourceDocuments) ? dp.hasAsSourceDocuments : [];
                const dpRels = dp.relationships && typeof dp.relationships === 'object' ? dp.relationships : {};
                const dpIncoming = Array.isArray(dp.incoming_relationships) ? dp.incoming_relationships : [];
                const dpExtraProps = dp.extra_properties && typeof dp.extra_properties === 'object' ? dp.extra_properties : {};
                const dpExtraPropertyLabels = dp._extra_property_labels && typeof dp._extra_property_labels === 'object'
                  ? dp._extra_property_labels
                  : {};
                const dpExtraPropertyValueDetails = dp._extra_property_value_details && typeof dp._extra_property_value_details === 'object'
                  ? dp._extra_property_value_details
                  : {};
                const dpTargetLabels = dp._target_labels && typeof dp._target_labels === 'object'
                  ? dp._target_labels
                  : {};
                const dpTypes = Array.isArray(dp.types) ? dp.types : [];
                const dpClassProperties = Array.isArray(dp.class_properties) ? dp.class_properties : [];
                const dpClassAttributes = Array.isArray(dp.class_attributes) ? dp.class_attributes : [];
                const dpPrimaryLabel = getPrimaryEnglishText(dp.label_english, dp.label || selectedRow.name);
                const dpSecondaryLabel = getSecondaryOriginalText(dp.label_english, dp.label);
                const dpPrimaryRequirementText = getPrimaryEnglishText(dp.requirement_text_english, dp.requirement_text);
                const dpSecondaryRequirementText = getSecondaryOriginalText(dp.requirement_text_english, dp.requirement_text);
                const dpFieldIds = new Set(
                  dpFields
                    .map((field) => String(field?.id || '').trim())
                    .filter(Boolean)
                );
                const displayExtraProps = Object.entries(dpExtraProps).filter(([propKey]) => {
                  if (section.key !== 'classes') return true;
                  return !['sh:property', 'rdfs:subClassOf'].includes(String(propKey || '').trim());
                });

                const extraPropertyKeys = new Set(
                  displayExtraProps
                    .map(([propKey]) => String(propKey || '').trim())
                    .filter(Boolean)
                );

                const visibleExtraProps = displayExtraProps.filter(
                  ([propKey]) => {
                    const normalizedPropKey = String(propKey || '').trim();
                    if (!normalizedPropKey || dpFieldIds.has(normalizedPropKey)) {
                      return false;
                    }

                    const isEnglishKey = normalizedPropKey.endsWith('_english') || normalizedPropKey.endsWith('_en');

                    // If an English sibling exists, keep only the English entry as primary.
                    if (!isEnglishKey && (extraPropertyKeys.has(`${normalizedPropKey}_english`) || extraPropertyKeys.has(`${normalizedPropKey}_en`))) {
                      return false;
                    }

                    return true;
                  }
                );

                const classShRulesByPath = {};
                dpClassProperties.forEach((rule, ruleIndex) => {
                  const pathKey = String(rule?.pathId || '').trim() || `__rule_${ruleIndex}`;
                  if (!classShRulesByPath[pathKey]) classShRulesByPath[pathKey] = [];
                  classShRulesByPath[pathKey].push(rule);
                });

                const classAttributesForDisplay = [];
                const usedShPathKeys = new Set();

                dpClassAttributes.forEach((attribute, attributeIndex) => {
                  const pathKey = String(attribute?.pathId || '').trim() || `__attr_${attributeIndex}`;
                  const shRules = classShRulesByPath[pathKey] || [];
                  if (classShRulesByPath[pathKey]) usedShPathKeys.add(pathKey);
                  classAttributesForDisplay.push({
                    ...attribute,
                    shRules,
                  });
                });

                Object.entries(classShRulesByPath).forEach(([pathKey, shRules]) => {
                  if (usedShPathKeys.has(pathKey)) return;
                  const fallbackRule = shRules[0] || {};
                  classAttributesForDisplay.push({
                    restrictionType: '',
                    pathId: fallbackRule.pathId || '',
                    pathLabel: fallbackRule.pathLabel || fallbackRule.pathId || '-',
                    pathLabel_english: fallbackRule.pathLabel_english || '',
                    minCardinality: null,
                    hasValue: '',
                    hasValue_english: fallbackRule.hasValue_english || '',
                    hasValueId: '',
                    shRules,
                    ...(fallbackRule.propertyDetails ? { propertyDetails: fallbackRule.propertyDetails } : {}),
                  });
                });

                classAttributesForDisplay.sort((left, right) => {
                  const leftName = String(left?.pathLabel_english || left?.pathLabel || left?.pathId || '').toLowerCase();
                  const rightName = String(right?.pathLabel_english || right?.pathLabel || right?.pathId || '').toLowerCase();
                  return leftName.localeCompare(rightName);
                });

                const resolveTypeToken = (value) => {
                  const text = String(value || '').trim();
                  if (!text) return '';
                  return text.replace(/^<|>$/g, '').split('#').pop().split('/').pop().split(':').pop().toLowerCase();
                };

                const typeRefIndex = {};
                Object.entries(vs1EntityDirectory || {}).forEach(([uri, entry]) => {
                  const token = resolveTypeToken(uri);
                  if (!token) return;
                  const current = typeRefIndex[token];
                  const isClassEntry = entry?.entity_type === 'Class_Project';
                  const isCurrentClassEntry = current?.entry?.entity_type === 'Class_Project';
                  if (!current || (isClassEntry && !isCurrentClassEntry)) {
                    typeRefIndex[token] = { uri, entry };
                  }
                });

                const outgoingEntries = Object.entries(dpRels);
                const totalRelCount = outgoingEntries.reduce((sum, [, t]) => sum + (Array.isArray(t) ? t.length : 1), 0) + dpIncoming.length;
                const showObjectRequirementsTab = section.key === 'objects';
                const showRequirementObjectsTab = section.key === 'requirements';

                const {
                  linkedClassRows: objectLinkedClassRows,
                  linkedRequirementRows: objectLinkedRequirementRows,
                  unresolvedRequirementUris: objectUnresolvedRequirementUris,
                } = (() => {
                  if (!showObjectRequirementsTab) {
                    return {
                      linkedClassRows: [],
                      linkedRequirementRows: [],
                      unresolvedRequirementUris: [],
                    };
                  }

                  const classRows = Array.isArray(vs1Sections?.classes) ? vs1Sections.classes : [];
                  const requirementRows = Array.isArray(vs1Sections?.requirements) ? vs1Sections.requirements : [];

                  const classRowsByUri = new Map();
                  const classUrisByToken = new Map();
                  classRows.forEach((classRow) => {
                    const classPayload = classRow?.data && typeof classRow.data === 'object' ? classRow.data : {};
                    const classUri = String(classPayload.uri || classRow?.uri || '').trim();
                    if (!classUri) return;
                    classRowsByUri.set(classUri, classRow);

                    const token = resolveTypeToken(classUri);
                    if (!token) return;
                    if (!classUrisByToken.has(token)) classUrisByToken.set(token, []);
                    classUrisByToken.get(token).push(classUri);
                  });

                  const matchedClassUris = new Set();
                  dpTypes
                    .map((rawType) => String(rawType || '').trim())
                    .filter(Boolean)
                    .forEach((typeUri) => {
                      if (classRowsByUri.has(typeUri)) {
                        matchedClassUris.add(typeUri);
                        return;
                      }

                      const directDirectoryEntry = vs1EntityDirectory[typeUri];
                      const resolvedUri = directDirectoryEntry
                        ? typeUri
                        : '';
                      if (resolvedUri && classRowsByUri.has(resolvedUri)) {
                        matchedClassUris.add(resolvedUri);
                        return;
                      }

                      const token = resolveTypeToken(typeUri);
                      if (token && classUrisByToken.has(token)) {
                        classUrisByToken.get(token).forEach((classUri) => matchedClassUris.add(classUri));
                      }
                    });

                  const linkedClassRows = Array.from(matchedClassUris)
                    .map((classUri) => classRowsByUri.get(classUri))
                    .filter(Boolean);

                  const requirementUris = new Set();
                  linkedClassRows.forEach((classRow) => {
                    const classPayload = classRow?.data && typeof classRow.data === 'object' ? classRow.data : {};
                    const classRelationships = classPayload.relationships && typeof classPayload.relationships === 'object'
                      ? classPayload.relationships
                      : {};

                    Object.entries(classRelationships).forEach(([predicate, targets]) => {
                      const normalizedPredicate = String(predicate || '').trim().toLowerCase();
                      if (!normalizedPredicate || !normalizedPredicate.includes('hasrequirement')) {
                        return;
                      }
                      const targetUris = Array.isArray(targets) ? targets : [targets];
                      targetUris
                        .map((value) => String(value || '').trim())
                        .filter(Boolean)
                        .forEach((value) => requirementUris.add(value));
                    });
                  });

                  const requirementRowsByUri = new Map();
                  const requirementRowsByToken = new Map();
                  requirementRows.forEach((requirementRow) => {
                    const requirementPayload = requirementRow?.data && typeof requirementRow.data === 'object' ? requirementRow.data : {};
                    const requirementUri = String(requirementPayload.uri || requirementRow?.uri || '').trim();
                    if (!requirementUri) return;

                    requirementRowsByUri.set(requirementUri, requirementRow);

                    const token = resolveTypeToken(requirementUri);
                    if (!token) return;
                    if (!requirementRowsByToken.has(token)) requirementRowsByToken.set(token, requirementRow);
                  });

                  const linkedRequirementRows = [];
                  const unresolvedRequirementUris = [];
                  const seenRequirementRows = new Set();

                  Array.from(requirementUris).forEach((requirementUri) => {
                    let requirementRow = requirementRowsByUri.get(requirementUri) || null;

                    if (!requirementRow) {
                      const token = resolveTypeToken(requirementUri);
                      if (token && requirementRowsByToken.has(token)) {
                        requirementRow = requirementRowsByToken.get(token);
                      }
                    }

                    if (requirementRow) {
                      const rowKey = String(requirementRow.id || requirementRow.uri || requirementRow.name || requirementUri);
                      if (!seenRequirementRows.has(rowKey)) {
                        seenRequirementRows.add(rowKey);
                        linkedRequirementRows.push(requirementRow);
                      }
                      return;
                    }

                    unresolvedRequirementUris.push(requirementUri);
                  });

                  return {
                    linkedClassRows,
                    linkedRequirementRows,
                    unresolvedRequirementUris,
                  };
                })();

                const {
                  linkedClassRows: requirementLinkedClassRows,
                  linkedObjectRows: requirementLinkedObjectRows,
                } = (() => {
                  if (!showRequirementObjectsTab) {
                    return {
                      linkedClassRows: [],
                      linkedObjectRows: [],
                    };
                  }

                  const classRows = Array.isArray(vs1Sections?.classes) ? vs1Sections.classes : [];
                  const objectRows = Array.isArray(vs1Sections?.objects) ? vs1Sections.objects : [];

                  const requirementUris = new Set();
                  const requirementUriCandidates = [
                    String(dp.uri || '').trim(),
                    String(selectedRow?.uri || '').trim(),
                  ].filter(Boolean);
                  requirementUriCandidates.forEach((uri) => requirementUris.add(uri));

                  const requirementTokens = new Set(
                    Array.from(requirementUris)
                      .map((uri) => resolveTypeToken(uri))
                      .filter(Boolean)
                  );

                  const linkedClassRows = classRows.filter((classRow) => {
                    const classPayload = classRow?.data && typeof classRow.data === 'object' ? classRow.data : {};
                    const classRelationships = classPayload.relationships && typeof classPayload.relationships === 'object'
                      ? classPayload.relationships
                      : {};

                    return Object.entries(classRelationships).some(([predicate, targets]) => {
                      const normalizedPredicate = String(predicate || '').trim().toLowerCase();
                      if (!normalizedPredicate || !normalizedPredicate.includes('hasrequirement')) {
                        return false;
                      }

                      const targetUris = (Array.isArray(targets) ? targets : [targets])
                        .map((value) => String(value || '').trim())
                        .filter(Boolean);

                      return targetUris.some((targetUri) => {
                        if (requirementUris.has(targetUri)) {
                          return true;
                        }
                        const token = resolveTypeToken(targetUri);
                        return Boolean(token && requirementTokens.has(token));
                      });
                    });
                  });

                  const linkedClassUris = new Set(
                    linkedClassRows
                      .map((classRow) => {
                        const classPayload = classRow?.data && typeof classRow.data === 'object' ? classRow.data : {};
                        return String(classPayload.uri || classRow?.uri || '').trim();
                      })
                      .filter(Boolean)
                  );

                  const linkedClassTokens = new Set(
                    Array.from(linkedClassUris)
                      .map((uri) => resolveTypeToken(uri))
                      .filter(Boolean)
                  );

                  const linkedObjectRows = objectRows.filter((objectRow) => {
                    const objectPayload = objectRow?.data && typeof objectRow.data === 'object' ? objectRow.data : {};
                    const objectTypes = Array.isArray(objectPayload.types) ? objectPayload.types : [];
                    return objectTypes.some((rawType) => {
                      const typeUri = String(rawType || '').trim();
                      if (!typeUri) return false;
                      if (linkedClassUris.has(typeUri)) return true;
                      const token = resolveTypeToken(typeUri);
                      return Boolean(token && linkedClassTokens.has(token));
                    });
                  });

                  return {
                    linkedClassRows,
                    linkedObjectRows,
                  };
                })();

                const objectRequirementsCount = objectLinkedRequirementRows.length + objectUnresolvedRequirementUris.length;
                const requirementObjectsCount = requirementLinkedObjectRows.length;
                const hasLinkedEntitiesTab = showObjectRequirementsTab || showRequirementObjectsTab;
                const normalizedVs1DetailTab = hasLinkedEntitiesTab ? vs1DetailTab : Math.min(vs1DetailTab, 3);
                const graphTabIndex = hasLinkedEntitiesTab ? 4 : 3;
                const isGraphTabActive = normalizedVs1DetailTab === graphTabIndex;

                const getResolutionOrigin = (entry, isDirectMatch) => {
                  const crossSource = String(entry?.cross_source || '').trim().toLowerCase();
                  const entityType = String(entry?.entity_type || '').trim();
                  if (crossSource === 'ontology') return 'ontology';
                  if (entityType === 'Class_Project' || entityType === 'Class_Ontology') return 'class';
                  if (isDirectMatch) return 'own-data';
                  return 'class';
                };

                const resolveUri = (uri) => {
                  const direct = vs1EntityDirectory[uri];
                  if (direct) {
                    return {
                      origin: getResolutionOrigin(direct, true),
                      resolutionMethod: 'direct-uri',
                      resolvedUri: uri,
                      notation: direct.notation,
                      name: direct.name_en || direct.name_english || direct.name,
                      nameOriginal: direct.name,
                      type: humanizeEntityType(direct.entity_type),
                    };
                  }

                  const token = resolveTypeToken(uri);
                  if (token && typeRefIndex[token]) {
                    const fallbackUri = typeRefIndex[token].uri;
                    const fallback = typeRefIndex[token].entry;
                    return {
                      origin: getResolutionOrigin(fallback, false),
                      resolutionMethod: 'token-fallback',
                      resolvedUri: fallbackUri,
                      notation: fallback.notation,
                      name: fallback.name_en || fallback.name_english || fallback.name,
                      nameOriginal: fallback.name,
                      type: humanizeEntityType(fallback.entity_type),
                    };
                  }

                  return null;
                };

                const resolvePropertyLabel = (propKey) => {
                  const key = String(propKey || '').trim();
                  if (!key) return '-';
                  return dpExtraPropertyLabels[key] || KNOWN_PROPERTY_LABELS[key] || humanizeKey(key) || key;
                };

                const splitPropertyKeySuffix = (propKey) => {
                  const normalized = String(propKey || '').trim();
                  if (normalized.endsWith('_english')) {
                    return { normalized, base: normalized.slice(0, -8), isEnglish: true };
                  }
                  if (normalized.endsWith('_en')) {
                    return { normalized, base: normalized.slice(0, -3), isEnglish: true };
                  }
                  return { normalized, base: normalized, isEnglish: false };
                };

                const resolvePropertyValue = (rawValue) => {
                  const text = String(rawValue ?? '').trim();
                  if (!text) {
                    return {
                      primary: '-',
                      technical: '',
                      typeLabel: '',
                      typeId: '',
                      definition: '',
                      isUrl: false,
                    };
                  }

                  const targetInfo = dpTargetLabels[text];
                  if (targetInfo && typeof targetInfo === 'object') {
                    const primary = String(targetInfo.name_english || targetInfo.name_en || targetInfo.name || targetInfo.notation || '').trim();
                    const secondary = getSecondaryOriginalText(
                      targetInfo.name_english || targetInfo.name_en,
                      targetInfo.name
                    );
                    return {
                      primary: primary || text,
                      technical: primary && primary !== text ? text : '',
                      secondary,
                      typeLabel: targetInfo.entity_type ? humanizeEntityType(targetInfo.entity_type) : '',
                      typeLabelSecondary: '',
                      typeId: '',
                      definition: '',
                      isUrl: false,
                    };
                  }

                  const resolved = resolveUri(text);
                  if (resolved) {
                    const primary = String(resolved.name || resolved.notation || '').trim();
                    return {
                      primary: primary || text,
                      technical: primary && primary !== text ? text : '',
                      secondary: '',
                      typeLabel: String(resolved.type || '').trim(),
                      typeLabelSecondary: '',
                      typeId: '',
                      definition: '',
                      isUrl: false,
                    };
                  }

                  if (isLikelyUrl(text)) {
                    return {
                      primary: text,
                      technical: '',
                      secondary: '',
                      typeLabel: '',
                      typeLabelSecondary: '',
                      typeId: '',
                      definition: '',
                      isUrl: true,
                    };
                  }

                  return {
                    primary: text,
                    technical: '',
                    secondary: '',
                    typeLabel: '',
                    typeLabelSecondary: '',
                    typeId: '',
                    definition: '',
                    isUrl: false,
                  };
                };

                const renderRelTarget = (targetUri) => {
                  const resolved = resolveUri(targetUri);
                  const dirEntry = vs1EntityDirectory[targetUri];
                  const crossSource = dirEntry && dirEntry.cross_source;
                  if (resolved) {
                    return (
                      <Box sx={{ p: 1.25, bgcolor: crossSource ? orange[50] : blue[50], borderRadius: 1, borderLeft: '3px solid', borderColor: crossSource ? orange[400] : 'primary.main' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Chip label={resolved.type} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: 'primary.main', color: '#fff' }} />
                          {crossSource && (
                            <Chip label={crossSource === 'ontology' ? 'Ontology' : 'Project'} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: orange[700], color: '#fff' }} />
                          )}
                          <Typography variant="body2" sx={{ fontWeight: 600, color: grey[900] }}>
                            {resolved.notation || resolved.name}
                          </Typography>
                        </Box>
                        {resolved.notation && resolved.name && resolved.name !== resolved.notation && (
                          <Typography variant="caption" sx={{ color: grey[600], display: 'block', mt: 0.25 }}>
                            {resolved.name}
                          </Typography>
                        )}
                      </Box>
                    );
                  }
                  return (
                    <Box sx={{ p: 1.25, bgcolor: grey[100], borderRadius: 1, borderLeft: '3px solid', borderColor: grey[400] }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.72rem', color: grey[600], wordBreak: 'break-all' }}>
                        {targetUri}
                      </Typography>
                    </Box>
                  );
                };

                const selectedApplicabilityStatus = String(getSectionRowValue(selectedRow, 'applicability_status') || '').trim().toLowerCase();
                const isSelectedApplies = selectedApplicabilityStatus === 'aplica';
                const isSelectedNoApplies = selectedApplicabilityStatus === 'no_aplica';
                const isApplicabilityIndeterminate = !isSelectedApplies && !isSelectedNoApplies;

                return (
                  <>
                    {/* Header */}
                    <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                        {dp.notation || selectedRow.name || '-'}
                      </Typography>
                      {dpPrimaryLabel && dpPrimaryLabel !== (dp.notation || '') && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                          {dpPrimaryLabel}
                        </Typography>
                      )}
                      {dpSecondaryLabel && (
                        <Typography variant="caption" color="text.disabled" sx={{ mt: 0.2, display: 'block' }}>
                          NL: {dpSecondaryLabel}
                        </Typography>
                      )}

                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                        {(section.key === 'objects' || section.key === 'requirements') && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Assessment fontSize="small" />}
                            disabled={
                              loadingVs1Sections
                              || updatingVs1ApplicabilityRowId === selectedRow.id
                              || runningSingleApplicabilityAnalysisKey === `${section.key}:${selectedRow.id}`
                            }
                            onClick={() => handleRunSingleApplicabilityAnalysis(section.key, selectedRow)}
                          >
                            {runningSingleApplicabilityAnalysisKey === `${section.key}:${selectedRow.id}`
                              ? 'Analyzing...'
                              : 'Analyze this row'}
                          </Button>
                        )}
                        <Button
                          size="small"
                          variant={isSelectedApplies ? 'contained' : 'outlined'}
                          color={isSelectedApplies ? 'success' : (isApplicabilityIndeterminate ? 'secondary' : 'success')}
                          startIcon={<CheckCircleOutline fontSize="small" />}
                          disabled={updatingVs1ApplicabilityRowId === selectedRow.id}
                          onClick={() => handleUpdateVs1RowApplicability(selectedRow, 'aplica')}
                        >
                          Applies
                        </Button>
                        <Button
                          size="small"
                          variant={isSelectedNoApplies ? 'contained' : 'outlined'}
                          color={isSelectedNoApplies ? 'warning' : (isApplicabilityIndeterminate ? 'secondary' : 'warning')}
                          startIcon={<Close fontSize="small" />}
                          disabled={updatingVs1ApplicabilityRowId === selectedRow.id}
                          onClick={() => handleUpdateVs1RowApplicability(selectedRow, 'no_aplica')}
                        >
                          Does not apply
                        </Button>
                        <Chip size="small" color={isRowValidated(selectedRow) ? 'success' : 'default'} label={`Validated: ${isRowValidated(selectedRow) ? 'Yes' : 'No'}`} />
                        {selectedDecisionSourceLabel && (
                          <Chip size="small" variant="outlined" label={`Applicability: ${selectedDecisionSourceLabel}`} />
                        )}
                        {section.key === 'requirements' && (
                          <Chip size="small" color={isRowSmartAnalyzed(selectedRow) ? 'success' : 'default'} label={`SMART: ${isRowSmartAnalyzed(selectedRow) ? 'Yes' : 'No'}`} />
                        )}
                      </Box>
                      {String(getSectionRowValue(selectedRow, 'applicability_reason') || '').trim() && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
                          Reason: {String(getSectionRowValue(selectedRow, 'applicability_reason') || '').trim()}
                        </Typography>
                      )}
                      {selectedSearchTargets.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 700, mb: 0.35 }}>
                            Nodes searched by the system
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {selectedSearchTargets.map((target, index) => (
                              <Chip
                                key={`selected-search-target-${index}-${target}`}
                                size="small"
                                variant="outlined"
                                label={target}
                              />
                            ))}
                          </Box>
                          {selectedSearchTargetsReason && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                              Criteria: {selectedSearchTargetsReason}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>

                    {/* Tabs */}
                    <Tabs
                      value={normalizedVs1DetailTab}
                      onChange={(_, newTab) => setVs1DetailTab(newTab)}
                      sx={{
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        px: 2,
                        minHeight: 40,
                        '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 500, fontSize: '0.8rem', py: 0.5 },
                      }}
                    >
                      <Tab icon={<Info fontSize="small" />} iconPosition="start" label="Overview" />
                      <Tab icon={<CheckCircleOutline fontSize="small" />} iconPosition="start" label="Applies / Does not apply" />
                      <Tab
                        icon={<AccountTree fontSize="small" />}
                        iconPosition="start"
                        label={
                          totalRelCount > 0
                            ? <Badge badgeContent={totalRelCount} color="primary" sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 14, minWidth: 14 } }}>Relationships</Badge>
                            : 'Relationships'
                        }
                      />
                      {hasLinkedEntitiesTab && (
                        <Tab
                          icon={<Assessment fontSize="small" />}
                          iconPosition="start"
                          label={
                            (() => {
                              const linkedEntitiesLabel = showObjectRequirementsTab ? 'Requirements' : 'Objects';
                              const linkedEntitiesCount = showObjectRequirementsTab ? objectRequirementsCount : requirementObjectsCount;
                              return linkedEntitiesCount > 0
                                ? <Badge badgeContent={linkedEntitiesCount} color="primary" sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 14, minWidth: 14 } }}>{linkedEntitiesLabel}</Badge>
                                : linkedEntitiesLabel;
                            })()
                          }
                        />
                      )}
                      <Tab icon={<Hub fontSize="small" />} iconPosition="start" label="Graph" />
                    </Tabs>

                    {/* Tab content */}
                    <Box sx={{
                      flex: 1, overflowY: isGraphTabActive ? 'hidden' : 'auto',
                      p: isGraphTabActive ? 1 : 2,
                      display: 'flex', flexDirection: 'column',
                      '&::-webkit-scrollbar': { width: 6 },
                      '&::-webkit-scrollbar-thumb': { bgcolor: grey[400], borderRadius: 3 },
                    }}>
                      {/* OVERVIEW TAB */}
                      {normalizedVs1DetailTab === 0 && (
                        <Stack spacing={2.5}>
                          {/* Notation */}
                          {dp.notation && (
                            <Box>
                              <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 600, color: grey[700] }}>Notation</Typography>
                              <Box sx={{ p: 1.25, bgcolor: grey[100], borderRadius: 1 }}>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', color: grey[900] }}>{dp.notation}</Typography>
                              </Box>
                            </Box>
                          )}

                          {/* Label */}
                          {dpPrimaryLabel && (
                            <Box>
                              <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 600, color: grey[700] }}>Label</Typography>
                              <Box sx={{ p: 1.25, bgcolor: grey[100], borderRadius: 1 }}>
                                <Typography variant="body2" sx={{ color: grey[900] }}>{dpPrimaryLabel}</Typography>
                              </Box>
                              {dpSecondaryLabel && (
                                <Box sx={{ p: 1.25, mt: 0.5, bgcolor: 'success.50', borderRadius: 1, border: '1px solid', borderColor: 'success.200' }}>
                                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'success.dark', display: 'block', mb: 0.25 }}>NL</Typography>
                                  <Typography variant="body2" sx={{ color: grey[900] }}>{dpSecondaryLabel}</Typography>
                                </Box>
                              )}
                            </Box>
                          )}

                          {/* Requirement Text */}
                          {dpPrimaryRequirementText && (
                            <Box>
                              <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 600, color: grey[700] }}>Requirement Text</Typography>
                              <Box sx={{ p: 1.25, bgcolor: grey[100], borderRadius: 1 }}>
                                <Typography variant="body2" sx={{ color: grey[900], whiteSpace: 'pre-wrap' }}>{dpPrimaryRequirementText}</Typography>
                              </Box>
                              {dpSecondaryRequirementText && (
                                <Box sx={{ p: 1.25, mt: 0.5, bgcolor: 'success.50', borderRadius: 1, border: '1px solid', borderColor: 'success.200' }}>
                                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'success.dark', display: 'block', mb: 0.25 }}>NL</Typography>
                                  <Typography variant="body2" sx={{ color: grey[900], whiteSpace: 'pre-wrap' }}>{dpSecondaryRequirementText}</Typography>
                                </Box>
                              )}
                            </Box>
                          )}

                          {/* Comment */}
                          {dp.comment && (
                            <Box>
                              <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 600, color: grey[700] }}>Comment</Typography>
                              <Box sx={{ p: 1.25, bgcolor: grey[100], borderRadius: 1 }}>
                                <Typography variant="body2" sx={{ color: grey[900], whiteSpace: 'pre-wrap' }}>{dp.comment}</Typography>
                              </Box>
                            </Box>
                          )}

                          {/* Types */}
                          {(() => {
                            const visibleTypes = dpTypes
                              .map((t, ti) => {
                                const resolvedType = resolveUri(t);
                                const typeToken = String(t).split('#').pop().split('/').pop();
                                const typeEN = String(resolvedType?.name || resolvedType?.notation || typeToken).trim();
                                const typeNL = String(resolvedType?.nameOriginal || typeEN).trim();
                                const typeOrigin = String(resolvedType?.origin || 'own-data').trim();
                                return {
                                  key: ti,
                                  en: typeEN,
                                  nl: typeNL,
                                  origin: typeOrigin,
                                };
                              })
                              .filter((item) => item.origin !== 'ontology');

                            if (visibleTypes.length === 0) {
                              return null;
                            }

                            return (
                              <Box>
                                <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 600, color: grey[700] }}>Types</Typography>
                                <Stack spacing={0.6}>
                                  {visibleTypes.map((item) => (
                                    <Box key={item.key} sx={{ p: 0.9, bgcolor: blue[50], borderRadius: 1, borderLeft: '3px solid', borderColor: blue[300] }}>
                                      <Typography variant="caption" sx={{ display: 'block', color: grey[700], fontWeight: 600 }}>
                                        EN: {item.en}
                                      </Typography>
                                      <Typography variant="caption" sx={{ display: 'block', color: grey[500], mt: 0.15 }}>
                                        NL: {item.nl}
                                      </Typography>
                                      <Typography variant="caption" sx={{ display: 'block', color: grey[400], mt: 0.05, fontSize: '0.62rem' }}>
                                        Origin: {item.origin}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Stack>
                              </Box>
                            );
                          })()}

                          {/* Fields (Requirement-specific) */}
                          {dpFields.length > 0 && (
                            <Box>
                              <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 600, color: grey[700] }}>Fields</Typography>
                              <Stack spacing={0.75}>
                                {dpFields.map((field, fi) => {
                                  const fieldId = String(field?.id || '').trim();
                                  const fieldLabelPrimary = getPrimaryEnglishText(field?.fieldName_english, field?.fieldName || fieldId);
                                  const fieldLabelSecondary = getSecondaryOriginalText(field?.fieldName_english, field?.fieldName);
                                  const explicitEnglishValues = field?.values_english ?? dpExtraProps[`${fieldId}_english`];
                                  const primaryValues = toUniqueNonEmptyValues(
                                    explicitEnglishValues !== undefined ? explicitEnglishValues : field?.values
                                  );
                                  const secondaryValues = explicitEnglishValues !== undefined
                                    ? toUniqueNonEmptyValues(field?.values)
                                    : [];

                                  return (
                                    <Box
                                      key={field.id || fi}
                                      sx={{ p: 1.25, bgcolor: grey[50], borderRadius: 1, border: '1px solid', borderColor: grey[200], display: 'flex', gap: 1, alignItems: 'baseline', flexWrap: 'wrap' }}
                                    >
                                      <Typography variant="caption" sx={{ fontWeight: 600, color: grey[700], minWidth: 120 }}>
                                        {fieldLabelPrimary || field.id}
                                        {field.typeLabel && field.typeLabel !== 'Control' && (
                                          <Typography component="span" variant="caption" sx={{ color: grey[400], ml: 0.5 }}>({field.typeLabel})</Typography>
                                        )}
                                        {fieldLabelSecondary && (
                                          <Typography component="span" variant="caption" sx={{ color: grey[500], ml: 0.75 }}>
                                            NL: {fieldLabelSecondary}
                                          </Typography>
                                        )}
                                      </Typography>

                                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                        {primaryValues.map((valueText, vi) => {
                                          const valueIsUrl = isLikelyUrl(valueText);

                                          if (valueIsUrl) {
                                            return (
                                              <Typography
                                                key={vi}
                                                variant="caption"
                                                color="primary"
                                                component="a"
                                                href={valueText}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{ display: 'block', width: '100%', overflowWrap: 'anywhere' }}
                                              >
                                                {valueText}
                                              </Typography>
                                            );
                                          }

                                          return (
                                            <Chip
                                              key={vi}
                                              size="small"
                                              label={valueText}
                                              variant="outlined"
                                              sx={{ height: 20, fontSize: '0.72rem', maxWidth: '100%' }}
                                            />
                                          );
                                        })}

                                        {secondaryValues.length > 0 && (
                                          <Typography variant="caption" sx={{ color: grey[500], width: '100%', overflowWrap: 'anywhere' }}>
                                            NL: {secondaryValues.join(' | ')}
                                          </Typography>
                                        )}
                                      </Box>
                                    </Box>
                                  );
                                })}
                              </Stack>
                            </Box>
                          )}

                          {/* Documents (refersTo / hasAsSource) */}
                          {(dpRefersTo.length > 0 || dpHasSource.length > 0) && (
                            <Box>
                              <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 600, color: grey[700] }}>Documents</Typography>
                              <Stack spacing={0.75}>
                                {dpHasSource.map((doc, di) => {
                                  const documentVersionUri = String(doc.documentVersionUri || doc.document_version_uri || '').trim();
                                  const documentVersionSeeAlso = String(doc.documentVersionSeeAlso || doc.document_version_see_also || doc.seeAlso || '').trim();

                                  return (
                                    <Box key={`src-${di}`} sx={{ p: 1.25, bgcolor: blue[50], borderRadius: 1, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                                      <Typography variant="caption" sx={{ fontWeight: 600, color: grey[500], display: 'block' }}>Source Document</Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 600, color: grey[900] }}>{doc.name || doc.id}</Typography>
                                      {doc.definition && <Typography variant="caption" color="text.secondary">{doc.definition}</Typography>}
                                      {documentVersionUri && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
                                          {`tennet:DocumentVersion <${documentVersionUri}>`}
                                        </Typography>
                                      )}
                                      {doc.file_url && (
                                        <Typography variant="caption" color="primary" component="a" href={doc.file_url} target="_blank" rel="noopener noreferrer" sx={{ display: 'block' }}>
                                          Open uploaded file
                                        </Typography>
                                      )}
                                      {documentVersionSeeAlso && (
                                        <Typography variant="caption" color="primary" component="a" href={documentVersionSeeAlso} target="_blank" rel="noopener noreferrer" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
                                          {`rdfs:seeAlso <${documentVersionSeeAlso}>`}
                                        </Typography>
                                      )}
                                    </Box>
                                  );
                                })}
                                {dpRefersTo.map((doc, di) => {
                                  const documentVersionUri = String(doc.documentVersionUri || doc.document_version_uri || '').trim();
                                  const documentVersionSeeAlso = String(doc.documentVersionSeeAlso || doc.document_version_see_also || doc.seeAlso || '').trim();

                                  return (
                                    <Box key={`ref-${di}`} sx={{ p: 1.25, bgcolor: grey[50], borderRadius: 1, borderLeft: '3px solid', borderColor: grey[400] }}>
                                      <Typography variant="caption" sx={{ fontWeight: 600, color: grey[500], display: 'block' }}>Refers To</Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 600, color: grey[900] }}>{doc.name || doc.id}</Typography>
                                      {doc.definition && <Typography variant="caption" color="text.secondary">{doc.definition}</Typography>}
                                      {documentVersionUri && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
                                          {`tennet:DocumentVersion <${documentVersionUri}>`}
                                        </Typography>
                                      )}
                                      {doc.file_url && (
                                        <Typography variant="caption" color="primary" component="a" href={doc.file_url} target="_blank" rel="noopener noreferrer" sx={{ display: 'block' }}>
                                          Open uploaded file
                                        </Typography>
                                      )}
                                      {documentVersionSeeAlso && (
                                        <Typography variant="caption" color="primary" component="a" href={documentVersionSeeAlso} target="_blank" rel="noopener noreferrer" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
                                          {`rdfs:seeAlso <${documentVersionSeeAlso}>`}
                                        </Typography>
                                      )}
                                    </Box>
                                  );
                                })}
                              </Stack>
                            </Box>
                          )}

                          {/* Class Attributes + SH Rules */}
                          {section.key === 'classes' && (
                            <Box>
                              <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 600, color: grey[700] }}>
                                Attributes ({classAttributesForDisplay.length})
                              </Typography>
                              {classAttributesForDisplay.length > 0 ? (
                                <Stack spacing={0.75}>
                                  {classAttributesForDisplay.map((attribute, ai) => {
                                    const attributeLabelPrimary = getPrimaryEnglishText(attribute.pathLabel_english, attribute.pathLabel || attribute.pathId || '-');
                                    const attributeLabelSecondary = getSecondaryOriginalText(attribute.pathLabel_english, attribute.pathLabel);
                                    const attributeHasValuePrimary = getPrimaryEnglishText(attribute.hasValue_english, attribute.hasValue);
                                    const attributeHasValueSecondary = getSecondaryOriginalText(attribute.hasValue_english, attribute.hasValue);
                                    const propertyDetails = attribute.propertyDetails || null;
                                    const prefLabels = toUniqueNonEmptyValues(propertyDetails?.prefLabels_english ?? propertyDetails?.prefLabels);
                                    const altLabels = toUniqueNonEmptyValues(propertyDetails?.altLabels_english ?? propertyDetails?.altLabels);
                                    const definitions = toUniqueNonEmptyValues(propertyDetails?.definitions_english ?? propertyDetails?.definitions);
                                    const subPropertyOfRows = toUniqueNonEmptyValues(propertyDetails?.subPropertyOf_english ?? propertyDetails?.subPropertyOf);

                                    const propertyExtraRows = (() => {
                                      const rawExtra = propertyDetails?.extra && typeof propertyDetails.extra === 'object'
                                        ? propertyDetails.extra
                                        : {};
                                      const byBaseKey = new Map();

                                      Object.entries(rawExtra).forEach(([rawExtraKey, rawExtraValue]) => {
                                        const normalizedExtraKey = toTrimmedText(rawExtraKey);
                                        if (!normalizedExtraKey) return;

                                        const isEnglishExtra = normalizedExtraKey.endsWith('_english');
                                        const baseExtraKey = isEnglishExtra
                                          ? normalizedExtraKey.slice(0, -8)
                                          : normalizedExtraKey;

                                        if (!byBaseKey.has(baseExtraKey)) {
                                          byBaseKey.set(baseExtraKey, {
                                            label: baseExtraKey,
                                            englishValues: [],
                                            originalValues: [],
                                          });
                                        }

                                        const holder = byBaseKey.get(baseExtraKey);
                                        const normalizedValues = toUniqueNonEmptyValues(rawExtraValue);
                                        if (normalizedValues.length === 0) return;

                                        if (isEnglishExtra) {
                                          holder.englishValues.push(...normalizedValues);
                                        } else {
                                          holder.originalValues.push(...normalizedValues);
                                        }
                                      });

                                      return Array.from(byBaseKey.values())
                                        .map((holder) => {
                                          const displayValues = toUniqueNonEmptyValues(
                                            holder.englishValues.length > 0
                                              ? holder.englishValues
                                              : holder.originalValues
                                          );
                                          const secondaryValues = holder.englishValues.length > 0
                                            ? toUniqueNonEmptyValues(holder.originalValues)
                                            : [];
                                          return {
                                            label: holder.label,
                                            values: displayValues,
                                            secondaryValues,
                                          };
                                        })
                                        .filter((row) => row.values.length > 0);
                                    })();

                                    return (
                                      <Box key={`${attribute.pathId || 'attr'}-${ai}`} sx={{ p: 1.25, bgcolor: grey[50], borderRadius: 1, borderLeft: '3px solid', borderColor: blue[300] }}>
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: grey[700], display: 'block' }}>
                                          Attribute: {attributeLabelPrimary}
                                        </Typography>
                                        {attributeLabelSecondary && (
                                          <Typography variant="caption" sx={{ color: grey[600], display: 'block' }}>
                                            NL: {attributeLabelSecondary}
                                          </Typography>
                                        )}
                                        {attribute.pathId && (
                                          <Typography variant="caption" sx={{ fontFamily: 'monospace', color: grey[500], display: 'block' }}>
                                            {attribute.pathId}
                                          </Typography>
                                        )}

                                        {/* Property Details (resolved from owl:onProperty / sh:path URI) */}
                                        {propertyDetails && (
                                          <Box sx={{ mt: 0.5, pl: 1, borderLeft: '2px solid', borderColor: blue[200], bgcolor: blue[50], borderRadius: 1, p: 0.75 }}>
                                            {propertyDetails.types && propertyDetails.types.length > 0 && (
                                              <Typography variant="caption" sx={{ color: grey[700], display: 'block' }}>
                                                <strong>rdf:type:</strong> {propertyDetails.types.join(', ')}
                                              </Typography>
                                            )}
                                            {propertyDetails.ranges && propertyDetails.ranges.length > 0 && (
                                              <Typography variant="caption" sx={{ color: grey[700], display: 'block' }}>
                                                <strong>rdfs:range:</strong> {propertyDetails.ranges.join(', ')}
                                              </Typography>
                                            )}
                                            {prefLabels.length > 0 && (
                                              <Typography variant="caption" sx={{ color: grey[700], display: 'block' }}>
                                                <strong>skos:prefLabel:</strong> {prefLabels.join(' | ')}
                                              </Typography>
                                            )}
                                            {altLabels.length > 0 && (
                                              <Typography variant="caption" sx={{ color: grey[700], display: 'block' }}>
                                                <strong>skos:altLabel:</strong> {altLabels.join(' | ')}
                                              </Typography>
                                            )}
                                            {definitions.length > 0 && (
                                              <Typography variant="caption" sx={{ color: grey[700], display: 'block', fontStyle: 'italic' }}>
                                                {definitions.join(' | ')}
                                              </Typography>
                                            )}
                                            {subPropertyOfRows.length > 0 && (
                                              <Typography variant="caption" sx={{ color: grey[700], display: 'block' }}>
                                                <strong>rdfs:subPropertyOf:</strong> {subPropertyOfRows.join(', ')}
                                              </Typography>
                                            )}
                                            {propertyExtraRows.length > 0 && (
                                              <Box sx={{ mt: 0.25 }}>
                                                {propertyExtraRows.map(({ label, values, secondaryValues }) => (
                                                  <Box key={label} sx={{ mt: 0.5 }}>
                                                    <Typography variant="caption" sx={{ color: grey[700], display: 'block', fontWeight: 600 }}>
                                                      {label}
                                                    </Typography>
                                                    {values.map((extraValue, extraIndex) => {
                                                      const extraIsUrl = isLikelyUrl(extraValue);
                                                      if (extraIsUrl) {
                                                        return (
                                                          <Typography
                                                            key={`${label}-${extraIndex}`}
                                                            variant="caption"
                                                            color="primary"
                                                            component="a"
                                                            href={extraValue}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            sx={{ display: 'block', overflowWrap: 'anywhere' }}
                                                          >
                                                            {extraValue}
                                                          </Typography>
                                                        );
                                                      }

                                                      return (
                                                        <Typography
                                                          key={`${label}-${extraIndex}`}
                                                          variant="caption"
                                                          sx={{ color: grey[600], display: 'block', overflowWrap: 'anywhere' }}
                                                        >
                                                          {extraValue}
                                                        </Typography>
                                                      );
                                                    })}
                                                    {secondaryValues.length > 0 && (
                                                      <Typography variant="caption" sx={{ color: grey[500], display: 'block', mt: 0.2, overflowWrap: 'anywhere' }}>
                                                        NL: {secondaryValues.join(' | ')}
                                                      </Typography>
                                                    )}
                                                  </Box>
                                                ))}
                                              </Box>
                                            )}
                                          </Box>
                                        )}

                                        <Typography variant="caption" sx={{ color: grey[700], display: 'block', mt: 0.25 }}>
                                          rdf:type: {attribute.restrictionType || '-'}
                                        </Typography>

                                        {attribute.minCardinality !== null && attribute.minCardinality !== undefined && (
                                          <Typography variant="caption" sx={{ color: grey[700], display: 'block', mt: 0.25 }}>
                                            owl:minCardinality: {attribute.minCardinality}
                                          </Typography>
                                        )}

                                        {attributeHasValuePrimary && (
                                          <Typography variant="caption" sx={{ color: grey[700], display: 'block', mt: 0.25 }}>
                                            owl:hasValue: {attributeHasValuePrimary}
                                            {attribute.hasValueId && (
                                              <Typography component="span" sx={{ ml: 0.75, fontFamily: 'monospace', color: grey[500] }}>
                                                ({attribute.hasValueId})
                                              </Typography>
                                            )}
                                          </Typography>
                                        )}
                                        {attributeHasValueSecondary && (
                                          <Typography variant="caption" sx={{ color: grey[500], display: 'block', mt: 0.2 }}>
                                            NL: {attributeHasValueSecondary}
                                          </Typography>
                                        )}

                                        <Box sx={{ mt: 0.6, pl: 1, borderLeft: '2px solid', borderColor: grey[300] }}>
                                          <Typography variant="caption" sx={{ fontWeight: 600, color: grey[700], display: 'block' }}>
                                            sh:property ({(attribute.shRules || []).length})
                                          </Typography>

                                          {(attribute.shRules || []).length > 0 ? (
                                            <Stack spacing={0.4} sx={{ mt: 0.25 }}>
                                              {(attribute.shRules || []).map((rule, ri) => (
                                                <Box key={`${attribute.pathId || 'attr'}-rule-${ri}`}>
                                                  {rule.datatype && (
                                                    <Typography variant="caption" sx={{ color: grey[700], display: 'block' }}>
                                                      datatype: {rule.datatype}
                                                    </Typography>
                                                  )}

                                                  {rule.minCount !== null && rule.minCount !== undefined && (
                                                    <Typography variant="caption" sx={{ color: grey[700], display: 'block' }}>
                                                      minCount: {rule.minCount}
                                                    </Typography>
                                                  )}

                                                  {rule.maxCount !== null && rule.maxCount !== undefined && (
                                                    <Typography variant="caption" sx={{ color: grey[700], display: 'block' }}>
                                                      maxCount: {rule.maxCount}
                                                    </Typography>
                                                  )}

                                                  {getPrimaryEnglishText(rule.hasValue_english, rule.hasValue) && (
                                                    <Typography variant="caption" sx={{ color: grey[700], display: 'block' }}>
                                                      hasValue: {getPrimaryEnglishText(rule.hasValue_english, rule.hasValue)}
                                                      {rule.hasValueId && (
                                                        <Typography component="span" sx={{ ml: 0.75, fontFamily: 'monospace', color: grey[500] }}>
                                                          ({rule.hasValueId})
                                                        </Typography>
                                                      )}
                                                    </Typography>
                                                  )}
                                                  {getSecondaryOriginalText(rule.hasValue_english, rule.hasValue) && (
                                                    <Typography variant="caption" sx={{ color: grey[500], display: 'block' }}>
                                                      NL: {getSecondaryOriginalText(rule.hasValue_english, rule.hasValue)}
                                                    </Typography>
                                                  )}

                                                  {rule.classId && (
                                                    <Typography variant="caption" sx={{ color: grey[700], display: 'block' }}>
                                                      class: {getPrimaryEnglishText(rule.classLabel_english, rule.classLabel || rule.classId)}
                                                      <Typography component="span" sx={{ ml: 0.75, fontFamily: 'monospace', color: grey[500] }}>
                                                        ({rule.classId})
                                                      </Typography>
                                                    </Typography>
                                                  )}
                                                </Box>
                                              ))}
                                            </Stack>
                                          ) : (
                                            <Typography variant="caption" color="text.secondary">
                                              No SH rules for this attribute.
                                            </Typography>
                                          )}
                                        </Box>
                                      </Box>
                                    );
                                  })}
                                </Stack>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  No attributes detected for this class.
                                </Typography>
                              )}
                            </Box>
                          )}

                          {/* Extra Properties (for non-requirement entities or as fallback) */}
                          {visibleExtraProps.length > 0 && (
                            <Box>
                              <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 600, color: grey[700] }}>Properties</Typography>
                              <Stack spacing={0.75}>
                                {visibleExtraProps.map(([propKey, propVal]) => {
                                  const normalizedPropKey = String(propKey || '').trim();
                                  const propKeyInfo = splitPropertyKeySuffix(normalizedPropKey);
                                  const isEnglishProperty = propKeyInfo.isEnglish;
                                  const basePropertyKey = propKeyInfo.base;
                                  const detailValues = Array.isArray(dpExtraPropertyValueDetails[propKey])
                                    ? dpExtraPropertyValueDetails[propKey]
                                    : Array.isArray(dpExtraPropertyValueDetails[basePropertyKey])
                                      ? dpExtraPropertyValueDetails[basePropertyKey]
                                    : [];

                                  const englishPropertyLabel = resolvePropertyLabel(normalizedPropKey);
                                  const originalPropertyLabel = resolvePropertyLabel(basePropertyKey || normalizedPropKey);
                                  const inferredEnglishPropertyLabel = (() => {
                                    const firstDetail = detailValues[0];
                                    if (!firstDetail || typeof firstDetail !== 'object') return '';
                                    const typeUri = String(firstDetail.typeUri || '').trim();
                                    if (!typeUri) return '';
                                    const target = dpTargetLabels[typeUri];
                                    if (target && typeof target === 'object') {
                                      const targetName = String(target.name_english || target.name_en || '').trim();
                                      if (targetName) return targetName;
                                    }
                                    const resolved = resolveUri(typeUri);
                                    return String(resolved?.name || '').trim();
                                  })();

                                  const propertyLabel = getPrimaryEnglishText(
                                    inferredEnglishPropertyLabel || englishPropertyLabel,
                                    originalPropertyLabel
                                  );
                                  const propertyLabelSecondary = getSecondaryOriginalText(
                                    inferredEnglishPropertyLabel || englishPropertyLabel,
                                    originalPropertyLabel
                                  );

                                  const rawPrimaryValues = isEnglishProperty
                                    ? propVal
                                    : (dpExtraProps[`${basePropertyKey}_english`] ?? dpExtraProps[`${basePropertyKey}_en`] ?? propVal);

                                  const rawSecondaryValues = isEnglishProperty
                                    ? dpExtraProps[basePropertyKey]
                                    : null;

                                  const fallbackValues = toUniqueNonEmptyValues(rawPrimaryValues)
                                    .map((value) => resolvePropertyValue(value))
                                    .filter((value) => String(value.primary || '').trim());

                                  const secondaryValues = toUniqueNonEmptyValues(rawSecondaryValues);

                                  const valuesForDisplay = detailValues.length > 0
                                    ? detailValues.map((valueDetail) => {
                                      const valueUri = String(valueDetail.uri || '').trim();
                                      const typeUri = String(valueDetail.typeUri || '').trim();
                                      const valueTarget = valueUri ? dpTargetLabels[valueUri] : null;
                                      const typeTarget = typeUri ? dpTargetLabels[typeUri] : null;
                                      const resolvedValue = valueUri ? resolveUri(valueUri) : null;
                                      const resolvedType = typeUri ? resolveUri(typeUri) : null;

                                      const englishValue = String(
                                        valueTarget?.name_english
                                        || valueTarget?.name_en
                                        || valueDetail.label_english
                                        || valueDetail.label_en
                                        || resolvedValue?.name
                                        || valueDetail.label
                                        || valueDetail.id
                                        || '-'
                                      ).trim();

                                      const originalValue = String(
                                        valueDetail.label
                                        || valueTarget?.name
                                        || resolvedValue?.nameOriginal
                                        || ''
                                      ).trim();

                                      const englishType = String(
                                        valueDetail.typeLabel_english
                                        || valueDetail.typeLabel_en
                                        || typeTarget?.name_english
                                        || typeTarget?.name_en
                                        || resolvedType?.name
                                        || valueDetail.typeLabel
                                        || ''
                                      ).trim();

                                      const originalType = String(
                                        valueDetail.typeLabel
                                        || typeTarget?.name
                                        || resolvedType?.nameOriginal
                                        || ''
                                      ).trim();

                                      return {
                                        primary: englishValue,
                                        technical: '',
                                        secondary: getSecondaryOriginalText(englishValue, originalValue),
                                        typeLabel: englishType,
                                        typeLabelSecondary: getSecondaryOriginalText(englishType, originalType),
                                        typeLabelOriginal: originalType || englishType,
                                        typeId: valueDetail.typeId || '',
                                        definition: valueDetail.definition_english || valueDetail.definition_en || valueDetail.definition || '',
                                        isUrl: false,
                                      };
                                    })
                                    : fallbackValues.map((valueInfo, valueIndex) => ({
                                      ...valueInfo,
                                      secondary: valueInfo.secondary || (
                                        secondaryValues[valueIndex] && secondaryValues[valueIndex] !== valueInfo.primary
                                          ? secondaryValues[valueIndex]
                                          : ''
                                      ),
                                      typeLabelOriginal: valueInfo.typeLabel || '',
                                    }));

                                  return (
                                    <Box key={propKey} sx={{ p: 1.25, bgcolor: grey[50], borderRadius: 1, border: '1px solid', borderColor: grey[200] }}>
                                      <Typography variant="caption" sx={{ fontWeight: 700, color: grey[700], display: 'block' }}>
                                        {propertyLabel}
                                      </Typography>
                                      {propertyLabelSecondary && (
                                        <Typography variant="caption" sx={{ color: grey[500], display: 'block' }}>
                                          NL: {propertyLabelSecondary}
                                        </Typography>
                                      )}

                                      <Stack spacing={0.5} sx={{ mt: 0.75 }}>
                                        {valuesForDisplay.length > 0 ? valuesForDisplay.map((valueInfo, valueIndex) => (
                                          <Box key={`${propKey}-value-${valueIndex}`} sx={{ p: 0.75, bgcolor: blue[50], borderRadius: 1, borderLeft: '3px solid', borderColor: blue[300] }}>
                                            {valueInfo.isUrl ? (
                                              <Typography
                                                variant="body2"
                                                color="primary"
                                                component="a"
                                                href={valueInfo.primary}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{ fontWeight: 600, display: 'block', overflowWrap: 'anywhere' }}
                                              >
                                                {valueInfo.primary || '-'}
                                              </Typography>
                                            ) : (
                                              <Typography variant="body2" sx={{ fontWeight: 600, color: grey[900], overflowWrap: 'anywhere' }}>
                                                {valueInfo.primary || '-'}
                                              </Typography>
                                            )}

                                            {valueInfo.technical && (
                                              <Typography variant="caption" sx={{ display: 'block', color: grey[600], mt: 0.2, fontFamily: 'monospace', overflowWrap: 'anywhere' }}>
                                                {valueInfo.technical}
                                              </Typography>
                                            )}

                                            {valueInfo.secondary && (
                                              <Typography variant="caption" sx={{ display: 'block', color: grey[500], mt: 0.2, overflowWrap: 'anywhere' }}>
                                                NL: {valueInfo.secondary}
                                              </Typography>
                                            )}

                                            {valueInfo.typeLabel && (
                                              <Typography variant="caption" sx={{ display: 'block', color: grey[700], mt: 0.2 }}>
                                                EN Type: {valueInfo.typeLabel}
                                                {valueInfo.typeId && (
                                                  <Typography component="span" sx={{ ml: 0.75, color: grey[500], fontFamily: 'monospace' }}>
                                                    ({valueInfo.typeId})
                                                  </Typography>
                                                )}
                                              </Typography>
                                            )}
                                            {(valueInfo.typeLabelOriginal || valueInfo.typeLabelSecondary || valueInfo.typeLabel) && (
                                              <Typography variant="caption" sx={{ display: 'block', color: grey[500], mt: 0.2, overflowWrap: 'anywhere' }}>
                                                NL Type: {valueInfo.typeLabelOriginal || valueInfo.typeLabelSecondary || valueInfo.typeLabel}
                                              </Typography>
                                            )}

                                            {valueInfo.definition && (
                                              <Typography variant="caption" sx={{ display: 'block', color: grey[700], mt: 0.2 }}>
                                                {valueInfo.definition}
                                              </Typography>
                                            )}
                                          </Box>
                                        )) : (
                                          <Typography variant="body2" color="text.secondary">-</Typography>
                                        )}
                                      </Stack>
                                    </Box>
                                  );
                                })}
                              </Stack>
                            </Box>
                          )}

                          {/* Metadata */}
                          <Box>
                            <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 600, color: grey[700] }}>Metadata</Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.75 }}>
                              <Typography variant="caption" color="text.secondary">
                                Updated: {formatDetailDate(selectedRow.updated_at || selectedRow.uploaded_at)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Type: {humanizeEntityType(selectedRow.entity_type) || '-'}
                              </Typography>
                            </Box>
                          </Box>
                        </Stack>
                      )}

                      {/* APPLICABILITY TAB */}
                      {normalizedVs1DetailTab === 1 && (
                        <Stack spacing={2}>
                          <Box>
                            <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 600, color: grey[700] }}>
                              Applicability Result
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                              <Chip
                                size="small"
                                color={selectedApplicabilityLabel === 'Applies' ? 'success' : (selectedApplicabilityLabel === 'Does not apply' ? 'warning' : 'default')}
                                label={selectedApplicabilityLabel}
                              />
                              {selectedDecisionSourceLabel && (
                                <Chip size="small" variant="outlined" label={`Source: ${selectedDecisionSourceLabel}`} />
                              )}
                              {selectedApplicabilityStatusRaw && (
                                <Chip size="small" variant="outlined" label={`Raw status: ${selectedApplicabilityStatusRaw}`} />
                              )}
                            </Box>
                          </Box>

                          <Box>
                            <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 600, color: grey[700] }}>
                              Reason
                            </Typography>
                            <Box sx={{ p: 1.25, bgcolor: grey[100], borderRadius: 1, border: '1px solid', borderColor: grey[300] }}>
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: grey[900] }}>
                                {selectedApplicabilityReason || 'No calculated reason for this element.'}
                              </Typography>
                            </Box>
                          </Box>

                          {section.key === 'requirements' && (
                            <Box>
                              <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 600, color: grey[700] }}>
                                Requirement Evidence Documents
                              </Typography>

                              <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap', mb: 1 }}>
                                <Chip size="small" variant="outlined" label={`Documents: ${selectedEvidenceDocuments.length}`} />
                                <Chip size="small" variant="outlined" label={`Mentions: ${selectedSecondPassMentionsCount}`} />
                                {selectedSecondPassLlmContextStats && (
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label={`LLM context: ${Number(selectedSecondPassLlmContextStats.documents_count || 0)} docs · ${Number(selectedSecondPassLlmContextStats.pages_count || 0)} pages`}
                                  />
                                )}
                              </Box>

                              {selectedEvidenceDocuments.length > 0 ? (
                                <Stack spacing={1}>
                                  {selectedEvidenceDocuments.map((doc, index) => (
                                    <Box
                                      key={`requirement-evidence-doc-${doc.key}-${index}`}
                                      sx={{
                                        p: 1,
                                        borderRadius: 1,
                                        bgcolor: grey[50],
                                        border: '1px solid',
                                        borderColor: grey[200],
                                      }}
                                    >
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: grey[900] }}>
                                          {doc.title || `Document ${doc.documentId || '-'}`}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                          <Chip size="small" label={`${doc.pages.length} page${doc.pages.length !== 1 ? 's' : ''}`} />
                                          <Chip size="small" variant="outlined" label={`${Number(doc.mentionsCount || 0)} mention${Number(doc.mentionsCount || 0) !== 1 ? 's' : ''}`} />
                                        </Box>
                                      </Box>

                                      {doc.pages.length > 0 ? (
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.8 }}>
                                          {doc.pages.map((pageNumber) => (
                                            <Button
                                              key={`requirement-evidence-doc-${doc.key}-page-${pageNumber}`}
                                              size="small"
                                              variant="outlined"
                                              onClick={() => {
                                                const mentionTexts = getRankedMentionTextsForPage(doc, pageNumber);
                                                handleLoadDocumentPagePreview(
                                                  doc.documentId,
                                                  pageNumber,
                                                  {
                                                    candidateName: 'Requirement second-pass evidence',
                                                    documentTitle: doc.title || '',
                                                    pageNumber,
                                                    mentionTexts,
                                                    extractedText: mentionTexts[0] || '',
                                                  },
                                                );
                                              }}
                                              disabled={!doc.documentId}
                                            >
                                              Page {pageNumber}
                                            </Button>
                                          ))}
                                        </Box>
                                      ) : (
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.7, display: 'block' }}>
                                          No explicit page numbers found for this document.
                                        </Typography>
                                      )}

                                      <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap', mt: 0.8 }}>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          onClick={() => handleOpenOriginalDocumentById(doc.documentId)}
                                          disabled={!doc.documentId}
                                        >
                                          Open Original Document
                                        </Button>
                                        <Button
                                          size="small"
                                          variant="contained"
                                          onClick={() => {
                                            const defaultPage = doc.pages[0] || null;
                                            const mentionTexts = defaultPage
                                              ? getRankedMentionTextsForPage(doc, defaultPage)
                                              : [];
                                            handleLoadDocumentPagePreview(
                                              doc.documentId,
                                              defaultPage,
                                              {
                                                candidateName: 'Requirement second-pass evidence',
                                                documentTitle: doc.title || '',
                                                pageNumber: defaultPage,
                                                mentionTexts,
                                                extractedText: mentionTexts[0] || '',
                                              },
                                            );
                                          }}
                                          disabled={!doc.documentId}
                                        >
                                          Open Text Viewer
                                        </Button>
                                      </Box>
                                    </Box>
                                  ))}
                                </Stack>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  No evidence documents were persisted for this requirement.
                                </Typography>
                              )}

                              <Box sx={{ mt: 1.5 }}>
                                <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 600, color: grey[700] }}>
                                  Property-driven Search Evidence
                                </Typography>

                                {selectedPropertySearchTargets.length > 0 ? (
                                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                                    {selectedPropertySearchTargets.map((target, index) => (
                                      <Chip
                                        key={`property-search-target-${index}-${target}`}
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                        label={target}
                                      />
                                    ))}
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    No property search targets persisted.
                                  </Typography>
                                )}

                                <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap', mb: 1 }}>
                                  <Chip size="small" variant="outlined" label={`Property docs: ${selectedPropertyEvidenceDocuments.length}`} />
                                  <Chip size="small" variant="outlined" label={`Property mentions: ${selectedPropertyMentionsCount}`} />
                                </Box>

                                {selectedPropertyEvidenceDocuments.length > 0 ? (
                                  <Stack spacing={1}>
                                    {selectedPropertyEvidenceDocuments.map((doc, index) => (
                                      <Box
                                        key={`requirement-property-evidence-doc-${doc.key}-${index}`}
                                        sx={{
                                          p: 1,
                                          borderRadius: 1,
                                          bgcolor: '#f6f8ff',
                                          border: '1px solid',
                                          borderColor: blue[100],
                                        }}
                                      >
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                                          <Typography variant="body2" sx={{ fontWeight: 600, color: grey[900] }}>
                                            {doc.title || `Document ${doc.documentId || '-'}`}
                                          </Typography>
                                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                            <Chip size="small" label={`${doc.pages.length} page${doc.pages.length !== 1 ? 's' : ''}`} />
                                            <Chip size="small" variant="outlined" label={`${Number(doc.mentionsCount || 0)} mention${Number(doc.mentionsCount || 0) !== 1 ? 's' : ''}`} />
                                          </Box>
                                        </Box>

                                        {doc.pages.length > 0 ? (
                                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.8 }}>
                                            {doc.pages.map((pageNumber) => (
                                              <Button
                                                key={`requirement-property-evidence-doc-${doc.key}-page-${pageNumber}`}
                                                size="small"
                                                variant="outlined"
                                                onClick={() => {
                                                    const mentionTexts = getRankedMentionTextsForPage(doc, pageNumber);
                                                  handleLoadDocumentPagePreview(
                                                    doc.documentId,
                                                    pageNumber,
                                                    {
                                                      candidateName: 'Requirement property-driven evidence',
                                                      documentTitle: doc.title || '',
                                                      pageNumber,
                                                      mentionTexts,
                                                      extractedText: mentionTexts[0] || '',
                                                    },
                                                  );
                                                }}
                                                disabled={!doc.documentId}
                                              >
                                                Page {pageNumber}
                                              </Button>
                                            ))}
                                          </Box>
                                        ) : (
                                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.7, display: 'block' }}>
                                            No explicit page numbers found for this document.
                                          </Typography>
                                        )}

                                        <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap', mt: 0.8 }}>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleOpenOriginalDocumentById(doc.documentId)}
                                            disabled={!doc.documentId}
                                          >
                                            Open Original Document
                                          </Button>
                                          <Button
                                            size="small"
                                            variant="contained"
                                            onClick={() => {
                                              const defaultPage = doc.pages[0] || null;
                                              const mentionTexts = defaultPage
                                                ? getRankedMentionTextsForPage(doc, defaultPage)
                                                : [];
                                              handleLoadDocumentPagePreview(
                                                doc.documentId,
                                                defaultPage,
                                                {
                                                  candidateName: 'Requirement property-driven evidence',
                                                  documentTitle: doc.title || '',
                                                  pageNumber: defaultPage,
                                                  mentionTexts,
                                                  extractedText: mentionTexts[0] || '',
                                                },
                                              );
                                            }}
                                            disabled={!doc.documentId}
                                          >
                                            Open Text Viewer
                                          </Button>
                                        </Box>
                                      </Box>
                                    ))}
                                  </Stack>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    No property-driven evidence documents were persisted for this requirement.
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          )}

                          {(section.key !== 'requirements' || selectedSearchTargets.length > 0) && (
                            <Box>
                              <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 600, color: grey[700] }}>
                                Nodes searched by the system
                              </Typography>
                              {selectedSearchTargets.length > 0 ? (
                                <>
                                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                    {selectedSearchTargets.map((target, index) => (
                                      <Chip
                                        key={`applicability-target-${index}-${target}`}
                                        size="small"
                                        variant="outlined"
                                        label={target}
                                      />
                                    ))}
                                  </Box>
                                  {selectedSearchTargetsReason && (
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.6, display: 'block' }}>
                                      Criteria: {selectedSearchTargetsReason}
                                    </Typography>
                                  )}
                                </>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  No searched nodes are registered for this element.
                                </Typography>
                              )}
                            </Box>
                          )}

                          {(section.key !== 'requirements' || selectedTopCandidates.length > 0) && (
                            <Box>
                              <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 600, color: grey[700] }}>
                                Top Candidates
                              </Typography>
                              {selectedTopCandidates.length > 0 && (
                                <Box sx={{ mb: 1 }}>
                                  <ToggleButtonGroup
                                    size="small"
                                    value={candidateLlmFilter}
                                    exclusive
                                    onChange={(_, newFilter) => {
                                      if (newFilter !== null) {
                                        setCandidateLlmFilter(newFilter);
                                      }
                                    }}
                                  >
                                    <ToggleButton value="all">All</ToggleButton>
                                    <ToggleButton value="not_relevant">Not Relevant</ToggleButton>
                                    <ToggleButton value="clue">Clue</ToggleButton>
                                    <ToggleButton value="object">Object</ToggleButton>
                                  </ToggleButtonGroup>
                                </Box>
                              )}
                              {selectedTopCandidates.length > 0 ? (
                                (() => {
                                const filteredCandidates = candidateLlmFilter === 'all'
                                  ? selectedTopCandidates
                                  : selectedTopCandidates.filter((c) => c.llmLabel === candidateLlmFilter);
                                
                                return filteredCandidates.length > 0 ? (
                                  <Stack spacing={0.75}>
                                    {filteredCandidates.map((candidate, candidateIndex) => {
                                  const candidateNodeId = Number(candidate.nodeId || 0);
                                  const candidateKey = `${Number(selectedRow?.id || 0)}:${candidateNodeId}`;
                                  const candidateLocationsData = candidateLocationsByKey[candidateKey] || null;
                                  const candidateLocations = Array.isArray(candidateLocationsData?.locations)
                                    ? candidateLocationsData.locations
                                    : [];
                                  const isLocationsExpanded = expandedCandidateLocationsKey === candidateKey;
                                  const isLocationsLoading = loadingCandidateLocationsKey === candidateKey;
                                  const hasLocationsLoaded = Boolean(candidateLocationsData);

                                  return (
                                    <Box
                                      key={`top-candidate-${candidateIndex}-${candidate.name}`}
                                      sx={{
                                        p: 1,
                                        bgcolor: candidateIndex === 0 ? blue[50] : grey[50],
                                        borderRadius: 1,
                                        border: '1px solid',
                                        borderColor: candidateIndex === 0 ? blue[200] : grey[200],
                                      }}
                                    >
                                      <Typography variant="body2" sx={{ fontWeight: 600, color: grey[900] }}>
                                        {candidateIndex + 1}. {candidate.name}
                                      </Typography>
                                      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.25 }}>
                                        <Typography variant="caption" color="text.secondary">
                                          Similarity: {Number(candidate.score || 0).toFixed(3)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          Target score: {Number(candidate.targetScore || 0).toFixed(3)}
                                        </Typography>
                                        {candidate.llmLabel && (
                                          <Chip size="small" variant="outlined" label={`LLM: ${candidate.llmLabel}`} />
                                        )}
                                        {candidateNodeId > 0 && (
                                          <Chip size="small" variant="outlined" label={`Node ID: ${candidateNodeId}`} />
                                        )}
                                      </Box>

                                      {candidateNodeId > 0 && (
                                        <Box sx={{ mt: 0.9 }}>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleLoadCandidateLocations(selectedRow, candidate, { toggleOnly: hasLocationsLoaded })}
                                            disabled={isLocationsLoading}
                                          >
                                            {isLocationsLoading
                                              ? 'Loading locations...'
                                              : (isLocationsExpanded ? 'Hide locations' : (hasLocationsLoaded ? `Show locations (${candidateLocations.length})` : 'Load locations'))}
                                          </Button>

                                          {isLocationsExpanded && (
                                            <Box sx={{ mt: 1 }}>
                                              {candidateLocations.length > 0 ? (
                                                <Stack spacing={0.75}>
                                                  {candidateLocations.map((locationItem) => {
                                                    const locationPage = locationItem?.page_number ? `Page ${locationItem.page_number}` : 'Page n/a';
                                                    return (
                                                      <Box
                                                        key={`location-${candidateKey}-${locationItem.mention_id}`}
                                                        sx={{
                                                          p: 1,
                                                          borderRadius: 1,
                                                          bgcolor: '#ffffff',
                                                          border: '1px solid',
                                                          borderColor: grey[200],
                                                        }}
                                                      >
                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                          {locationItem.document_title || `Document ${locationItem.document_id}`} · {locationPage}
                                                        </Typography>
                                                        {(locationItem.section || locationItem.heading || locationItem.chunk) && (
                                                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                            {locationItem.section ? `Section: ${locationItem.section}` : ''}
                                                            {locationItem.section && locationItem.heading ? ' · ' : ''}
                                                            {locationItem.heading ? `Heading: ${locationItem.heading}` : ''}
                                                            {(locationItem.section || locationItem.heading) && locationItem.chunk ? ' · ' : ''}
                                                            {locationItem.chunk ? `Chunk: ${locationItem.chunk}` : ''}
                                                          </Typography>
                                                        )}
                                                        {locationItem.extracted_text && (
                                                          <Typography variant="caption" sx={{ display: 'block', mt: 0.4, whiteSpace: 'pre-wrap', color: grey[700] }}>
                                                            "{locationItem.extracted_text}"
                                                          </Typography>
                                                        )}
                                                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.85 }}>
                                                          <Button
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={() => handleOpenOriginalDocumentById(locationItem.document_id)}
                                                          >
                                                            Open Original Document
                                                          </Button>
                                                          <Button
                                                            size="small"
                                                            variant="contained"
                                                            onClick={() => handleLoadDocumentPagePreview(
                                                              locationItem.document_id,
                                                              locationItem.page_number || null,
                                                              {
                                                                candidateName: candidate.name,
                                                                mentionId: locationItem.mention_id,
                                                                extractedText: locationItem.extracted_text || '',
                                                                documentTitle: locationItem.document_title || '',
                                                                pageNumber: locationItem.page_number || null,
                                                              },
                                                            )}
                                                          >
                                                            View Original/Translated Text
                                                          </Button>
                                                        </Box>
                                                      </Box>
                                                    );
                                                  })}
                                                </Stack>
                                              ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                  No mention locations found for this candidate node in project documents.
                                                </Typography>
                                              )}
                                            </Box>
                                          )}
                                        </Box>
                                      )}
                                    </Box>
                                  );
                                })}
                                {selectedTopCandidate && (
                                  <Typography variant="caption" color="text.secondary">
                                    Main candidate: {selectedTopCandidate.name} ({Number(selectedTopCandidate.score || 0).toFixed(3)})
                                  </Typography>
                                )}
                                {renderPagePreviewDialog()}
                              </Stack>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No candidates match the selected filter.
                              </Typography>
                            );
                                })()
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  No candidates are registered for this decision.
                                </Typography>
                              )}
                            </Box>
                          )}

                          {selectedTopCandidates.length === 0 && renderPagePreviewDialog()}
                        </Stack>
                      )}

                      {/* RELATIONSHIPS TAB */}
                      {normalizedVs1DetailTab === 2 && (
                        <Stack spacing={2.5}>
                          {totalRelCount === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 6 }}>
                              <LinkOff sx={{ fontSize: 48, color: grey[300], mb: 1.5 }} />
                              <Typography variant="body1" color="text.secondary" gutterBottom>No Relationships Found</Typography>
                              <Typography variant="caption" color="text.disabled">This entity has no extracted RDF relationships</Typography>
                            </Box>
                          ) : (
                            <>
                              {/* Outgoing */}
                              {outgoingEntries.map(([predicate, targets]) => {
                                const label = KNOWN_RELATIONSHIP_LABELS[predicate] || humanizeKey(predicate);
                                const targetList = Array.isArray(targets) ? targets : [targets];
                                return (
                                  <Box key={predicate}>
                                    <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 600, color: grey[700], display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <AccountTree fontSize="small" sx={{ color: grey[500] }} />
                                      {label}
                                      <Chip label={targetList.length} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: grey[200], color: grey[800], ml: 0.5 }} />
                                    </Typography>
                                    <Stack spacing={0.75}>
                                      {targetList.map((target, ti) => (
                                        <React.Fragment key={`${predicate}-${ti}`}>
                                          {renderRelTarget(String(target))}
                                        </React.Fragment>
                                      ))}
                                    </Stack>
                                  </Box>
                                );
                              })}

                              {/* Incoming */}
                              {dpIncoming.length > 0 && (
                                <Box>
                                  <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 600, color: grey[700], display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <AccountTree fontSize="small" sx={{ color: grey[500] }} />
                                    Incoming Relationships
                                    <Chip label={dpIncoming.length} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: grey[200], color: grey[800], ml: 0.5 }} />
                                  </Typography>
                                  <Stack spacing={0.75}>
                                    {dpIncoming.map((rel, ri) => {
                                      const predLabel = KNOWN_RELATIONSHIP_LABELS[rel.predicate] || humanizeKey(rel.predicate);
                                      const resolved = resolveUri(rel.from);
                                      const incDirEntry = vs1EntityDirectory[rel.from];
                                      const incCrossSource = incDirEntry && incDirEntry.cross_source;
                                      return (
                                        <Box
                                          key={ri}
                                          sx={{
                                            p: 1.25,
                                            bgcolor: incCrossSource ? orange[50] : (resolved ? blue[50] : grey[100]),
                                            borderRadius: 1,
                                            borderLeft: '3px solid',
                                            borderColor: incCrossSource ? orange[400] : (resolved ? 'primary.main' : grey[400]),
                                          }}
                                        >
                                          <Typography variant="caption" sx={{ fontWeight: 600, color: grey[600], display: 'block', mb: 0.25 }}>
                                            {predLabel}
                                          </Typography>
                                          {resolved ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                              <Chip label={resolved.type} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: 'primary.main', color: '#fff' }} />
                                              {incCrossSource && (
                                                <Chip label={incCrossSource === 'ontology' ? 'Ontology' : 'Project'} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: orange[700], color: '#fff' }} />
                                              )}
                                              <Typography variant="body2" sx={{ fontWeight: 600, color: grey[900] }}>
                                                {resolved.notation || resolved.name}
                                              </Typography>
                                              {resolved.notation && resolved.name && resolved.name !== resolved.notation && (
                                                <Typography variant="caption" sx={{ color: grey[600] }}>
                                                  {resolved.name}
                                                </Typography>
                                              )}
                                            </Box>
                                          ) : (
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.72rem', color: grey[600], wordBreak: 'break-all' }}>
                                              {rel.from || '-'}
                                            </Typography>
                                          )}
                                        </Box>
                                      );
                                    })}
                                  </Stack>
                                </Box>
                              )}
                            </>
                          )}
                        </Stack>
                      )}

                      {/* LINKED ENTITIES TAB */}
                      {hasLinkedEntitiesTab && normalizedVs1DetailTab === 3 && (
                        <Stack spacing={1.5}>
                          {showObjectRequirementsTab && (
                            <>
                              {objectLinkedClassRows.length > 0 && (
                                <Box>
                                  <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 600, color: grey[700] }}>
                                    Associated Class{objectLinkedClassRows.length > 1 ? 'es' : ''}
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                                    {objectLinkedClassRows.map((classRow, classIndex) => {
                                      const classPayload = classRow?.data && typeof classRow.data === 'object' ? classRow.data : {};
                                      const classLabel = String(
                                        classPayload.notation
                                        || classPayload.label_english
                                        || classPayload.label_en
                                        || classPayload.label
                                        || classRow?.name_en
                                        || classRow?.name
                                        || `Class ${classIndex + 1}`
                                      ).trim();
                                      return (
                                        <Chip
                                          key={`object-linked-class-${classRow?.id || classPayload.uri || classIndex}`}
                                          size="small"
                                          color="primary"
                                          variant="outlined"
                                          label={classLabel}
                                        />
                                      );
                                    })}
                                  </Box>
                                </Box>
                              )}

                              {objectRequirementsCount === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 5 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    No requirements linked through hasRequirement in the associated class.
                                  </Typography>
                                </Box>
                              ) : (
                                <Stack spacing={1}>
                                  {objectLinkedRequirementRows.map((requirementRow, reqIndex) => {
                                    const requirementPayload = requirementRow?.data && typeof requirementRow.data === 'object'
                                      ? requirementRow.data
                                      : {};
                                    const requirementRowId = Number(requirementRow?.id || 0);
                                    const requirementNotation = String(requirementPayload.notation || '').trim();
                                    const requirementLabel = String(
                                      getPrimaryEnglishText(
                                        requirementPayload.label_english || requirementPayload.label_en,
                                        requirementPayload.label
                                      )
                                      || requirementRow?.name_en
                                      || requirementRow?.name
                                      || requirementNotation
                                      || `Requirement ${reqIndex + 1}`
                                    ).trim();
                                    const requirementText = String(
                                      getPrimaryEnglishText(
                                        requirementPayload.requirement_text_english || requirementPayload.requirement_text_en,
                                        requirementPayload.requirement_text
                                      )
                                    ).trim();
                                    const requirementVakgebied = getRowVakgebied(requirementRow);
                                    const applicabilityStatus = normalizeMetricText(getSectionRowValue(requirementRow, 'applicability_status'));
                                    const hasApplies = applicabilityStatus === 'aplica';
                                    const hasNoApplies = applicabilityStatus === 'no_aplica';
                                    const isIndeterminate = isRowIndeterminate(requirementRow);
                                    const applicabilityLabel = getRowApplicabilityLabel(requirementRow);
                                    const applicabilityReason = String(getSectionRowValue(requirementRow, 'applicability_reason') || '').trim();
                                    const requirementCardBackground = hasApplies
                                      ? '#e8f5e9'
                                      : (hasNoApplies ? '#ffebee' : (isIndeterminate ? '#fff8e1' : blue[50]));
                                    const requirementCardBorder = hasApplies
                                      ? '#81c784'
                                      : (hasNoApplies ? '#e57373' : (isIndeterminate ? '#ffb74d' : blue[300]));
                                    const canOpenRequirementDetails = requirementRowId > 0;
                                    const openRequirementDetails = () => {
                                      if (!canOpenRequirementDetails) {
                                        return;
                                      }
                                      const shouldKeepModalContext = Boolean(isModalView && vs1SectionModalOpen);
                                      handleNavigateToVs1LinkedRow('requirements', requirementRowId, {
                                        preferModal: shouldKeepModalContext,
                                      });
                                    };

                                    return (
                                      <Box
                                        key={`object-linked-requirement-${requirementRow?.id || requirementPayload.uri || reqIndex}`}
                                        sx={{ p: 1.25, bgcolor: requirementCardBackground, borderRadius: 1, borderLeft: '3px solid', borderColor: requirementCardBorder }}
                                      >
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: grey[900] }}>
                                          {requirementNotation && requirementLabel && requirementNotation !== requirementLabel
                                            ? `${requirementNotation} - ${requirementLabel}`
                                            : (requirementNotation || requirementLabel)}
                                        </Typography>
                                        {requirementText && (
                                          <Typography variant="body2" sx={{ color: grey[800], mt: 0.35, whiteSpace: 'pre-wrap' }}>
                                            {requirementText}
                                          </Typography>
                                        )}
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.7 }}>
                                          <Chip size="small" variant="outlined" label={`Vakgebied: ${requirementVakgebied}`} />
                                          <Chip
                                            size="small"
                                            color={hasApplies ? 'success' : (hasNoApplies ? 'error' : 'default')}
                                            label={applicabilityLabel}
                                          />
                                          <Chip size="small" color={isRowValidated(requirementRow) ? 'success' : 'default'} label={`Validated: ${isRowValidated(requirementRow) ? 'Yes' : 'No'}`} />
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={openRequirementDetails}
                                            disabled={!canOpenRequirementDetails}
                                          >
                                            Open requirement details
                                          </Button>
                                        </Box>
                                        <Typography variant="caption" sx={{ color: grey[700], display: 'block', mt: 0.7, whiteSpace: 'pre-wrap' }}>
                                          {applicabilityReason || 'Run the applicability analysis to generate a reason.'}
                                        </Typography>
                                      </Box>
                                    );
                                  })}

                                  {objectUnresolvedRequirementUris.map((requirementUri) => {
                                    const resolvedRequirement = resolveUri(requirementUri);
                                    return (
                                      <Box key={`object-unresolved-requirement-${requirementUri}`} sx={{ p: 1.25, bgcolor: grey[100], borderRadius: 1, borderLeft: '3px solid', borderColor: grey[400] }}>
                                        <Typography variant="caption" sx={{ color: grey[600], display: 'block' }}>
                                          Requirement reference
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: grey[900], overflowWrap: 'anywhere' }}>
                                          {resolvedRequirement?.notation || resolvedRequirement?.name || requirementUri}
                                        </Typography>
                                      </Box>
                                    );
                                  })}
                                </Stack>
                              )}
                            </>
                          )}

                          {showRequirementObjectsTab && (
                            <>
                              {requirementLinkedClassRows.length > 0 && (
                                <Box>
                                  <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 600, color: grey[700] }}>
                                    Associated Class{requirementLinkedClassRows.length > 1 ? 'es' : ''}
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                                    {requirementLinkedClassRows.map((classRow, classIndex) => {
                                      const classPayload = classRow?.data && typeof classRow.data === 'object' ? classRow.data : {};
                                      const classLabel = String(
                                        classPayload.notation
                                        || classPayload.label_english
                                        || classPayload.label_en
                                        || classPayload.label
                                        || classRow?.name_en
                                        || classRow?.name
                                        || `Class ${classIndex + 1}`
                                      ).trim();
                                      return (
                                        <Chip
                                          key={`requirement-linked-class-${classRow?.id || classPayload.uri || classIndex}`}
                                          size="small"
                                          color="primary"
                                          variant="outlined"
                                          label={classLabel}
                                        />
                                      );
                                    })}
                                  </Box>
                                </Box>
                              )}

                              {requirementObjectsCount === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 5 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    No objects linked to this requirement through hasRequirement in associated classes.
                                  </Typography>
                                </Box>
                              ) : (
                                <Stack spacing={1}>
                                  {requirementLinkedObjectRows.map((objectRow, objectIndex) => {
                                    const objectPayload = objectRow?.data && typeof objectRow.data === 'object'
                                      ? objectRow.data
                                      : {};
                                    const objectRowId = Number(objectRow?.id || 0);
                                    const objectNotation = String(objectPayload.notation || '').trim();
                                    const objectLabel = String(
                                      getPrimaryEnglishText(objectPayload.label_english || objectPayload.label_en, objectPayload.label)
                                      || objectRow?.name_en
                                      || objectRow?.name
                                      || objectNotation
                                      || `Object ${objectIndex + 1}`
                                    ).trim();
                                    const applicabilityStatus = normalizeMetricText(getSectionRowValue(objectRow, 'applicability_status'));
                                    const hasApplies = applicabilityStatus === 'aplica';
                                    const hasNoApplies = applicabilityStatus === 'no_aplica';
                                    const isIndeterminate = isRowIndeterminate(objectRow);
                                    const applicabilityLabel = getRowApplicabilityLabel(objectRow);
                                    const applicabilityReason = String(getSectionRowValue(objectRow, 'applicability_reason') || '').trim();
                                    const objectVakgebied = getRowVakgebied(objectRow);
                                    const objectCardBackground = hasApplies
                                      ? '#e8f5e9'
                                      : (hasNoApplies ? '#ffebee' : (isIndeterminate ? '#fff8e1' : blue[50]));
                                    const objectCardBorder = hasApplies
                                      ? '#81c784'
                                      : (hasNoApplies ? '#e57373' : (isIndeterminate ? '#ffb74d' : blue[300]));
                                    const canOpenObjectDetails = objectRowId > 0;
                                    const openObjectDetails = () => {
                                      if (!canOpenObjectDetails) {
                                        return;
                                      }
                                      const shouldKeepModalContext = Boolean(isModalView && vs1SectionModalOpen);
                                      handleNavigateToVs1LinkedRow('objects', objectRowId, {
                                        preferModal: shouldKeepModalContext,
                                      });
                                    };

                                    return (
                                      <Box
                                        key={`requirement-linked-object-${objectRow?.id || objectPayload.uri || objectIndex}`}
                                        sx={{ p: 1.25, bgcolor: objectCardBackground, borderRadius: 1, borderLeft: '3px solid', borderColor: objectCardBorder }}
                                      >
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: grey[900] }}>
                                          {objectNotation && objectLabel && objectNotation !== objectLabel
                                            ? `${objectNotation} - ${objectLabel}`
                                            : (objectNotation || objectLabel)}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.7 }}>
                                          <Chip size="small" variant="outlined" label={`Vakgebied: ${objectVakgebied}`} />
                                          <Chip
                                            size="small"
                                            color={hasApplies ? 'success' : (hasNoApplies ? 'error' : 'default')}
                                            label={applicabilityLabel}
                                          />
                                          <Chip size="small" color={isRowValidated(objectRow) ? 'success' : 'default'} label={`Validated: ${isRowValidated(objectRow) ? 'Yes' : 'No'}`} />
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={openObjectDetails}
                                            disabled={!canOpenObjectDetails}
                                          >
                                            Open object details
                                          </Button>
                                        </Box>
                                        <Typography variant="caption" sx={{ color: grey[700], display: 'block', mt: 0.7, whiteSpace: 'pre-wrap' }}>
                                          {applicabilityReason || 'Run the applicability analysis to generate a reason.'}
                                        </Typography>
                                      </Box>
                                    );
                                  })}
                                </Stack>
                              )}
                            </>
                          )}
                        </Stack>
                      )}

                      {/* GRAPH TAB */}
                      {normalizedVs1DetailTab === graphTabIndex && (() => {
                        const vs1GraphData = buildVs1GraphData(dp);
                        if (!vs1GraphData || vs1GraphData.nodes.length <= 1) {
                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 200 }}>
                              <Typography variant="body2" color="text.secondary">No relationships to display</Typography>
                            </Box>
                          );
                        }

                        return (
                          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', flex: 1, minHeight: 0 }}>
                            <Box ref={(el) => {
                              if (vs1GraphRoRef.current) { vs1GraphRoRef.current.disconnect(); vs1GraphRoRef.current = null; }
                              vs1GraphContainerRef.current = el;
                              if (el) {
                                const ro = new ResizeObserver((entries) => {
                                  for (const entry of entries) {
                                    const { width, height } = entry.contentRect;
                                    if (width > 0 && height > 0) setVs1GraphDims({ w: Math.round(width), h: Math.round(height) });
                                  }
                                });
                                ro.observe(el);
                                vs1GraphRoRef.current = ro;
                              }
                            }} sx={{ width: '100%', flex: 1, minHeight: 'clamp(320px, 58dvh, 500px)', position: 'relative', border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', bgcolor: '#f0f4f8' }}>
                              {vs1GraphDims.h > 0 && <ForceGraph2D
                                ref={(el) => {
                                  vs1GraphRef.current = el;
                                }}
                                graphData={vs1GraphData}
                                width={vs1GraphDims.w}
                                height={vs1GraphDims.h}
                                backgroundColor="#f0f4f8"
                              nodeCanvasObject={(node, ctx, globalScale) => {
                                const isSelf = node.type === 'self';
                                const isHovered = vs1HoveredNode && vs1HoveredNode.id === node.id;
                                const color = node.color || '#2196F3';
                                const size = isSelf ? 14 : 8;

                                // Circle
                                ctx.beginPath();
                                ctx.arc(node.x, node.y, size, 0, 2 * Math.PI);
                                ctx.fillStyle = isSelf ? color : `${color}DD`;
                                ctx.fill();
                                ctx.strokeStyle = isSelf ? '#fff' : 'rgba(255,255,255,0.5)';
                                ctx.lineWidth = isSelf ? 2.5 : 1.5;
                                ctx.stroke();

                                if (!isSelf && !isHovered) return;

                                // Word-wrapped label
                                const rawLabel = node.label || '';
                                const fontSize = Math.max(10 / globalScale, isSelf ? 5 : 4);
                                const fontWeight = isSelf ? 'bold' : '500';
                                ctx.font = `${fontWeight} ${fontSize}px -apple-system, BlinkMacSystemFont, sans-serif`;
                                ctx.textAlign = 'center';
                                ctx.textBaseline = 'top';

                                const maxLineWidth = Math.max(80, size * 8);
                                const words = rawLabel.split(' ');
                                const lines = [];
                                let current = '';
                                for (const word of words) {
                                  const test = current ? `${current} ${word}` : word;
                                  if (ctx.measureText(test).width > maxLineWidth && current) {
                                    lines.push(current);
                                    current = word;
                                  } else {
                                    current = test;
                                  }
                                }
                                if (current) lines.push(current);
                                if (lines.length > 3) {
                                  lines.splice(3);
                                  lines[2] = lines[2].slice(0, -1) + '\u2026';
                                }

                                const lineH = fontSize + 1;
                                const boxW = Math.max(...lines.map((l) => ctx.measureText(l).width));
                                const labelY = node.y + size + 4;
                                const padX = 4;
                                const padY = 2;
                                const bRadius = 3;
                                const bx = node.x - boxW / 2 - padX;
                                const by = labelY - padY;
                                const bw = boxW + padX * 2;
                                const bh = lines.length * lineH + padY * 2;

                                // Pill background
                                ctx.fillStyle = isSelf ? 'rgba(255,248,200,0.95)' : 'rgba(255,255,255,0.92)';
                                ctx.beginPath();
                                ctx.moveTo(bx + bRadius, by);
                                ctx.lineTo(bx + bw - bRadius, by);
                                ctx.quadraticCurveTo(bx + bw, by, bx + bw, by + bRadius);
                                ctx.lineTo(bx + bw, by + bh - bRadius);
                                ctx.quadraticCurveTo(bx + bw, by + bh, bx + bw - bRadius, by + bh);
                                ctx.lineTo(bx + bRadius, by + bh);
                                ctx.quadraticCurveTo(bx, by + bh, bx, by + bh - bRadius);
                                ctx.lineTo(bx, by + bRadius);
                                ctx.quadraticCurveTo(bx, by, bx + bRadius, by);
                                ctx.closePath();
                                ctx.fill();
                                if (isSelf) {
                                  ctx.strokeStyle = 'rgba(200,160,0,0.5)';
                                  ctx.lineWidth = 0.8;
                                  ctx.stroke();
                                }

                                ctx.fillStyle = isSelf ? '#7a5900' : '#333';
                                lines.forEach((line, i) => {
                                  ctx.fillText(line, node.x, labelY + i * lineH);
                                });
                              }}
                              nodeCanvasObjectMode={() => 'replace'}
                              linkCanvasObject={(link, ctx, globalScale) => {
                                const src = link.source;
                                const tgt = link.target;
                                if (!src || !tgt || typeof src.x === 'undefined') return;

                                const color = link.color || '#999';
                                const dx = tgt.x - src.x;
                                const dy = tgt.y - src.y;
                                const dist = Math.sqrt(dx * dx + dy * dy) || 1;

                                // Curved edge
                                const curvature = 0.2;
                                const cpX = (src.x + tgt.x) / 2 - curvature * dy;
                                const cpY = (src.y + tgt.y) / 2 + curvature * dx;

                                ctx.beginPath();
                                ctx.moveTo(src.x, src.y);
                                ctx.quadraticCurveTo(cpX, cpY, tgt.x, tgt.y);
                                ctx.strokeStyle = `${color}99`;
                                ctx.lineWidth = 1.8;
                                ctx.stroke();

                                // Arrow at t=0.78
                                const t = 0.78;
                                const arrowX = (1 - t) * (1 - t) * src.x + 2 * (1 - t) * t * cpX + t * t * tgt.x;
                                const arrowY = (1 - t) * (1 - t) * src.y + 2 * (1 - t) * t * cpY + t * t * tgt.y;
                                const tanX = 2 * (1 - t) * (cpX - src.x) + 2 * t * (tgt.x - cpX);
                                const tanY = 2 * (1 - t) * (cpY - src.y) + 2 * t * (tgt.y - cpY);
                                const angle = Math.atan2(tanY, tanX);
                                const arrowLen = 7;

                                ctx.beginPath();
                                ctx.moveTo(arrowX, arrowY);
                                ctx.lineTo(arrowX - arrowLen * Math.cos(angle - Math.PI / 6), arrowY - arrowLen * Math.sin(angle - Math.PI / 6));
                                ctx.lineTo(arrowX - arrowLen * Math.cos(angle + Math.PI / 6), arrowY - arrowLen * Math.sin(angle + Math.PI / 6));
                                ctx.closePath();
                                ctx.fillStyle = `${color}AA`;
                                ctx.fill();

                                // Edge label
                                if (link.label && globalScale >= 0.6) {
                                  const midX = (src.x + tgt.x) / 2 - curvature * dy * 0.5;
                                  const midY = (src.y + tgt.y) / 2 + curvature * dx * 0.5;
                                  const perpOffsetDist = 10 / globalScale;
                                  const labelX = midX - (dy / dist) * perpOffsetDist;
                                  const labelY = midY + (dx / dist) * perpOffsetDist;

                                  const fontSize = Math.max(9 / globalScale, 4);
                                  ctx.font = `500 ${fontSize}px -apple-system, BlinkMacSystemFont, sans-serif`;
                                  ctx.textAlign = 'center';
                                  ctx.textBaseline = 'middle';

                                  const textWidth = ctx.measureText(link.label).width;
                                  const padX = 3;
                                  const padY2 = 1.5;
                                  ctx.fillStyle = 'rgba(255,255,255,0.88)';
                                  const rx = labelX - textWidth / 2 - padX;
                                  const ry = labelY - fontSize / 2 - padY2;
                                  const rw = textWidth + padX * 2;
                                  const rh = fontSize + padY2 * 2;
                                  ctx.beginPath();
                                  ctx.roundRect(rx, ry, rw, rh, 3);
                                  ctx.fill();

                                  ctx.fillStyle = `${color}CC`;
                                  ctx.fillText(link.label, labelX, labelY);
                                }
                              }}
                              linkCanvasObjectMode={() => 'replace'}
                              nodeLabel={(node) => `${node.label}${node.type && node.type !== 'self' ? ` (${node.type})` : ''}`}
                              linkDirectionalArrowLength={0}
                              enableNodeDrag={true}
                              enableZoomInteraction={true}
                              enablePanInteraction={true}
                              cooldownTicks={250}
                              d3AlphaDecay={0.008}
                              d3VelocityDecay={0.3}
                              d3AlphaMin={0.001}
                              nodeRelSize={7}
                              warmupTicks={100}
                              onEngineStop={() => {}}
                              onEngineStart={() => {
                                if (vs1GraphRef.current) {
                                  vs1GraphRef.current.d3Force('charge').strength(-800).distanceMax(800);
                                  const linkForce = vs1GraphRef.current.d3Force('link');
                                  if (linkForce) linkForce.distance(250).strength(0.3);
                                  const centerForce = vs1GraphRef.current.d3Force('center');
                                  if (centerForce) centerForce.x(0).y(0);
                                }
                              }}
                              onNodeHover={(node) => setVs1HoveredNode(node || null)}
                              onNodeDragEnd={(node) => { node.fx = node.x; node.fy = node.y; }}
                            />}
                            </Box>
                            {/* Legend - outside graph canvas */}
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', pt: 1, px: 0.5 }}>
                              <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.65rem', color: grey[600] }}>
                                Legend:
                              </Typography>
                              {Object.entries(GRAPH_NODE_COLORS).filter(([k]) => k !== 'default').map(([label, color]) => (
                                <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, border: label === 'self' ? '2px solid #fff' : 'none', flexShrink: 0 }} />
                                  <Typography variant="caption" sx={{ fontSize: '0.65rem', color: grey[700] }}>
                                    {label === 'self' ? 'Selected' : label}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        );
                      })()}
                    </Box>
                  </>
                );
              })() : (
                <Box
                  sx={{
                    minHeight: 208,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    px: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {rows.length === 0
                      ? `No ${section.label.toLowerCase()} found for this source.`
                      : `Select a ${sectionItemLabel} to view details`}
                  </Typography>
                </Box>
              )}
            </Paper>
          )}
        </Box>
      </Paper>
    );
  };

  const renderProjectDetailTab = () => {
    const currentView = projectViews.find((item) => item.key === projectDetailTab) || null;

    if (projectDetailTab === 'documents') {
      const documents = Array.isArray(currentView?.documents) ? currentView.documents : [];
      const uploadedDocuments = documents.filter((item) => Boolean(item?.is_available));
      const projectGraphInProgress = Boolean(currentView?.graph_generation_in_progress);
      const projectGraphProgress = Number(currentView?.graph_generation_progress || 0);
      const projectGraphStep = String(currentView?.graph_generation_step || '').trim();

      const uploadStats = {
        total: Number(documentUploadSummary.total || 0),
        success: Number(documentUploadSummary.success || 0),
        duplicate: Number(documentUploadSummary.duplicate || 0),
        failed: Number(documentUploadSummary.failed || 0),
        cancelled: Number(documentUploadSummary.cancelled || 0),
        skipped: Number(documentUploadSummary.skipped || 0),
      };
      const uploadProcessedCount = uploadStats.success + uploadStats.duplicate + uploadStats.failed + uploadStats.cancelled;
      const uploadPendingCount = Math.max(0, uploadStats.total - uploadProcessedCount);
      const uploadInFlightItems = documentUploadItems.filter((item) => (
        item.status === 'pending' || item.status === 'uploading' || item.status === 'processing'
      ));
      const hasDocumentUploadsInProgress = uploadInFlightItems.length > 0 || uploadingProjectViewKey === 'documents';
      const uploadInFlightFraction = uploadInFlightItems.length > 0
        ? uploadInFlightItems.reduce((acc, item) => {
          const itemProgress = Number(item?.progress || 0);
          return acc + (Math.min(100, Math.max(0, itemProgress)) / 100);
        }, 0)
        : (typeof projectViewUploadProgress === 'number'
          ? Math.min(1, Math.max(0, Number(projectViewUploadProgress) / 100))
          : 0);
      const uploadProgress = uploadStats.total > 0
        ? Math.round((Math.min(uploadStats.total, uploadProcessedCount + uploadInFlightFraction) * 100) / uploadStats.total)
        : (typeof projectViewUploadProgress === 'number' ? projectViewUploadProgress : 0);
      const retryableFailedCount = documentUploadItems.filter((item) => item.status === 'failed' && item.file instanceof File).length;
      const filteredDocumentUploadItems = documentUploadItems.filter((item) => (
        documentUploadStatusFilter === 'all' ? true : item.status === documentUploadStatusFilter
      ));

      const getUploadItemChipConfig = (statusValue) => {
        const normalizedStatus = String(statusValue || '').toLowerCase();
        if (normalizedStatus === 'completed') {
          return { color: 'success', icon: <CheckCircleOutline fontSize="inherit" />, label: 'Uploaded' };
        }
        if (normalizedStatus === 'duplicate') {
          return { color: 'warning', icon: <Info fontSize="inherit" />, label: 'Duplicate' };
        }
        if (normalizedStatus === 'failed') {
          return { color: 'error', icon: <ErrorOutline fontSize="inherit" />, label: 'Failed' };
        }
        if (normalizedStatus === 'processing') {
          return { color: 'info', icon: null, label: 'Processing' };
        }
        if (normalizedStatus === 'uploading') {
          return { color: 'primary', icon: null, label: 'Uploading' };
        }
        if (normalizedStatus === 'cancelled') {
          return { color: 'default', icon: <Info fontSize="inherit" />, label: 'Cancelled' };
        }
        return { color: 'default', icon: null, label: 'Pending' };
      };

      return (
        <Box sx={{ width: '100%' }}>
          <Paper sx={{ p: 1.5, mb: 2, border: '1px dashed', borderColor: 'divider' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Project Documents
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Upload documents (PDF or DOCX) using a single file or a folder. We recommend uploading the main document first: Requirements specification / Vraagspecificatie. Files are processed one by one with extraction, summary and translation.
                </Typography>
                <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                  If an upload fails immediately, make sure the file is closed in Word/PDF viewer and try again.
                </Typography>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => handleUploadProjectViewClick('documents', { uploadMode: 'single' })}
                  disabled={!canCreateProjects || Boolean(uploadingProjectViewKey)}
                >
                  Upload Document
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<FolderOpen fontSize="small" />}
                  onClick={() => handleUploadProjectViewClick('documents', { uploadMode: 'folder' })}
                  disabled={!canCreateProjects || Boolean(uploadingProjectViewKey)}
                >
                  Upload Documents Folder
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Hub fontSize="small" />}
                  onClick={() => handleGenerateProjectDocumentsGraph()}
                  disabled={hasDocumentUploadsInProgress || projectGraphInProgress || uploadedDocuments.length === 0}
                >
                  {projectGraphInProgress ? 'Generating Graph...' : 'Generate Project Graph'}
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AccountTree fontSize="small" />}
                  onClick={handleOpenProjectDocumentsGraph}
                  disabled={uploadedDocuments.length === 0}
                >
                  View Project Graph
                </Button>
              </Stack>
            </Stack>

            {projectGraphInProgress && (
              <Box sx={{ mt: 1.25 }}>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, Math.max(0, Number(projectGraphProgress || 0)))}
                  sx={{ mb: 0.5 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {`${Math.round(projectGraphProgress)}%${projectGraphStep ? ` - ${projectGraphStep}` : ''}`}
                </Typography>
              </Box>
            )}

            {(uploadingProjectViewKey === 'documents' || documentUploadItems.length > 0) && (
              <Box sx={{ mt: 1.25 }}>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, Math.max(0, Number(uploadProgress || 0)))}
                  sx={{ mb: 0.5 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {projectViewUploadStep || `Upload progress: ${uploadProgress}% (${uploadProcessedCount}/${uploadStats.total || 0})`}
                </Typography>
                {projectViewUploadPages.total > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {`Pages: ${projectViewUploadPages.processed}/${projectViewUploadPages.total}`}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  <Chip size="small" color="success" variant="outlined" label={`Uploaded: ${uploadStats.success}`} />
                  <Chip size="small" color="warning" variant="outlined" label={`Duplicates: ${uploadStats.duplicate}`} />
                  <Chip size="small" color="error" variant="outlined" label={`Failed: ${uploadStats.failed}`} />
                  <Chip size="small" color="default" variant="outlined" label={`Cancelled: ${uploadStats.cancelled}`} />
                  <Chip size="small" variant="outlined" label={`Pending: ${uploadPendingCount}`} />
                  {uploadStats.skipped > 0 && (
                    <Chip size="small" color="default" variant="outlined" label={`Skipped unsupported: ${uploadStats.skipped}`} />
                  )}
                </Box>
              </Box>
            )}

            {hasDocumentUploadsInProgress && (
              <Alert severity="info" sx={{ mt: 1.25 }}>
                Upload all documents first. Project graph generation will be available once the queue is fully completed.
              </Alert>
            )}
          </Paper>

          <Stack spacing={1.5} sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Documents are free-uploaded by users. We recommend always uploading the main document first: Requirements specification / Vraagspecificatie. This summary shows processing status for the current queue.
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip
                size="small"
                color={uploadPendingCount === 0 ? 'success' : 'warning'}
                label={`Pending (${uploadPendingCount})`}
              />
              <Chip
                size="small"
                color="success"
                variant="outlined"
                label={`Success (${uploadStats.success})`}
              />
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                label={`Duplicates (${uploadStats.duplicate})`}
              />
              <Chip
                size="small"
                color="error"
                variant="outlined"
                label={`Failed (${uploadStats.failed})`}
              />
              <Chip
                size="small"
                variant="outlined"
                label={`All (${uploadStats.total})`}
              />
            </Box>
          </Stack>

          {documentUploadItems.length > 0 && (
            <Box sx={{ mt: 1.5, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <Typography variant="subtitle2">
                  Current Upload Queue
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={documentUploadStatusFilter}
                    onChange={(_, newValue) => {
                      if (newValue) {
                        setDocumentUploadStatusFilter(newValue);
                      }
                    }}
                  >
                    <ToggleButton value="all">All</ToggleButton>
                    <ToggleButton value="pending">Pending</ToggleButton>
                    <ToggleButton value="uploading">Uploading</ToggleButton>
                    <ToggleButton value="processing">Processing</ToggleButton>
                    <ToggleButton value="completed">Uploaded</ToggleButton>
                    <ToggleButton value="duplicate">Duplicate</ToggleButton>
                    <ToggleButton value="failed">Failed</ToggleButton>
                    <ToggleButton value="cancelled">Cancelled</ToggleButton>
                  </ToggleButtonGroup>

                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    onClick={handleCancelDocumentUploadQueue}
                    disabled={uploadingProjectViewKey !== 'documents'}
                  >
                    Cancel Queue
                  </Button>

                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleRetryFailedDocumentUploads}
                    disabled={uploadingProjectViewKey === 'documents' || retryableFailedCount === 0}
                  >
                    Retry Failed ({retryableFailedCount})
                  </Button>

                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Download fontSize="small" />}
                    onClick={() => handleExportDocumentUploadReport('csv')}
                    disabled={documentUploadItems.length === 0}
                  >
                    Export CSV
                  </Button>

                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Download fontSize="small" />}
                    onClick={() => handleExportDocumentUploadReport('json')}
                    disabled={documentUploadItems.length === 0}
                  >
                    Export JSON
                  </Button>

                  <Button
                    size="small"
                    variant="text"
                    color="inherit"
                    onClick={handleClearDocumentUploadQueue}
                    disabled={uploadingProjectViewKey === 'documents' && !documentUploadCancelRef.current}
                  >
                    Clear Queue
                  </Button>
                </Stack>
              </Box>
              <TableContainer sx={{ overflowX: 'hidden' }}>
                <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: '42%' }}>File</TableCell>
                      <TableCell sx={{ width: '20%' }}>Status</TableCell>
                      <TableCell sx={{ width: '30%' }}>Detail</TableCell>
                      <TableCell sx={{ width: '8%' }} align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredDocumentUploadItems.map((uploadItem) => {
                      const itemChip = getUploadItemChipConfig(uploadItem.status);
                      const canCancelItem = uploadItem.taskId && (uploadItem.status === 'processing' || uploadItem.status === 'uploading');
                      return (
                        <TableRow key={uploadItem.id}>
                          <TableCell sx={{ minWidth: 0, maxWidth: 0 }}>
                            <Typography variant="body2" sx={{ overflowWrap: 'anywhere', fontWeight: 600 }}>
                              {uploadItem.name}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ minWidth: 0, maxWidth: 0 }}>
                            <Chip
                              size="small"
                              color={itemChip.color}
                              icon={itemChip.icon}
                              label={itemChip.label}
                            />
                          </TableCell>
                          <TableCell sx={{ minWidth: 0, maxWidth: 0 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
                              {uploadItem.step || '-'}
                            </Typography>
                            {uploadItem.error && (
                              <Typography variant="caption" color="error" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
                                {uploadItem.error}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center" sx={{ minWidth: 0, maxWidth: 0 }}>
                            {canCancelItem ? (
                              <Tooltip title="Cancel this upload">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={async () => {
                                    try {
                                      await cancelIngestionUpload(uploadItem.taskId);
                                      updateUploadItem(uploadItem.id, {
                                        status: 'cancelled',
                                        step: 'Cancelled by user',
                                        error: '',
                                      });
                                      if (showSuccess) {
                                        showSuccess(`Upload cancelled: ${uploadItem.name}`);
                                      }
                                    } catch (error) {
                                      if (showError) {
                                        showError(`Failed to cancel upload: ${error.message}`);
                                      }
                                    }
                                  }}
                                >
                                  <Close fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {filteredDocumentUploadItems.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4}>
                          <Typography variant="body2" color="text.secondary" sx={{ py: 1, textAlign: 'center' }}>
                            No items for the selected filter.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          <Box sx={{ mt: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Uploaded Documents
            </Typography>

            {uploadedDocuments.length === 0 ? (
              <Alert severity="info">
                No uploaded documents yet. Upload a PDF/DOCX file or a folder with PDF/DOCX files to start processing.
              </Alert>
            ) : (
              <TableContainer sx={{ overflowX: 'hidden' }}>
                <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: '45%' }}>Document</TableCell>
                      <TableCell sx={{ width: '20%' }}>Uploaded By</TableCell>
                      <TableCell sx={{ width: '20%' }}>Uploaded At</TableCell>
                      <TableCell sx={{ width: '15%' }} align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {uploadedDocuments.map((documentItem, index) => {
                      return (
                        <TableRow key={`${documentItem.key || documentItem.original_filename || 'uploaded-document'}-${index}`}>
                          <TableCell sx={{ minWidth: 0, maxWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
                              {documentItem.label || documentItem.original_filename || 'Project document'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
                              {documentItem.original_filename || '-'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {formatFileSize(documentItem.file_size)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ minWidth: 0, maxWidth: 0 }}>
                            <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                              {documentItem.uploaded_by || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ minWidth: 0, maxWidth: 0 }}>
                            <Typography variant="body2" color="text.secondary">
                              {documentItem.uploaded_at
                                ? new Date(documentItem.uploaded_at).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })
                                : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ minWidth: 0, maxWidth: 0 }}>
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', alignItems: 'center' }}>
                              <Tooltip title="View document graph">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="secondary"
                                    onClick={() => handleOpenSingleDocumentGraph(documentItem)}
                                    disabled={!Number(documentItem?.document_model_id || 0)}
                                  >
                                    <Hub fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="View document">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleViewProjectDocument(documentItem)}
                                  disabled={deletingDocument || viewingProjectDocumentId === (Number(documentItem?.repository_document_entity_id || documentItem?.document_model_id || 0))}
                                >
                                  <VisibilityOutlined fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {documentItem.file_url ? (
                                <Button
                                  size="small"
                                  variant="text"
                                  startIcon={<OpenInNew fontSize="small" />}
                                  onClick={() => window.open(documentItem.file_url, '_blank', 'noopener,noreferrer')}
                                >
                                  Open
                                </Button>
                              ) : null}
                              <Tooltip title="Re-upload and overwrite this document">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleReplaceDocument(documentItem)}
                                  disabled={!canCreateProjects || Boolean(uploadingProjectViewKey)}
                                >
                                  <UploadFile fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Remove document from project">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteDocument(documentItem)}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Box>
      );
    }

    const ttlTabLabel = currentView?.label || projectDetailTab.toUpperCase();

    if (!currentView?.is_loaded) {
      return (
        <Alert
          severity="warning"
          action={
            <Button
              size="small"
              variant="contained"
              onClick={() => handleUploadProjectViewClick(projectDetailTab)}
              disabled={!canCreateProjects || Boolean(uploadingProjectViewKey && uploadingProjectViewKey !== projectDetailTab)}
            >
              {uploadingProjectViewKey === projectDetailTab ? 'Uploading...' : `Upload ${ttlTabLabel}`}
            </Button>
          }
        >
          No {ttlTabLabel} TTL uploaded yet. Please upload it.
        </Alert>
      );
    }

    return (
      <Box sx={{ width: '100%' }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
          {ttlTabLabel} TTL loaded
        </Typography>
        <Typography variant="body2" color="text.secondary">
          File: {currentView.original_filename || '-'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Uploaded by: {currentView.uploaded_by || '-'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Uploaded at: {currentView.uploaded_at
            ? new Date(currentView.uploaded_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })
            : '-'}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleUploadProjectViewClick(projectDetailTab)}
            disabled={!canCreateProjects || Boolean(uploadingProjectViewKey && uploadingProjectViewKey !== projectDetailTab)}
          >
            {uploadingProjectViewKey === projectDetailTab ? 'Uploading...' : `Replace ${ttlTabLabel}`}
          </Button>

          {projectDetailTab === 'vs1' && (
            <Button
              size="small"
              variant="contained"
              onClick={handleApplyVs1}
              disabled={loadingVs1Sections || Boolean(uploadingProjectViewKey)}
            >
              {loadingVs1Sections ? 'Applying...' : 'Apply VS1'}
            </Button>
          )}

          <Button
            size="small"
            variant="outlined"
            startIcon={<AccountTree />}
            onClick={() => setEntityGraphOpen(true)}
          >
            Ver Grafo
          </Button>

          {/* Download original TTL */}
          <Tooltip title="Download original TTL">
            <Button
              size="small"
              variant="outlined"
              startIcon={<Download />}
              onClick={async () => {
                try {
                  const resp = await downloadProjectViewTTL(selectedProject.id, projectDetailTab);
                  const fname = currentView.original_filename || `${projectDetailTab}.ttl`;
                  triggerBlobDownload(resp.data, fname);
                } catch (e) { /* ignore */ }
              }}
            >
              Original
            </Button>
          </Tooltip>

          {/* Download translated TTL — only when available */}
          {currentView.translation_status === 'completed' && (
            <Tooltip title="Download English translation">
              <Button
                size="small"
                variant="outlined"
                color="success"
                startIcon={<Translate />}
                onClick={async () => {
                  try {
                    const resp = await downloadProjectViewTTL(selectedProject.id, projectDetailTab, 'en');
                    const stem = (currentView.original_filename || `${projectDetailTab}.ttl`).replace(/\.ttl$/i, '');
                    triggerBlobDownload(resp.data, `${stem}_en.ttl`);
                  } catch (e) { /* ignore */ }
                }}
              >
                English
              </Button>
            </Tooltip>
          )}
        </Box>

        {/* Translation status chip — only for files uploaded after translation feature */}
        {currentView.translation_status && (() => {
          const ts = currentView.translation_status;
          const cfg = TRANSLATION_STATUS_CHIP[ts] || TRANSLATION_STATUS_CHIP.pending;
          return (
            <Box sx={{ mb: 1.5 }}>
              <Chip size="small" icon={<Translate />} label={cfg.label} color={cfg.color} />
            </Box>
          );
        })()}

        {projectDetailTab === 'vs1' && (
          <Box sx={{ mt: 2.5 }}>
            <Divider sx={{ mb: 2 }} />

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
                mb: 1.5,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                VS1 Sections
              </Typography>

              <ToggleButtonGroup
                size="small"
                exclusive
                value={vs1Source}
                onChange={handleVs1SourceChange}
              >
                <ToggleButton value="project">Project</ToggleButton>
                <ToggleButton value="ontology">Ontology</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {vs1Source === 'project'
                ? 'Objects, Classes, Requirements and Verification Plans'
                : 'Classes, Requirements and Verification Plans'} loaded from {vs1Source === 'project' ? 'Project TTL' : 'Ontology'}.
            </Typography>

            {loadingVs1Sections ? (
              <Box sx={{ py: 1 }}>
                <LinearProgress sx={{ mb: 1 }} />
                <Typography variant="body2" color="text.secondary">Loading VS1 sections...</Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
                  gap: 1.5,
                }}
              >
                {visibleVs1SectionDefinitions.map((section) => renderVs1SectionCard(section))}
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
  };

  const visibleVs1SectionDefinitions = vs1Source === 'ontology'
    ? VS_SECTION_DEFINITIONS.filter((section) => section.key !== 'objects')
    : VS_SECTION_DEFINITIONS;

  const deepDiveSection = visibleVs1SectionDefinitions.find((section) => section.key === deepDiveSectionKey)
    || visibleVs1SectionDefinitions[0]
    || null;

  useEffect(() => {
    if (!deepDiveSection && visibleVs1SectionDefinitions.length > 0) {
      setDeepDiveSectionKey(visibleVs1SectionDefinitions[0].key);
    }
  }, [deepDiveSection, visibleVs1SectionDefinitions]);

  const modalSection = visibleVs1SectionDefinitions.find((section) => section.key === vs1SectionModalKey) || null;
  const modalSectionRows = modalSection ? (vs1Sections?.[modalSection.key] || []) : [];
  const modalPendingValidationCount = modalSectionRows.filter((row) => !isRowValidated(row)).length;
  const renderProjectDialogTopActions = (isFullScreen = false) => (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
      {canCreateProjects && selectedProject && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<DriveFileRenameOutline />}
          onClick={(event) => handleRenameProjectClick(event, selectedProject)}
          disabled={deletingProject || renamingProject}
          sx={isFullScreen
            ? {
              color: '#ffffff',
              borderColor: 'rgba(255,255,255,0.35)',
              '&:hover': {
                borderColor: '#ffffff',
                backgroundColor: 'rgba(255,255,255,0.08)',
              },
            }
            : undefined}
        >
          Rename
        </Button>
      )}
      {canDeleteProjects && selectedProject && (
        <Button
          size="small"
          color="error"
          variant="outlined"
          startIcon={<DeleteOutline />}
          onClick={(event) => handleDeleteProjectClick(event, selectedProject)}
          disabled={deletingProject}
          sx={isFullScreen
            ? {
              borderColor: 'rgba(244,67,54,0.7)',
              color: '#ff8a80',
              '&:hover': {
                borderColor: '#ff8a80',
                backgroundColor: 'rgba(244,67,54,0.15)',
              },
            }
            : undefined}
        >
          Delete
        </Button>
      )}
      <Button
        size="small"
        variant="outlined"
        onClick={handleCloseProjectDialog}
        sx={isFullScreen
          ? {
            color: '#ffffff',
            borderColor: 'rgba(255,255,255,0.35)',
            '&:hover': {
              borderColor: '#ffffff',
              backgroundColor: 'rgba(255,255,255,0.08)',
            },
          }
          : undefined}
      >
        Close
      </Button>
    </Stack>
  );

  return (
    <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', py: { xs: 1, sm: 1.5, lg: 2 } }}>
      <Card elevation={12} sx={{ borderRadius: 3, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <CardHeader
          sx={{
            p: { xs: 1.25, sm: 1.5, md: 1.75 },
            '& .MuiCardHeader-content': { minWidth: 0 },
            '& .MuiCardHeader-action': { m: 0, alignSelf: 'center' },
          }}
          avatar={
            <Avatar sx={{ bgcolor: '#000000', color: '#ffffff' }}>
              <Workspaces />
            </Avatar>
          }
          title={
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Projects
            </Typography>
          }
          subheader={`${projects.length} project TTL file(s) loaded`}
          action={
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Button
                size="small"
                variant="contained"
                startIcon={<CloudUpload />}
                disabled={creatingProject || deletingProject || !canCreateProjects}
                onClick={handleAddProjectClick}
                sx={{ background: '#000000', '&:hover': { background: '#212121' } }}
              >
                {creatingProject ? 'Creating...' : 'Add Project'}
              </Button>
              <Tooltip title="Refresh projects">
                <span>
                  <IconButton onClick={fetchProjects} disabled={loadingProjects || creatingProject}>
                    <Refresh />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          }
        />

        <Divider sx={{ borderColor: 'rgba(0, 0, 0, 0.08)' }} />

        <CardContent sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', p: { xs: 1.25, sm: 1.5, md: 2 }, '&:last-child': { pb: { xs: 1.25, sm: 1.5, md: 2 } } }}>
          {!canCreateProjects && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              You do not have permission to upload new project files.
            </Alert>
          )}

          <Box sx={{ display: 'none' }}>
            <input
              ref={ontologyInputRef}
              id="project-ontology-input"
              type="file"
              accept=".ttl,.turtle,.rdf,.n3"
              style={{ display: 'none' }}
              onChange={handleOntologyFileSelect}
            />

            <input
              ref={projectViewInputRef}
              id="project-view-input"
              type="file"
              accept={pendingProjectViewKey === 'documents' ? '.pdf,.docx' : '.ttl,.turtle,.rdf,.n3'}
              multiple={false}
              style={{ display: 'none' }}
              onChange={handleProjectViewFileSelect}
            />

            <input
              ref={projectViewFolderInputRef}
              id="project-view-folder-input"
              type="file"
              accept=".pdf,.docx"
              multiple
              directory=""
              webkitdirectory=""
              style={{ display: 'none' }}
              onChange={handleProjectViewFileSelect}
            />

            <input
              ref={objectsApplicabilityReviewInputRef}
              id="objects-applicability-review-input"
              type="file"
              accept=".xlsx,.xls"
              multiple
              style={{ display: 'none' }}
              onChange={handleObjectsApplicabilityReviewFileSelect}
            />
          </Box>

          {loadingProjects ? (
            <Box sx={{ py: 4 }}>
              <LinearProgress sx={{ mb: 1.5 }} />
            </Box>
          ) : projects.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 0.5 }}>
                No projects yet
              </Typography>
              <Typography variant="body2" color="text.disabled">
                Create your first project using the button above.
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <Table sx={{ minWidth: 760 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'rgba(0, 0, 0, 0.04)' }}>
                    <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>Project</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>Uploaded By</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>Upload Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'primary.main' }} align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {projects.map((project) => {
                    const metadata = project.data || {};
                    const uploadedBy = project.uploaded_by || metadata.uploaded_by || 'Unknown';
                    const uploadedAt = project.uploaded_at || metadata.uploaded_at;
                    const originalFilename = project.original_filename || metadata.original_filename;

                    return (
                      <TableRow
                        key={project.id}
                        hover
                        onClick={() => handleProjectRowClick(project)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            bgcolor: 'rgba(0, 0, 0, 0.04)',
                          },
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {project.name}
                          </Typography>
                          {originalFilename && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {originalFilename}
                            </Typography>
                          )}
                          <Typography variant="caption" color="primary" display="block">
                            Click to open project views and ontology checklist
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.primary">
                            {uploadedBy}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {uploadedAt
                              ? new Date(uploadedAt).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })
                              : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {project.ontologies_summary ? (
                            <Chip
                              size="small"
                              color={project.ontologies_summary.pending === 0 ? 'success' : 'warning'}
                              label={`${project.ontologies_summary.available ?? 0}/${project.ontologies_summary.required} available`}
                            />
                          ) : (
                            <Chip size="small" variant="outlined" label="Pending ontology setup" />
                          )}
                        </TableCell>
                        <TableCell align="right" onClick={(event) => event.stopPropagation()}>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<DriveFileRenameOutline />}
                              onClick={(event) => handleRenameProjectClick(event, project)}
                              disabled={!canCreateProjects || deletingProject || renamingProject}
                            >
                              Rename
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              variant="outlined"
                              startIcon={<DeleteOutline />}
                              onClick={(event) => handleDeleteProjectClick(event, project)}
                              disabled={!canDeleteProjects || deletingProject}
                            >
                              Delete
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={createProjectDialogOpen}
        onClose={handleCloseCreateProjectDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Project</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Set the project name. After creation, you can upload TTL files for ontologies, classes, requirements and verification plans.
          </Typography>

          <TextField
            autoFocus
            fullWidth
            label="Project Name"
            value={newProjectName}
            onChange={(event) => setNewProjectName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleCreateProject();
              }
            }}
            disabled={creatingProject}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateProjectDialog} disabled={creatingProject}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreateProject} disabled={creatingProject || !newProjectName.trim()}>
            {creatingProject ? 'Creating...' : 'Create Project'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={renameProjectDialogOpen}
        onClose={handleCloseRenameProjectDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Rename Project</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Update the project name.
          </Typography>

          <TextField
            autoFocus
            fullWidth
            label="Project Name"
            value={renameProjectName}
            onChange={(event) => setRenameProjectName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleConfirmRenameProject();
              }
            }}
            disabled={renamingProject}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRenameProjectDialog} disabled={renamingProject}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmRenameProject}
            disabled={renamingProject || !renameProjectName.trim()}
          >
            {renamingProject ? 'Renaming...' : 'Rename'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={applyConfirmation.open}
        onClose={() => setApplyConfirmation({ open: false, kind: '', viewKey: '' })}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderTop: '3px solid',
            borderColor: 'warning.main',
            borderRadius: 2,
            boxShadow: '0 16px 40px rgba(0, 0, 0, 0.18)',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, pb: 1, px: 2.5, pt: 2.25 }}>
          <Box sx={{ display: 'grid', placeItems: 'center', width: 34, height: 34, flexShrink: 0, borderRadius: '50%', bgcolor: 'warning.light', color: 'warning.dark' }}>
            <Info fontSize="small" />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
              {applyConfirmation.kind === 'vs1'
                ? 'Apply VS1 to this project?'
                : `Apply ontologies to ${String(applyConfirmation.viewKey || '').toUpperCase()}?`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Review this action before continuing
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ px: 2.5, py: 1.5 }}>
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
            {selectedProject?.name || 'Selected project'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {applyConfirmation.kind === 'vs1'
              ? 'This will rebuild the project entities and relationships from the VS1 file.'
              : 'This may replace previously applied ontology entities and relationships for this view.'}
          </Typography>
          <Alert severity="warning" sx={{ mt: 1.5, py: 0 }}>
            Make sure the uploaded source files are correct before continuing.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 1, px: 2.5, py: 1.5 }}>
          <Button onClick={() => setApplyConfirmation({ open: false, kind: '', viewKey: '' })} sx={{ minWidth: 96 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={applyConfirmation.kind === 'vs1' ? handleConfirmApply : handleConfirmApplyOntologies}
            sx={{ minWidth: 128 }}
          >
            Apply {applyConfirmation.kind === 'vs1' ? 'VS1' : 'Ontologies'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDeleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Project?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            {projectToDelete
              ? `This will permanently delete project "${projectToDelete.name}" and its uploaded files. This action cannot be undone.`
              : 'This action cannot be undone.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deletingProject}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDeleteProject}
            disabled={deletingProject || !projectToDelete}
          >
            {deletingProject ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDeleteDocumentDialogOpen}
        onClose={() => setConfirmDeleteDocumentDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remove Document from Project?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            {documentToDelete
              ? `This will remove the relation between project "${selectedProject?.name || 'Project'}" and document "${documentToDelete.original_filename || documentToDelete.label}".`
              : 'This will remove the relation between the selected project and document.'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            The document file and its processed data will not be deleted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteDocumentDialogOpen(false)} disabled={deletingDocument}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDeleteDocument}
            disabled={deletingDocument || !documentToDelete}
          >
            {deletingDocument ? 'Removing...' : 'Remove from Project'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={vs1SectionModalOpen}
        onClose={handleCloseVs1SectionModal}
        maxWidth="lg"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            height: { xs: '92dvh', md: 'calc(100dvh - 32px)' },
            maxHeight: { xs: '92dvh', md: 'calc(100dvh - 32px)' },
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Assessment />
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h5" component="div">
                  Analysis Results
                </Typography>
                {modalSection && modalPendingValidationCount > 0 && (
                  <Chip
                    label={`${modalPendingValidationCount} pending validation`}
                    color="warning"
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary">
                {modalSection
                  ? `${selectedProject?.name || 'Project'} - ${modalSection.label}`
                  : (selectedProject?.name || 'Project')}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            p: { xs: 2, lg: 2 },
          }}
        >
          {modalSection ? (
            renderVs1SectionCard(modalSection, { isModalView: true })
          ) : (
            <Alert severity="info">
              Select a section from VS1 to open it in expanded mode.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseVs1SectionModal}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={projectDialogOpen}
        onClose={handleCloseProjectDialog}
        maxWidth="xl"
        fullWidth
        fullScreen={projectDialogFullScreen}
        hideBackdrop={projectDialogFullScreen}
        disableEnforceFocus={projectDialogFullScreen}
        PaperProps={{
          sx: {
            minHeight: projectDialogFullScreen ? '100dvh' : 'min(84dvh, 760px)',
            borderRadius: projectDialogFullScreen ? 0 : undefined,
          },
        }}
      >
        {projectDialogFullScreen ? (
          <DialogTitle
            sx={{
              p: 0,
              background: '#000000',
              color: '#ffffff',
              borderBottom: '1px solid #212121',
            }}
          >
            <Box component="div" sx={{ px: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 64, px: 1.5 }}>
                <Avatar sx={{ bgcolor: '#ffffff', color: '#000000', mr: 2 }}>
                  <DataObject />
                </Avatar>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#ffffff', lineHeight: 1.15 }}>
                    Requirements Platform
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selectedProject ? `Project: ${selectedProject.name}` : 'Project'}
                  </Typography>
                </Box>
                <Box sx={{ mr: 2, display: { xs: 'none', xl: 'block' } }}>
                  {renderProjectDialogTopActions(true)}
                </Box>
                <Chip icon={<Assessment />} label={`${Number(filesCount || 0)} Files`} color="primary" sx={{ mr: 2 }} />
                <Button
                  startIcon={<Home />}
                  variant="outlined"
                  onClick={() => {
                    if (typeof onNavigateHome === 'function') {
                      onNavigateHome();
                    }
                  }}
                  sx={{
                    mr: 2,
                    color: '#ffffff',
                    borderColor: 'rgba(255,255,255,0.35)',
                    '&:hover': {
                      borderColor: '#ffffff',
                      backgroundColor: 'rgba(255,255,255,0.08)'
                    }
                  }}
                >
                  Home
                </Button>
                <IconButton
                  onClick={() => {
                    if (typeof onToggleProcessingPanel === 'function') {
                      onToggleProcessingPanel(true);
                    }
                  }}
                  color="inherit"
                  sx={{ mr: 2 }}
                >
                  <Badge badgeContent={Number(processingQueueCount || 0)} color="primary">
                    <Queue />
                  </Badge>
                </IconButton>
                <IconButton
                  onClick={() => {
                    if (typeof onLogout === 'function') {
                      onLogout();
                    }
                  }}
                  color="inherit"
                >
                  <Logout />
                </IconButton>
              </Box>
            </Box>
            <Box sx={{ px: 2, pb: 1.25, display: { xs: 'block', xl: 'none' } }}>
              {renderProjectDialogTopActions(true)}
            </Box>
          </DialogTitle>
        ) : (
          <DialogTitle sx={{ pb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography variant="h6" component="div">
                {selectedProject ? `Project Ontologies: ${selectedProject.name}` : 'Project Ontologies'}
              </Typography>
              {renderProjectDialogTopActions(false)}
            </Box>
          </DialogTitle>
        )}
        <DialogContent dividers ref={projectDialogContentRef}>
          {selectedProject && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {selectedProject.name}
              </Typography>
            </Box>
          )}

          {projectDialogFullScreen && (
            <Box sx={{ mb: 2 }}>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={projectViewMode}
                onChange={handleProjectViewModeChange}
              >
                <ToggleButton value="upload">Upload</ToggleButton>
                <ToggleButton value="explore">Exploration</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}

          {projectDialogFullScreen && projectViewMode === 'explore' ? (
            <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1,
                  mb: 1.5,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {selectedProject?.name || 'Detailed Project Exploration'}
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={deepDiveSection?.key || ''}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setDeepDiveSectionKey(newValue);
                    }
                  }}
                >
                  {visibleVs1SectionDefinitions.map((section) => (
                    <ToggleButton key={`deep-dive-${section.key}`} value={section.key}>
                      {section.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>

              <Divider sx={{ mb: 1.5 }} />

              {(bootstrappingProjectDetail || loadingVs1Sections) ? (
                <Box sx={{ py: 1 }}>
                  <LinearProgress sx={{ mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">Loading VS1 sections...</Typography>
                </Box>
              ) : deepDiveSection ? (
                <Box sx={{ minHeight: '70vh', display: 'flex' }}>
                  {renderVs1SectionCard(deepDiveSection, { isModalView: true })}
                </Box>
              ) : (
                <Alert severity="info">No section available for exploration.</Alert>
              )}
            </Paper>
          ) : (
            <>
          <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider', mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              Ontologies {projectDetailTab === 'documents' ? '(Documents)' : `(${projectDetailTab.toUpperCase()})`}
            </Typography>

            {projectDetailTab === 'documents' ? (
              <Alert severity="info">Documents view does not require ontology TTL dependencies.</Alert>
            ) : loadingOntologies ? (
              <Box sx={{ py: 2 }}>
                <LinearProgress sx={{ mb: 1.5 }} />
                <Typography color="text.secondary">Loading project ontologies...</Typography>
              </Box>
            ) : !ontologiesSummary?.view_has_ttl ? (
              <Alert severity="warning">
                Upload {projectDetailTab.toUpperCase()} TTL first to discover and validate its ontologies.
              </Alert>
            ) : projectOntologies.length === 0 ? (
              <Alert severity="info">No ontology requirements defined for this project yet.</Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'rgba(0, 0, 0, 0.04)' }}>
                      <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>Ontology</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>Version</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>Availability</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>Loaded File</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'primary.main' }} align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...projectOntologies]
                      .sort((leftOntology, rightOntology) => {
                        const leftInfoOnly = Boolean(
                          leftOntology?.info_only || leftOntology?.upload_allowed === false
                        );
                        const rightInfoOnly = Boolean(
                          rightOntology?.info_only || rightOntology?.upload_allowed === false
                        );

                        if (leftInfoOnly === rightInfoOnly) {
                          return 0;
                        }

                        return leftInfoOnly ? -1 : 1;
                      })
                      .map((ontology) => {
                      const isInfoOnly = Boolean(ontology.info_only || ontology.upload_allowed === false);

                      return (
                        <TableRow key={ontology.key}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {ontology.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {ontology.ontology_id || ontology.key}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {ontology.version || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {ontology.date || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={isInfoOnly ? 'info' : (ontology.is_available ? 'success' : 'default')}
                              label={isInfoOnly ? 'Info only' : (ontology.is_available ? 'Available' : 'Not available')}
                            />
                          </TableCell>
                          <TableCell>
                            {ontology.is_loaded ? (
                              <>
                                <Typography variant="body2">{ontology.original_filename || '-'}</Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {ontology.uploaded_at
                                    ? new Date(ontology.uploaded_at).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })
                                    : '-'}
                                </Typography>
                              </>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                {isInfoOnly
                                  ? (ontology.info_text || 'Informational reference from project TTL')
                                  : 'Not uploaded yet'}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {isInfoOnly ? (
                              <Chip size="small" variant="outlined" color="info" label="No upload" />
                            ) : (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-end' }}>
                                <Button
                                  size="small"
                                  variant={ontology.is_loaded ? 'outlined' : 'contained'}
                                  onClick={() => handleUploadOntologyClick(ontology.key)}
                                  disabled={
                                    !canCreateProjects
                                    || Boolean(uploadingOntologyKey && uploadingOntologyKey !== ontology.key)
                                  }
                                >
                                  {uploadingOntologyKey === ontology.key
                                    ? 'Uploading...'
                                    : ontology.is_loaded
                                      ? 'Replace Ontology'
                                      : 'Upload Ontology'}
                                </Button>
                                {ontology.is_loaded && (
                                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                    <Tooltip title="Download original TTL">
                                      <Button
                                        size="small"
                                        variant="text"
                                        startIcon={<Download fontSize="small" />}
                                        onClick={async () => {
                                          try {
                                            const resp = await downloadProjectOntologyTTL(selectedProject.id, ontology.key, projectDetailTab);
                                            const fname = ontology.original_filename || `${ontology.key}.ttl`;
                                            triggerBlobDownload(resp.data, fname);
                                          } catch (e) { /* ignore */ }
                                        }}
                                      >
                                        Original
                                      </Button>
                                    </Tooltip>
                                    {ontology.translation_status === 'completed' && (
                                      <Tooltip title="Download English translation">
                                        <Button
                                          size="small"
                                          variant="text"
                                          color="success"
                                          startIcon={<Translate fontSize="small" />}
                                          onClick={async () => {
                                            try {
                                              const resp = await downloadProjectOntologyTTL(selectedProject.id, ontology.key, projectDetailTab, 'en');
                                              const stem = (ontology.original_filename || `${ontology.key}.ttl`).replace(/\.ttl$/i, '');
                                              triggerBlobDownload(resp.data, `${stem}_en.ttl`);
                                            } catch (e) { /* ignore */ }
                                          }}
                                        >
                                          English
                                        </Button>
                                      </Tooltip>
                                    )}
                                    {ontology.translation_status && (() => {
                                      const ts = ontology.translation_status;
                                      const cfg = TRANSLATION_STATUS_CHIP[ts] || TRANSLATION_STATUS_CHIP.pending;
                                      return <Chip size="small" icon={<Translate />} label={cfg.label} color={cfg.color} />;
                                    })()}
                                  </Box>
                                )}
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {(ontologiesSummary && ontologiesSummary.view_supports_ontologies !== false) || projectDetailTab !== 'documents' ? (
              <Box
                sx={{
                  mt: 2,
                  width: '100%',
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', lg: '1fr auto 1fr' },
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                {ontologiesSummary && ontologiesSummary.view_supports_ontologies !== false && (
                  <Box sx={{ gridColumn: { xs: 'auto', lg: 1 }, justifySelf: { xs: 'stretch', lg: 'start' }, minWidth: 0 }}>
                    <Chip
                      size="small"
                      color={ontologiesSummary.pending === 0 ? 'success' : 'warning'}
                      label={`${ontologiesSummary.available ?? 0}/${ontologiesSummary.required} ontologies available in DB`}
                    />
                  </Box>
                )}

                {projectDetailTab !== 'documents' && (
                  <Box sx={{ gridColumn: { xs: 'auto', lg: 2 }, justifySelf: { xs: 'stretch', lg: 'center' } }}>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleApplyOntologies}
                      disabled={
                        applyingOntologies
                        || loadingOntologies
                        || Boolean(uploadingOntologyKey)
                        || Boolean(uploadingProjectViewKey)
                        || (projectDetailTab === 'vs1' && loadingVs1Sections)
                      }
                    >
                      {applyingOntologies || loadingOntologies || (projectDetailTab === 'vs1' && loadingVs1Sections)
                        ? 'Applying...'
                        : 'Apply Ontologies'}
                    </Button>
                  </Box>
                )}
              </Box>
            ) : null}
          </Paper>

          <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
            <Tabs
              value={projectDetailTab}
              onChange={handleProjectDetailTabChange}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab value="vs1" label="VS1" />
              <Tab value="vs2" label="VS2" />
              <Tab value="documents" label="Documents" />
            </Tabs>

            <Divider sx={{ my: 1.5 }} />

            <Box sx={{ minHeight: 170, display: 'flex', alignItems: 'flex-start', pt: 0.5 }}>
              {renderProjectDetailTab()}
            </Box>
          </Paper>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={projectDocumentsGraphDialogOpen}
        onClose={handleCloseProjectDocumentsGraphDialog}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: { xs: '98vw', sm: '96vw', md: '95vw' },
            maxWidth: 'none',
            minHeight: '92vh',
            height: '92vh',
            m: { xs: 0.5, sm: 1, md: 1.5 },
          },
        }}
      >
        <DialogTitle>
          {selectedProject ? `Project Graph: ${selectedProject.name}` : 'Project Graph'}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 1.5 }}>
          {projectDocumentsGraphLoading ? (
            <Box sx={{ py: 2 }}>
              <LinearProgress sx={{ mb: 1 }} />
              <Typography variant="body2" color="text.secondary">Loading project graph...</Typography>
            </Box>
          ) : projectDocumentsGraphError ? (
            <Alert severity="error">{projectDocumentsGraphError}</Alert>
          ) : (
            <DocumentsStyleGraphViewer
              graphData={projectDocumentsGraphData}
              emptyMessage="No graph nodes available yet for this project."
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseProjectDocumentsGraphDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={singleDocumentGraphDialogOpen}
        onClose={handleCloseSingleDocumentGraphDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { minHeight: '80vh' } }}
      >
        <DialogTitle>{singleDocumentGraphTitle}</DialogTitle>
        <DialogContent dividers sx={{ p: 1.5 }}>
          {singleDocumentGraphLoading ? (
            <Box sx={{ py: 2 }}>
              <LinearProgress sx={{ mb: 1 }} />
              <Typography variant="body2" color="text.secondary">Loading document graph...</Typography>
            </Box>
          ) : singleDocumentGraphError ? (
            <Alert severity="error">{singleDocumentGraphError}</Alert>
          ) : (
            <DocumentsStyleGraphViewer
              graphData={singleDocumentGraphData}
              emptyMessage="No graph nodes available yet for this document."
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSingleDocumentGraphDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      <EntityGraphDialog
        open={entityGraphOpen}
        onClose={() => setEntityGraphOpen(false)}
        projectId={selectedProject?.id}
        projectName={selectedProject?.name}
      />
    </Box>
  );
};

export default ProjectsTab;
